import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { useDeviceType } from '../../hooks/useDeviceType.ts';

import simplify from 'simplify-js';
import { nanoid } from 'nanoid';
import debounce from '../../utils/debounce';

import { getStroke } from 'perfect-freehand';

// eslint-disable-next-line
import {
    changeCursor,
    addNewElement,
    addNewLine,
    addNewArea,
    addNewColumnInTheEnd,
    addNewPenSegment,
    disableBtnNewTableClick,
    disableBtnNewNoteClick,
    disableBtnExportImage,
    updateAreasPositions,
    updateElementsPositions,
    updateLiveElementsPositions,
    updateFocusedElements,
    updateFocusedGroup,
    updateFocusedLine,
    updateFocusedInsideElement,
    updateScale,
    updateOriginXY,
    updateFocusedArea,
    setNewAreaIdForElement,
    updateTableColumnName,
    updateTableColumnType,
    updateTableHeaderName,
    focusOnColumnBelow,
    focusOnColumnAbove,
    addNewColumnBelow,
    moveColumnBelow,
    moveColumnAbove,
    deleteColumn,
    deleteElements,
    addNewFirstColumn,
    focusOnFirstColumn,
    setIsHistoryClicked,
    disableBtnNewArea,
    enableCanvasPricingPlanLimit,
    disableExportImageClick,
} from '../../redux/diagram/diagram.actions';

// eslint-disable-next-line
import {
    selectThemeMode,
    selectIsBtnNewTableClickEnabled,
    selectIsBtnNewTableGrabEnabled,
    selectIsBtnNewNoteClickEnabled,
    selectIsBtnNewNoteGrabEnabled,
    selectIsBtnNewLineEnabled,
    selectIsBtnNewAreaEnabled,
    selectIsBtnPenEnabled,
    selectIsCursorHand,
    selectIsHistoryClicked,
    selectLines,
    selectElements,
    selectElementsChanges,
    selectAreas,
    selectFocusedElementsIds,
    selectFocusedLineId,
    selectFocusedAreaId,
    selectFocusedInsideElement,
    selectFocusedGroupElementsIds,
    selectFocusedGroupAreasIds,
    selectScale,
    selectOriginXY,
    selectIsBtnExportImageEnabled,
    selectIsBtnRedGreenViewEnabled,
    selectIsBtnSearchEnabled,
    selectViewMode,
    selectIsBtnMinimapEnabled,
    selectCanvasLimits,
    selectNewPenProps,
    selectIsBtnExportImageClickEnabled,
} from '../../redux/diagram/diagram.selectors';

import { themes } from '../../utils/themes';

import ContextMenu from '../ContextMenu/ContextMenu';
import { _buildLinePath, _drawLinePath } from './CanvasLine';
import EditableTableColumn from '../EditableElements/EditableTableColumn/EditableTableColumn';
import EditableTableHeader from '../EditableElements/EditableTableHeader/EditableTableHeader';

import LoaderBar from '../Loaders/LoaderBar/LoaderBar';

// import TableEditableText from './TableEditableText'; // with redrawed full table, can be used for 'note' element
// import TableEditableText2 from './TableEditableText2';

import './Canvas.scss';


let tables_limit_free = 40;
let columns_limit_free = 20;
let notes_limit_free = 20;
let pens_limit_free = 100;
let areas_limit_free = 10;

let tables_limit_max = 400;
let columns_limit_max = 80;
let notes_limit_max = 200;
let pens_limit_max = 400;
let areas_limit_max = 200;



function checkTables(elementsValue, addCount = 1, isP = false) {
    let tablesLimit = isP ? tables_limit_max : tables_limit_free;
    let tablesCount = 0;

    for (const key in elementsValue) {
        const element = elementsValue[key];

        switch (element.type) {
            case 'table': {
                tablesCount++;

                if ((tablesCount + addCount) > tablesLimit) {
                    return false;
                }
                break;
            }
            default:
                break;
        }
    }
    return true;
}

function checkColumns(elementsValue, elementId, addCount = 1, isP = false) {
    let columnsLimit = isP ? columns_limit_max : columns_limit_free;
    if (elementId) {
        const element = elementsValue[elementId];
        const columns = element?.value?.columns;

        if (columns && (Object.keys(columns)?.length + addCount) > columnsLimit) {
            return false;
        }
    }
    return true;
}

function checkNotes(elementsValue, addCount = 1, isP = false) {
    let notesLimit = isP ? notes_limit_max : notes_limit_free;
    let notesCount = 0;

    for (const key in elementsValue) {
        const element = elementsValue[key];

        switch (element.type) {
            case 'note': {
                notesCount++;

                if ((notesCount + addCount) > notesLimit) {
                    return false;
                }
                break;
            }
            default:
                break;
        }
    }
    return true;
}

function checkPens(elementsValue, addCount = 1, isP = false) {
    let pensLimit = isP ? pens_limit_max : pens_limit_free;
    let pensCount = 0;

    for (const key in elementsValue) {
        const element = elementsValue[key];

        switch (element.type) {
            case 'pen-segment': {
                pensCount++;

                if ((pensCount + addCount) > pensLimit) {
                    return false;
                }
                break;
            }
            default:
                break;
        }
    }
    return true;
}

function checkAreas(areasValue, addCount = 1, isP = false) {
    let areasLimit = isP ? areas_limit_max : areas_limit_free;
    if (areasValue && (Object.keys(areasValue)?.length + addCount) > areasLimit) {
        return false;
    }
    return true;
}

const TABLE_HEADER_HEIGHT = 40;
const TABLE_ROW_HEIGHT = 36; // prev value 28 with font 13(not 16), and after 30
const TABLE_BORDER_RADIUS = 4;

const HEADER_BODY_MARGIN = 4;

const NEW_TABLE_WIDTH = 320;
const NEW_TABLE_HEIGHT = 84;

const NEW_NOTE_WIDTH = 360;
const NEW_NOTE_HEIGHT = 190;
// const NEW_NOTE_WIDTH = 360;
// const NEW_NOTE_HEIGHT = 360;

const TABLE_NAME_WIDTH = 250;
const TABLE_NAME_HEIGHT = 100;
// eslint-disable-next-line
const LETTERS_MAP = {
    _: {
        width: 0,
    },
    1: {
        width: 0,
    },
    2: {
        width: 0,
    },
    3: {
        width: 0,
    },
    4: {
        width: 0,
    },
    5: {
        width: 0,
    },
    6: {
        width: 0,
    },
    7: {
        width: 0,
    },
    8: {
        width: 0,
    },
    9: {
        width: 0,
    },
    0: {
        width: 0,
    },
    a: {
        width: 9.3,
    },
    b: {
        width: 10.7,
    },
    c: {
        width: 10.0,
    },
    d: {
        width: 10.3, // 10.25
    },
    e: {
        width: 10.2,
    },
    f: {
        width: 6.8,
    },
    g: {
        width: 10.3, // 10.25
    },
    h: {
        width: 9.85,
    },
    i: {
        width: 3.5,
    },
    j: {
        width: 3.55,
    },
    k: {
        width: 10.15,
    },
    l: {
        width: 3.25,
    },
    m: {
        width: 15.25,
    },
    n: {
        width: 9.8,
    },
    o: {
        width: 10.5,
    },
    p: {
        width: 10.7,
    },
    q: {
        width: 10.3,
    },
    r: {
        width: 6.8,
    },
    s: {
        width: 9.1,
    },
    t: {
        width: 5.9,
    },
    u: {
        width: 9.9,
    },
    v: {
        width: 10.2,
    },
    w: {
        width: 15.0,
    },
    x: {
        width: 9.8,
    },
    y: {
        width: 10.2,
    },
    z: {
        width: 9.45,
    },
};

function getIndexNumber(columns, columnId) {
    let newInsideElementIndex = 0;
    const keys = Object.keys(columns);

    for (let i = 0; i < keys.length; i++) {
        if (keys[i] === columnId) {
            newInsideElementIndex = i;
            break;
        }
    }
    return newInsideElementIndex;
}

function calculateScaleProgress(value) {
    const SCALE_MIN = 0.25;
    const SCALE_MAX = 5.0;
    return ((value - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100;
}
function calculateResizeHandleCoefValue(percents) {
    const COEF_MIN = 0.6;
    const COEF_MAX = 1.5;
    return (percents / 100) * (COEF_MAX - COEF_MIN) + COEF_MIN;
}
function calculateResizeOffsetValue(percents) {
    const COEF_MIN = 0.4;
    const COEF_MAX = 5;
    return (percents / 100) * (COEF_MAX - COEF_MIN) + COEF_MIN;
}
// resize coordinates format = { nw: { x, y }, ne: { x,y }, sw: { x, y }, se: { x, y } };
function calculateItemResizeCoordinates(item, resizeHandleSize, outlineOffset) {
    const itemFrameOffset = outlineOffset;
    const width = resizeHandleSize;

    const { x, y, width: itemWidth, height: itemHeight } = item;

    const itemFrameWidth = itemWidth + itemFrameOffset * 2;
    const itemFrameHeight = itemHeight + itemFrameOffset * 2;
    const resizeX = x - itemFrameOffset;
    const resizeY = y - itemFrameOffset;

    const resizeCoordinates = {
        width,
        points: {
            nw: {
                x: resizeX - width / 2,
                y: resizeY - width / 2,
            },
            ne: {
                x: resizeX + itemFrameWidth - width / 2,
                y: resizeY - width / 2,
            },
            sw: {
                x: resizeX - width / 2,
                y: resizeY + itemFrameHeight - width / 2,
            },
            se: {
                x: resizeX + itemFrameWidth - width / 2,
                y: resizeY + itemFrameHeight - width / 2,
            },
        },
        lines: {
            w: {
                x: { value: resizeX },
                y: {
                    min: resizeY + width / 2,
                    max: resizeY + itemFrameHeight - width / 2,
                },
            },
            e: {
                x: { value: resizeX + itemFrameWidth },
                y: {
                    min: resizeY + width / 2,
                    max: resizeY + itemFrameHeight - width / 2,
                },
            },
            n: {
                x: {
                    min: resizeX + width / 2,
                    max: resizeX + itemFrameWidth - width / 2,
                },
                y: { value: resizeY },
            },
            s: {
                x: {
                    min: resizeX + width / 2,
                    max: resizeX + itemFrameWidth - width / 2,
                },
                y: { value: resizeY + itemFrameHeight },
            },
        },
    };

    return resizeCoordinates;
}
function calculateOutlineWidth(scaleValue) {
    if (scaleValue > 4.5) {
        return 0.55 + 1;
    } else if (scaleValue > 3.5) {
        return 0.65 + 1;
    } else if (scaleValue > 2.5) {
        return 0.75 + 1;
    } else if (scaleValue > 1.5) {
        return 1 + 1;
    } else if (scaleValue > 1.2) {
        return 1.2 + 1;
    } else if (scaleValue > 1) {
        return 1.3 + 1;
    } else if (scaleValue > 0.75) {
        return 1.5 + 1;
    } else if (scaleValue > 0.6) {
        return 1.75 + 1;
    } else if (scaleValue > 0.5) {
        return 2 + 1;
    } else if (scaleValue > 0.35) {
        return 2.5;
    } else if (scaleValue > 0.25) {
        return 3;
    } else if (scaleValue > 0.15) {
        return 5;
    } else if (scaleValue > 0.12) {
        return 8;
    } else if (scaleValue > 0.1) {
        return 10;
    } else if (scaleValue > 0.08) {
        return 15;
    } else if (scaleValue > 0.07) {
        return 20;
    } else if (scaleValue > 0.06) {
        return 25;
    } else if (scaleValue > 0.04) {
        return 30;
    } else if (scaleValue > 0.01) {
        return 40;
    } else {
        return 50;
    }
}

function getSelectedElements(startXY, currentXY, elementsValue, viewMode) {
    if (viewMode === 'keys') {
        return getSelectedElementsForViewKeys(
            startXY,
            currentXY,
            elementsValue
        );
    }

    const minX = Math.min(startXY.x, currentXY.x);
    const minY = Math.min(startXY.y, currentXY.y);
    const maxX = Math.max(startXY.x, currentXY.x);
    const maxY = Math.max(startXY.y, currentXY.y);
    return Object.keys(elementsValue).filter((key) => {
        const { x, y, width, height } = elementsValue[key];

        return x + width > minX && x < maxX && y + height > minY && y < maxY;
    });
}

function getSelectedElementsForViewKeys(startXY, currentXY, elementsValue) {
    const minX = Math.min(startXY.x, currentXY.x);
    const minY = Math.min(startXY.y, currentXY.y);
    const maxX = Math.max(startXY.x, currentXY.x);
    const maxY = Math.max(startXY.y, currentXY.y);
    return Object.keys(elementsValue).filter((key) => {
        const element = elementsValue[key];

        if (element.type === 'table') {
            const {
                x,
                y,
                width,
                value: { columns },
            } = element;

            const columnsKeys = Object.keys(columns).filter(
                (columnKey) =>
                    columns[columnKey]?.isPK ||
                    columns[columnKey]?.isFK ||
                    columns[columnKey]?.isRelated
            );
            const columnsCount = columnsKeys?.length || 0;
            const tableHeight =
                TABLE_HEADER_HEIGHT +
                HEADER_BODY_MARGIN +
                (columnsCount + 1) * TABLE_ROW_HEIGHT +
                TABLE_BORDER_RADIUS +
                1;

            return (
                x + width > minX &&
                x < maxX &&
                y + tableHeight > minY &&
                y < maxY
            );
        } else {
            const { x, y, width, height } = element;

            return (
                x + width > minX && x < maxX && y + height > minY && y < maxY
            );
        }
    });
}

function getSelectedAreas(startXY, currentXY, areasValue) {
    const minX = Math.min(startXY.x, currentXY.x);
    const minY = Math.min(startXY.y, currentXY.y);
    const maxX = Math.max(startXY.x, currentXY.x);
    const maxY = Math.max(startXY.y, currentXY.y);

    return Object.keys(areasValue).filter((key) => {
        const { x, y, width, height } = areasValue[key];
        return (x >= minX && y >= minY && x + width <= maxX && y + height <= maxY);
    });
}

function getAreaSelectedElements(
    startXY,
    currentXY,
    elementsValue,
    areasValue,
    viewMode
) {
    if (viewMode === 'keys') {
        return getAreaSelectedElementsForViewKeys(
            startXY,
            currentXY,
            elementsValue,
            areasValue
        );
    }

    const otherAreasSelectedElementsIds = [];

    if (areasValue) {
        for (const key in areasValue) {
            const area = areasValue[key];
            const {
                value: { nestedElementsIds },
            } = area;
            otherAreasSelectedElementsIds.push(...nestedElementsIds);
        }
    }

    const minX = Math.min(startXY.x, currentXY.x);
    const minY = Math.min(startXY.y, currentXY.y);
    const maxX = Math.max(startXY.x, currentXY.x);
    const maxY = Math.max(startXY.y, currentXY.y);
    return Object.keys(elementsValue).filter((key) => {
        const { id, x, y, width, height } = elementsValue[key];

        return (
            x + width > minX &&
            x < maxX &&
            y + height > minY &&
            y < maxY &&
            !otherAreasSelectedElementsIds.includes(id)
        );
    });
}

function getAreaSelectedElementsForViewKeys(
    startXY,
    currentXY,
    elementsValue,
    areasValue
) {
    const otherAreasSelectedElementsIds = [];
    if (areasValue) {
        for (const key in areasValue) {
            const area = areasValue[key];
            const {
                value: { nestedElementsIds },
            } = area;
            otherAreasSelectedElementsIds.push(...nestedElementsIds);
        }
    }

    const minX = Math.min(startXY.x, currentXY.x);
    const minY = Math.min(startXY.y, currentXY.y);
    const maxX = Math.max(startXY.x, currentXY.x);
    const maxY = Math.max(startXY.y, currentXY.y);
    return Object.keys(elementsValue).filter((key) => {
        const element = elementsValue[key];

        if (element.type === 'table') {
            const {
                id,
                x,
                y,
                width,
                value: { columns },
            } = element;

            const columnsKeys = Object.keys(columns).filter(
                (columnKey) =>
                    columns[columnKey]?.isPK ||
                    columns[columnKey]?.isFK ||
                    columns[columnKey]?.isRelated
            );
            const columnsCount = columnsKeys?.length || 0;
            const tableHeight =
                TABLE_HEADER_HEIGHT +
                HEADER_BODY_MARGIN +
                (columnsCount + 1) * TABLE_ROW_HEIGHT +
                TABLE_BORDER_RADIUS +
                1;

            return (
                x + width > minX &&
                x < maxX &&
                y + tableHeight > minY &&
                y < maxY &&
                !otherAreasSelectedElementsIds.includes(id)
            );
        } else {
            const { id, x, y, width, height } = element;

            return (
                x + width > minX &&
                x < maxX &&
                y + height > minY &&
                y < maxY &&
                !otherAreasSelectedElementsIds.includes(id)
            );
        }
    });
}






function getAreaSelectedElementsForResize(
    startXY,
    currentXY,
    elementsValue,
    areasValue,
    viewMode,
    areaId
) {
    if (viewMode === 'keys') {
        return getAreaSelectedElementsForViewKeysForResize(
            startXY,
            currentXY,
            elementsValue,
            areasValue,
            areaId
        );
    }

    const otherAreasSelectedElementsIds = [];

    if (areasValue) {
        for (const key in areasValue) {
            const area = areasValue[key];
            const { value: { nestedElementsIds } } = area;

            if (area.id !== areaId) {
                otherAreasSelectedElementsIds.push(...nestedElementsIds);
            }
        }
    }

    const minX = Math.min(startXY.x, currentXY.x);
    const minY = Math.min(startXY.y, currentXY.y);
    const maxX = Math.max(startXY.x, currentXY.x);
    const maxY = Math.max(startXY.y, currentXY.y);
    return Object.keys(elementsValue).filter((key) => {
        const { id, x, y, width, height } = elementsValue[key];

        return (
            x + width > minX &&
            x < maxX &&
            y + height > minY &&
            y < maxY &&
            !otherAreasSelectedElementsIds.includes(id)
        );
    });
}
function getAreaSelectedElementsForViewKeysForResize(
    startXY,
    currentXY,
    elementsValue,
    areasValue,
    areaId
) {
    const otherAreasSelectedElementsIds = [];
    if (areasValue) {
        for (const key in areasValue) {
            const area = areasValue[key];
            const { value: { nestedElementsIds } } = area;

            if (area.id !== areaId) {
                otherAreasSelectedElementsIds.push(...nestedElementsIds);
            }
        }
    }

    const minX = Math.min(startXY.x, currentXY.x);
    const minY = Math.min(startXY.y, currentXY.y);
    const maxX = Math.max(startXY.x, currentXY.x);
    const maxY = Math.max(startXY.y, currentXY.y);
    return Object.keys(elementsValue).filter((key) => {
        const element = elementsValue[key];

        if (element.type === 'table') {
            const {
                id,
                x,
                y,
                width,
                value: { columns },
            } = element;

            const columnsKeys = Object.keys(columns).filter(
                (columnKey) =>
                    columns[columnKey]?.isPK ||
                    columns[columnKey]?.isFK ||
                    columns[columnKey]?.isRelated
            );
            const columnsCount = columnsKeys?.length || 0;
            const tableHeight =
                TABLE_HEADER_HEIGHT +
                HEADER_BODY_MARGIN +
                (columnsCount + 1) * TABLE_ROW_HEIGHT +
                TABLE_BORDER_RADIUS +
                1;

            return (
                x + width > minX &&
                x < maxX &&
                y + tableHeight > minY &&
                y < maxY &&
                !otherAreasSelectedElementsIds.includes(id)
            );
        } else {
            const { id, x, y, width, height } = element;

            return (
                x + width > minX &&
                x < maxX &&
                y + height > minY &&
                y < maxY &&
                !otherAreasSelectedElementsIds.includes(id)
            );
        }
    });
}




// function scrollIfNeeded(e) {
//     const margin = 50; // margin to start scrolling
//     if (e.clientX < margin) {
//       window.scrollBy(-10, 0);
//       offsetX = Math.max(0, offsetX - 10);
//     } else if (e.clientX > window.innerWidth - margin) {
//       window.scrollBy(10, 0);
//       offsetX = Math.min(canvas.width - window.innerWidth, offsetX + 10);
//     }
//     if (e.clientY < margin) {
//       window.scrollBy(0, -10);
//       offsetY = Math.max(0, offsetY - 10);
//     } else if (e.clientY > window.innerHeight - margin) {
//       window.scrollBy(0, 10);
//       offsetY = Math.min(canvas.height - window.innerHeight, offsetY + 10);
//     }
//   }

const arraysAreEqual = (array1, array2) => {
    if (typeof array1 !== 'object' || typeof array2 !== 'object') {
        return false;
    }

    if (array1.length !== array2.length) {
        return false;
    }

    const sortedArray1 = array1.slice().sort();
    const sortedArray2 = array2.slice().sort();

    for (let i = 0; i < sortedArray1.length; i++) {
        if (sortedArray1[i] !== sortedArray2[i]) {
            return false;
        }
    }

    return true;
};

function drawTextWithLetterSpacing(ctx, text, x, y, letterSpacing, textAlign) {
    // Зберігаємо початкові координати X
    let currentX = x;
    ctx.textAlign = textAlign;

    // Проходимо через кожен символ у тексті
    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        // Малюємо символ
        ctx.fillText(char, currentX, y);

        // Розраховуємо ширину символу та додаємо інтервал між літерами
        const charWidth = ctx.measureText(char).width;
        currentX += charWidth + letterSpacing;
    }
}
function drawTextRight(ctx, text, x, y, letterSpacing) {
    let startX = x;
    let currentX = startX;
    ctx.textAlign = 'left';

    // Проходимо через кожен символ у тексті в зворотньому порядку
    for (let i = text.length - 1; i >= 0; i--) {
        const char = text[i];
        // Розраховуємо ширину символу та зменшуємо currentX
        const charWidth = ctx.measureText(char).width;
        currentX -= charWidth + letterSpacing;

        // Малюємо символ
        ctx.fillText(char, currentX, y);
    }
}
// function drawText(ctx, text, x, y, letterSpacing, textAlign = 'left') {
//     // Визначаємо ширину всього тексту з урахуванням інтервалів між літерами
//     const totalWidth = ctx.measureText(text).width + (text.length - 1) * letterSpacing;

//     // Розраховуємо початкову координату X залежно від вирівнювання
//     let startX = x;
//     if (textAlign === 'center') {
//         startX = x - totalWidth / 2;
//     } else if (textAlign === 'right') {
//         startX = x - totalWidth;
//     }

//     // Зберігаємо початкові координати X
//     let currentX = startX;

//     // Проходимо через кожен символ у тексті
//     for (let i = 0; i < text.length; i++) {
//         const char = text[i];
//         // Малюємо символ
//         ctx.fillText(char, currentX, y);

//         // Розраховуємо ширину символу та додаємо інтервал між літерами
//         const charWidth = ctx.measureText(char).width;
//         currentX += charWidth + letterSpacing;
//     }
// }

const average = (a, b) => (a + b) / 2;

const getSvgPathFromStroke = (points) => {
    let p1 = points[0];
    let p2 = points[1];
    const p3 = points[2];

    let result = `M${p1[0].toFixed(2)}, ${p1[1].toFixed(2)} Q${p2[0].toFixed(
        2
    )}, ${p2[1].toFixed(2)} ${average(p2[0], p3[0]).toFixed(2)}, ${average(
        p2[1],
        p3[1]
    ).toFixed(2)} T`;

    for (let i = 2, max = points.length - 1; i < max; i++) {
        p1 = points[i];
        p2 = points[i + 1];
        result += `${average(p1[0], p2[0]).toFixed(2)},${average(
            p1[1],
            p2[1]
        ).toFixed(2)} `;
    }
    return result;
};

const Canvas = React.forwardRef(({ minimapRef }, ref) => {
    // dispatch
    const dispatch = useDispatch();
    const { isDesktop } = useDeviceType(); // mobile/tablet => false

    // selectors
    const themeMode = useSelector(selectThemeMode);
    let theme = themes[`${themeMode ? 'light' : 'dark'}`];

    const clickTimesRef = useRef([]);

    const alignmentLinesRef = useRef(null);
    const visibleElementsIdsRef = useRef([]);
    const visibleLinesIdsRef = useRef([]);
    const visibleViewportElementsIdsRef = useRef([]);
    const sumOriginDxyRef = useRef({ dx: 0, dy: 0 });


    // const exportImgCanvasRef = useRef(null);
    // const exportImgContextRef = useRef(null);

    // ===================== 0. NEW AREA | START XY / CURRENT XY =====================
    const newAreaPropsRef = useRef(null); // { startX: 0, startY: 0, endX: 0, endY: 0 }

    // ===================== 0. RESIZE AREA | START XY / CURRENT XY =====================
    const resizeAreaPropsRef = useRef(null); // { selectedElements }

    // ===================== 0. RESIZE AREA | START XY / CURRENT XY =====================
    const resizeElementPropsRef = useRef(null); // { elementId, areaId }

    // is selection started
    // const isSelectionClickedRef = useRef(false); // ?? try to use isBoardClickedRef & isCursorHand

    const canvasLimits = useSelector(selectCanvasLimits);
    const canvasLimitsRef = useRef(null);

    const minimapClickedXYRef = useRef(null);
    // const minimapZoomRef = useRef(null);
    const minimapZoomRef = useRef({
        zoom: 1,
        originX: 1,
        originY: 1,
    });
    // minimapZoomRef.current = {
    // 	zoom: newZoom,
    // 	originX: newOriginX,
    // 	originY: newOriginY,
    // };

    const isExportImageClickedRef = useRef(false);

    const selectedElementsIdsRef = useRef([]);
    const selectedAreasIdsRef = useRef([]);

    const isUnfocusElementsBeforeRef = useRef(false);

    const scale = useSelector(selectScale);
    const originXY = useSelector(selectOriginXY);

    const elementsRedux = useSelector(selectElements);
    const elementsChangesRedux = useSelector(selectElementsChanges);
    const linesRedux = useSelector(selectLines);
    const areasRedux = useSelector(selectAreas);

    const focusedElementsIds = useSelector(selectFocusedElementsIds);
    const focusedLineId = useSelector(selectFocusedLineId);

    const focusedAreaId = useSelector(selectFocusedAreaId);

    const focusedGroupElementsIds = useSelector(selectFocusedGroupElementsIds);
    const focusedGroupAreasIds = useSelector(selectFocusedGroupAreasIds);

    const focusedInsideElementRedux = useSelector(selectFocusedInsideElement);

    const isCursorHand = useSelector(selectIsCursorHand);

    const isHistoryClicked = useSelector(selectIsHistoryClicked);

    const isBtnMinimapEnabled = useSelector(selectIsBtnMinimapEnabled);

    const isBtnNewTableClickEnabled = useSelector(
        selectIsBtnNewTableClickEnabled
    );
    const isBtnNewTableGrabEnabled = useSelector(
        selectIsBtnNewTableGrabEnabled
    );
    const isNewTableGrabStartedRef = useRef(false);

    const isBtnNewNoteClickEnabled = useSelector(
        selectIsBtnNewNoteClickEnabled
    );
    const isBtnNewNoteGrabEnabled = useSelector(selectIsBtnNewNoteGrabEnabled);
    const isNewNoteGrabStartedRef = useRef(false);

    const isBtnNewLineEnabled = useSelector(selectIsBtnNewLineEnabled);

    const isBtnNewAreaEnabled = useSelector(selectIsBtnNewAreaEnabled);

    const isBtnExportImageEnabled = useSelector(selectIsBtnExportImageEnabled);
    const isBtnExportImageClickEnabled = useSelector(selectIsBtnExportImageClickEnabled);

    const isBtnPenEnabled = useSelector(selectIsBtnPenEnabled);
    const newPenProps = useSelector(selectNewPenProps);

    const isBtnSearchEnabled = useSelector(selectIsBtnSearchEnabled);

    const isBtnRedGreenViewEnabled = useSelector(
        selectIsBtnRedGreenViewEnabled
    );

    const viewMode = useSelector(selectViewMode);

    const [isStateUpdated, setIsStateUpdated] = useState(false);

    const [isHistoryClickedState, setIsHistoryClickedState] = useState(false);

    const exportImageBoxPropsRef = useRef({
        isPressed: false,
        x: 0,
        y: 0,
        boxWidth: 0,
        boxHeight: 0,
    });
    // TODO:
    // - if line is focused, then unfocus other elements on hover

    // ===================== 0. ELEMENTS HAS CHANGES | STATE | UPDATE LIVE ELEMENTS =====================
    // has changes from last previous value focusedElementsIdsRef, if yes then update redis store
    const [elementsHasChanges, setElementsHasChanges] = useState(false);

    const [columnNameState, setColumnNameState] = useState('');
    const [columnTypeState, setColumnTypeState] = useState('');
    const [columnState, setColumnState] = useState(null);

    // ===================== 0. ELEMENTS AND LINES | WITH REDIS =====================
    // elements, updates from/to redis store value
    const elementsRef = useRef({});

    // elements changes, updates from/to redis store value
    const elementsChangesRef = useRef({});

    // lines, updates from/to redis store value
    const linesRef = useRef({});

    // areas, updates from/to redis store value
    const areasRef = useRef({});

    // ===================== 0. FOCUSED ELEMENTS AND LINES | WITH REDIS =====================
    // focused elements ids, debounce updates from/to redis store value
    const focusedElementsIdsRef = useRef([]);

    // focused lines ids, debounce updates from/to redis store value
    const focusedLineIdRef = useRef(null);

    // focused areas ids, debounce updates from/to redis store value
    const focusedAreaIdRef = useRef(null);

    // focused group, areas ids, when its selected together with elements
    const focusedGroupAreasIdsRef = useRef([]);
    // focused group, elements ids, when its selected together with areas
    const focusedGroupElementsIdsRef = useRef([]);

    // ===================== 0. FOCUSED INSIDE ELEMENT =====================
    // focused inside element
    const [focusedInsideElementState, setFocusedInsideElementState] =
        useState(null);

    // is focused inside element changed
    const [isFocusedInsideElementChanged, setIsFocusedInsideElementChanged] =
        useState(false);

    // ===================== 0. RELATED ELEMENTS FOR FOCUSED ELEMENTS AND LINES =====================
    // related or connected elements/lines to 'focusedElementsIdsRef'
    const focusedElementsRelatedPropsRef = useRef({
        elementsIds: [],
        insideElementsIds: [],
        linesIds: [],
    });

    // related or connected elements to 'focusedLineIdRef'
    const focusedLineRelatedPropsRef = useRef({
        elementsIds: [],
        insideElementsIds: [],
    });

    // ===================== 0. HOVERED ELEMENT AND LINE =====================
    // hovered element
    const hoveredElementIdRef = useRef(null); // null | id

    // hovered insideElement
    const hoveredInsideElementRef = useRef(null); // null | { id, index }

    // hovered element header
    const hoveredElementIdHeaderRef = useRef(null); // null | id

    // hovered element column
    const hoveredColumnIdRef = useRef(null); // null | id
    const hoveredColumnIndexRef = useRef(null); // null | id

    // hovered line
    const hoveredLineIdRef = useRef(null); // null | id

    // hovered empty column
    const hoveredElementIdEmptyColumnRef = useRef(null); // null | id

    // hovered area
    const hoveredAreaIdRef = useRef(null); // null | id

    // ===================== 0. RELATED ELEMENTS FOR HOVERED ELEMENT AND LINE =====================
    // related or connected elements/lines to 'hoveredElementIdRef'
    const hoveredElementRelatedPropsRef = useRef({
        elementsIds: [],
        insideElementsIds: [],
        linesIds: [],
    });

    // related or connected elements to 'hoveredLineIdRef'
    const hoveredLineRelatedPropsRef = useRef({
        elementsIds: [],
        insideElementsIds: [],
    });

    // ===================== 0. NEW LINE 'FROM' / 'TO'  =====================
    const lineFromRef = useRef(null);
    const lineToRef = useRef(null);

    // ===================== 0. HOVERED CIRCLE OF ELEMENT  =====================
    // hovered circle for add new internal element in the end
    const hoveredCircleRef = useRef(null); // { elementId }

    // hovered circle area for show/hide 'circle' for add new internal element in the end
    const hoveredCircleAreaRef = useRef(null); // { elementsIds }

    // ===================== 0. HOVERED POINT OF ELEMENT  =====================
    // hovered point for add new line from element
    const hoveredPointRef = useRef(null); // { insideElementId, direction }

    // hovered point area for show/hide 'point' for add new line from element
    const hoveredPointAreaRef = useRef(null); // { insideElementsIds }

    // ===================== 0. CLICKED ELEMENT AND LINE =====================
    const alignmentLinePropsRef = useRef({});

    // clicked element by 'mousedown'. clear on 'click'
    const clickedElementIdRef = useRef(null);

    // clicked inside element inside main element by 'mousedown'. clear on 'click'
    const clickedInsideElementRef = useRef(null);

    // clicked line
    const clickedLineIdRef = useRef(null);

    // clicked area
    const clickedAreaIdRef = useRef(null);

    // ===================== 0. CLICKED CIRCLE OF ELEMENT =====================
    // clicked circle to add new row
    const clickedCircleElementIdRef = useRef(null);

    // ===================== 0. CLICKED POINT OF ELEMENT =====================
    // clicked column's point to add new line
    const clickedPointRef = useRef(null); // { elementId, insideElementId, insideElementIndex, direction });

    //??? ===================== 0.  =====================
    const [isStartedLineState, setIsStartedLineState] = useState(false);

    // ===================== 0. CANVAS =====================
    const canvasWrapRef = useRef(null); // don't need anymore
    const contextRef = useRef(null);

    const newPenCursorPointRef = useRef(null);

    const newPenSegmentRef = useRef({
        isPressed: false,
        points: [],
    });

    // ===================== 1. BOARD - ORIGIN XY / SCALE =====================
    // board origin {x, y} - offsets when you drag & move board | = (e.clientX)
    const originXYRef = useRef({ x: 0, y: 300 });

    // board scale
    const scaleRef = useRef(scale);

    // is board scaling right now | timer for update isScaling state after pause 200ms
    const [isScaling, setIsScaling] = useState(false);
    const scalingTimerRef = useRef(null);

    const scalingRedrawTimerRef = useRef(null);
    const scalingRedrawCounterRef = useRef(0);

    // ===================== 2. SCALE NUMBERS REF =====================
    // saved to use without recalculating in render/distances/drawTable/drawNote
    const scaleNumbersRef = useRef({
        outlineOffset: 0,
        outlineWidth: 0,
        resizeHandleSize: 0,
        resizeHandleRadius: 0,
    });

    // ===================== 2. ELEMENTS MOVING - START XY =====================
    // start {x,y} of mouse on 'mousedown' event | = (e.clientX) | CHANGABLE ON MOUSEMOVE
    const startXYRef = useRef({ x: 0, y: 0 });

    // ===================== 3. MOUSE SELECTION BOX - START XY / CURRENT XY =====================
    // is selection started
    const isSelectionClickedRef = useRef(false); // ?? try to use isBoardClickedRef & isCursorHand

    // selection start {x,y} of mouse on 'mousedown' event | = ((e.clientX - originXY.x) / scale)
    const selectionStartXYRef = useRef({ x: 0, y: 0 });

    // selection current {x,y} of mouse on 'mousemove' event | = ((e.clientX - originXY.x) / scale)
    const selectionCurrentXYRef = useRef({ x: 0, y: 0 });

    // ===================== 4. BOARD MOVING =====================
    // is board clicked by 'mousedown'
    const isBoardClickedRef = useRef(false);

    // is element resizing
    const [isElementResizing, setIsElementResizing] = useState(false);

    // is board moving right now | timer for update isBoardMoving state after pause 200ms
    const [isBoardMoving, setIsBoardMoving] = useState(false);
    const boardMovingTimerRef = useRef(null);

    // is board started move by 'mousemove'
    const [isBoardMovingStarted, setIsBoardMovingStarted] = useState(false);

    // ===================== 7. ELEMENTS MOVING =====================
    // is element moving
    const [isMovingElementId, setIsMovingElementId] = useState(null);

    // ===================== 7. ITEM RESIZE  =====================
    // clicked item resize point by 'mousedown'. clear on 'click'
    const isItemResizeClickedRef = useRef(false);
    const resizeKeyRef = useRef(null);

    // ===================== =====================

    function calculateAndSetScaleNumbers(scale) {
        const scalePercents = calculateScaleProgress(scale);
        const resizeHandleCoef = calculateResizeHandleCoefValue(scalePercents);
        const resizeOffsetCoef = calculateResizeOffsetValue(scalePercents);

        // 24
        // 10
        const outlineOffset = (5 * resizeOffsetCoef) / scale;
        const outlineWidth = calculateOutlineWidth(scale);
        const relationLineWidth =
            scale < 0.1
                ? Math.min(outlineWidth, 15) * 0.7
                : Math.min(outlineWidth, 16);

        // 11
        const resizeHandleSize = (9 / scale) * resizeHandleCoef;
        // 3
        const resizeHandleRadius = (1.5 / scale) * resizeHandleCoef;

        scaleNumbersRef.current = {
            outlineOffset,
            outlineWidth,
            relationLineWidth,
            resizeHandleSize,
            resizeHandleRadius,
            selectionBoxLine: 1.5 / scale,
        };
    }

    function calculateVisibleElementsAndLines() {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        const visibleElementsIds = [];
        const visibleLinesIds = [];
        // for alignment lines
        const visibleViewportElementsIds = [];

        // if elements / lines points (x1,y1  x2,y2) includes in (startXY, endXY) frame =>
        // => add to visibleElementsIds/visibleLinesIds

        const newOriginX = originXYRef.current.x;
        const newOriginY = originXYRef.current.y;

        const newWindowWidth = windowWidth;
        const newWindowHeight = windowHeight;

        // const startX = (0 - newOriginX) / scaleRef.current - 1000;
        // const startY = (0 - newOriginY) / scaleRef.current - 1000;
        // const endX = (newWindowWidth - newOriginX) / scaleRef.current + 1000;
        // const endY = (newWindowHeight - newOriginY) / scaleRef.current + 1000;

        // bigger viewport area for 'visible elements/lines'
        const frameStartX = (0 - newOriginX) / scaleRef.current - 2000;
        const frameStartY = (0 - newOriginY) / scaleRef.current - 2000;
        const frameEndX =
            (newWindowWidth - newOriginX) / scaleRef.current + 2000;
        const frameEndY =
            (newWindowHeight - newOriginY) / scaleRef.current + 2000;

        // viewport area for 'alignment lines'
        const viewportStartX = (0 - newOriginX) / scaleRef.current;
        const viewportStartY = (0 - newOriginY) / scaleRef.current;
        const viewportEndX = (newWindowWidth - newOriginX) / scaleRef.current;
        const viewportEndY = (newWindowHeight - newOriginY) / scaleRef.current;

        // elements
        for (const key in elementsRef.current) {
            const element = elementsRef.current[key];
            const { id, x, y, width, height } = element;

            const elementMinX = x;
            const elementMaxX = x + width;
            const elementMinY = y;
            const elementMaxY = y + height;

            // frameX < x + width &&
            // frameX + frameWidth > x &&
            // frameY < y + height &&
            // frameY + frameHeight > y

            // if (
            // 	(elementMinX >= startX && elementMinX <= endX || elementMaxX >= startX && elementMaxX <= endX) &&
            // 	(elementMinY >= startY && elementMinY <= endY || elementMaxY >= startY && elementMaxY <= endY)
            // ) {
            if (
                frameStartX <= elementMaxX &&
                frameEndX >= elementMinX &&
                frameStartY <= elementMaxY &&
                frameEndY >= elementMinY
            ) {
                // visibleElementsIds.push(element.value.header.name);
                visibleElementsIds.push(id);

                if (
                    viewportStartX <= elementMaxX &&
                    viewportEndX >= elementMinX &&
                    viewportStartY <= elementMaxY &&
                    viewportEndY >= elementMinY
                ) {
                    visibleViewportElementsIds.push(id);
                }
            }
        }

        // lines
        for (const key in linesRef.current) {
            const lineObject = linesRef.current[key];
            let {
                id,
                value: { path },
            } = lineObject;

            if (!path) {
                const lineObject = linesRef.current[key];
                const {
                    value: {
                        fromElementId,
                        fromInsideElementId,
                        fromRelationType,
                        toElementId,
                        toInsideElementId,
                        toRelationType,
                    },
                } = lineObject;

                const fromElement = elementsRef.current[fromElementId];
                const toElement = elementsRef.current[toElementId];

                if (!fromElement || !toElement) {
                    // return;
                    continue;
                }

                const fromInsideElement =
                    fromElement.value.columns[fromInsideElementId];
                const toInsideElement =
                    toElement.value.columns[toInsideElementId];

                if (!fromInsideElement || !toInsideElement) {
                    // return;
                    continue;
                }

                const fromElementColumnsKeys = Object.keys(
                    fromElement.value.columns
                );
                let fromInsideElementIndex = -1;
                fromElementColumnsKeys.forEach((key, index) => {
                    if (fromInsideElementId === key) {
                        fromInsideElementIndex = index;
                    }
                });

                const toElementColumnsKeys = Object.keys(
                    toElement.value.columns
                );
                let toInsideElementIndex = -1;
                toElementColumnsKeys.forEach((key, index) => {
                    if (toInsideElementId === key) {
                        toInsideElementIndex = index;
                    }
                });

                const lineData = {
                    mouseX: null,
                    mouseY: null,

                    fromElementType: fromElement.type,
                    fromElementWidth: fromElement.width,
                    fromElementX: fromElement.x,
                    fromElementY: fromElement.y,
                    fromInsideElementIndex,
                    fromRelationType,

                    toElementType: toElement.type,
                    toElementWidth: toElement.width,
                    toElementX: toElement.x,
                    toElementY: toElement.y,
                    toInsideElementIndex,
                    toRelationType,
                };

                path = _buildLinePath(lineData);
            }

            const [lines, points, bounds] = path;
            Math.random() === 12 && console.log('lines', lines, points);

            const lineMinX = bounds.minX;
            const lineMaxX = bounds.maxX;
            const lineMinY = bounds.minY;
            const lineMaxY = bounds.maxY;

            if (
                frameStartX <= lineMaxX &&
                frameEndX >= lineMinX &&
                frameStartY <= lineMaxY &&
                frameEndY >= lineMinY
            ) {
                visibleLinesIds.push(id);
            }
        }

        // result
        visibleElementsIdsRef.current = visibleElementsIds;
        visibleLinesIdsRef.current = visibleLinesIds;
        visibleViewportElementsIdsRef.current = visibleViewportElementsIds;
    }

    function drawTableName(element) {
        const isStartedNewLine =
            lineFromRef.current?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment =
            newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        const ctx = contextRef.current;

        // let { id, x, y, width, height, value: { header: { name } } } = element;
        let {
            id,
            x,
            y,
            value: {
                header: { name },
            },
        } = element;
        let width = TABLE_NAME_WIDTH;
        let height = TABLE_NAME_HEIGHT;

        // ===================== 0. FOCUSED TABLE | OUTLINE FRAME =====================
        if (
            // table is focused
            focusedElementsIdsRef.current.includes(id) ||
            // table is hovered and its not new line drawing
            (hoveredElementIdRef.current === id &&
                !isStartedNewLine &&
                !isStartedNewPenSegment)
        ) {
            ctx.strokeStyle = '#5f88ff';
            ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3;

            ctx.beginPath();
            // ctx.roundRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1, 1);
            ctx.roundRect(
                x - scaleNumbersRef.current.outlineOffset,
                y - scaleNumbersRef.current.outlineOffset,
                width + 2 * scaleNumbersRef.current.outlineOffset,
                height + 2 * scaleNumbersRef.current.outlineOffset,
                0.75
            );

            ctx.stroke();

            // ctx.shadowColor = 'transparent';
            // ctx.fillStyle = '#39424c'; // canvas background
            // ctx.fillRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1);
            // ctx.stroke();
        }

        // ===================== TABLE SHADOW =====================
        // Set shadow properties
        ctx.shadowColor = theme.table.shadow.color;
        ctx.shadowBlur = 8; // Розмиття тіні
        ctx.shadowOffsetX = 2; // Зміщення по горизонталі
        ctx.shadowOffsetY = 2; // Зміщення по вертикалі

        // ===================== 3. TABLE BACKGROUND | RECTANGLE =====================
        // #f7e968
        // #e7db6d
        // ctx.fillStyle = 'rgba(27, 106, 242, 0.55)';
        ctx.fillStyle = 'lightgreen'; // : 'lightgreen';

        ctx.beginPath();
        ctx.roundRect(x, y, width, height, 0.5);
        ctx.fill();

        // Reset shadow after drawing the table
        ctx.shadowColor = 'transparent';

        // ===================== TABLE | NAME =====================
        ctx.fillStyle = 'black';
        ctx.fillStyle = 'white';

        ctx.textAlign = 'left';
        // ctx.font = `500 30px Roboto`;
        ctx.font = `500 30px Roboto, sans-serif`;
        ctx.fillText(name, x + 38, y + 60);
    }

    function drawTableKey(element) {
        const isSelectionMoving =
            isSelectionClickedRef.current &&
            (selectionStartXYRef.current.x !==
                selectionCurrentXYRef.current.x ||
                selectionStartXYRef.current.y !==
                selectionCurrentXYRef.current.y);
        const isStartedNewLine = lineFromRef.current?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment = newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        const ctx = contextRef.current;
        // let { id, x, y, width: tableWidth, height: tableHeight, value: { header: { name }, columns } } = element;
        let {
            id,
            x,
            y,
            width: tableWidth,
            value: {
                header: { name },
                columns,
                color,
                border,
            },
        } = element;

        const columnsKeys = Object.keys(columns).filter(
            (columnKey) =>
                columns[columnKey]?.isPK ||
                columns[columnKey]?.isFK ||
                columns[columnKey]?.isRelated
        );
        const columnsCount = columnsKeys?.length || 0;

        const tableHeight =
            TABLE_HEADER_HEIGHT +
            HEADER_BODY_MARGIN +
            (columnsCount + 1) * TABLE_ROW_HEIGHT +
            TABLE_BORDER_RADIUS +
            1;

        // ===================== 0. FOCUSED TABLE | OUTLINE FRAME =====================
        if (
            !isBtnNewAreaEnabled &&
            // is not export enabled
            !isBtnExportImageEnabled &&
            // table is selected by mouse selection box
            (selectedElementsIdsRef.current.includes(id) ||
                focusedGroupElementsIdsRef.current.includes(id) ||
                // table is focused
                focusedElementsIdsRef.current.includes(id) ||
                // table is hovered and its not new line drawing
                (hoveredElementIdRef.current === id &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment))
        ) {
            ctx.strokeStyle = '#5f88ff';
            ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3;

            if (
                hoveredElementIdRef.current === id &&
                !focusedElementsIdsRef.current.includes(id) &&
                !selectedElementsIdsRef.current.includes(id) &&
                !focusedGroupElementsIdsRef.current.includes(id)
            ) {
                // ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3 - 0.7;
                // ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3 - 0.7 - 0.1; // ASAP LAST
                ctx.lineWidth =
                    scaleNumbersRef.current.outlineWidth - 0.3 - 0.7 - 0.1;
                // ctx.strokeStyle = 'rgba(95, 136, 255, 0.8)';
            }

            ctx.beginPath();
            ctx.roundRect(
                x - scaleNumbersRef.current.outlineOffset,
                y - scaleNumbersRef.current.outlineOffset - 0.5,
                tableWidth + 2 * scaleNumbersRef.current.outlineOffset,
                tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1,
                0.75
            );
            ctx.stroke();
            // ctx.shadowColor = 'transparent';

            ctx.fillStyle = '#39424c'; // canvas background

            // ctx.fillRect(
            //     x - scaleNumbersRef.current.outlineOffset,
            //     y - scaleNumbersRef.current.outlineOffset - 0.5,
            //     tableWidth + 2 * scaleNumbersRef.current.outlineOffset,
            //     tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1
            // );
            ctx.strokeRect(
                x - scaleNumbersRef.current.outlineOffset,
                y - scaleNumbersRef.current.outlineOffset - 0.5,
                tableWidth + 2 * scaleNumbersRef.current.outlineOffset,
                tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1
            );
            // ctx.stroke();
        }

        // ===================== 2. TABLE SHADOW =====================
        ctx.shadowColor = theme.table.shadow.color;
        ctx.shadowBlur = 8; // Розмиття тіні
        ctx.shadowOffsetX = 2; // Зміщення по горизонталі
        ctx.shadowOffsetY = 2; // Зміщення по вертикалі

        // ===================== 3. TABLE BACKGROUND | RECTANGLE =====================
        ctx.beginPath();
        ctx.moveTo(x + TABLE_BORDER_RADIUS, y); // Верхній лівий кут
        ctx.lineTo(x + tableWidth - TABLE_BORDER_RADIUS, y); // Верхній правий кут
        ctx.quadraticCurveTo(
            x + tableWidth,
            y,
            x + tableWidth,
            y + TABLE_BORDER_RADIUS
        ); // Верхня права дуга
        ctx.lineTo(x + tableWidth, y + tableHeight - TABLE_BORDER_RADIUS); // Нижня права сторона
        ctx.quadraticCurveTo(
            x + tableWidth,
            y + tableHeight,
            x + tableWidth - TABLE_BORDER_RADIUS,
            y + tableHeight
        ); // Нижня права дуга
        ctx.lineTo(x + TABLE_BORDER_RADIUS, y + tableHeight); // Нижній лівий кут
        ctx.quadraticCurveTo(
            x,
            y + tableHeight,
            x,
            y + tableHeight - TABLE_BORDER_RADIUS
        ); // Нижня ліва дуга
        ctx.lineTo(x, y + TABLE_BORDER_RADIUS); // Верхня ліва сторона
        ctx.quadraticCurveTo(x, y, x + TABLE_BORDER_RADIUS, y); // Верхня ліва дуга

        ctx.fillStyle = theme.table.body.background;
        // -----------------------------------------------------------
        let headerColor = null;
        let bodyColor = theme.table.body.background;
        let focusedBodyColor = '#1e2226';
        let hoveredBodyColor = theme.table.body.row.hover.background;

        if (color) {
            switch (color) {
                case 'blue': {
                    // headerColor = '#031c39a2'; // #011833b9
                    // bodyColor = '#05274e6e';

                    headerColor = '#031c39'; // #011833b9
                    bodyColor = '#05274e';
                    focusedBodyColor = '#042040';
                    hoveredBodyColor = '#0b3564';


                    headerColor = '#092d58ff'; // #011833b9
                    headerColor = '#082850ff'; // #011833b9
                    headerColor = '#0b2a50ff'; // #011833b9
                    // headerColor = '#072549ff'; // #011833b9
                    break;
                }
                case 'green': {
                    // headerColor = '#012b22';
                    // bodyColor = '#003f31';

                    // from Red/Green View
                    headerColor = '#002e28';
                    bodyColor = '#003931';
                    focusedBodyColor = '#002e29';
                    hoveredBodyColor = '#04473f';


                    headerColor = '#01372fff';
                    headerColor = '#033c33ff';
                    break;
                }
                case 'orange': {
                    headerColor = '#451d01'; // //562605 662e09
                    bodyColor = '#6a2c03';
                    focusedBodyColor = '#05274e';
                    hoveredBodyColor = '#05274e';
                    break;
                }
                case 'yellow': {
                    headerColor = '#573900';
                    bodyColor = '#6d4700'; //ad7000
                    focusedBodyColor = '#05274e';
                    hoveredBodyColor = '#05274e';
                    break;
                }
                case 'pink': {
                    headerColor = '#660928';
                    bodyColor = '#7e1b3c';
                    focusedBodyColor = '#05274e';
                    hoveredBodyColor = '#05274e';
                    break;
                }
                case 'purple': {
                    headerColor = '#220131';
                    bodyColor = '#320149';
                    focusedBodyColor = '#29013c';
                    hoveredBodyColor = '#40045d';

                    headerColor = '#32004aff';
                    headerColor = '#34054aff';
                    break;
                }
                case 'violet': {
                    // headerColor = '#2f002f';
                    headerColor = '#350235';
                    bodyColor = '#490149';
                    focusedBodyColor = '#3d013d';
                    hoveredBodyColor = '#530353';


                    headerColor = '#61044eff';
                    break;
                }
                case 'red': {
                    // headerColor = '#440813';
                    // bodyColor = '#5b1623';

                    // from Red/Green View
                    headerColor = '#47021f';
                    bodyColor = '#550225';
                    focusedBodyColor = '#47021f';
                    hoveredBodyColor = '#5d0429';

                    headerColor = '#530123ff';
                    headerColor = '#6e002eff';
                    // headerColor = '#6e0030ff';

                    break;
                }
                case 'default': {
                    break;
                }
                default:
                    break;
            }
        }

        bodyColor = theme.table.body.background;
        focusedBodyColor = '#1b1f22';
        hoveredBodyColor = theme.table.body.row.hover.background;

        // asas
        if (bodyColor) {
            ctx.fillStyle = bodyColor;
        }
        // -----------------------------------------------------------

        ctx.fill();

        ctx.lineWidth = 1;

        // Reset shadow after drawing the table
        ctx.shadowColor = 'transparent';


        if (scaleRef.current > 0.15) {

            // ===================== 4. TABLE HEADER =====================
            // ===================== 4.1. TABLE HEADER | BACKGROUND =====================
            // draw background rectangle
            ctx.fillStyle = '#16191d'; //theme.table.header.background;
            ctx.fillStyle = '#121316';
            ctx.fillStyle = 'black';

            // asas
            if (headerColor) {
                ctx.fillStyle = headerColor;
            }

            ctx.beginPath();
            ctx.moveTo(x + TABLE_BORDER_RADIUS, y); // Верхній лівий кут заголовка
            ctx.lineTo(x + tableWidth - TABLE_BORDER_RADIUS, y); // Верхній правий кут заголовка
            ctx.quadraticCurveTo(
                x + tableWidth,
                y,
                x + tableWidth,
                y + TABLE_BORDER_RADIUS
            ); // Верхня права дуга заголовка
            ctx.lineTo(x + tableWidth, y + TABLE_HEADER_HEIGHT); // Нижня права сторона заголовка
            ctx.lineTo(x, y + TABLE_HEADER_HEIGHT); // Нижня ліва сторона заголовка
            ctx.lineTo(x, y + TABLE_BORDER_RADIUS); // Верхня ліва сторона заголовка
            ctx.quadraticCurveTo(x, y, x + TABLE_BORDER_RADIUS, y); // Верхня ліва дуга заголовка
            ctx.fill();

            // -----------------------------------------------------------
            let headColor = '#333b44';
            let cellsColor = '#444e5a';
            let linesColor = '#262c31';

            if (color) {
                switch (color) {
                    case 'blue': {
                        headColor = '#0f325a';
                        cellsColor = '#15467d';
                        linesColor = '#071b31';
                        break;
                    }
                    case 'green': {
                        headColor = '#10533c';
                        cellsColor = '#14694c';
                        linesColor = '#0b3b2a';
                        break;
                    }
                    case 'orange': {
                        // headerColor = '#451d01'; // //562605 662e09
                        // bodyColor = '#6a2c03';
                        // headColor = '#40085a';
                        // cellsColor = '#540b76';
                        // linesColor = '#300643';
                        break;
                    }
                    case 'yellow': {
                        // headerColor = '#573900';
                        // bodyColor = '#6d4700'; //ad7000
                        // headColor = '#40085a';
                        // cellsColor = '#540b76';
                        // linesColor = '#300643';
                        break;
                    }
                    case 'pink': {
                        // headColor = '#760b2f';
                        // cellsColor = '#900e39';
                        // linesColor = '#660928';

                        break;
                    }
                    case 'purple': {
                        headColor = '#40085a';
                        cellsColor = '#540b76';
                        linesColor = '#300643';
                        break;
                    }
                    case 'violet': {
                        headColor = '#4d064d';
                        cellsColor = '#6c0c6c';
                        linesColor = '#350235';
                        break;
                    }
                    case 'red': {
                        headColor = '#661320';
                        cellsColor = '#821829';
                        linesColor = '#490e16';
                        break;
                    }
                    case 'default': {
                        break;
                    }
                    default:
                        break;
                }
            }
            // -----------------------------------------------------------

            if (scaleRef.current > 0.35) {

                // ===================== 4.1. TABLE HEADER | ICON =====================
                const pathHeader =
                    'M45,0 L3,0 C1.343,0 0,1.344 0,3 L0,12 L48,12 L48,3 C48,1.344 46.657,0 45,0';
                const pathCells =
                    'M0,45 C0,46.656 1.343,48 3,48 L12,48 L12,36 L0,36 L0,45 M0,36 L12,36 L12,24 L0,24 M12,48 L24,48 L24,36 L12,36 M24,48 L36,48 L36,36 L24,36 M12,36 L24,36 L24,24 L12,24 M24,36 L36,36 L36,24 L24,24 M36,36 L48,36 L48,24 L36,24 M0,24 L12,24 L12,12 L0,12 M12,24 L24,24 L24,12 L12,12 M24,24 L36,24 L36,12 L24,12 M36,24 L48,24 L48,12 L36,12 M36,36 L36,48 L45,48 C46.657,48 48,46.656 48,45 L48,36 L36,36 Z';
                const pathLines =
                    'M45,0 L3,0 C1.343,0 0,1.343 0,3 L0,45 C0,46.657 1.343,48 3,48 L45,48 C46.657,48 48,46.657 48,45 L48,3 C48,1.343 46.657,0 45,0 M0,12 L48,12M0,24 L48,24 M0,36 L48,36 M24,12 L24,48 M36,12 L36,48 M12,12 L12,48';
                const pathHeaderCanvas = new Path2D(pathHeader);
                const pathCellsCanvas = new Path2D(pathCells);
                const pathLinesCanvas = new Path2D(pathLines);

                ctx.save(); // Зберігаємо поточний стан канвасу
                // ctx.scale(0.36, 0.36); // Масштабування шляху для більшого розміру
                ctx.scale(0.38, 0.38); // Масштабування шляху для більшого розміру

                // ctx.translate((x + TABLE_BORDER_RADIUS + 10) / 0.36, (y + 11.5) / 0.36); // Переміщуємо початкову точку малювання
                // ctx.translate((x + TABLE_BORDER_RADIUS + 10) / 0.38, (y + 11.5) / 0.38); // Переміщуємо початкову точку малювання
                ctx.translate((x + TABLE_BORDER_RADIUS + 8) / 0.38, (y + 10.25) / 0.38); // Переміщуємо початкову точку малювання
                ctx.strokeStyle = theme.controls.background;
                ctx.lineWidth = 2; // Лінії мають бути тонкими після масштабування

                // ctx.fillStyle = '#333b44';
                ctx.fillStyle = headColor;
                ctx.beginPath();
                ctx.fill(pathHeaderCanvas);

                // ctx.fillStyle = '#444e5a';
                ctx.fillStyle = cellsColor;
                ctx.beginPath();
                ctx.fill(pathCellsCanvas);

                // ctx.strokeStyle = '#262c31';
                ctx.strokeStyle = linesColor;
                ctx.beginPath();
                ctx.stroke(pathLinesCanvas);

                ctx.restore(); // Відновлюємо попередній стан канвасу


                // ===================== 4.1.2. TABLE HEADER | DESCRIPTION ICON =====================
                // if (element.value.description || element.value.isTableWithIndexes) {
                if (element.value.description) {

                    ctx.font = `600 21px Inter, sans-serif`;
                    const tableNameWidth = ctx.measureText(name).width;

                    const pathDescription = 'M509.039,324.633a1.891,1.891,0,0,1,1.889,1.89V349.7a1.891,1.891,0,0,1-1.889,1.89H485.862a1.892,1.892,0,0,1-1.89-1.89V326.523a1.892,1.892,0,0,1,1.89-1.89h23.177m0-2H485.862a3.89,3.89,0,0,0-3.89,3.89V349.7a3.89,3.89,0,0,0,3.89,3.89h23.177a3.889,3.889,0,0,0,3.889-3.89V326.523a3.889,3.889,0,0,0-3.889-3.89Z M505.105,332.478H489.523a1,1,0,1,1,0-2h15.582a1,1,0,0,1,0,2Z M505.105,339.188H489.523a1,1,0,1,1,0-2h15.582a1,1,0,0,1,0,2Z M505.105,345.9H489.523a1,1,0,0,1,0-2h15.582a1,1,0,0,1,0,2Z';
                    const pathDescriptionCanvas = new Path2D(pathDescription);

                    ctx.save(); // Зберігаємо поточний стан канвасу
                    ctx.scale(0.55, 0.55); // Масштабування шляху для більшого розміру

                    // ctx.translate((x + 8) / 0.55, (y - 149) / 0.55); // Переміщуємо початкову точку малювання
                    ctx.translate((x - 264 + tableWidth / 2 + tableNameWidth / 2 + 5.5 - 0.3) / 0.55, (y - 166 + 0.25) / 0.55); // Переміщуємо початкову точку малювання

                    // ctx.strokeStyle = theme.controls.background;
                    ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування
                    // ctx.lineWidth = 0; // Лінії мають бути тонкими після масштабування

                    ctx.fillStyle = theme.table.header.color;
                    ctx.strokeStyle = theme.table.header.color;

                    ctx.beginPath();
                    ctx.stroke(pathDescriptionCanvas);
                    ctx.fill(pathDescriptionCanvas);

                    ctx.restore(); // Відновлюємо попередній стан канвасу
                }

            }
            // ===================== 4.1. TABLE HEADER | TEXT =====================
            ctx.textAlign = 'center';
            ctx.fillStyle = theme.table.header.color;
            ctx.font = `600 21px Inter, sans-serif`;
            ctx.fillText(name, x + tableWidth / 2, y + 27);

            // ===================== 5. TABLE HEADER/BODY PADDING =====================
            ctx.fillStyle = theme.table.body.background; // Колір заголовка

            // -----------------------------------------------------------
            // asas
            if (bodyColor) {
                ctx.fillStyle = bodyColor;
            }
            // -----------------------------------------------------------

            ctx.fillRect(x, y + TABLE_HEADER_HEIGHT, tableWidth, 4);
        }

        if (scaleRef.current > 0.15) {

            // ===================== 6. TABLE BODY =====================
            // ===================== 6. TABLE COLUMNS =====================
            for (let index = 0; index < columnsKeys.length; index++) {
                const key = columnsKeys[index];
                const column = columns[key];
                const columnY =
                    y +
                    TABLE_HEADER_HEIGHT +
                    index * TABLE_ROW_HEIGHT +
                    HEADER_BODY_MARGIN;

                // column's background is light grey
                const isHoveredBgStyle =
                    !isElementResizing &&
                    // is not export enabled
                    !isBtnExportImageEnabled &&
                    !isSelectionMoving &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment && // hovered table and hovered column
                    ((hoveredElementIdRef.current === id &&
                        hoveredInsideElementRef.current?.id === column.id) ||
                        // hovered column's point
                        hoveredPointRef.current?.insideElementId === column.id);

                // column's background is black
                const isFocusedBgStyle =
                    focusedInsideElementState?.elementId === id &&
                    focusedInsideElementState?.insideElementId === column.id;

                // column's background is black
                const isRelatedToFocusedElementsBgStyle =
                    focusedElementsRelatedPropsRef.current?.insideElementsIds.includes(
                        column.id
                    );

                // column's background is black
                const isRelatedToHoveredElementBgStyle =
                    !isStartedNewPenSegment &&
                    hoveredElementRelatedPropsRef.current?.insideElementsIds.includes(
                        column.id
                    );

                // column's background is black
                const isRelatedToFocusedLineBgStyle =
                    focusedLineRelatedPropsRef.current?.insideElementsIds.includes(
                        column.id
                    );

                // column's background is black
                const isRelatedToHoveredLineBgStyle =
                    !isStartedNewPenSegment &&
                    hoveredLineRelatedPropsRef.current?.insideElementsIds.includes(
                        column.id
                    );

                // column's background is black
                const isColumnsFromToNewLineBgStyle =
                    // new line's column 'from'
                    (lineFromRef.current?.elementId === id &&
                        lineFromRef.current?.insideElementId === column.id) ||
                    // new line's column 'to'
                    (hoveredElementIdRef.current === id &&
                        hoveredInsideElementRef.current?.id === column.id &&
                        lineFromRef.current?.insideElementId &&
                        lineFromRef.current?.insideElementId !==
                        hoveredInsideElementRef.current?.id);

                // ===================== 6.1. TABLE COLUMNS | START DRAWING =====================
                let columnColor = theme.table.body.background;
                let focusedColumnColor = '#1e2226';
                let hoveredColumnColor = theme.table.body.row.hover.background;

                // -----------------------------------------------------------
                // asas
                if (bodyColor) {
                    columnColor = bodyColor;
                    focusedColumnColor = focusedBodyColor
                        ? focusedBodyColor
                        : bodyColor;
                    hoveredColumnColor = hoveredBodyColor
                        ? hoveredBodyColor
                        : bodyColor;
                }
                // -----------------------------------------------------------

                ctx.fillStyle =
                    (isFocusedBgStyle ||
                        isRelatedToFocusedElementsBgStyle ||
                        isRelatedToHoveredElementBgStyle ||
                        isRelatedToFocusedLineBgStyle ||
                        isRelatedToHoveredLineBgStyle ||
                        isColumnsFromToNewLineBgStyle) &&
                        !isBtnExportImageEnabled
                        ? focusedColumnColor
                        : isHoveredBgStyle
                            ? hoveredColumnColor
                            : columnColor;

                ctx.beginPath();
                ctx.roundRect(x, columnY + 1, tableWidth, TABLE_ROW_HEIGHT - 2, 3);
                ctx.fill();

                let columnNameFontSize = 400;
                ctx.font = `${columnNameFontSize} 19px Inter, sans-serif`;

                // ===================== 6.2. TABLE COLUMNS | PRIMARY KEY & FOREIGN KEY =====================
                if (column.isPK || column.isFK) {
                    columnNameFontSize = 700;
                    ctx.font = `${columnNameFontSize} 19px Inter, sans-serif`;

                    const path =
                        'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z';
                    const pathCanvas = new Path2D(path);

                    ctx.save(); // Зберігаємо поточний стан канвасу
                    // ctx.translate(x + 4, columnY + 8.5); // Переміщуємо початкову точку малювання
                    // ctx.translate(x + 5.5, columnY + 8.5); // Переміщуємо початкову точку малювання
                    ctx.translate(x + 4, columnY + 8.5 + 1.2); // Переміщуємо початкову точку малювання

                    ctx.scale(0.75, 0.75); // Масштабування шляху для більшого розміру
                    ctx.strokeStyle = theme.controls.background;
                    ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування


                    // ctx.fillStyle = column.isPK ? '#ffc65e' : '#bd7bff';
                    // ctx.fillStyle = column.isPK ? '#ffc65e' : '#8b76ff';  // b092ff'; // a784ff
                    ctx.fillStyle = column.isPK ? '#edb855' : '#7b8fff';  // b092ff'; // a784ff // 7b7bff

                    ctx.fill(pathCanvas);

                    ctx.stroke(pathCanvas);
                    ctx.restore(); // Відновлюємо попередній стан канвасу
                }

                // ===================== 6.2.1. TABLE COLUMNS | DESCRIPTION ICON =====================
                // if (column.description || column.defaultValue || column.enumValues || column.generatedValue || column.checkValue) {
                if (column.description) {
                    const columnNameWidth = ctx.measureText(column.name).width;

                    const pathDescription = 'M509.039,324.633a1.891,1.891,0,0,1,1.889,1.89V349.7a1.891,1.891,0,0,1-1.889,1.89H485.862a1.892,1.892,0,0,1-1.89-1.89V326.523a1.892,1.892,0,0,1,1.89-1.89h23.177m0-2H485.862a3.89,3.89,0,0,0-3.89,3.89V349.7a3.89,3.89,0,0,0,3.89,3.89h23.177a3.889,3.889,0,0,0,3.889-3.89V326.523a3.889,3.889,0,0,0-3.889-3.89Z M505.105,332.478H489.523a1,1,0,1,1,0-2h15.582a1,1,0,0,1,0,2Z M505.105,339.188H489.523a1,1,0,1,1,0-2h15.582a1,1,0,0,1,0,2Z M505.105,345.9H489.523a1,1,0,0,1,0-2h15.582a1,1,0,0,1,0,2Z';
                    const pathDescriptionCanvas = new Path2D(pathDescription);

                    ctx.save(); // Зберігаємо поточний стан канвасу
                    ctx.scale(0.525, 0.525); // Масштабування шляху для більшого розміру

                    // ctx.translate((x + 8) / 0.55, (y - 149) / 0.55); // Переміщуємо початкову точку малювання
                    // ctx.translate((startColumnX - 264 + 25 + columnNameWidth + 4.5) / 0.55, (columnY - 168) / 0.55); // Переміщуємо початкову точку малювання
                    // ctx.translate((startColumnX - 264 + 25 + columnNameWidth + 5) / 0.55, (columnY - 168) / 0.55); // Переміщуємо початкову точку малювання
                    ctx.translate((x - 264 + 25 + columnNameWidth + 5 + 11) / 0.525, (columnY - 168 + 9) / 0.525); // Переміщуємо початкову точку малювання

                    // ctx.strokeStyle = theme.controls.background;
                    ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування
                    // ctx.lineWidth = 0; // Лінії мають бути тонкими після масштабування

                    // ctx.fillStyle = theme.table.header.color;
                    // ctx.strokeStyle = theme.table.header.color;
                    ctx.fillStyle = theme.table.body.row.name.color;
                    ctx.strokeStyle = theme.table.body.row.name.color;

                    ctx.beginPath();
                    ctx.stroke(pathDescriptionCanvas);
                    ctx.fill(pathDescriptionCanvas);

                    ctx.restore(); // Відновлюємо попередній стан канвасу
                }

                // ===================== 6.3. TABLE COLUMNS | NAME =====================
                ctx.textAlign = 'left';
                ctx.fillStyle = theme.table.body.row.name.color;

                ctx.font = `${columnNameFontSize} 19.2px Inter, sans-serif`;

                // ctx.fillText(column.name, x + 28, columnY + 23);
                // ctx.fillText(column.name, x + 25, columnY + 23);
                ctx.fillText(column.name, x + 25, columnY + 23 + 1.8);

                Math.random() === 2 && drawTextWithLetterSpacing();
                Math.random() === 2 && drawTextRight();

                // ===================== 6.4. TABLE COLUMNS | DEFAULT VALUE  =====================
                // ===================== 6.4. TABLE COLUMNS | NULLABLE       =====================
                // ===================== 6.4. TABLE COLUMNS | ENUM           =====================
                // ===================== 6.4. TABLE COLUMNS | AUTO INCREMENT =====================

                let widthGenerated = 17 - 1;
                let widthDef = 17;
                let widthNN = 25;
                let widthEnum = 15.25;
                let widthAutoInc = 15.5;
                let distanceTextInternal = 4.35;
                let distanceBetween = 4.5; // or 8, like with index and nn

                let endX = x + tableWidth - 28; // = tableEnd - indexWidth with 2x distance
                let positionNext = endX;

                // ===================== 6.4. TABLE COLUMNS | GENERATED VALUE =====================
                if (column.generatedValue) {
                    ctx.fillStyle = '#23a4df'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#299ec2'; // theme.table.body.row.type.color;
                    // ctx.fillStyle = '#e3ad48ff'; // theme.table.body.row.type.color;
                    // ctx.fillStyle = '#dba33cff'; // theme.table.body.row.type.color;

                    // #edb855
                    // #7b8fff

                    ctx.beginPath();
                    ctx.roundRect(positionNext - widthGenerated, columnY + 23 - 14 + 1.7 + 0.75, widthGenerated, 16.5 - 1.5, 2.7);
                    ctx.fill();

                    ctx.textAlign = 'right';
                    ctx.font = `600 9.7px Inter, sans-serif`;
                    ctx.fillStyle = '#ebebeb'; // theme.table.body.row.name.color;

                    ctx.fillStyle = '#e5e7e9'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#fff'; // theme.table.body.row.name.color;

                    ctx.fillText('G', positionNext - distanceTextInternal, columnY + 23 - 2 + 2 - 0.4);

                    positionNext -= widthGenerated + distanceBetween;
                }

                // ===================== 6.4. TABLE COLUMNS | DEFAULT VALUE =====================
                if (column.defaultValue) {
                    ctx.fillStyle = '#8d452b'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#a24929'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#b36525'; // theme.table.body.row.type.color;
                    // ctx.fillStyle = '#e3ad48ff'; // theme.table.body.row.type.color;

                    ctx.beginPath();
                    ctx.roundRect(positionNext - widthDef + 0.5, columnY + 23 - 14 + 1.7 + 0.75, widthDef - 1, 16.5 - 1.5, 2.7);
                    ctx.fill();

                    ctx.textAlign = 'right';
                    ctx.font = `600 9.7px Inter, sans-serif`;
                    ctx.fillStyle = '#ebebeb'; // theme.table.body.row.name.color;

                    ctx.fillStyle = '#fff'; // theme.table.body.row.name.color;

                    ctx.fillText('D', positionNext - distanceTextInternal - 0.4, columnY + 23 - 2 + 2 - 0.4);

                    positionNext -= widthDef + distanceBetween;
                }

                // ===================== 6.4. TABLE COLUMNS | NULLABLE =====================
                if (column.isNN) {
                    ctx.fillStyle = '#3d47a5'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#2e357c'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#3d47a5'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#4a5aaf'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#4254afff'; // theme.table.body.row.type.color;

                    ctx.beginPath();
                    ctx.roundRect(positionNext - widthNN + 0.5, columnY + 23 - 14 + 1.7 + 0.75, widthNN - 1, 16.5 - 1.5, 2.7);
                    ctx.fill();

                    ctx.textAlign = 'right';
                    ctx.font = `600 9.7px Inter, sans-serif`;
                    ctx.fillStyle = '#ebebeb'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#e6ebef'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#ecf0f3'; // theme.table.body.row.name.color;

                    // ctx.fillStyle = '#ecf0f3'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#e5e7e9'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#fff'; // theme.table.body.row.name.color;

                    ctx.fillText('NN', positionNext - distanceTextInternal - 0.8, columnY + 23 - 2 + 2 - 0.6);

                    positionNext -= widthNN + distanceBetween;
                }

                // ===================== 6.4. TABLE COLUMNS | ENUM =====================
                if (column.isEnum) {
                    // ctx.fillStyle = '#ab7b21'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#bf8a27ff'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#b98523'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#b38f25'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#e3ad48'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#dca745ff'; // theme.table.body.row.type.color;

                    ctx.beginPath();
                    ctx.roundRect(positionNext - widthEnum + 0.5, columnY + 23 - 14 + 1.7 + 0.75, widthEnum - 1, 16.5 - 1.5, 2.7);
                    ctx.fill();

                    ctx.textAlign = 'right';
                    ctx.font = `600 9.7px Inter, sans-serif`;
                    ctx.fillStyle = '#ebebeb'; // theme.table.body.row.name.color;

                    ctx.fillStyle = '#e5e7e9'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#fff'; // theme.table.body.row.name.color;

                    ctx.fillText('E', positionNext - distanceTextInternal - 0.4, columnY + 23 - 2 + 2 - 0.4);

                    positionNext -= widthEnum + distanceBetween;
                }

                // ===================== 6.4. TABLE COLUMNS | AUTO INCREMENT =====================
                if (column.isAutoIncrement) {
                    // ctx.fillStyle = '#297d4e'; // theme.table.body.row.type.color;
                    ctx.fillStyle = 'seagreen'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#7bffb7'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#69d79b'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#58b481'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#3eb372ff'; // theme.table.body.row.type.color;
                    // ctx.fillStyle = '#e3ad48ff'; // theme.table.body.row.type.color;

                    ctx.beginPath();
                    ctx.roundRect(positionNext - widthAutoInc + 0.5, columnY + 23 - 14 + 1.7 + 0.75, widthAutoInc - 1, 16.5 - 1.5, 2.7);
                    ctx.fill();

                    ctx.textAlign = 'right';
                    ctx.font = `600 15px Inter, sans-serif`;
                    ctx.fillStyle = '#ebebeb'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#ecf0f3'; // theme.table.body.row.name.color;

                    ctx.fillStyle = '#e5e7e9'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#fff'; // theme.table.body.row.name.color;

                    ctx.fillText('+', positionNext - distanceTextInternal + 2 - 0.2, columnY + 23 - 2 + 2 + 0.4);

                    positionNext -= widthAutoInc + distanceBetween;
                }


                // ===================== 6.4.2. TABLE COLUMNS | TYPE =====================
                if (
                    isStartedLineState ||
                    isBoardMovingStarted ||
                    isBoardMoving ||
                    isScaling ||
                    isMovingElementId ||
                    isElementResizing ||

                    !focusedInsideElementState ||
                    focusedInsideElementState?.elementId !== id ||
                    focusedInsideElementState?.insideElementId !== column.id
                ) {
                    ctx.fillStyle = theme.table.body.row.type.color;

                    // POINT 1
                    if (color) {
                        ctx.fillStyle = '#7d8e9c';
                    }

                    ctx.textAlign = 'right';
                    ctx.font = `400 18px Inter, sans-serif`;
                    ctx.fillText(column.type, positionNext, columnY + 23 + 1.8);

                    // if (scaleRef.current <= 0.18) {
                    // 	ctx.fillStyle = '#cbd6df25';
                    // 	// ctx.fillRect(startColumnX + 25, columnY + 23 + 1.8, 100, 10);
                    // 	ctx.beginPath();
                    // 	ctx.roundRect(positionX - 75, columnY + (23 + 1.8) / 2, 75, 8, 3);
                    // 	ctx.fill();
                    // } else {
                    // 	ctx.fillStyle = theme.table.body.row.type.color;

                    // 	// POINT 1
                    // 	if (color) {
                    // 		ctx.fillStyle = '#7d8e9c';
                    // 	}

                    // 	ctx.textAlign = "right";
                    // 	ctx.font = `400 18px Inter, sans-serif`;
                    // 	ctx.fillText(column.type, positionX, columnY + 23 + 1.8);
                    // }
                }


                // ===================== 6.5. TABLE COLUMNS | INDEX =====================
                if (column.index) {
                    // const path = "M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z";
                    // const pathCanvas = new Path2D(path);

                    // ctx.save(); // Зберігаємо поточний стан канвасу
                    // ctx.translate(x+5, columnY+9); // Переміщуємо початкову точку малювання
                    // ctx.scale(0.7, 0.7); // Масштабування шляху для більшого розміру
                    // ctx.strokeStyle = '#ffffff';
                    // ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування
                    // ctx.stroke(pathCanvas); // Малюємо шлях
                    // ctx.restore(); // Відновлюємо попередній стан канвасу

                    // ctx.setTransform(10, 0, 0, 10, 0, 0); // Масштабування
                    // ctx.strokeStyle = '#ffffff';
                    // ctx.lineWidth = 1;
                    // ctx.stroke(pathCanvas);

                    ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування

                    ctx.fillStyle = theme.table.indexes[column.index?.type] || null;
                    ctx.strokeStyle =
                        theme.table.indexes[column.index?.type] || null;

                    const coords = {
                        top: {
                            x: x + tableWidth - 14,
                            y: columnY + 11.5,
                        },
                        left: {
                            x: x + tableWidth - 19.5,
                            y: columnY + 17,
                        },
                        right: {
                            x: x + tableWidth - 8.5,
                            y: columnY + 17,
                        },
                        bottom: {
                            x: x + tableWidth - 14,
                            y: columnY + 22.5,
                        },
                    };

                    ctx.beginPath();
                    ctx.moveTo(coords.top.x - 0.3, coords.top.y - 0.2 + 2); // Top point
                    ctx.lineTo(coords.right.x - 0.3, coords.right.y - 0.2 + 2); // Right point
                    ctx.lineTo(coords.bottom.x - 0.3, coords.bottom.y - 0.2 + 2); // Bottom point
                    ctx.lineTo(coords.left.x - 0.3, coords.left.y - 0.2 + 2); // Left point

                    ctx.closePath();
                    ctx.stroke();
                    ctx.fill();
                }

                // ===================== 6.6. TABLE COLUMNS | HIGHLIGHTED COLUMN | FOCUSED COLUMN | (BLUE) =====================
                if (
                    !isBtnExportImageEnabled &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment &&
                    focusedInsideElementState?.elementId === id &&
                    focusedInsideElementState?.insideElementId === column.id
                ) {
                    ctx.strokeStyle = '#5f88ff';
                    ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
                    // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
                    ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;

                    ctx.beginPath();
                    ctx.roundRect(
                        x + 1,
                        columnY,
                        tableWidth - 2,
                        TABLE_ROW_HEIGHT,
                        3
                    );
                    ctx.stroke();
                }

                // ===================== 6.7. TABLE COLUMNS | HIGHLIGHTED COLUMN | RELATED COLUMN | ON TABLE HOVER | (PURPLE) =====================

                const isColumnRelatedToFocusedElements =
                    // column is related to focused elements
                    focusedElementsRelatedPropsRef.current?.insideElementsIds.includes(
                        column.id
                    ) &&
                    // parent element is not focused
                    !focusedElementsIdsRef.current.includes(id);

                const isColumnRelatedToFocusedLine =
                    // column is related to focused line
                    focusedLineRelatedPropsRef.current?.insideElementsIds.includes(
                        column.id
                    ) &&
                    // no one focused elements (hover uses only when there is no focused elements)
                    !focusedElementsIdsRef.current.length;

                const isColumnRelatedToHoveredElements =
                    // column is related to hovered element
                    hoveredElementRelatedPropsRef.current?.insideElementsIds.includes(
                        column.id
                    ) &&
                    // parent element is not hovered
                    hoveredElementIdRef.current !== id &&
                    // no one focused elements (hover uses only when there is no focused elements)
                    !focusedElementsIdsRef.current.length &&
                    // no one focused line (hover uses only when there is no focused line)
                    !focusedLineIdRef.current;

                const isColumnRelatedToHoveredLine =
                    // column is related to focused line
                    hoveredLineRelatedPropsRef.current?.insideElementsIds.includes(
                        column.id
                    ) &&
                    // no one focused elements (hover uses only when there is no focused elements)
                    !focusedElementsIdsRef.current.length &&
                    // no one focused line (hover uses only when there is no focused line)
                    !focusedLineIdRef.current &&
                    // no hovered element
                    hoveredElementIdRef.current !== id;

                if (
                    // is not export enabled
                    !isBtnExportImageEnabled &&
                    !isSelectionMoving &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment && // column is related to focused elements
                    (isColumnRelatedToFocusedElements ||
                        // column is related to hovered element
                        isColumnRelatedToHoveredElements ||
                        // column is related to focused line
                        isColumnRelatedToFocusedLine ||
                        // column is related to hovered line
                        isColumnRelatedToHoveredLine)
                ) {
                    const columnY =
                        y +
                        TABLE_HEADER_HEIGHT +
                        index * TABLE_ROW_HEIGHT +
                        HEADER_BODY_MARGIN;

                    ctx.strokeStyle = '#8b5fff';
                    // ctx.fillStyle = '#cc9ff8a9';
                    ctx.fillStyle = '#a46ddc8e';
                    ctx.fillStyle = '#a46ddc76';
                    ctx.fillStyle = 'rgba(255, 199, 94, 0.55)';

                    // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
                    ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;

                    ctx.beginPath();
                    ctx.roundRect(
                        x + 1,
                        columnY,
                        tableWidth - 2,
                        TABLE_ROW_HEIGHT,
                        3
                    );
                    // ctx.stroke();
                    ctx.fill();

                    // ---------------
                    // const radius = 5;
                    // const smallRadius = 2.5;

                    // const radius = 5;
                    // const smallRadius = 3.25;

                    // const leftCenterX = x + 1;
                    // const rightCenterX = x - 1 + tableWidth;
                    // const centerY = columnY + TABLE_ROW_HEIGHT / 2;

                    // // left circle, big one
                    // ctx.beginPath();
                    // ctx.fillStyle = '#8b5fff';
                    // ctx.arc(leftCenterX, centerY, radius, 0, 2 * Math.PI); // Малюємо коло
                    // ctx.fill();

                    // // left circle, small one
                    // ctx.beginPath();
                    // ctx.fillStyle = '#e6e6e6';
                    // ctx.arc(leftCenterX, centerY, smallRadius, 0, 2 * Math.PI); // Малюємо коло
                    // ctx.fill();

                    // // right circle, big one
                    // ctx.beginPath();
                    // ctx.fillStyle = '#8b5fff';
                    // ctx.arc(rightCenterX, centerY, radius, 0, 2 * Math.PI); // Малюємо коло
                    // ctx.fill();

                    // // right circle, small one
                    // ctx.beginPath();
                    // ctx.fillStyle = '#e6e6e6';
                    // ctx.arc(rightCenterX, centerY, smallRadius, 0, 2 * Math.PI); // Малюємо коло
                    // ctx.fill();
                }

                // // ===================== 6.8. COLUMNS POINTS (TO CREATE NEW LINES) =====================
                // if (
                // 	// is not export enabled
                // 	!isBtnExportImageEnabled &&
                // 	!isSelectionMoving &&
                // 	!isStartedNewLine &&
                // 	!isStartedNewPenSegment &&
                // 	(   // table is one focused element
                // 		(focusedElementsIdsRef.current.includes(id) && focusedElementsIdsRef.current.length === 1)
                // 		// table is not focused, but this column is hovered
                // 		|| ((hoveredElementIdRef.current === id) && (hoveredInsideElementRef.current?.id === column.id))
                // 		// table is not focused, but this column's 'point area' is hovered
                // 		|| hoveredPointAreaRef.current?.insideElementsIds.includes(column.id)
                // 	)
                // ) {
                // 	const POINT_RADIUS_BEFORE_HOVER = 2.5;
                // 	const POINT_RADIUS_ON_HOVER = 6;
                // 	const POINT_DISTANCE = 20;

                // 	let leftRadius = POINT_RADIUS_BEFORE_HOVER;
                // 	let rightRadius = POINT_RADIUS_BEFORE_HOVER;

                // 	let isHoveredColumn = hoveredPointRef.current?.insideElementId === column.id;
                // 	let direction = hoveredPointRef.current?.direction;

                // 	// ===================== 6.8.1. COLUMNS POINTS | LEFT POINT =====================

                // 	const centerXLeft = x - POINT_DISTANCE;
                // 	const centerYLeft = columnY + TABLE_ROW_HEIGHT / 2;

                // 	ctx.fillStyle = '#667688';
                // 	if (isHoveredColumn && direction === 'left') {
                // 		ctx.fillStyle = '#5f88ff';
                // 		leftRadius = POINT_RADIUS_ON_HOVER;
                // 	}

                // 	ctx.beginPath();
                // 	ctx.arc(centerXLeft, centerYLeft, leftRadius, 0, 2 * Math.PI); // Малюємо коло
                // 	ctx.fill();

                // 	// ===================== 6.8.1. COLUMNS POINTS | RIGHT POINT =====================

                // 	const centerXRight = x + tableWidth + POINT_DISTANCE;
                // 	const centerYRight = columnY + TABLE_ROW_HEIGHT / 2;

                // 	ctx.fillStyle = '#667688';
                // 	if (isHoveredColumn && direction === 'right') {
                // 		ctx.fillStyle = '#5f88ff';
                // 		rightRadius = POINT_RADIUS_ON_HOVER;
                // 	}

                // 	ctx.beginPath();
                // 	ctx.arc(centerXRight, centerYRight, rightRadius, 0, 2 * Math.PI); // Малюємо коло
                // 	ctx.fill();
                // }

                // ===================== 6.9. TABLE COLUMNS | END =====================
            }
        }
        // ===================== 7.1. HIGHLIGHTED COLUMN | RELATED COLUMN | ON CREATE NEW LINE | FROM (PURPLE) =====================
        if (
            // is not export enabled
            !isBtnExportImageEnabled &&
            !isSelectionMoving &&
            isStartedNewLine &&
            lineFromRef.current?.elementId === id &&
            lineFromRef.current?.insideElementId
        ) {
            const isSideLeft = true;
            const isSideRight = true;

            const columnY =
                y +
                TABLE_HEADER_HEIGHT +
                lineFromRef.current.insideElementIndex * TABLE_ROW_HEIGHT +
                HEADER_BODY_MARGIN;

            const leftCenterX = x + 1;
            const rightCenterX = x - 1 + tableWidth;
            const centerY = columnY + TABLE_ROW_HEIGHT / 2;

            const bigRadius = 5.25;

            // let leftMidRadius = 2.75;
            // let rightMidRadius = 2.75;
            // const smallRadius = 1.95;

            let leftMidRadius = 2.75;
            let rightMidRadius = 2.75;
            const smallRadius = 2.05;

            // if (isSideLeft) { leftMidRadius = 3.35; }
            // if (isSideRight) { rightMidRadius = 3.35; }
            if (isSideLeft) {
                leftMidRadius = 3.75;
            }
            if (isSideRight) {
                rightMidRadius = 3.75;
            }

            // ===================== FOCUSED FRAME (PURPLE) =====================
            ctx.strokeStyle = '#8b5fff';
            // ctx.fillStyle = '#cc9ff8a9';
            ctx.fillStyle = '#a46ddc8e';
            ctx.fillStyle = '#a46ddc76';
            ctx.fillStyle = 'rgba(255, 199, 94, 0.55)';

            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;

            ctx.beginPath();
            ctx.roundRect(x + 1, columnY, tableWidth - 2, TABLE_ROW_HEIGHT, 3);
            // ctx.stroke();
            ctx.fill();
            // ===================== LEFT SIDE =====================

            // big circle
            ctx.beginPath();
            ctx.fillStyle = '#8b5fff';
            ctx.arc(leftCenterX, centerY, bigRadius, 0, 2 * Math.PI);
            ctx.fill();

            // mid circle
            ctx.beginPath();
            ctx.fillStyle = '#e6e6e6';
            ctx.arc(leftCenterX, centerY, leftMidRadius, 0, 2 * Math.PI);
            ctx.fill();

            if (isSideLeft) {
                // small circle
                ctx.beginPath();
                // ctx.fillStyle = 'cornflowerblue';
                ctx.fillStyle = '#6f94ff';
                ctx.arc(leftCenterX, centerY, smallRadius, 0, 2 * Math.PI);
                ctx.fill();
            }

            // ===================== RIGHT SIDE =====================

            // big circle
            ctx.beginPath();
            ctx.fillStyle = '#8b5fff';
            ctx.arc(rightCenterX, centerY, bigRadius, 0, 2 * Math.PI);
            ctx.fill();

            // mid circle
            ctx.beginPath();
            ctx.fillStyle = '#e6e6e6';
            ctx.arc(rightCenterX, centerY, rightMidRadius, 0, 2 * Math.PI);
            ctx.fill();

            if (isSideRight) {
                // small circle
                ctx.beginPath();
                // ctx.fillStyle = 'cornflowerblue';
                ctx.fillStyle = '#6f94ff';
                ctx.arc(rightCenterX, centerY, smallRadius, 0, 2 * Math.PI);
                ctx.fill();
            }
        }

        // ===================== 7.2. TABLE COLUMNS | HIGHLIGHTED COLUMN | RELATED COLUMN | ON CREATE NEW LINE | TO (PURPLE) =====================
        if (
            // is not export enabled
            !isBtnExportImageEnabled &&
            !isSelectionMoving &&
            isStartedNewLine &&
            hoveredElementIdRef.current === id &&
            hoveredInsideElementRef.current?.id &&
            lineFromRef.current?.insideElementId !==
            hoveredInsideElementRef.current?.id
        ) {
            const isSideLeft = true;
            const isSideRight = true;

            const columnY =
                y +
                TABLE_HEADER_HEIGHT +
                hoveredInsideElementRef.current?.index * TABLE_ROW_HEIGHT +
                HEADER_BODY_MARGIN;

            const leftCenterX = x + 1;
            const rightCenterX = x - 1 + tableWidth;
            const centerY = columnY + TABLE_ROW_HEIGHT / 2;

            const bigRadius = 5.25;
            let leftMidRadius = 2.75;
            let rightMidRadius = 2.75;
            const smallRadius = 1.95;

            if (isSideLeft) {
                leftMidRadius = 3.35;
            }
            if (isSideRight) {
                rightMidRadius = 3.35;
            }

            // ===================== FOCUSED FRAME (PURPLE) =====================
            ctx.strokeStyle = '#8b5fff';
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;

            ctx.beginPath();
            ctx.roundRect(x + 1, columnY, tableWidth - 2, TABLE_ROW_HEIGHT, 3);
            ctx.stroke();

            // ===================== LEFT SIDE =====================

            // big circle
            ctx.beginPath();
            ctx.fillStyle = '#8b5fff';
            ctx.arc(leftCenterX, centerY, bigRadius, 0, 2 * Math.PI);
            ctx.fill();

            // mid circle
            ctx.beginPath();
            ctx.fillStyle = '#e6e6e6';
            ctx.arc(leftCenterX, centerY, leftMidRadius, 0, 2 * Math.PI);
            ctx.fill();

            if (isSideLeft) {
                // small circle
                ctx.beginPath();
                // ctx.fillStyle = 'cornflowerblue';
                ctx.fillStyle = '#6f94ff';
                ctx.arc(leftCenterX, centerY, smallRadius, 0, 2 * Math.PI);
                ctx.fill();
            }

            // ===================== RIGHT SIDE =====================

            // big circle
            ctx.beginPath();
            ctx.fillStyle = '#8b5fff';
            ctx.arc(rightCenterX, centerY, bigRadius, 0, 2 * Math.PI);
            ctx.fill();

            // mid circle
            ctx.beginPath();
            ctx.fillStyle = '#e6e6e6';
            ctx.arc(rightCenterX, centerY, rightMidRadius, 0, 2 * Math.PI);
            ctx.fill();

            if (isSideRight) {
                // small circle
                ctx.beginPath();
                // ctx.fillStyle = 'cornflowerblue';
                ctx.fillStyle = '#6f94ff';
                ctx.arc(rightCenterX, centerY, smallRadius, 0, 2 * Math.PI);
                ctx.fill();
            }
        }


        if (scaleRef.current > 0.15) {
            // ===================== 8. TABLE HEADER/BODY LINE (BETWEEN HEADER AND BODY) =====================
            // ===================== 10. DASHES FRAME =====================
            // ctx.strokeStyle = '#9aa4ad';
            ctx.strokeStyle = '#a9b3bdff';

            if (color) {
                // ctx.strokeStyle = '#7b91a5';
                ctx.strokeStyle = '#a9b3bdff';
            } else {
                // ctx.strokeStyle = '#7b91a5';
                ctx.strokeStyle = '#a9b3bdff';
            }

            if (!border) {
                ctx.fillStyle = theme.table.header.border;
                ctx.fillStyle = '#4c5866';
                ctx.fillRect(x, y + TABLE_HEADER_HEIGHT, tableWidth, 1.25);
            } else if (border === 'dashes') {
                ctx.lineCap = 'butt';
                ctx.setLineDash([12, 15]);

                const width =
                    scaleRef.current < 1
                        ? Math.min((1.1 * 3) / scaleRef.current, (1.1 * 3) / 0.65)
                        : 1 * 3;
                ctx.lineWidth = width;

                ctx.beginPath();
                ctx.moveTo(x + 6, y + 2.4 + TABLE_HEADER_HEIGHT);
                ctx.lineTo(x + tableWidth, y + 2.4 + TABLE_HEADER_HEIGHT);
                // ctx.stroke();

                // ctx.rect(x - 2.4, y - 2.4, tableWidth + 4.8, tableHeight + 4.8);
                ctx.rect(x - 1.5, y - 1.5, tableWidth + 3, tableHeight + 3);
                ctx.stroke();
            } else if (border === 'dots') {
                ctx.lineCap = 'butt';
                ctx.setLineDash([4, 6]);

                const width =
                    scaleRef.current < 1
                        ? Math.min((1.2 * 3) / scaleRef.current, (1.2 * 3) / 0.65)
                        : 1 * 3;
                ctx.lineWidth = width;

                ctx.beginPath();
                ctx.moveTo(x + 2.4, y + 2.4 + TABLE_HEADER_HEIGHT);
                ctx.lineTo(x + tableWidth, y + 2.4 + TABLE_HEADER_HEIGHT);
                ctx.stroke();

                // ctx.rect(x - 2.4, y - 2.4, tableWidth + 4.8, tableHeight + 4.8);
                ctx.rect(x - 1.5, y - 1.5, tableWidth + 3, tableHeight + 3);
                ctx.stroke();
            } else if (border === 'solid') {
                ctx.lineCap = 'square';
                const width =
                    scaleRef.current < 1
                        ? Math.min((1 * 2.5) / scaleRef.current, (1 * 2.75) / 0.65)
                        : 1 * 2.75;
                ctx.lineWidth = width;

                ctx.beginPath();
                ctx.moveTo(x, y + 2.4 + TABLE_HEADER_HEIGHT);
                ctx.lineTo(x + tableWidth, y + 2.4 + TABLE_HEADER_HEIGHT);
                ctx.stroke();

                // ctx.rect(x - 2.4, y - 2.4, tableWidth + 4.8, tableHeight + 4.8);
                ctx.rect(x - 1.5, y - 1.5, tableWidth + 3, tableHeight + 3);
                ctx.stroke();
            }

            ctx.setLineDash([]);

        }
        // ctx.fillStyle = theme.table.header.border;
        // ctx.fillStyle = '#4c5866';

        // ctx.fillRect(x, y + TABLE_HEADER_HEIGHT, tableWidth, 1.25);


        if (scaleRef.current > 0.15) {

            // ===================== 9. TABLE | EMPTY COLUMN =====================
            // background
            const emptyColumnY =
                y +
                TABLE_HEADER_HEIGHT +
                columnsCount * TABLE_ROW_HEIGHT +
                HEADER_BODY_MARGIN;

            const isHoveredBgStyle =
                !isElementResizing &&
                // is not export enabled
                !isBtnExportImageEnabled &&
                !isSelectionMoving &&
                !isStartedNewLine &&
                !isStartedNewPenSegment && // hovered table and hovered empty column
                hoveredElementIdRef.current === id &&
                hoveredElementIdEmptyColumnRef.current === id;
            // (hoveredElementIdRef.current === id && true)

            ctx.fillStyle = isHoveredBgStyle
                ? theme.table.body.row.hover.background
                : theme.table.body.background;

            // -----------------------------------------------------------
            // asas
            if (bodyColor) {
                ctx.fillStyle = bodyColor;
            }
            // -----------------------------------------------------------

            ctx.beginPath();
            ctx.roundRect(x, emptyColumnY + 1, tableWidth, TABLE_ROW_HEIGHT - 2, 3);
            ctx.fill();

            // points
            const radius = 2.5;
            const distance = 15;

            const pointY = emptyColumnY + TABLE_ROW_HEIGHT / 2; // + 4;
            const leftPointX = x + tableWidth / 2 - distance;
            const centerPointX = x + tableWidth / 2;
            const rightPointX = x + tableWidth / 2 + distance;

            ctx.fillStyle = '#667688';

            ctx.beginPath();
            ctx.arc(leftPointX, pointY, radius, 0, 2 * Math.PI);
            ctx.arc(centerPointX, pointY, radius, 0, 2 * Math.PI);
            ctx.arc(rightPointX, pointY, radius, 0, 2 * Math.PI);
            ctx.fill();
        }
    }

    // ===================== DRAWING TABLE =====================
    function drawTable(element, exportImgCanvas = null) {
        if (viewMode === 'names') {
            drawTableName(element);
            return;
        } else if (viewMode === 'keys') {
            drawTableKey(element);
            return;
        }

        let ctx = contextRef.current;

        if (isBtnExportImageEnabled && exportImgCanvas) {
            const exportImgContext = exportImgCanvas?.getContext('2d');
            ctx = exportImgContext;
        }

        const isSelectionMoving =
            isSelectionClickedRef.current &&
            (selectionStartXYRef.current.x !==
                selectionCurrentXYRef.current.x ||
                selectionStartXYRef.current.y !==
                selectionCurrentXYRef.current.y);
        const isStartedNewLine = lineFromRef.current?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment = newPenSegmentRef.current.isPressed || isBtnPenEnabled;
        const isStartedNewArea = isBtnNewAreaEnabled;
        const isResizingArea = resizeAreaPropsRef.current?.areaId;

        let {
            id,
            x,
            y,
            width: initTableWidth,
            height: tableHeightInit,
            value: {
                headers,
                header: { name },
                columns,
                isHeaderChanged,
                isColumnsChanged,
                isAdded,
                isDeleted,
                border,
                color,
                areaId,
            },
        } = element;

        let startColumnX = x;
        let tableWidth = initTableWidth;
        const TABLE_RED_GREEN_COLUMN_WIDTH = 20;

        if (isDeleted || isAdded || isColumnsChanged) {
            tableWidth += TABLE_RED_GREEN_COLUMN_WIDTH;
            startColumnX += TABLE_RED_GREEN_COLUMN_WIDTH;
        }

        const columnsCount = Object.keys(columns).length;

        const normalTableHeight =
            TABLE_HEADER_HEIGHT +
            HEADER_BODY_MARGIN +
            (25 + 1) * TABLE_ROW_HEIGHT +
            TABLE_BORDER_RADIUS +
            1;
        const changesTableHeight =
            TABLE_HEADER_HEIGHT +
            HEADER_BODY_MARGIN +
            Math.min(columnsCount, 25 + 1) * TABLE_ROW_HEIGHT +
            TABLE_BORDER_RADIUS +
            1;
        // let tableHeight = Object.keys(columns).length > 25 ? normalTableHeight : tableHeightInit;
        let tableHeight = tableHeightInit;

        if (isColumnsChanged) {
            tableHeight = changesTableHeight;
        }

        // ===================== 0. FOCUSED TABLE | OUTLINE FRAME =====================
        if (
            !isBtnNewAreaEnabled &&
            !isResizingArea &&
            // is not export enabled
            !isBtnExportImageEnabled &&
            // table is selected by mouse selection box
            (selectedElementsIdsRef.current.includes(id) ||
                focusedGroupElementsIdsRef.current.includes(id) ||
                // table is focused
                focusedElementsIdsRef.current.includes(id) ||
                // table is hovered and its not new line drawing
                (hoveredElementIdRef.current === id &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment))
        ) {
            ctx.strokeStyle = '#5f88ff'; // ASAP LAST
            // ctx.strokeStyle = '#6f94ff';
            ctx.strokeStyle = 'rgba(176, 200, 255, 0.85)';
            ctx.strokeStyle = 'rgba(176, 200, 255, 1)';
            // ctx.strokeStyle = 'rgba(157, 187, 255, 1)';
            ctx.strokeStyle = 'rgba(119, 160, 255, 1)';

            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3;

            if (
                hoveredElementIdRef.current === id &&
                !focusedElementsIdsRef.current.includes(id) &&
                !selectedElementsIdsRef.current.includes(id) &&
                !focusedGroupElementsIdsRef.current.includes(id)
            ) {
                // ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3 - 0.7;
                // ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3 - 0.7 - 0.1; // ASAP LAST
                ctx.lineWidth =
                    scaleNumbersRef.current.outlineWidth - 0.3 - 0.7 - 0.1;
                // ctx.strokeStyle = 'rgba(95, 136, 255, 0.8)';
            }

            ctx.beginPath();
            ctx.roundRect(
                x - scaleNumbersRef.current.outlineOffset,
                y - scaleNumbersRef.current.outlineOffset - 0.5,
                tableWidth + 2 * scaleNumbersRef.current.outlineOffset,
                tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1,
                0.75
            );
            ctx.stroke();
            // ctx.shadowColor = 'transparent';

            // ctx.fillStyle = '#39424c'; // prev canvas background
            ctx.fillStyle = '#3a495a'; // canvas background

            // // AREA COLOR
            // if (areaId && areasRef.current[areaId]?.value?.color) {
            //     switch (areasRef.current[areaId]?.value?.color) {
            //         case 'blue': {
            //             ctx.fillStyle = 'rgba(56, 125, 254, 0.08)';
            //             break;
            //         }
            //         case 'green': {
            //             ctx.fillStyle = 'rgba(6, 255, 151, 0.06)';
            //             break;
            //         }
            //         case 'purple': {
            //             ctx.fillStyle = 'rgba(116, 84, 232, 0.08)';
            //             break;
            //         }
            //         case 'violet': {
            //             ctx.fillStyle = 'rgba(220, 97, 220, 0.07)';
            //             break;
            //         }
            //         case 'red': {
            //             ctx.fillStyle = 'rgba(255, 0, 0, 0.08)';
            //             ctx.fillStyle = 'rgba(255, 0, 0, 0.03)'; // 0.06 // 0.08
            //             break;
            //         }
            //         case 'default': { // default 'grey'
            //             ctx.fillStyle = 'rgba(175, 197, 240, 0.09)';
            //             break;
            //         }
            //         default: // default 'grey'
            //             ctx.fillStyle = 'rgba(175, 197, 240, 0.09)';
            //             break;
            //     }
            // }

            // ctx.fillRect(
            //     x - scaleNumbersRef.current.outlineOffset,
            //     y - scaleNumbersRef.current.outlineOffset - 0.5,
            //     tableWidth + 2 * scaleNumbersRef.current.outlineOffset,
            //     tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1
            // );
            ctx.strokeRect(
                x - scaleNumbersRef.current.outlineOffset,
                y - scaleNumbersRef.current.outlineOffset - 0.5,
                tableWidth + 2 * scaleNumbersRef.current.outlineOffset,
                tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1
            );
            // ctx.stroke();
        }

        // ===================== 1. FOCUSED TABLE | RESIZE POINTS =====================
        if (
            scaleRef.current > 0.15 &&
            !isBtnRedGreenViewEnabled &&
            // is not export enabled
            !isBtnExportImageEnabled &&
            // is not mouse selection
            !isSelectionMoving &&
            // table is focused
            focusedElementsIdsRef.current.includes(id) &&
            // focused only one table
            focusedElementsIdsRef.current.length === 1
        ) {
            let resizeHandleSize =
                scaleNumbersRef.current.resizeHandleSize - 4 > 0
                    ? scaleNumbersRef.current.resizeHandleSize - 3
                    : scaleNumbersRef.current.resizeHandleSize;
            resizeHandleSize = Math.max(resizeHandleSize, 4);
            let resizeHandleRadius =
                scaleNumbersRef.current.resizeHandleRadius - 1 > 0
                    ? scaleNumbersRef.current.resizeHandleRadius - 1
                    : scaleNumbersRef.current.resizeHandleRadius;
            resizeHandleRadius = Math.max(resizeHandleRadius, 1);

            const resizeOutlineWidth =
                tableWidth + scaleNumbersRef.current.outlineOffset * 2;
            const resizeOutlineHeight =
                tableHeight + scaleNumbersRef.current.outlineOffset * 2;
            const resizeX = x - scaleNumbersRef.current.outlineOffset;
            const resizeY = y - scaleNumbersRef.current.outlineOffset;

            ctx.beginPath();
            ctx.strokeStyle = '#5f88ff';
            ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
            ctx.fillStyle = '#e6e6e6';
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7 + 1.8;
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7 + 1.5;
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 1;

            ctx.roundRect(
                resizeX - resizeHandleSize / 2 - 1,
                resizeY - resizeHandleSize / 2 - 1,
                resizeHandleSize + 2,
                resizeHandleSize + 2,
                resizeHandleRadius
            );
            ctx.roundRect(
                resizeX + resizeOutlineWidth - resizeHandleSize / 2 - 1,
                resizeY - resizeHandleSize / 2 - 1,
                resizeHandleSize + 2,
                resizeHandleSize + 2,
                resizeHandleRadius
            );
            ctx.roundRect(
                resizeX - resizeHandleSize / 2 - 1,
                resizeY + resizeOutlineHeight - resizeHandleSize / 2 - 1,
                resizeHandleSize + 2,
                resizeHandleSize + 2,
                resizeHandleRadius
            );
            ctx.roundRect(
                resizeX + resizeOutlineWidth - resizeHandleSize / 2 - 1,
                resizeY + resizeOutlineHeight - resizeHandleSize / 2 - 1,
                resizeHandleSize + 2,
                resizeHandleSize + 2,
                resizeHandleRadius
            );

            ctx.stroke();
            ctx.fill();
        }

        // ===================== 2. TABLE SHADOW =====================
        ctx.shadowColor = theme.table.shadow.color;
        ctx.shadowBlur = 8; // Розмиття тіні
        ctx.shadowOffsetX = 2; // Зміщення по горизонталі
        ctx.shadowOffsetY = 2; // Зміщення по вертикалі

        // ===================== 3. TABLE BACKGROUND | RECTANGLE =====================
        ctx.beginPath();
        ctx.moveTo(x + TABLE_BORDER_RADIUS, y); // Верхній лівий кут
        ctx.lineTo(x + tableWidth - TABLE_BORDER_RADIUS, y); // Верхній правий кут
        ctx.quadraticCurveTo(
            x + tableWidth,
            y,
            x + tableWidth,
            y + TABLE_BORDER_RADIUS
        ); // Верхня права дуга
        ctx.lineTo(x + tableWidth, y + tableHeight - TABLE_BORDER_RADIUS); // Нижня права сторона
        ctx.quadraticCurveTo(
            x + tableWidth,
            y + tableHeight,
            x + tableWidth - TABLE_BORDER_RADIUS,
            y + tableHeight
        ); // Нижня права дуга
        ctx.lineTo(x + TABLE_BORDER_RADIUS, y + tableHeight); // Нижній лівий кут
        ctx.quadraticCurveTo(
            x,
            y + tableHeight,
            x,
            y + tableHeight - TABLE_BORDER_RADIUS
        ); // Нижня ліва дуга
        ctx.lineTo(x, y + TABLE_BORDER_RADIUS); // Верхня ліва сторона
        ctx.quadraticCurveTo(x, y, x + TABLE_BORDER_RADIUS, y); // Верхня ліва дуга

        // ctx.fillStyle = theme.table.body.background;
        ctx.fillStyle = isAdded
            ? '#003931'
            : // ? '#0e4632'
            isDeleted
                ? // ? '#58111b'
                '#550225'
                : theme.table.body.background;

        // -----------------------------------------------------------
        let headerColor = null;
        let bodyColor = theme.table.body.background;
        let focusedBodyColor = '#1b1f22';
        let hoveredBodyColor = theme.table.body.row.hover.background;

        if (color) {
            switch (color) {
                case 'blue': {
                    // headerColor = '#031c39a2'; // #011833b9
                    // bodyColor = '#05274e6e';

                    headerColor = '#031c39'; // #011833b9
                    bodyColor = '#05274e';
                    focusedBodyColor = '#042040';
                    hoveredBodyColor = '#0b3564';


                    headerColor = '#092d58ff'; // #011833b9
                    headerColor = '#082850ff'; // #011833b9
                    headerColor = '#0b2a50ff'; // #011833b9
                    // headerColor = '#072549ff'; // #011833b9
                    break;
                }
                case 'green': {
                    // headerColor = '#012b22';
                    // bodyColor = '#003f31';

                    // from Red/Green View
                    headerColor = '#002e28';
                    bodyColor = '#003931';
                    focusedBodyColor = '#002e29';
                    hoveredBodyColor = '#04473f';


                    headerColor = '#01372fff';
                    headerColor = '#033c33ff';
                    break;
                }
                case 'orange': {
                    headerColor = '#451d01'; // //562605 662e09
                    bodyColor = '#6a2c03';
                    focusedBodyColor = '#05274e';
                    hoveredBodyColor = '#05274e';
                    break;
                }
                case 'yellow': {
                    headerColor = '#573900';
                    bodyColor = '#6d4700'; //ad7000
                    focusedBodyColor = '#05274e';
                    hoveredBodyColor = '#05274e';
                    break;
                }
                case 'pink': {
                    headerColor = '#660928';
                    bodyColor = '#7e1b3c';
                    focusedBodyColor = '#05274e';
                    hoveredBodyColor = '#05274e';
                    break;
                }
                case 'purple': {
                    headerColor = '#220131';
                    bodyColor = '#320149';
                    focusedBodyColor = '#29013c';
                    hoveredBodyColor = '#40045d';

                    headerColor = '#32004aff';
                    headerColor = '#34054aff';
                    break;
                }
                case 'violet': {
                    // headerColor = '#2f002f';
                    headerColor = '#350235';
                    bodyColor = '#490149';
                    focusedBodyColor = '#3d013d';
                    hoveredBodyColor = '#530353';


                    headerColor = '#61044eff';
                    break;
                }
                case 'red': {
                    // headerColor = '#440813';
                    // bodyColor = '#5b1623';

                    // from Red/Green View
                    headerColor = '#47021f';
                    bodyColor = '#550225';
                    focusedBodyColor = '#47021f';
                    hoveredBodyColor = '#5d0429';

                    headerColor = '#530123ff';
                    headerColor = '#6e002eff';
                    // headerColor = '#6e0030ff';

                    break;
                }
                case 'default': {
                    break;
                }
                default:
                    break;
            }
        }


        bodyColor = theme.table.body.background;
        focusedBodyColor = '#1b1f22';
        hoveredBodyColor = theme.table.body.row.hover.background;

        // asas
        if (bodyColor) {
            ctx.fillStyle = bodyColor;
        }
        // -----------------------------------------------------------

        ctx.fill();

        ctx.lineWidth = 1;

        // Reset shadow after drawing the table
        ctx.shadowColor = 'transparent';

        // ===================== 4. TABLE MULTI-HEADERS =====================
        if (scaleRef.current > 0.15) {

            // const headersCount = Object.keys(headers).length;
            if (isBtnRedGreenViewEnabled && headers) {
                const headersKeys = Object.keys(headers);
                for (let index = 0; index < headersKeys.length; index++) {
                    const headerKey = headersKeys[index];
                    const header = headers[headerKey];
                    const headerY = y + index * TABLE_HEADER_HEIGHT;

                    // ===================== 4.1. TABLE HEADER | BACKGROUND =====================
                    // draw background rectangle
                    ctx.fillStyle =
                        header.isDeleted || isDeleted
                            ? // ? '#4d0e18'
                            '#47021f'
                            : header.isAdded || isAdded
                                ? '#002e28'
                                : // ? '#002b25'
                                // ? '#0b3929'
                                '#16191d';
                    // : theme.table.header.background;

                    ctx.beginPath();
                    ctx.moveTo(x + TABLE_BORDER_RADIUS, headerY); // Верхній лівий кут заголовка
                    ctx.lineTo(x + tableWidth - TABLE_BORDER_RADIUS, headerY); // Верхній правий кут заголовка
                    ctx.quadraticCurveTo(
                        x + tableWidth,
                        headerY,
                        x + tableWidth,
                        headerY + TABLE_BORDER_RADIUS
                    ); // Верхня права дуга заголовка
                    ctx.lineTo(x + tableWidth, headerY + TABLE_HEADER_HEIGHT); // Нижня права сторона заголовка
                    ctx.lineTo(x, headerY + TABLE_HEADER_HEIGHT); // Нижня ліва сторона заголовка
                    ctx.lineTo(x, headerY + TABLE_BORDER_RADIUS); // Верхня ліва сторона заголовка
                    ctx.quadraticCurveTo(
                        x,
                        headerY,
                        x + TABLE_BORDER_RADIUS,
                        headerY
                    ); // Верхня ліва дуга заголовка
                    ctx.fill();


                    if (scaleRef.current > 0.35) {

                        // ===================== 4.1. TABLE HEADER | TABLE ICON =====================
                        const pathHeader =
                            'M45,0 L3,0 C1.343,0 0,1.344 0,3 L0,12 L48,12 L48,3 C48,1.344 46.657,0 45,0';
                        const pathCells =
                            'M0,45 C0,46.656 1.343,48 3,48 L12,48 L12,36 L0,36 L0,45 M0,36 L12,36 L12,24 L0,24 M12,48 L24,48 L24,36 L12,36 M24,48 L36,48 L36,36 L24,36 M12,36 L24,36 L24,24 L12,24 M24,36 L36,36 L36,24 L24,24 M36,36 L48,36 L48,24 L36,24 M0,24 L12,24 L12,12 L0,12 M12,24 L24,24 L24,12 L12,12 M24,24 L36,24 L36,12 L24,12 M36,24 L48,24 L48,12 L36,12 M36,36 L36,48 L45,48 C46.657,48 48,46.656 48,45 L48,36 L36,36 Z';
                        const pathLines =
                            'M45,0 L3,0 C1.343,0 0,1.343 0,3 L0,45 C0,46.657 1.343,48 3,48 L45,48 C46.657,48 48,46.657 48,45 L48,3 C48,1.343 46.657,0 45,0 M0,12 L48,12M0,24 L48,24 M0,36 L48,36 M24,12 L24,48 M36,12 L36,48 M12,12 L12,48';
                        const pathHeaderCanvas = new Path2D(pathHeader);
                        const pathCellsCanvas = new Path2D(pathCells);
                        const pathLinesCanvas = new Path2D(pathLines);

                        ctx.save(); // Зберігаємо поточний стан канвасу
                        // ctx.scale(0.36, 0.36); // Масштабування шляху для більшого розміру
                        ctx.scale(0.38, 0.38); // Масштабування шляху для більшого розміру

                        // ctx.translate((x + TABLE_BORDER_RADIUS + 10) / 0.36, (y + 11.5) / 0.36); // Переміщуємо початкову точку малювання

                        // ctx.translate((x + TABLE_BORDER_RADIUS + 10) / 0.38, (y + 11.5) / 0.38); // Переміщуємо початкову точку малювання
                        ctx.translate(
                            (x + TABLE_BORDER_RADIUS + 8) / 0.38,
                            (headerY + 10.25) / 0.38
                        ); // Переміщуємо початкову точку малювання
                        ctx.strokeStyle = theme.controls.background;
                        ctx.lineWidth = 2; // Лінії мають бути тонкими після масштабування

                        // ctx.fillStyle = '#333b44';
                        ctx.fillStyle =
                            header.isDeleted || isDeleted
                                ? '#661320'
                                : header.isAdded || isAdded
                                    ? '#10533c'
                                    : '#333b44';

                        if (
                            !header.isDeleted &&
                            !isDeleted &&
                            !header.isAdded &&
                            !isAdded
                        ) {
                            ctx.beginPath();
                            ctx.fill(pathHeaderCanvas);
                        }
                        // ctx.fillStyle = '#444e5a';
                        ctx.fillStyle =
                            header.isDeleted || isDeleted
                                ? '#821829'
                                : header.isAdded || isAdded
                                    ? '#14694c'
                                    : '#444e5a';

                        if (
                            !header.isDeleted &&
                            !isDeleted &&
                            !header.isAdded &&
                            !isAdded
                        ) {
                            ctx.beginPath();
                            ctx.fill(pathCellsCanvas);
                        }

                        // ctx.strokeStyle = '#262c31';
                        ctx.strokeStyle =
                            header.isDeleted || isDeleted
                                ? '#450d16'
                                : header.isAdded || isAdded
                                    ? '#0a3325'
                                    : '#262c31';

                        if (
                            !header.isDeleted &&
                            !isDeleted &&
                            !header.isAdded &&
                            !isAdded
                        ) {
                            ctx.beginPath();
                            ctx.stroke(pathLinesCanvas);
                        }

                        ctx.restore(); // Відновлюємо попередній стан канвасу

                    }
                    // ===================== 4.1. TABLE HEADER | TEXT =====================
                    let mesureTextSum = 0;
                    let marginLeft = 0;

                    if (header.isDeleted || header.isAdded) {
                        ctx.textAlign = 'left';
                        ctx.font = `600 21px Inter, sans-serif`;
                        header.highlights.forEach(([isHighlighted, text]) => {
                            if (isHighlighted) {
                                const mesureTextHighlight =
                                    ctx.measureText(text).width + 5;
                                mesureTextSum += mesureTextHighlight + 1.65 * 2;
                            } else {
                                const mesureText = ctx.measureText(text).width;
                                mesureTextSum += mesureText;
                            }
                        });

                        marginLeft = tableWidth / 2 - mesureTextSum / 2;
                    }
                    // let positionX = x + 45;
                    let positionX = x + marginLeft;

                    if (header.isDeleted) {
                        header.highlights.forEach(([isHighlighted, text]) => {
                            if (isHighlighted) {
                                ctx.textAlign = 'left';
                                ctx.font = `600 21px Inter, sans-serif`;
                                const mesureTextHighlight =
                                    ctx.measureText(text).width + 5;
                                positionX += 1.65;

                                ctx.fillStyle = '#a30547';
                                ctx.beginPath();
                                ctx.roundRect(
                                    positionX,
                                    headerY + 8,
                                    mesureTextHighlight,
                                    26,
                                    4
                                );
                                ctx.fill();

                                ctx.fillStyle = '#b5bdc5';
                                ctx.fillText(text, positionX + 2.5, headerY + 27);

                                positionX += mesureTextHighlight + 1.65;
                            } else {
                                ctx.textAlign = 'left';
                                ctx.font = `600 21px Inter, sans-serif`;
                                const mesureText = ctx.measureText(text).width;

                                ctx.fillStyle = theme.table.header.color;
                                ctx.fillText(text, positionX, headerY + 27);

                                positionX += mesureText;
                            }
                        });
                    } else if (header.isAdded) {
                        header.highlights.forEach(([isHighlighted, text]) => {
                            if (isHighlighted) {
                                ctx.textAlign = 'left';
                                ctx.font = `600 21px Inter, sans-serif`;
                                const mesureTextHighlight =
                                    ctx.measureText(text).width + 5;
                                positionX += 1.65;

                                ctx.fillStyle = '#008a6e';
                                // ctx.fillStyle = '#157652';
                                ctx.beginPath();
                                ctx.roundRect(
                                    positionX,
                                    headerY + 8,
                                    mesureTextHighlight,
                                    26,
                                    4
                                );
                                ctx.fill();

                                ctx.fillStyle = '#b5bdc5';
                                ctx.fillText(text, positionX + 2.5, headerY + 27);

                                positionX += mesureTextHighlight + 1.65;
                            } else {
                                ctx.textAlign = 'left';
                                ctx.font = `600 21px Inter, sans-serif`;
                                const mesureText = ctx.measureText(text).width;

                                ctx.fillStyle = theme.table.header.color;
                                ctx.fillText(text, positionX, headerY + 27);

                                positionX += mesureText;
                            }
                        });
                    } else {
                        ctx.textAlign = 'center';
                        ctx.fillStyle = theme.table.header.color;
                        ctx.font = `600 21px Inter, sans-serif`;
                        ctx.fillText(header.name, x + tableWidth / 2, headerY + 27);
                    }
                }

                // ===================== 5. TABLE HEADER/BODY PADDING =====================
                const lastHeaderY =
                    y + (headersKeys.length - 1) * TABLE_HEADER_HEIGHT;
                // ctx.fillStyle = theme.table.body.background; // Колір заголовка
                ctx.fillStyle = isAdded
                    ? '#003931'
                    : // ? '#0e4632'
                    isDeleted
                        ? '#58111b'
                        : theme.table.body.background;

                ctx.fillRect(x, lastHeaderY + TABLE_HEADER_HEIGHT, tableWidth, 4);
            } else {
                // ===================== 4.1. TABLE HEADER | BACKGROUND =====================
                // draw background rectangle
                ctx.fillStyle = '#16191d'; //theme.table.header.background;
                ctx.fillStyle = '#0d0e10';
                ctx.fillStyle = '#121316';
                ctx.fillStyle = 'black';

                // -----------------------------------------------------------

                // asas header
                if (headerColor) {
                    ctx.fillStyle = headerColor;
                }

                // -----------------------------------------------------------

                // ctx.fillStyle = theme.table.header.background;

                // ctx.fillStyle = isDeleted
                // 	? '#4d0e18'
                // 	: isAdded
                // 		? '#0b3929'
                // 		: theme.table.header.background;

                // -----------------------------------------------------------
                let headColor = '#333b44';
                let cellsColor = '#444e5a';
                let linesColor = '#262c31';

                if (color) {
                    switch (color) {
                        case 'blue': {
                            headColor = '#0f325a';
                            cellsColor = '#15467d';
                            linesColor = '#071b31';
                            break;
                        }
                        case 'green': {
                            headColor = '#10533c';
                            cellsColor = '#14694c';
                            linesColor = '#0b3b2a';
                            break;
                        }
                        case 'orange': {
                            // headerColor = '#451d01'; // //562605 662e09
                            // bodyColor = '#6a2c03';
                            // headColor = '#40085a';
                            // cellsColor = '#540b76';
                            // linesColor = '#300643';
                            break;
                        }
                        case 'yellow': {
                            // headerColor = '#573900';
                            // bodyColor = '#6d4700'; //ad7000
                            // headColor = '#40085a';
                            // cellsColor = '#540b76';
                            // linesColor = '#300643';
                            break;
                        }
                        case 'pink': {
                            // headColor = '#760b2f';
                            // cellsColor = '#900e39';
                            // linesColor = '#660928';

                            break;
                        }
                        case 'purple': {
                            headColor = '#40085a';
                            cellsColor = '#540b76';
                            linesColor = '#300643';
                            break;
                        }
                        case 'violet': {
                            headColor = '#4d064d';
                            cellsColor = '#6c0c6c';
                            linesColor = '#350235';
                            break;
                        }
                        case 'red': {
                            headColor = '#661320';
                            cellsColor = '#821829';
                            linesColor = '#490e16';
                            break;
                        }
                        case 'default': {
                            break;
                        }
                        default:
                            break;
                    }
                }
                // -----------------------------------------------------------

                ctx.beginPath();
                ctx.moveTo(x + TABLE_BORDER_RADIUS, y); // Верхній лівий кут заголовка
                ctx.lineTo(x + tableWidth - TABLE_BORDER_RADIUS, y); // Верхній правий кут заголовка
                ctx.quadraticCurveTo(
                    x + tableWidth,
                    y,
                    x + tableWidth,
                    y + TABLE_BORDER_RADIUS
                ); // Верхня права дуга заголовка
                ctx.lineTo(x + tableWidth, y + TABLE_HEADER_HEIGHT); // Нижня права сторона заголовка
                ctx.lineTo(x, y + TABLE_HEADER_HEIGHT); // Нижня ліва сторона заголовка
                ctx.lineTo(x, y + TABLE_BORDER_RADIUS); // Верхня ліва сторона заголовка
                ctx.quadraticCurveTo(x, y, x + TABLE_BORDER_RADIUS, y); // Верхня ліва дуга заголовка
                ctx.fill();

                if (scaleRef.current > 0.35) {

                    // ===================== 4.1. TABLE HEADER | ICON =====================
                    const pathHeader =
                        'M45,0 L3,0 C1.343,0 0,1.344 0,3 L0,12 L48,12 L48,3 C48,1.344 46.657,0 45,0';
                    const pathCells =
                        'M0,45 C0,46.656 1.343,48 3,48 L12,48 L12,36 L0,36 L0,45 M0,36 L12,36 L12,24 L0,24 M12,48 L24,48 L24,36 L12,36 M24,48 L36,48 L36,36 L24,36 M12,36 L24,36 L24,24 L12,24 M24,36 L36,36 L36,24 L24,24 M36,36 L48,36 L48,24 L36,24 M0,24 L12,24 L12,12 L0,12 M12,24 L24,24 L24,12 L12,12 M24,24 L36,24 L36,12 L24,12 M36,24 L48,24 L48,12 L36,12 M36,36 L36,48 L45,48 C46.657,48 48,46.656 48,45 L48,36 L36,36 Z';
                    const pathLines =
                        'M45,0 L3,0 C1.343,0 0,1.343 0,3 L0,45 C0,46.657 1.343,48 3,48 L45,48 C46.657,48 48,46.657 48,45 L48,3 C48,1.343 46.657,0 45,0 M0,12 L48,12M0,24 L48,24 M0,36 L48,36 M24,12 L24,48 M36,12 L36,48 M12,12 L12,48';
                    const pathHeaderCanvas = new Path2D(pathHeader);
                    const pathCellsCanvas = new Path2D(pathCells);
                    const pathLinesCanvas = new Path2D(pathLines);

                    ctx.save(); // Зберігаємо поточний стан канвасу
                    // ctx.scale(0.36, 0.36); // Масштабування шляху для більшого розміру
                    ctx.scale(0.38, 0.38); // Масштабування шляху для більшого розміру

                    // ctx.translate((x + TABLE_BORDER_RADIUS + 10) / 0.36, (y + 11.5) / 0.36); // Переміщуємо початкову точку малювання

                    // ctx.translate((x + TABLE_BORDER_RADIUS + 10) / 0.38, (y + 11.5) / 0.38); // Переміщуємо початкову точку малювання
                    ctx.translate(
                        (x + TABLE_BORDER_RADIUS + 8) / 0.38,
                        (y + 10.25) / 0.38
                    ); // Переміщуємо початкову точку малювання
                    ctx.strokeStyle = theme.controls.background;
                    ctx.lineWidth = 2; // Лінії мають бути тонкими після масштабування

                    // ctx.fillStyle = '#333b44';
                    ctx.fillStyle = headColor;
                    ctx.beginPath();
                    ctx.fill(pathHeaderCanvas);

                    // ctx.fillStyle = '#444e5a';
                    ctx.fillStyle = cellsColor;
                    ctx.beginPath();
                    ctx.fill(pathCellsCanvas);

                    // ctx.strokeStyle = '#262c31';
                    ctx.strokeStyle = linesColor;
                    ctx.beginPath();
                    ctx.stroke(pathLinesCanvas);

                    ctx.restore(); // Відновлюємо попередній стан канвасу


                    // ===================== 4.1.2. TABLE HEADER | DESCRIPTION ICON =====================
                    // if (element.value.description || element.value.isTableWithIndexes) {
                    if (element.value.description) {
                        let isHeaderEditedAndChanged =
                            columnState?.elementId === id &&
                            columnState?.insideElementType === 'header' &&
                            columnNameState !== name;

                        // POINT 1
                        let nameValue = name;
                        if (isHeaderEditedAndChanged) {
                            // show local state value
                            nameValue = columnNameState;
                        }

                        if (nameValue === '') {
                            nameValue = 'untitled';
                        }


                        ctx.font = `600 21px Inter, sans-serif`;
                        const tableNameWidth = ctx.measureText(nameValue).width;

                        const pathDescription = 'M509.039,324.633a1.891,1.891,0,0,1,1.889,1.89V349.7a1.891,1.891,0,0,1-1.889,1.89H485.862a1.892,1.892,0,0,1-1.89-1.89V326.523a1.892,1.892,0,0,1,1.89-1.89h23.177m0-2H485.862a3.89,3.89,0,0,0-3.89,3.89V349.7a3.89,3.89,0,0,0,3.89,3.89h23.177a3.889,3.889,0,0,0,3.889-3.89V326.523a3.889,3.889,0,0,0-3.889-3.89Z M505.105,332.478H489.523a1,1,0,1,1,0-2h15.582a1,1,0,0,1,0,2Z M505.105,339.188H489.523a1,1,0,1,1,0-2h15.582a1,1,0,0,1,0,2Z M505.105,345.9H489.523a1,1,0,0,1,0-2h15.582a1,1,0,0,1,0,2Z';
                        const pathDescriptionCanvas = new Path2D(pathDescription);

                        ctx.save(); // Зберігаємо поточний стан канвасу
                        ctx.scale(0.55, 0.55); // Масштабування шляху для більшого розміру

                        // ctx.translate((x + 8) / 0.55, (y - 149) / 0.55); // Переміщуємо початкову точку малювання
                        ctx.translate((x - 264 + tableWidth / 2 + tableNameWidth / 2 + 5.5 - 0.3) / 0.55, (y - 166 + 0.25) / 0.55); // Переміщуємо початкову точку малювання

                        // ctx.strokeStyle = theme.controls.background;
                        ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування
                        // ctx.lineWidth = 0; // Лінії мають бути тонкими після масштабування

                        ctx.fillStyle = theme.table.header.color;
                        ctx.strokeStyle = theme.table.header.color;

                        ctx.beginPath();
                        ctx.stroke(pathDescriptionCanvas);
                        ctx.fill(pathDescriptionCanvas);

                        ctx.restore(); // Відновлюємо попередній стан канвасу
                    }

                }
                // ===================== 4.1. TABLE HEADER | TEXT =====================
                let isHeaderFocused =
                    focusedInsideElementState?.elementId === id &&
                    focusedInsideElementState?.insideElementType === 'header';

                if (
                    isStartedLineState ||
                    isBoardMovingStarted ||
                    isBoardMoving ||
                    isScaling ||
                    isMovingElementId ||
                    isElementResizing ||
                    // header is not focused
                    !isHeaderFocused
                ) {
                    let isHeaderEditedAndChanged =
                        columnState?.elementId === id &&
                        columnState?.insideElementType === 'header' &&
                        columnNameState !== name;

                    // POINT 1
                    let nameValue = name;
                    if (isHeaderEditedAndChanged) {
                        // show local state value
                        nameValue = columnNameState;
                    }

                    if (isElementResizing) {
                        nameValue = name;
                    }

                    if (nameValue === '') {
                        nameValue = 'untitled';
                    }

                    // if (
                    // 	!focusedInsideElementState ||
                    // 	focusedInsideElementState.elementId !== id ||
                    // 	focusedInsideElementState.insideElementType !== 'header'
                    // ) {
                    ctx.textAlign = 'center';
                    ctx.fillStyle = theme.table.header.color;
                    ctx.font = `600 21px Inter, sans-serif`;
                    ctx.fillText(nameValue, x + tableWidth / 2, y + 27);
                } else {
                    ctx.strokeStyle = '#5f88ff';
                    ctx.strokeStyle = 'rgba(116, 155, 246, 1)';
                    // ctx.strokeStyle = 'rgba(125, 164, 255, 1)';
                    // ctx.strokeStyle = 'rgba(125, 170, 255, 1)';
                    // ctx.strokeStyle = 'rgba(125, 166, 255, 1)';

                    ctx.strokeStyle = 'rgba(124, 160, 237, 1)';

                    // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
                    ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;

                    ctx.beginPath();
                    ctx.roundRect(
                        x + 45,
                        y + 5,
                        tableWidth - 90,
                        TABLE_HEADER_HEIGHT - 11,
                        3
                    );
                    // ctx.roundRect(x + 1, columnY+1, tableWidth - 2, TABLE_ROW_HEIGHT -2, 3);
                    ctx.stroke();
                }
                // ===================== 5. TABLE HEADER/BODY PADDING =====================
                ctx.fillStyle = theme.table.body.background; // Колір заголовка
                // -----------------------------------------------------------
                // asas
                // if (bodyColor) {
                // 	ctx.fillStyle = bodyColor;
                // }
                // // -----------------------------------------------------------
                // ctx.fillRect(x, y + TABLE_HEADER_HEIGHT, tableWidth, 4);
            }
        }
        // ===================== 6. TABLE BODY =====================
        // ===================== 6. TABLE COLUMNS =====================
        ctx.shadowColor = null;
        ctx.shadowBlur = 0; // Розмиття тіні
        ctx.shadowOffsetX = 0; // Зміщення по горизонталі
        ctx.shadowOffsetY = 0; // Зміщення по вертикалі


        const headersCount = headers ? Object.keys(headers).length : 1;
        // const keys = Object.keys(columns).slice(0, 25);
        const keys = Object.keys(columns);

        if (scaleRef.current > 0.15) {

            for (let index = 0; index < keys.length; index++) {
                const key = keys[index];
                const column = columns[key];
                const columnY =
                    y +
                    headersCount * TABLE_HEADER_HEIGHT +
                    index * TABLE_ROW_HEIGHT +
                    HEADER_BODY_MARGIN;

                // if (column.isChanged && column.isDeleted && column.name !== 'column_new') {
                // 	// ctx.fillStyle = '#566473'; // Колір заголовка
                // 	// ctx.fillStyle = '#8b5fff'; // Колір заголовка
                // 	ctx.fillStyle = null; // Колір заголовка

                // 	// ctx.fillRect(x, columnY - 1.35, tableWidth, 1.35);
                // 	ctx.fillRect(x, columnY - 2, tableWidth, 2);
                // }

                // column's background is light grey
                const isHoveredBgStyle =
                    !isElementResizing &&
                    // is not export enabled
                    !isBtnExportImageEnabled &&
                    !isSelectionMoving &&
                    !isStartedNewLine &&
                    !isBtnNewAreaEnabled &&
                    !isResizingArea &&
                    !isStartedNewPenSegment && // hovered table and hovered column
                    ((hoveredElementIdRef.current === id &&
                        hoveredInsideElementRef.current?.id === column.id) ||
                        // hovered column's point
                        hoveredPointRef.current?.insideElementId === column.id);

                // column's background is black
                const isFocusedBgStyle =
                    focusedInsideElementState?.elementId === id &&
                    focusedInsideElementState?.insideElementId === column.id;

                // column's background is black
                const isRelatedToFocusedElementsBgStyle =
                    focusedElementsRelatedPropsRef.current?.insideElementsIds.includes(
                        column.id
                    );

                // column's background is black
                const isRelatedToHoveredElementBgStyle =
                    !isStartedNewPenSegment &&
                    hoveredElementRelatedPropsRef.current?.insideElementsIds.includes(
                        column.id
                    );

                // column's background is black
                const isRelatedToFocusedLineBgStyle =
                    focusedLineRelatedPropsRef.current?.insideElementsIds.includes(
                        column.id
                    );

                // column's background is black
                const isRelatedToHoveredLineBgStyle =
                    !isStartedNewPenSegment &&
                    hoveredLineRelatedPropsRef.current?.insideElementsIds.includes(
                        column.id
                    );

                // column's background is black
                const isColumnsFromToNewLineBgStyle =
                    // new line's column 'from'
                    (lineFromRef.current?.elementId === id &&
                        lineFromRef.current?.insideElementId === column.id) ||
                    // new line's column 'to'
                    (hoveredElementIdRef.current === id &&
                        hoveredInsideElementRef.current?.id === column.id &&
                        lineFromRef.current?.insideElementId &&
                        lineFromRef.current?.insideElementId !==
                        hoveredInsideElementRef.current?.id);

                // ===================== 6.1. TABLE COLUMNS | START DRAWING =====================
                let columnColor = theme.table.body.background;
                let focusedColumnColor = '#1e2226';
                let hoveredColumnColor = theme.table.body.row.hover.background;

                // -----------------------------------------------------------
                // asas
                if (bodyColor) {
                    columnColor = bodyColor;
                    focusedColumnColor = focusedBodyColor ? focusedBodyColor : bodyColor;
                    hoveredColumnColor = hoveredBodyColor ? hoveredBodyColor : bodyColor;
                }
                // -----------------------------------------------------------

                ctx.fillStyle =
                    column.isAdded || isAdded
                        ? isHoveredBgStyle
                            ? '#004239'
                            : // ? '#0f5037'
                            '#003931'
                        : // : '#003931'
                        // : '#003c34'
                        // : '#003f31cf'
                        // : '#003f30d9'
                        // : '#003f30'
                        // : '#004739'
                        // : '#004f40'
                        // ? '#0e4632'
                        column.isDeleted || isDeleted
                            ? isHoveredBgStyle
                                ? '#60022a'
                                : // ? '#6a1421'
                                '#550225'
                            : // : '#580226'
                            // : '#5e0228'
                            // ? '#58111b'
                            (isFocusedBgStyle ||
                                isRelatedToFocusedElementsBgStyle ||
                                isRelatedToHoveredElementBgStyle ||
                                isRelatedToFocusedLineBgStyle ||
                                isRelatedToHoveredLineBgStyle ||
                                isColumnsFromToNewLineBgStyle) &&
                                !isBtnExportImageEnabled &&
                                !isBtnNewAreaEnabled &&
                                !isResizingArea
                                ? focusedColumnColor
                                : // ? '#0b3929'
                                isHoveredBgStyle
                                    ? hoveredColumnColor
                                    : columnColor;

                // ctx.fillStyle = '#0e4632';
                ctx.beginPath();

                // TODO: temp row
                if (column.isDeleted || column.isAdded) {
                    if (column.isDeleted || isDeleted) {
                        // ctx.fillRect(x, columnY, tableWidth, TABLE_ROW_HEIGHT);
                        ctx.fillRect(
                            x,
                            columnY + 1,
                            tableWidth,
                            TABLE_ROW_HEIGHT - 2
                        );
                    } else {
                        // ctx.fillRect(x, columnY, tableWidth, TABLE_ROW_HEIGHT);
                        ctx.fillRect(
                            x,
                            columnY + 1,
                            tableWidth,
                            TABLE_ROW_HEIGHT - 2
                        );
                    }
                } else {
                    ctx.roundRect(
                        x,
                        columnY + 1,
                        tableWidth,
                        TABLE_ROW_HEIGHT - 2,
                        3
                    );

                    // ctx.roundRect(x, columnY, tableWidth, TABLE_ROW_HEIGHT, 3);
                }
                // ctx.roundRect(x, columnY + 1, tableWidth, TABLE_ROW_HEIGHT - 2, 3);
                ctx.fill();

                // if (column.isChanged && column.isAdded && column.name !== 'column_new') {
                // 	// ctx.fillStyle = '#566473'; // Колір заголовка
                // 	// ctx.fillStyle = '#8b5fff'; // Колір заголовка
                // 	ctx.fillStyle = null; // Колір заголовка

                // 	// ctx.fillRect(x, columnY + TABLE_ROW_HEIGHT -1.35, tableWidth, 1.35);

                // 	// ctx.fillRect(x, columnY, 1.2, TABLE_ROW_HEIGHT);
                // 	// ctx.fillRect(x + tableWidth -1, columnY, 1.2, TABLE_ROW_HEIGHT);

                // 	ctx.fillRect(x, columnY + TABLE_ROW_HEIGHT -2, tableWidth, 2);

                // 	ctx.fillRect(x, columnY, 2, TABLE_ROW_HEIGHT);
                // 	ctx.fillRect(x + tableWidth -2, columnY, 2, TABLE_ROW_HEIGHT);

                // }
                // if (column.isChanged && column.isDeleted && column.name !== 'column_new') {
                // 	// ctx.fillStyle = '#566473'; // Колір заголовка
                // 	// ctx.fillStyle = '#8b5fff'; // Колір заголовка
                // 	ctx.fillStyle = null; // Колір заголовка

                // 	// ctx.fillRect(x, columnY, 1.2, TABLE_ROW_HEIGHT);
                // 	// ctx.fillRect(x + tableWidth -1, columnY - 1.35, 1.2, TABLE_ROW_HEIGHT + 1.35);

                // 	ctx.fillRect(x, columnY, 2, TABLE_ROW_HEIGHT);
                // 	ctx.fillRect(x + tableWidth -2, columnY - 2, 2, TABLE_ROW_HEIGHT + 2);
                // }

                let columnNameFontSize = 400;
                ctx.font = `${columnNameFontSize} 19px Inter, sans-serif`;

                // ===================== 6.2. TABLE COLUMNS | PRIMARY KEY & FOREIGN KEY =====================
                if (column.isPK || column.isFK) {
                    if (
                        (column.isDeleted || column.isAdded) &&
                        (column.isPKChanged || column.isFKChanged)
                    ) {
                        ctx.fillStyle = column.isDeleted ? '#a30547' : '#008a6e';

                        ctx.beginPath();
                        // ctx.roundRect(startColumnX + 4, columnY + 8.5, 25 +6, 16.5 +8.5, 4);
                        ctx.roundRect(
                            startColumnX + 4 - 1,
                            columnY + 4.75,
                            20,
                            16.5 + 8.5,
                            4
                        );
                        // ctx.roundRect(x + (tableWidth - 27 - 25 - 1) -3, columnY + 23 - 14 -4.25, 25 +6, 16.5 +8.5, 3.5);

                        ctx.fill();
                    }

                    columnNameFontSize = 700;
                    ctx.font = `${columnNameFontSize} 19px Inter, sans-serif`;

                    const path =
                        'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z';
                    const pathCanvas = new Path2D(path);

                    ctx.save(); // Зберігаємо поточний стан канвасу
                    // ctx.translate(x + 4, columnY + 8.5); // Переміщуємо початкову точку малювання
                    // ctx.translate(startColumnX + 5.5, columnY + 8.5); // Переміщуємо початкову точку малювання
                    // ctx.translate(startColumnX + 4, columnY + 8.5); // Переміщуємо початкову точку малювання
                    ctx.translate(startColumnX + 4, columnY + 8.5 + 1.2); // Переміщуємо початкову точку малювання
                    ctx.scale(0.75, 0.75); // Масштабування шляху для більшого розміру
                    ctx.strokeStyle = theme.controls.background;
                    ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування

                    // ctx.fillStyle = column.isPK ? '#ffc65e' : '#bd7bff';
                    // ctx.fillStyle = column.isPK ? '#ffc65e' : '#8b76ff';  // b092ff'; // a784ff // 9987ff // 6b63ff
                    ctx.fillStyle = column.isPK ? '#edb855' : '#7b8fff';  // b092ff'; // a784ff // 9987ff // 6b63ff // 7b7bff
                    // ctx.strokeStyle = 'white';

                    ctx.fill(pathCanvas);

                    ctx.stroke(pathCanvas);
                    ctx.restore(); // Відновлюємо попередній стан канвасу
                }

                // ===================== 6.2.1. TABLE COLUMNS | DESCRIPTION ICON =====================
                // if (column.description || column.defaultValue || column.enumValues || column.generatedValue || column.checkValue) {
                if (column.description) {
                    let isColumnEditedAndChanged =
                        columnState?.elementId === id &&
                        columnState?.insideElementId === column.id &&
                        columnNameState !== column.name;

                    // POINT 1
                    let nameValue = column.name;
                    if (isColumnEditedAndChanged) {
                        // show local state value
                        nameValue = columnNameState;
                    }

                    if (nameValue === '') {
                        nameValue = '​unnamed';
                    }

                    const columnNameWidth = ctx.measureText(nameValue).width;

                    const pathDescription = 'M509.039,324.633a1.891,1.891,0,0,1,1.889,1.89V349.7a1.891,1.891,0,0,1-1.889,1.89H485.862a1.892,1.892,0,0,1-1.89-1.89V326.523a1.892,1.892,0,0,1,1.89-1.89h23.177m0-2H485.862a3.89,3.89,0,0,0-3.89,3.89V349.7a3.89,3.89,0,0,0,3.89,3.89h23.177a3.889,3.889,0,0,0,3.889-3.89V326.523a3.889,3.889,0,0,0-3.889-3.89Z M505.105,332.478H489.523a1,1,0,1,1,0-2h15.582a1,1,0,0,1,0,2Z M505.105,339.188H489.523a1,1,0,1,1,0-2h15.582a1,1,0,0,1,0,2Z M505.105,345.9H489.523a1,1,0,0,1,0-2h15.582a1,1,0,0,1,0,2Z';
                    const pathDescriptionCanvas = new Path2D(pathDescription);

                    ctx.save(); // Зберігаємо поточний стан канвасу
                    ctx.scale(0.525, 0.525); // Масштабування шляху для більшого розміру

                    // ctx.translate((x + 8) / 0.55, (y - 149) / 0.55); // Переміщуємо початкову точку малювання
                    // ctx.translate((startColumnX - 264 + 25 + columnNameWidth + 4.5) / 0.55, (columnY - 168) / 0.55); // Переміщуємо початкову точку малювання
                    // ctx.translate((startColumnX - 264 + 25 + columnNameWidth + 5) / 0.55, (columnY - 168) / 0.55); // Переміщуємо початкову точку малювання
                    ctx.translate((startColumnX - 264 + 25 + columnNameWidth + 5 + 11) / 0.525, (columnY - 168 + 9) / 0.525); // Переміщуємо початкову точку малювання

                    // ctx.strokeStyle = theme.controls.background;
                    ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування
                    // ctx.lineWidth = 0; // Лінії мають бути тонкими після масштабування

                    // ctx.fillStyle = theme.table.header.color;
                    // ctx.strokeStyle = theme.table.header.color;
                    ctx.fillStyle = theme.table.body.row.name.color;
                    ctx.strokeStyle = theme.table.body.row.name.color;

                    ctx.beginPath();
                    ctx.stroke(pathDescriptionCanvas);
                    ctx.fill(pathDescriptionCanvas);

                    ctx.restore(); // Відновлюємо попередній стан канвасу
                }


                if (column.isDeleted) {
                    let positionX = x + 25;

                    // ======================== SYMBOL MINUS
                    ctx.textAlign = 'left';
                    ctx.font = `400 19px Inter, sans-serif`;
                    ctx.fillStyle = '#b5bdc5';
                    // ctx.fillText('–', positionX - 15, columnY + 23);
                    ctx.fillText('–', positionX - 15, columnY + 23);
                    ctx.font = `${columnNameFontSize} 19px Inter, sans-serif`;
                } else if (column.isAdded) {
                    let positionX = x + 25;

                    // ======================== SYMBOL PLUS
                    ctx.textAlign = 'left';
                    ctx.font = `400 19px Inter, sans-serif`;
                    ctx.fillStyle = '#b5bdc5';
                    // ctx.fillText('+', positionX - 16.7, columnY + 23);
                    ctx.fillText('+', positionX - 16.7, columnY + 22);
                    ctx.font = `${columnNameFontSize} 19px Inter, sans-serif`;
                }

                // ===================== 6.3. TABLE COLUMNS | NAME =====================
                if (column.isDeleted && column.nameHighlights) {
                    let positionX = startColumnX + 25;

                    column.nameHighlights.forEach(([isHighlighted, text]) => {
                        if (isHighlighted) {
                            ctx.textAlign = 'left';
                            ctx.font = `${columnNameFontSize} 19px Inter, sans-serif`;

                            const mesureTextHighlight =
                                ctx.measureText(text).width + 5;
                            positionX += 1.65;

                            ctx.fillStyle = '#a30547';
                            ctx.beginPath();
                            ctx.roundRect(
                                positionX,
                                columnY + 4.5,
                                mesureTextHighlight,
                                25,
                                4
                            );
                            ctx.fill();

                            ctx.fillStyle = '#b5bdc5';
                            ctx.fillText(text, positionX + 2.5, columnY + 23);

                            positionX += mesureTextHighlight + 1.65;
                        } else {
                            ctx.textAlign = 'left';
                            ctx.font = `${columnNameFontSize} 19px Inter, sans-serif`;
                            const mesureText = ctx.measureText(text).width;

                            ctx.fillStyle = theme.table.body.row.name.color;
                            ctx.fillText(text, positionX, columnY + 23);

                            positionX += mesureText;
                        }
                    });
                } else if (column.isAdded && column.nameHighlights) {
                    let positionX = startColumnX + 25;

                    column.nameHighlights.forEach(([isHighlighted, text]) => {
                        if (isHighlighted) {
                            ctx.textAlign = 'left';
                            ctx.font = `${columnNameFontSize} 19px Inter, sans-serif`;

                            const mesureTextHighlight =
                                ctx.measureText(text).width + 5;
                            positionX += 1.65;

                            ctx.fillStyle = '#008a6e';
                            ctx.beginPath();
                            ctx.roundRect(
                                positionX,
                                columnY + 4.5,
                                mesureTextHighlight,
                                25,
                                4
                            );
                            ctx.fill();

                            ctx.fillStyle = '#b5bdc5';
                            ctx.fillText(text, positionX + 2.5, columnY + 23);

                            positionX += mesureTextHighlight + 1.65;
                        } else {
                            ctx.textAlign = 'left';
                            ctx.font = `${columnNameFontSize} 19px Inter, sans-serif`;
                            const mesureText = ctx.measureText(text).width;

                            ctx.fillStyle = theme.table.body.row.name.color;
                            ctx.fillText(text, positionX, columnY + 23);

                            positionX += mesureText;
                        }
                    });
                } else {
                    let isColumnFocused =
                        focusedInsideElementState?.elementId === id &&
                        focusedInsideElementState?.insideElementId === column.id &&
                        focusedInsideElementState?.insideElementType === 'column';

                    if (
                        isStartedLineState ||
                        isBoardMovingStarted ||
                        isBoardMoving ||
                        isScaling ||
                        isMovingElementId ||
                        isElementResizing ||
                        // column is not focused
                        !isColumnFocused
                    ) {
                        let isColumnEditedAndChanged =
                            columnState?.elementId === id &&
                            columnState?.insideElementId === column.id &&
                            columnNameState !== column.name;

                        // POINT 1
                        let nameValue = column.name;
                        if (isColumnEditedAndChanged) {
                            // show local state value
                            nameValue = columnNameState;
                        }

                        if (isElementResizing) {
                            nameValue = column.name;
                        }

                        if (nameValue === '') {
                            nameValue = '​unnamed';
                        }


                        ctx.textAlign = 'left';
                        ctx.fillStyle = theme.table.body.row.name.color;
                        ctx.fillStyle = '#b7c2cb';

                        // ctx.font = `400 19.2px Inter, sans-serif`;
                        // ctx.font = `${columnNameFontSize} 19.2px Inter, sans-serif`;
                        ctx.font = `${columnNameFontSize} 19.2px Inter, sans-serif`;

                        if (color) {
                            ctx.fillStyle = '#b7c2cb';
                        }
                        // ctx.fillStyle = '#cbd6df';
                        ctx.fillStyle = theme.table.body.row.name.color;
                        ctx.fillText(
                            nameValue,
                            startColumnX + 25,
                            columnY + 23 + 1.8
                        );

                        // if (scaleRef.current <= 0.18) {
                        // 	ctx.fillStyle = '#cbd6df45';
                        // 	// ctx.fillRect(startColumnX + 25, columnY + 23 + 1.8, 100, 10);
                        // 	ctx.beginPath();
                        // 	ctx.roundRect(startColumnX + 25, columnY + (23 + 1.8) / 2, 175, 8, 3);
                        // 	ctx.fill();
                        // } else {
                        // 	ctx.textAlign = "left";
                        // 	ctx.fillStyle = theme.table.body.row.name.color;

                        // 	if (color) {
                        // 		ctx.fillStyle = '#b7c2cb';
                        // 	}
                        // 	// ctx.fillStyle = '#cbd6df';
                        // 	ctx.fillText(nameValue, startColumnX + 25, columnY + 23 + 1.8);
                        // }
                    }
                }

                // Math.random() === 2 && drawTextWithLetterSpacing();
                // Math.random() === 2 && drawTextRight();

                // // ===================== 6.4. TABLE COLUMNS | NULLABLE =====================
                // if (column.isNN) {
                //     if (
                //         (column.isDeleted || column.isAdded) &&
                //         column.isNNChanged
                //     ) {
                //         // // before
                //         // ctx.shadowColor = '#a30547';
                //         // ctx.shadowBlur = 2; // Розмиття тіні
                //         // ctx.shadowOffsetX = 0; // Зміщення по горизонталі
                //         // ctx.shadowOffsetY = 0; // Зміщення по вертикалі

                //         // // after
                //         // ctx.shadowColor = null;
                //         // ctx.shadowBlur = 0; // Розмиття тіні
                //         // ctx.shadowOffsetX = 0; // Зміщення по горизонталі
                //         // ctx.shadowOffsetY = 0; // Зміщення по вертикалі

                //         ctx.fillStyle = column.isDeleted ? '#a30547' : '#008a6e';

                //         ctx.beginPath();
                //         // ctx.roundRect(x + (tableWidth - 27 - 25 - 1) -2.5, columnY + 23 - 14 -2.5, 25 +5, 16.5 +5, 3);
                //         ctx.roundRect(
                //             x + (tableWidth - 27 - 25 - 1) - 3,
                //             columnY + 23 - 14 - 4.25,
                //             25 + 6,
                //             16.5 + 8.5,
                //             4
                //         );
                //         ctx.fill();
                //     }

                //     // ctx.fillStyle = theme.table.nullable || null;
                //     // ctx.fillStyle = '#333B89'; // theme.table.body.row.type.color;
                //     // ctx.fillStyle = '#3a439c'; // theme.table.body.row.type.color;
                //     ctx.fillStyle = '#3d47a5'; // theme.table.body.row.type.color;

                //     ctx.beginPath();
                //     // ctx.roundRect(x + (tableWidth - 27 - 25), columnY + 23 - 14, 25, 16.5, 3);
                //     // ctx.roundRect(x + (tableWidth - 27 - 25 - 1), columnY + 23 - 14, 25, 16.5, 3);

                //     // ctx.roundRect(x + (tableWidth - 27 - 25 - 1), columnY + 23 - 14 + 1.7, 25, 16.5, 3);
                //     ctx.roundRect(x + (tableWidth - 27 - 25 - 1), columnY + 23 - 14 + 1.7, 25, 16.5, 3);
                //     ctx.fill();

                //     ctx.textAlign = 'right';
                //     ctx.font = `600 11px Inter, sans-serif`;
                //     // ctx.font = `600 13px "Roboto"`;
                //     ctx.fillStyle = '#ebebeb'; // theme.table.body.row.name.color;
                //     // ctx.fillStyle = '#e8e8e8'; // theme.table.body.row.name.color;
                //     // ctx.fillStyle = '#e0e0e0'; // theme.table.body.row.name.color;

                //     // ctx.fillText('NN', x + (tableWidth - 27 - 4.35), columnY + 23 - 1.7);
                //     // ctx.fillText('NN', x + (tableWidth - 27 - 4.35 - 1), columnY + 23 - 1.7);

                //     // ctx.fillText('NN', x + (tableWidth - 27 - 4.35 - 1), columnY + 23 - 1.7 + 2);
                //     ctx.fillText(
                //         'NN',
                //         x + (tableWidth - 27 - 4.35 - 1),
                //         columnY + 23 - 2 + 2
                //     );
                // }



                // ===================== 6.4. TABLE COLUMNS | DEFAULT VALUE  =====================
                // ===================== 6.4. TABLE COLUMNS | NULLABLE       =====================
                // ===================== 6.4. TABLE COLUMNS | ENUM           =====================
                // ===================== 6.4. TABLE COLUMNS | AUTO INCREMENT =====================

                let widthGenerated = 17 - 1;
                let widthDef = 17;
                let widthNN = 25;
                let widthEnum = 15.25;
                let widthAutoInc = 15.5;
                let distanceTextInternal = 4.35;
                let distanceBetween = 4.5; // or 8, like with index and nn

                let endX = x + tableWidth - 28; // = tableEnd - indexWidth with 2x distance
                let positionNext = endX;

                // ===================== 6.4. TABLE COLUMNS | GENERATED VALUE =====================
                if (column.generatedValue) {
                    ctx.fillStyle = '#23a4df'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#299ec2'; // theme.table.body.row.type.color;
                    // ctx.fillStyle = '#e3ad48ff'; // theme.table.body.row.type.color;
                    // ctx.fillStyle = '#dba33cff'; // theme.table.body.row.type.color;

                    // #edb855
                    // #7b8fff

                    ctx.beginPath();
                    ctx.roundRect(positionNext - widthGenerated, columnY + 23 - 14 + 1.7 + 0.75, widthGenerated, 16.5 - 1.5, 2.7);
                    ctx.fill();

                    ctx.textAlign = 'right';
                    ctx.font = `600 9.7px Inter, sans-serif`;
                    ctx.fillStyle = '#ebebeb'; // theme.table.body.row.name.color;

                    ctx.fillStyle = '#e5e7e9'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#fff'; // theme.table.body.row.name.color;

                    ctx.fillText('G', positionNext - distanceTextInternal, columnY + 23 - 2 + 2 - 0.4);

                    positionNext -= widthGenerated + distanceBetween;
                }

                // ===================== 6.4. TABLE COLUMNS | DEFAULT VALUE =====================
                if (column.defaultValue) {
                    ctx.fillStyle = '#8d452b'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#a24929'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#b36525'; // theme.table.body.row.type.color;
                    // ctx.fillStyle = '#e3ad48ff'; // theme.table.body.row.type.color;

                    ctx.beginPath();
                    ctx.roundRect(positionNext - widthDef + 0.5, columnY + 23 - 14 + 1.7 + 0.75, widthDef - 1, 16.5 - 1.5, 2.7);
                    ctx.fill();

                    ctx.textAlign = 'right';
                    ctx.font = `600 9.7px Inter, sans-serif`;
                    ctx.fillStyle = '#ebebeb'; // theme.table.body.row.name.color;

                    ctx.fillStyle = '#fff'; // theme.table.body.row.name.color;

                    ctx.fillText('D', positionNext - distanceTextInternal - 0.4, columnY + 23 - 2 + 2 - 0.4);

                    positionNext -= widthDef + distanceBetween;
                }

                // ===================== 6.4. TABLE COLUMNS | NULLABLE =====================
                if (column.isNN) {
                    ctx.fillStyle = '#3d47a5'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#2e357c'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#3d47a5'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#4a5aaf'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#4254afff'; // theme.table.body.row.type.color;

                    ctx.beginPath();
                    ctx.roundRect(positionNext - widthNN + 0.5, columnY + 23 - 14 + 1.7 + 0.75, widthNN - 1, 16.5 - 1.5, 2.7);
                    ctx.fill();

                    ctx.textAlign = 'right';
                    ctx.font = `600 9.7px Inter, sans-serif`;
                    ctx.fillStyle = '#ebebeb'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#e6ebef'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#ecf0f3'; // theme.table.body.row.name.color;

                    // ctx.fillStyle = '#ecf0f3'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#e5e7e9'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#fff'; // theme.table.body.row.name.color;

                    ctx.fillText('NN', positionNext - distanceTextInternal - 0.8, columnY + 23 - 2 + 2 - 0.6);

                    positionNext -= widthNN + distanceBetween;
                }

                // ===================== 6.4. TABLE COLUMNS | ENUM =====================
                if (column.isEnum) {
                    // ctx.fillStyle = '#ab7b21'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#bf8a27ff'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#b98523'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#b38f25'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#e3ad48'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#dca745ff'; // theme.table.body.row.type.color;

                    ctx.beginPath();
                    ctx.roundRect(positionNext - widthEnum + 0.5, columnY + 23 - 14 + 1.7 + 0.75, widthEnum - 1, 16.5 - 1.5, 2.7);
                    ctx.fill();

                    ctx.textAlign = 'right';
                    ctx.font = `600 9.7px Inter, sans-serif`;
                    ctx.fillStyle = '#ebebeb'; // theme.table.body.row.name.color;

                    ctx.fillStyle = '#e5e7e9'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#fff'; // theme.table.body.row.name.color;

                    ctx.fillText('E', positionNext - distanceTextInternal - 0.4, columnY + 23 - 2 + 2 - 0.4);

                    positionNext -= widthEnum + distanceBetween;
                }

                // ===================== 6.4. TABLE COLUMNS | AUTO INCREMENT =====================
                if (column.isAutoIncrement) {
                    // ctx.fillStyle = '#297d4e'; // theme.table.body.row.type.color;
                    ctx.fillStyle = 'seagreen'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#7bffb7'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#69d79b'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#58b481'; // theme.table.body.row.type.color;
                    ctx.fillStyle = '#3eb372ff'; // theme.table.body.row.type.color;
                    // ctx.fillStyle = '#e3ad48ff'; // theme.table.body.row.type.color;

                    ctx.beginPath();
                    ctx.roundRect(positionNext - widthAutoInc + 0.5, columnY + 23 - 14 + 1.7 + 0.75, widthAutoInc - 1, 16.5 - 1.5, 2.7);
                    ctx.fill();

                    ctx.textAlign = 'right';
                    ctx.font = `600 15px Inter, sans-serif`;
                    ctx.fillStyle = '#ebebeb'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#ecf0f3'; // theme.table.body.row.name.color;

                    ctx.fillStyle = '#e5e7e9'; // theme.table.body.row.name.color;
                    ctx.fillStyle = '#fff'; // theme.table.body.row.name.color;

                    ctx.fillText('+', positionNext - distanceTextInternal + 2 - 0.2, columnY + 23 - 2 + 2 + 0.4);

                    positionNext -= widthAutoInc + distanceBetween;
                }


                // ===================== 6.4.2. TABLE COLUMNS | TYPE =====================
                let mesureTextSum = 0;

                if ((column.isDeleted || column.isAdded) && column.typeHighlights) {
                    ctx.textAlign = 'left';
                    ctx.font = `400 18px Inter, sans-serif`;
                    column.typeHighlights.forEach(([isHighlighted, text]) => {
                        if (isHighlighted) {
                            const mesureTextHighlight =
                                ctx.measureText(text).width + 5;
                            mesureTextSum += mesureTextHighlight + 1.65 * 2;
                        } else {
                            const mesureText = ctx.measureText(text).width;
                            mesureTextSum += mesureText;
                        }
                    });
                }

                let positionX = positionNext;
                // let positionX = column.isNN
                //     ? x + tableWidth - 27 - 30 - 1 - mesureTextSum
                //     : x + tableWidth - 27 - mesureTextSum;

                if (column.isDeleted && column.typeHighlights) {
                    column.typeHighlights.forEach(([isHighlighted, text]) => {
                        if (isHighlighted) {
                            ctx.textAlign = 'left';
                            ctx.font = `400 18px Inter, sans-serif`;

                            const mesureTextHighlight =
                                ctx.measureText(text).width + 5;
                            positionX += 1.65;

                            ctx.fillStyle = '#a30547';
                            ctx.beginPath();
                            ctx.roundRect(
                                positionX,
                                columnY + 4.5,
                                mesureTextHighlight,
                                25,
                                4
                            );
                            ctx.fill();

                            ctx.fillStyle = '#b5bdc5';
                            ctx.fillText(text, positionX + 2.5, columnY + 23);

                            positionX += mesureTextHighlight + 1.65;
                        } else {
                            ctx.textAlign = 'left';
                            ctx.font = `400 18px Inter, sans-serif`;

                            const mesureText = ctx.measureText(text).width;

                            ctx.fillStyle = theme.table.body.row.type.color;
                            ctx.fillText(text, positionX, columnY + 23);

                            positionX += mesureText;
                        }
                    });
                } else if (column.isAdded && column.typeHighlights) {
                    column.typeHighlights.forEach(([isHighlighted, text]) => {
                        if (isHighlighted) {
                            ctx.textAlign = 'left';
                            ctx.font = `400 18px Inter, sans-serif`;

                            const mesureTextHighlight =
                                ctx.measureText(text).width + 5;
                            positionX += 1.65;

                            ctx.fillStyle = '#008a6e';
                            ctx.beginPath();
                            ctx.roundRect(
                                positionX,
                                columnY + 4.5,
                                mesureTextHighlight,
                                25,
                                4
                            );
                            ctx.fill();

                            ctx.fillStyle = '#b5bdc5';
                            ctx.fillText(text, positionX + 2.5, columnY + 23);

                            positionX += mesureTextHighlight + 1.65;
                        } else {
                            ctx.textAlign = 'left';
                            ctx.font = `400 18px Inter, sans-serif`;

                            const mesureText = ctx.measureText(text).width;

                            ctx.fillStyle = theme.table.body.row.type.color;
                            ctx.fillText(text, positionX, columnY + 23);

                            positionX += mesureText;
                        }
                    });
                } else {
                    if (
                        isStartedLineState ||
                        isBoardMovingStarted ||
                        isBoardMoving ||
                        isScaling ||
                        isMovingElementId ||
                        isElementResizing ||

                        !focusedInsideElementState ||
                        focusedInsideElementState?.elementId !== id ||
                        focusedInsideElementState?.insideElementId !== column.id
                    ) {
                        ctx.fillStyle = theme.table.body.row.type.color;

                        // POINT 1
                        if (color) {
                            ctx.fillStyle = '#7d8e9c';
                        }

                        let typeValue = column.type;
                        if (typeValue === '') {
                            typeValue = 'null';
                        }



                        ctx.textAlign = 'right';
                        ctx.font = `400 18px Inter, sans-serif`;
                        ctx.fillText(typeValue, positionNext, columnY + 23 + 1.8);

                        // if (scaleRef.current <= 0.18) {
                        // 	ctx.fillStyle = '#cbd6df25';
                        // 	// ctx.fillRect(startColumnX + 25, columnY + 23 + 1.8, 100, 10);
                        // 	ctx.beginPath();
                        // 	ctx.roundRect(positionX - 75, columnY + (23 + 1.8) / 2, 75, 8, 3);
                        // 	ctx.fill();
                        // } else {
                        // 	ctx.fillStyle = theme.table.body.row.type.color;

                        // 	// POINT 1
                        // 	if (color) {
                        // 		ctx.fillStyle = '#7d8e9c';
                        // 	}

                        // 	ctx.textAlign = "right";
                        // 	ctx.font = `400 18px Inter, sans-serif`;
                        // 	ctx.fillText(column.type, positionX, columnY + 23 + 1.8);
                        // }
                    }
                }

                // ===================== 6.5. TABLE COLUMNS | INDEX =====================
                if (column.index) {
                    if (
                        (column.isDeleted || column.isAdded) &&
                        (column.indexChanged || column.indexChanged)
                    ) {
                        ctx.fillStyle = column.isDeleted ? '#a30547' : '#008a6e';

                        ctx.beginPath();
                        // ctx.roundRect(startColumnX +4 -1, columnY + 4.75, 20, 16.5 +8.5, 4);
                        ctx.roundRect(
                            x + tableWidth - 19.5 - 3.5,
                            columnY + 5,
                            17.5,
                            16.5 + 8.5,
                            3.5
                        );

                        // ctx.roundRect(x + (tableWidth - 27 - 25 - 1) -3, columnY + 23 - 14 -4.25, 25 +6, 16.5 +8.5, 4);

                        ctx.fill();

                        // top: {
                        // 	x: x + tableWidth - 14,
                        // 	y: columnY + 11.5,
                        // },
                        // left: {
                        // 	x: x + tableWidth - 19.5,
                        // 	y: columnY + 17,
                        // },
                    }

                    // const path = "M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z";
                    // const pathCanvas = new Path2D(path);

                    // ctx.save(); // Зберігаємо поточний стан канвасу
                    // ctx.translate(x+5, columnY+9); // Переміщуємо початкову точку малювання
                    // ctx.scale(0.7, 0.7); // Масштабування шляху для більшого розміру
                    // ctx.strokeStyle = '#ffffff';
                    // ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування
                    // ctx.stroke(pathCanvas); // Малюємо шлях
                    // ctx.restore(); // Відновлюємо попередній стан канвасу

                    // ctx.setTransform(10, 0, 0, 10, 0, 0); // Масштабування
                    // ctx.strokeStyle = '#ffffff';
                    // ctx.lineWidth = 1;
                    // ctx.stroke(pathCanvas);

                    ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування

                    ctx.fillStyle = theme.table.indexes[column.index?.type] || null;
                    ctx.strokeStyle =
                        theme.table.indexes[column.index?.type] || null;

                    const coords = {
                        top: {
                            x: x + tableWidth - 14,
                            y: columnY + 11.5,
                        },
                        left: {
                            x: x + tableWidth - 19.5,
                            y: columnY + 17,
                        },
                        right: {
                            x: x + tableWidth - 8.5,
                            y: columnY + 17,
                        },
                        bottom: {
                            x: x + tableWidth - 14,
                            y: columnY + 22.5,
                        },
                    };

                    ctx.beginPath();
                    ctx.moveTo(coords.top.x - 0.3, coords.top.y - 0.2 + 2); // Top point
                    ctx.lineTo(coords.right.x - 0.3, coords.right.y - 0.2 + 2); // Right point
                    ctx.lineTo(coords.bottom.x - 0.3, coords.bottom.y - 0.2 + 2); // Bottom point
                    ctx.lineTo(coords.left.x - 0.3, coords.left.y - 0.2 + 2); // Left point

                    ctx.closePath();
                    ctx.stroke();
                    ctx.fill();
                }

                // ===================== 6.6. TABLE COLUMNS | HIGHLIGHTED COLUMN | FOCUSED COLUMN | (BLUE) =====================
                if (
                    !isStartedNewLine &&
                    !isBtnNewAreaEnabled &&
                    !isStartedNewPenSegment &&
                    focusedInsideElementState?.elementId === id &&
                    focusedInsideElementState?.insideElementId === column.id
                ) {
                    ctx.strokeStyle = '#5f88ff';
                    ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
                    // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
                    ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;

                    ctx.beginPath();
                    ctx.roundRect(
                        x + 1,
                        columnY,
                        tableWidth - 2,
                        TABLE_ROW_HEIGHT,
                        3
                    );
                    // ctx.roundRect(x + 1, columnY+1, tableWidth - 2, TABLE_ROW_HEIGHT -2, 3);
                    ctx.stroke();
                }

                // ===================== 6.7. TABLE COLUMNS | HIGHLIGHTED COLUMN | RELATED COLUMN | ON TABLE HOVER | (PURPLE) =====================

                const isColumnRelatedToFocusedElements =
                    // column is related to focused elements
                    focusedElementsRelatedPropsRef.current?.insideElementsIds.includes(column.id) &&
                    // parent element is not focused
                    !focusedElementsIdsRef.current.includes(id) &&
                    !focusedGroupElementsIdsRef.current?.length;

                const isColumnRelatedToFocusedLine =
                    // column is related to focused line
                    focusedLineRelatedPropsRef.current?.insideElementsIds.includes(
                        column.id
                    ) &&
                    // no one focused elements (hover uses only when there is no focused elements)
                    !focusedElementsIdsRef.current.length;

                const isColumnRelatedToHoveredElements =
                    // column is related to hovered element
                    hoveredElementRelatedPropsRef.current?.insideElementsIds.includes(
                        column.id
                    ) &&
                    // parent element is not hovered
                    hoveredElementIdRef.current !== id &&
                    // no one focused elements (hover uses only when there is no focused elements)
                    !focusedElementsIdsRef.current.length &&
                    // no one focused line (hover uses only when there is no focused line)
                    !focusedLineIdRef.current;

                const isColumnRelatedToHoveredLine =
                    // column is related to focused line
                    hoveredLineRelatedPropsRef.current?.insideElementsIds.includes(
                        column.id
                    ) &&
                    // no one focused elements (hover uses only when there is no focused elements)
                    !focusedElementsIdsRef.current.length &&
                    // no one focused line (hover uses only when there is no focused line)
                    !focusedLineIdRef.current &&
                    // no hovered element
                    hoveredElementIdRef.current !== id;

                if (
                    // is not export enabled
                    !isBtnExportImageEnabled &&
                    !isSelectionMoving &&
                    !isStartedNewLine &&
                    !isBtnNewAreaEnabled &&
                    !isResizingArea &&
                    !isStartedNewPenSegment && // column is related to focused elements
                    (isColumnRelatedToFocusedElements ||
                        // column is related to hovered element
                        isColumnRelatedToHoveredElements ||
                        // column is related to focused line
                        isColumnRelatedToFocusedLine ||
                        // column is related to hovered line
                        isColumnRelatedToHoveredLine)
                ) {
                    const columnY =
                        y +
                        TABLE_HEADER_HEIGHT +
                        index * TABLE_ROW_HEIGHT +
                        HEADER_BODY_MARGIN;

                    ctx.strokeStyle = '#8b5fff';
                    ctx.strokeStyle = 'rgba(144, 119, 255, 1)';
                    ctx.strokeStyle = 'rgba(119, 121, 255, 1)';
                    ctx.strokeStyle = 'rgba(159, 141, 231, 1)';
                    ctx.strokeStyle = 'rgba(167, 147, 250, 1)';
                    // ctx.fillStyle = '#cc9ff8a9';
                    ctx.fillStyle = '#a46ddc8e';
                    ctx.fillStyle = '#a46ddc76';
                    ctx.fillStyle = '#ffc65e';
                    ctx.fillStyle = 'rgba(255, 199, 94, 0.55)';

                    // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
                    ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;

                    ctx.beginPath();
                    ctx.roundRect(
                        x + 1,
                        columnY,
                        tableWidth - 2,
                        TABLE_ROW_HEIGHT,
                        3
                    );
                    // ctx.stroke();
                    ctx.fill();

                    // ---------------
                    // const radius = 5;
                    // const smallRadius = 2.5;

                    // const radius = 5;
                    // const smallRadius = 3.25;

                    // const leftCenterX = x + 1;
                    // const rightCenterX = x - 1 + tableWidth;
                    // const centerY = columnY + TABLE_ROW_HEIGHT / 2;

                    // // left circle, big one
                    // ctx.beginPath();
                    // ctx.fillStyle = '#8b5fff';
                    // ctx.arc(leftCenterX, centerY, radius, 0, 2 * Math.PI); // Малюємо коло
                    // ctx.fill();

                    // // left circle, small one
                    // ctx.beginPath();
                    // ctx.fillStyle = '#e6e6e6';
                    // ctx.arc(leftCenterX, centerY, smallRadius, 0, 2 * Math.PI); // Малюємо коло
                    // ctx.fill();

                    // // right circle, big one
                    // ctx.beginPath();
                    // ctx.fillStyle = '#8b5fff';
                    // ctx.arc(rightCenterX, centerY, radius, 0, 2 * Math.PI); // Малюємо коло
                    // ctx.fill();

                    // // right circle, small one
                    // ctx.beginPath();
                    // ctx.fillStyle = '#e6e6e6';
                    // ctx.arc(rightCenterX, centerY, smallRadius, 0, 2 * Math.PI); // Малюємо коло
                    // ctx.fill();
                }

                // ===================== 6.8. COLUMNS POINTS (TO CREATE NEW LINES) =====================
                if (
                    !isCursorHand &&
                    !isBtnRedGreenViewEnabled &&
                    // is not export enabled
                    !isBtnExportImageEnabled &&
                    !isSelectionMoving &&
                    !isStartedNewLine &&
                    !isBtnNewAreaEnabled &&
                    !isResizingArea &&
                    !isStartedNewPenSegment && // table is one focused element
                    ((focusedElementsIdsRef.current.includes(id) &&
                        focusedElementsIdsRef.current.length === 1) ||
                        // table is not focused, but this column is hovered
                        (hoveredElementIdRef.current === id &&
                            hoveredInsideElementRef.current?.id === column.id) ||
                        // table is not focused, but this column's 'point area' is hovered
                        hoveredPointAreaRef.current?.insideElementsIds.includes(
                            column.id
                        ))
                ) {
                    const POINT_RADIUS_BEFORE_HOVER = 2.5;
                    const POINT_RADIUS_ON_HOVER = 6;
                    const POINT_DISTANCE = 20;

                    let leftRadius = POINT_RADIUS_BEFORE_HOVER;
                    let rightRadius = POINT_RADIUS_BEFORE_HOVER;

                    let isHoveredColumn =
                        hoveredPointRef.current?.insideElementId === column.id;
                    let direction = hoveredPointRef.current?.direction;

                    // ===================== 6.8.1. COLUMNS POINTS | LEFT POINT =====================

                    const centerXLeft = x - POINT_DISTANCE;
                    const centerYLeft = columnY + TABLE_ROW_HEIGHT / 2;

                    ctx.fillStyle = '#667688';
                    if (isHoveredColumn && direction === 'left') {
                        ctx.fillStyle = '#5f88ff';
                        ctx.fillStyle = 'rgba(119, 160, 255, 1)';
                        leftRadius = POINT_RADIUS_ON_HOVER;
                    }

                    ctx.beginPath();
                    ctx.arc(centerXLeft, centerYLeft, leftRadius, 0, 2 * Math.PI); // Малюємо коло
                    ctx.fill();

                    // ===================== 6.8.1. COLUMNS POINTS | RIGHT POINT =====================

                    const centerXRight = x + tableWidth + POINT_DISTANCE;
                    const centerYRight = columnY + TABLE_ROW_HEIGHT / 2;

                    ctx.fillStyle = '#667688';
                    if (isHoveredColumn && direction === 'right') {
                        ctx.fillStyle = '#5f88ff';
                        ctx.fillStyle = 'rgba(119, 160, 255, 1)';
                        rightRadius = POINT_RADIUS_ON_HOVER;
                    }

                    ctx.beginPath();
                    ctx.arc(
                        centerXRight,
                        centerYRight,
                        rightRadius,
                        0,
                        2 * Math.PI
                    ); // Малюємо коло
                    ctx.fill();
                }
                // ===================== 6.9. TABLE COLUMNS | END =====================
            }
        }

        // ===================== 7.1. HIGHLIGHTED COLUMN | RELATED COLUMN | ON CREATE NEW LINE | FROM (PURPLE) =====================
        if (
            // is not export enabled
            !isBtnExportImageEnabled &&
            !isSelectionMoving &&
            !isBtnNewAreaEnabled &&
            !isResizingArea &&
            isStartedNewLine &&
            lineFromRef.current?.elementId === id &&
            lineFromRef.current?.insideElementId
        ) {
            const isSideLeft = true;
            const isSideRight = true;

            const columnY =
                y +
                TABLE_HEADER_HEIGHT +
                lineFromRef.current.insideElementIndex * TABLE_ROW_HEIGHT +
                HEADER_BODY_MARGIN;

            const leftCenterX = x + 1;
            const rightCenterX = x - 1 + tableWidth;
            const centerY = columnY + TABLE_ROW_HEIGHT / 2;

            const bigRadius = 5.25;

            // let leftMidRadius = 2.75;
            // let rightMidRadius = 2.75;
            // const smallRadius = 1.95;

            let leftMidRadius = 2.75;
            let rightMidRadius = 2.75;
            const smallRadius = 2.05;

            // if (isSideLeft) { leftMidRadius = 3.35; }
            // if (isSideRight) { rightMidRadius = 3.35; }
            if (isSideLeft) {
                leftMidRadius = 3.75;
            }
            if (isSideRight) {
                rightMidRadius = 3.75;
            }

            // ===================== FOCUSED FRAME (PURPLE) =====================
            ctx.strokeStyle = '#8b5fff';

            // ctx.fillStyle = '#cc9ff8a9';
            ctx.fillStyle = '#a46ddc8e';
            ctx.fillStyle = '#a46ddc76';
            ctx.fillStyle = 'rgba(255, 199, 94, 0.55)';

            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;

            ctx.beginPath();
            ctx.roundRect(x + 1, columnY, tableWidth - 2, TABLE_ROW_HEIGHT, 3);
            // ctx.stroke();
            ctx.fill();
            // ===================== LEFT SIDE =====================

            // big circle
            ctx.beginPath();
            ctx.fillStyle = '#8b5fff';

            // ctx.fillStyle = '#cc9ff8a9';
            ctx.fillStyle = '#a46ddc8e';
            ctx.fillStyle = '#a46ddc76';
            ctx.fillStyle = 'rgba(255, 199, 94, 0.55)';
            ctx.arc(leftCenterX, centerY, bigRadius, 0, 2 * Math.PI);
            ctx.fill();

            // mid circle
            ctx.beginPath();
            ctx.fillStyle = '#e6e6e6';
            ctx.arc(leftCenterX, centerY, leftMidRadius, 0, 2 * Math.PI);
            ctx.fill();

            if (isSideLeft) {
                // small circle
                ctx.beginPath();
                // ctx.fillStyle = 'cornflowerblue';
                ctx.fillStyle = '#6f94ff';
                ctx.arc(leftCenterX, centerY, smallRadius, 0, 2 * Math.PI);
                ctx.fill();
            }

            // ===================== RIGHT SIDE =====================

            // big circle
            ctx.beginPath();
            ctx.fillStyle = '#8b5fff';

            // ctx.fillStyle = '#cc9ff8a9';
            ctx.fillStyle = '#a46ddc8e';
            ctx.fillStyle = '#a46ddc76';
            ctx.fillStyle = 'rgba(255, 199, 94, 0.55)';
            ctx.arc(rightCenterX, centerY, bigRadius, 0, 2 * Math.PI);
            ctx.fill();

            // mid circle
            ctx.beginPath();
            ctx.fillStyle = '#e6e6e6';
            ctx.arc(rightCenterX, centerY, rightMidRadius, 0, 2 * Math.PI);
            ctx.fill();

            if (isSideRight) {
                // small circle
                ctx.beginPath();
                // ctx.fillStyle = 'cornflowerblue';
                ctx.fillStyle = '#6f94ff';
                ctx.arc(rightCenterX, centerY, smallRadius, 0, 2 * Math.PI);
                ctx.fill();
            }
        }

        // ===================== 7.2. TABLE COLUMNS | HIGHLIGHTED COLUMN | RELATED COLUMN | ON CREATE NEW LINE | TO (PURPLE) =====================
        if (
            // is not export enabled
            !isBtnExportImageEnabled &&
            !isSelectionMoving &&
            !isBtnNewAreaEnabled &&
            !isResizingArea &&
            isStartedNewLine &&
            hoveredElementIdRef.current === id &&
            hoveredInsideElementRef.current?.id &&
            lineFromRef.current?.insideElementId !==
            hoveredInsideElementRef.current?.id
        ) {
            const isSideLeft = true;
            const isSideRight = true;

            const columnY =
                y +
                TABLE_HEADER_HEIGHT +
                hoveredInsideElementRef.current?.index * TABLE_ROW_HEIGHT +
                HEADER_BODY_MARGIN;

            const leftCenterX = x + 1;
            const rightCenterX = x - 1 + tableWidth;
            const centerY = columnY + TABLE_ROW_HEIGHT / 2;

            const bigRadius = 5.25;
            let leftMidRadius = 2.75;
            let rightMidRadius = 2.75;
            const smallRadius = 1.95;

            if (isSideLeft) {
                leftMidRadius = 3.35;
            }
            if (isSideRight) {
                rightMidRadius = 3.35;
            }

            // ===================== FOCUSED FRAME (PURPLE) =====================
            ctx.strokeStyle = '#8b5fff';

            // ctx.fillStyle = '#cc9ff8a9';
            ctx.fillStyle = '#a46ddc8e';
            ctx.fillStyle = '#a46ddc76';
            ctx.fillStyle = 'rgba(255, 199, 94, 0.55)';
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;

            ctx.beginPath();
            ctx.roundRect(x + 1, columnY, tableWidth - 2, TABLE_ROW_HEIGHT, 3);
            // ctx.stroke();
            ctx.fill();

            // ===================== LEFT SIDE =====================

            // big circle
            ctx.beginPath();
            ctx.fillStyle = '#8b5fff';

            // ctx.fillStyle = '#cc9ff8a9';
            ctx.fillStyle = '#a46ddc8e';
            ctx.fillStyle = '#a46ddc76';
            ctx.fillStyle = 'rgba(255, 199, 94, 0.55)';
            ctx.arc(leftCenterX, centerY, bigRadius, 0, 2 * Math.PI);
            ctx.fill();

            // mid circle
            ctx.beginPath();
            ctx.fillStyle = '#e6e6e6';
            ctx.arc(leftCenterX, centerY, leftMidRadius, 0, 2 * Math.PI);
            ctx.fill();

            if (isSideLeft) {
                // small circle
                ctx.beginPath();
                // ctx.fillStyle = 'cornflowerblue';
                ctx.fillStyle = '#6f94ff';
                ctx.arc(leftCenterX, centerY, smallRadius, 0, 2 * Math.PI);
                ctx.fill();
            }

            // ===================== RIGHT SIDE =====================

            // big circle
            ctx.beginPath();
            ctx.fillStyle = '#8b5fff';

            // ctx.fillStyle = '#cc9ff8a9';
            ctx.fillStyle = '#a46ddc8e';
            ctx.fillStyle = '#a46ddc76';
            ctx.fillStyle = 'rgba(255, 199, 94, 0.55)';
            ctx.arc(rightCenterX, centerY, bigRadius, 0, 2 * Math.PI);
            ctx.fill();

            // mid circle
            ctx.beginPath();
            ctx.fillStyle = '#e6e6e6';
            ctx.arc(rightCenterX, centerY, rightMidRadius, 0, 2 * Math.PI);
            ctx.fill();

            if (isSideRight) {
                // small circle
                ctx.beginPath();
                // ctx.fillStyle = 'cornflowerblue';
                ctx.fillStyle = '#6f94ff';
                ctx.arc(rightCenterX, centerY, smallRadius, 0, 2 * Math.PI);
                ctx.fill();
            }
        }

        if (scaleRef.current > 0.15) {
            // ===================== 8. TABLE HEADER/BODY LINE (BETWEEN HEADER AND BODY) =====================
            if (isBtnRedGreenViewEnabled && headers) {
                const headersCount = Object.keys(headers).length;
                // const headersKeys = Object.keys(headers);
                // for (let index = 0; index < headersKeys.length; index++) {

                const headerY = y + (headersCount - 1) * TABLE_HEADER_HEIGHT;

                // ctx.fillStyle = theme.table.header.border;
                ctx.fillStyle =
                    isHeaderChanged || isDeleted || isAdded ? '#505d6b' : '#505d6b';
                // : theme.table.header.border;

                ctx.fillRect(x, headerY + TABLE_HEADER_HEIGHT, tableWidth, 1.25);

                // }
            } else {
                // ===================== 10. DASHES FRAME =====================

                // if (name === 'bo.landing_pages' || name === 'bo.customers_id_blocks') {

                // ctx.strokeStyle = '#a2adb6';
                // ctx.strokeStyle = '#939da5';
                // ctx.strokeStyle = '#9faab3';
                ctx.strokeStyle = '#9aa4ad';
                ctx.strokeStyle = '#a9b3bdff';

                // if (color) {
                //     ctx.strokeStyle = '#7b91a5';
                // ctx.strokeStyle = '#a9b3bdff';
                // } else {
                //     ctx.strokeStyle = '#7b91a5';
                // ctx.strokeStyle = '#a9b3bdff';
                // }

                if (!border) {
                    ctx.fillStyle = theme.table.header.border;
                    ctx.fillStyle = '#4c5866';
                    ctx.fillRect(x, y + TABLE_HEADER_HEIGHT, tableWidth, 1.25);
                } else if (border === 'dashes') {
                    ctx.lineCap = 'butt';
                    ctx.setLineDash([12, 15]);

                    const width =
                        scaleRef.current < 1
                            ? Math.min(
                                (1.1 * 3) / scaleRef.current,
                                (1.1 * 3) / 0.65
                            )
                            : 1 * 3;
                    ctx.lineWidth = width;

                    ctx.beginPath();
                    ctx.moveTo(x + 6, y + 2.4 + TABLE_HEADER_HEIGHT);
                    ctx.lineTo(x + tableWidth, y + 2.4 + TABLE_HEADER_HEIGHT);
                    // ctx.stroke();

                    // ctx.rect(x - 2.4, y - 2.4, tableWidth + 4.8, tableHeight + 4.8);
                    ctx.rect(x - 1.5, y - 1.5, tableWidth + 3, tableHeight + 3);
                    ctx.stroke();
                } else if (border === 'dots') {
                    ctx.lineCap = 'butt';
                    ctx.setLineDash([4, 6]);

                    const width =
                        scaleRef.current < 1
                            ? Math.min(
                                (1.2 * 3) / scaleRef.current,
                                (1.2 * 3) / 0.65
                            )
                            : 1 * 3;
                    ctx.lineWidth = width;

                    ctx.beginPath();
                    ctx.moveTo(x + 2.4, y + 2.4 + TABLE_HEADER_HEIGHT);
                    ctx.lineTo(x + tableWidth, y + 2.4 + TABLE_HEADER_HEIGHT);
                    ctx.stroke();

                    // ctx.rect(x - 2.4, y - 2.4, tableWidth + 4.8, tableHeight + 4.8);
                    ctx.rect(x - 1.5, y - 1.5, tableWidth + 3, tableHeight + 3);
                    ctx.stroke();
                } else if (border === 'solid') {
                    ctx.lineCap = 'square';
                    const width =
                        scaleRef.current < 1
                            ? Math.min(
                                (1 * 2.5) / scaleRef.current,
                                (1 * 2.75) / 0.65
                            )
                            : 1 * 2.75;
                    ctx.lineWidth = width;

                    ctx.beginPath();
                    ctx.moveTo(x, y + 2.4 + TABLE_HEADER_HEIGHT);
                    ctx.lineTo(x + tableWidth, y + 2.4 + TABLE_HEADER_HEIGHT);
                    ctx.stroke();

                    // ctx.rect(x - 2.4, y - 2.4, tableWidth + 4.8, tableHeight + 4.8);
                    ctx.rect(x - 1.5, y - 1.5, tableWidth + 3, tableHeight + 3);
                    ctx.stroke();
                }

                ctx.setLineDash([]);
            }
        }
        // ===================== 9. TABLE CIRCLE | ADD NEW ROW =====================
        if (
            scaleRef.current > 0.15 &&
            !isCursorHand &&
            !isBtnRedGreenViewEnabled &&
            // is not export enabled
            !isBtnExportImageEnabled &&
            !isSelectionMoving &&
            !isStartedNewLine &&
            !isBtnNewAreaEnabled &&
            !isResizingArea &&
            !isStartedNewPenSegment &&
            hoveredCircleAreaRef.current?.elementsIds.includes(id)
        ) {
            ctx.fillStyle = '#515e6c';
            ctx.beginPath();

            if (hoveredCircleRef.current?.elementId === id) {
                ctx.fillStyle = '#5f88ff';
                ctx.fillStyle = 'rgba(119, 160, 255, 1)';
            }

            const radius = 18;
            const distance = 36;
            const centerX = x + tableWidth / 2;
            const centerY = y + tableHeight + distance;
            ctx.arc(
                x + tableWidth / 2,
                y + tableHeight + distance,
                radius,
                0,
                2 * Math.PI
            ); // Малюємо коло
            ctx.fill();

            const lineLength = radius / 2 - 1.5;

            // Горизонтальна лінія
            ctx.beginPath();
            ctx.moveTo(centerX - lineLength, centerY);
            ctx.lineTo(centerX + lineLength, centerY);
            ctx.strokeStyle = '#e6e6e6'; // Встановлюємо колір обведення для плюса
            ctx.lineWidth = 2.3; // Встановлюємо ширину лінії
            ctx.lineCap = 'round';
            ctx.stroke();

            // Вертикальна лінія
            ctx.beginPath();
            ctx.moveTo(centerX, centerY - lineLength);
            ctx.lineTo(centerX, centerY + lineLength);
            ctx.strokeStyle = '#e6e6e6'; // Встановлюємо колір обведення для плюса
            ctx.lineWidth = 2.3; // Встановлюємо ширину лінії
            ctx.lineCap = 'round';
            ctx.stroke();
        }



        // ===================== 10. SELECTED TABLE | OUTLINE FRAME (IN AREA SELECTED FRAME) =====================
        if (
            // create new area: selection table
            (!isBtnExportImageEnabled &&
                isBtnNewAreaEnabled &&
                newAreaPropsRef?.current?.selectedElements?.includes(id))
            ||
            // resize/move area: selection table
            (!isBtnExportImageEnabled &&
                // isBtnNewAreaEnabled &&
                resizeAreaPropsRef.current?.selectedElements?.includes(id))
        ) {
            ctx.strokeStyle = 'rgba(250, 250, 250, 0.2)';
            ctx.strokeStyle = 'rgba(246, 250, 253, 0.2)';

            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3;

            ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3 - 0.7;
            // ctx.strokeStyle = 'rgba(95, 136, 255, 0.8)';

            ctx.beginPath();
            ctx.roundRect(
                x - scaleNumbersRef.current.outlineOffset,
                y - scaleNumbersRef.current.outlineOffset - 0.5,
                tableWidth + 2 * scaleNumbersRef.current.outlineOffset,
                tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1,
                0.75
            );
            ctx.stroke();
            // ctx.shadowColor = 'transparent';

            ctx.fillStyle = 'rgba(111, 190, 255, 0.15)';
            // rgb()
            // canvas background
            ctx.fillRect(
                x - scaleNumbersRef.current.outlineOffset,
                y - scaleNumbersRef.current.outlineOffset - 0.5,
                tableWidth + 2 * scaleNumbersRef.current.outlineOffset,
                tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1
            );
            ctx.stroke();
        }

        // ===================== 10. TABLE | END =====================
    }
    function drawNote(note) {
        const isSelectionMoving =
            isSelectionClickedRef.current &&
            (selectionStartXYRef.current.x !==
                selectionCurrentXYRef.current.x ||
                selectionStartXYRef.current.y !==
                selectionCurrentXYRef.current.y);
        const isStartedNewLine =
            lineFromRef.current?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment =
            newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        const ctx = contextRef.current;

        let {
            id,
            x,
            y,
            width,
            height,
            value: { text, isFontMonospace },
        } = note;

        // ===================== 0. FOCUSED NOTE | OUTLINE FRAME =====================
        if (
            // note is focused
            focusedElementsIdsRef.current.includes(id) ||
            // note is selected by mouse selection box
            selectedElementsIdsRef.current.includes(id) ||
            focusedGroupElementsIdsRef.current.includes(id) ||
            // note is hovered and its not new line drawing
            (hoveredElementIdRef.current === id &&
                !isStartedNewLine &&
                !isStartedNewPenSegment)
        ) {
            ctx.strokeStyle = '#5f88ff';
            ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3;

            if (
                hoveredElementIdRef.current === id &&
                !focusedElementsIdsRef.current.includes(id) &&
                !selectedElementsIdsRef.current.includes(id) &&
                !focusedGroupElementsIdsRef.current.includes(id)
            ) {
                // ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3 - 0.7;
                // ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3 - 0.7 - 0.1; // ASAP LAST
                ctx.lineWidth =
                    scaleNumbersRef.current.outlineWidth - 0.3 - 0.7 - 0.1;
                // ctx.strokeStyle = 'rgba(95, 136, 255, 0.8)';
            }

            ctx.beginPath();
            // ctx.roundRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1, 1);
            ctx.roundRect(
                x - scaleNumbersRef.current.outlineOffset,
                y - scaleNumbersRef.current.outlineOffset,
                width + 2 * scaleNumbersRef.current.outlineOffset,
                height + 2 * scaleNumbersRef.current.outlineOffset,
                0.75
            );

            ctx.stroke();

            // ctx.shadowColor = 'transparent';
            // ctx.fillStyle = '#39424c'; // canvas background
            // ctx.fillRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1);
            // ctx.stroke();
        }

        // ===================== 1. FOCUSED NOTE | RESIZE POINTS =====================
        if (
            scaleRef.current > 0.15 &&
            !isCursorHand &&
            !isBtnRedGreenViewEnabled &&
            // is not export enabled
            !isBtnExportImageEnabled &&
            // is not mouse selection
            !isSelectionMoving &&
            // note is focused
            focusedElementsIdsRef.current.includes(id) &&
            // focused only one note
            focusedElementsIdsRef.current.length === 1
        ) {
            let resizeHandleSize =
                scaleNumbersRef.current.resizeHandleSize - 4 > 0
                    ? scaleNumbersRef.current.resizeHandleSize - 3
                    : scaleNumbersRef.current.resizeHandleSize;
            resizeHandleSize = Math.max(resizeHandleSize, 4);
            let resizeHandleRadius =
                scaleNumbersRef.current.resizeHandleRadius - 1 > 0
                    ? scaleNumbersRef.current.resizeHandleRadius - 1
                    : scaleNumbersRef.current.resizeHandleRadius;
            resizeHandleRadius = Math.max(resizeHandleRadius, 1);

            const resizeOutlineWidth =
                width + scaleNumbersRef.current.outlineOffset * 2;
            const resizeOutlineHeight =
                height + scaleNumbersRef.current.outlineOffset * 2;
            const resizeX = x - scaleNumbersRef.current.outlineOffset;
            const resizeY = y - scaleNumbersRef.current.outlineOffset;

            ctx.beginPath();
            ctx.strokeStyle = '#5f88ff';
            ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
            ctx.fillStyle = '#e6e6e6';
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7 + 1.5;
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 1;

            ctx.roundRect(
                resizeX - resizeHandleSize / 2 - 1,
                resizeY - resizeHandleSize / 2 - 1,
                resizeHandleSize + 2,
                resizeHandleSize + 2,
                resizeHandleRadius
            );
            ctx.roundRect(
                resizeX + resizeOutlineWidth - resizeHandleSize / 2 - 1,
                resizeY - resizeHandleSize / 2 - 1,
                resizeHandleSize + 2,
                resizeHandleSize + 2,
                resizeHandleRadius
            );
            ctx.roundRect(
                resizeX - resizeHandleSize / 2 - 1,
                resizeY + resizeOutlineHeight - resizeHandleSize / 2 - 1,
                resizeHandleSize + 2,
                resizeHandleSize + 2,
                resizeHandleRadius
            );
            ctx.roundRect(
                resizeX + resizeOutlineWidth - resizeHandleSize / 2 - 1,
                resizeY + resizeOutlineHeight - resizeHandleSize / 2 - 1,
                resizeHandleSize + 2,
                resizeHandleSize + 2,
                resizeHandleRadius
            );
            ctx.stroke();
            ctx.fill();
        }

        // ===================== NOTE SHADOW =====================
        // Set shadow properties
        ctx.shadowColor = theme.table.shadow.color;
        ctx.shadowBlur = 8; // Розмиття тіні
        ctx.shadowOffsetX = 2; // Зміщення по горизонталі
        ctx.shadowOffsetY = 2; // Зміщення по вертикалі

        // ===================== 3. NOTE BACKGROUND | RECTANGLE =====================
        // #f7e968
        // #e7db6d
        // ctx.fillStyle = 'rgba(27, 106, 242, 0.55)';
        ctx.fillStyle = '#e7db6d'; // : 'lightgreen';

        ctx.beginPath();
        ctx.roundRect(x, y, width, height, 0.5);
        ctx.fill();

        // Reset shadow after drawing the note
        ctx.shadowColor = 'transparent';

        // ===================== NOTE TEXT =====================
        ctx.fillStyle = 'black';
        ctx.textAlign = 'left';
        if (isFontMonospace) {
            // ctx.font = `500 21px Roboto Mono`;
            ctx.font = `400 22px Roboto Mono, sans-serif`;
        } else {
            ctx.font = `400 22px Roboto, sans-serif`;
        }

        const textValue = text || 'Empty message.';
        ctx.fillText(textValue, x + 38, y + 72);
    }
    function drawSelectionBox() {
        const ctx = contextRef.current;

        ctx.setLineDash([]);

        // ctx.fillStyle = 'rgba(179, 194, 255, 0.2)';
        // ctx.fillStyle = 'rgba(0, 0, 255, 0.05)';
        ctx.fillStyle = 'rgba(159, 184, 255, 0.07)';
        // ctx.fillStyle = 'rgba(127, 160, 255, 0.05)';
        // ctx.fillStyle = '#9fb8ff';


        ctx.fillStyle = 'rgba(159, 184, 255, 0.17)';
        ctx.fillStyle = 'rgba(159, 185, 255, 0.17)';
        ctx.fillStyle = 'rgba(159, 185, 255, 0.14)';

        ctx.fillRect(
            selectionStartXYRef.current.x,
            selectionStartXYRef.current.y,
            selectionCurrentXYRef.current.x - selectionStartXYRef.current.x,
            selectionCurrentXYRef.current.y - selectionStartXYRef.current.y
        );

        // ctx.strokeStyle = 'cornflowerblue';
        // ctx.strokeStyle = 'rgb(0, 0, 255)';
        // ctx.strokeStyle = '#5f88ff'; // ASAP LAST

        // ctx.lineWidth = 0.5;
        // ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3 - 0.7;

        // ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.4; // ASAP LAST

        // ctx.strokeStyle = '#869afe';
        // ctx.strokeStyle = '#698fff';
        ctx.strokeStyle = '#6f94ff';
        ctx.strokeStyle = 'rgba(119, 160, 255, 1)';

        ctx.lineWidth = scaleNumbersRef.current.selectionBoxLine;
        ctx.strokeRect(
            selectionStartXYRef.current.x,
            selectionStartXYRef.current.y,
            selectionCurrentXYRef.current.x - selectionStartXYRef.current.x,
            selectionCurrentXYRef.current.y - selectionStartXYRef.current.y
        );
    }

    function drawArea(area, exportImgCanvas = null) {
        let ctx = contextRef.current;

        if (isBtnExportImageEnabled && exportImgCanvas) {
            const exportImgContext = exportImgCanvas?.getContext('2d');
            ctx = exportImgContext;
        }

        const {
            id,
            x,
            y,
            width,
            height,
            value: { border, color },
        } = area;
        // let { id, x, y, width, height, value: { name, color, nestedItems } } = area;

        const isSelectionMoving =
            isSelectionClickedRef.current &&
            (selectionStartXYRef.current.x !==
                selectionCurrentXYRef.current.x ||
                selectionStartXYRef.current.y !==
                selectionCurrentXYRef.current.y);
        const isStartedNewLine =
            lineFromRef.current?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment =
            newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        // ===================== 0. FOCUSED AREA | OUTLINE FRAME =====================
        const isAreaHovered =
            hoveredAreaIdRef.current === id &&
            !hoveredElementIdRef.current &&
            !hoveredLineIdRef.current;
        if (
            // area is focused
            area.id === focusedAreaIdRef.current ||
            focusedElementsIdsRef.current.includes(id) ||
            // area is hovered and its not new line drawing
            (isAreaHovered && !isStartedNewLine && !isStartedNewPenSegment) ||
            selectedAreasIdsRef.current.includes(id) ||
            focusedGroupAreasIdsRef.current.includes(id)
        ) {
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3;
            ctx.strokeStyle = '#5f88ff';
            ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
            ctx.setLineDash([]);

            if (isAreaHovered && area.id !== focusedAreaIdRef.current) {
                ctx.lineWidth =
                    scaleNumbersRef.current.outlineWidth - 0.3 - 0.7;
                // ctx.strokeStyle = 'rgba(95, 136, 255, 0.8)';
            }

            ctx.beginPath();
            // ctx.roundRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1, 1);

            // ctx.roundRect(x - scaleNumbersRef.current.outlineOffset - 5, y - scaleNumbersRef.current.outlineOffset - 5, width + 2 * scaleNumbersRef.current.outlineOffset + 10, height + 2 * scaleNumbersRef.current.outlineOffset + 10, 0.75); // ASAP LAST


            const xOffset = width < 0
                ? -scaleNumbersRef.current.outlineOffset
                : scaleNumbersRef.current.outlineOffset;
            const yOffset = height < 0
                ? -scaleNumbersRef.current.outlineOffset
                : scaleNumbersRef.current.outlineOffset;

            // ctx.roundRect(
            //     x - scaleNumbersRef.current.outlineOffset,
            //     y - scaleNumbersRef.current.outlineOffset,
            //     width + 2 * scaleNumbersRef.current.outlineOffset,
            //     height + 2 * scaleNumbersRef.current.outlineOffset,
            //     0.75
            // );
            ctx.roundRect(
                x - xOffset,
                y - yOffset,
                width + 2 * xOffset,
                height + 2 * yOffset,
                0.75
            );

            ctx.stroke();

            // ctx.shadowColor = 'transparent';
            // ctx.fillStyle = '#39424c'; // canvas background
            // ctx.fillRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1);
            // ctx.stroke();
        }

        // ===================== 1. FOCUSED AREA | RESIZE POINTS =====================
        if (
            scaleRef.current > 0.15 &&
            !isCursorHand &&
            !isBtnRedGreenViewEnabled &&
            // is not export enabled
            !isBtnExportImageEnabled &&
            // is not mouse selection
            !isSelectionMoving &&
            // area is focused
            area.id === focusedAreaIdRef.current
            // focusedElementsIdsRef.current.includes(id) &&
            // focused only one area
            // focusedElementsIdsRef.current.length === 1
        ) {
            let resizeHandleSize =
                scaleNumbersRef.current.resizeHandleSize - 4 > 0
                    ? scaleNumbersRef.current.resizeHandleSize - 3
                    : scaleNumbersRef.current.resizeHandleSize;
            resizeHandleSize = Math.max(resizeHandleSize, 4);
            let resizeHandleRadius =
                scaleNumbersRef.current.resizeHandleRadius - 1 > 0
                    ? scaleNumbersRef.current.resizeHandleRadius - 1
                    : scaleNumbersRef.current.resizeHandleRadius;
            resizeHandleRadius = Math.max(resizeHandleRadius, 1);

            // const resizeOutlineWidth = width + (scaleNumbersRef.current.outlineOffset + 5) * 2;
            // const resizeOutlineHeight = height + (scaleNumbersRef.current.outlineOffset + 5) * 2;
            // const resizeX = x - (scaleNumbersRef.current.outlineOffset + 5);
            // const resizeY = y - (scaleNumbersRef.current.outlineOffset + 5);



            const xOffset = width < 0
                ? -scaleNumbersRef.current.outlineOffset
                : scaleNumbersRef.current.outlineOffset;
            const yOffset = height < 0
                ? -scaleNumbersRef.current.outlineOffset
                : scaleNumbersRef.current.outlineOffset;

            const resizeOutlineWidth =
                width + xOffset * 2;
            const resizeOutlineHeight =
                height + yOffset * 2;
            const resizeX = x - xOffset;
            const resizeY = y - yOffset;

            ctx.beginPath();
            ctx.strokeStyle = '#5f88ff';
            ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
            ctx.fillStyle = '#e6e6e6';
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7 + 1.5;
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 1;

            ctx.roundRect(
                resizeX - resizeHandleSize / 2 - 1,
                resizeY - resizeHandleSize / 2 - 1,
                resizeHandleSize + 2,
                resizeHandleSize + 2,
                resizeHandleRadius
            );
            ctx.roundRect(
                resizeX + resizeOutlineWidth - resizeHandleSize / 2 - 1,
                resizeY - resizeHandleSize / 2 - 1,
                resizeHandleSize + 2,
                resizeHandleSize + 2,
                resizeHandleRadius
            );
            ctx.roundRect(
                resizeX - resizeHandleSize / 2 - 1,
                resizeY + resizeOutlineHeight - resizeHandleSize / 2 - 1,
                resizeHandleSize + 2,
                resizeHandleSize + 2,
                resizeHandleRadius
            );
            ctx.roundRect(
                resizeX + resizeOutlineWidth - resizeHandleSize / 2 - 1,
                resizeY + resizeOutlineHeight - resizeHandleSize / 2 - 1,
                resizeHandleSize + 2,
                resizeHandleSize + 2,
                resizeHandleRadius
            );
            ctx.stroke();
            ctx.fill();
        }

        // // ===================== AREA SHADOW =====================
        // // Set shadow properties
        // ctx.shadowColor = theme.table.shadow.color;
        // ctx.shadowBlur = 8; // Розмиття тіні
        // ctx.shadowOffsetX = 2; // Зміщення по горизонталі
        // ctx.shadowOffsetY = 2; // Зміщення по вертикалі

        // ===================== AREA FRAME LINE =====================
        // ===================== AREA BACKGROUND =====================
        // ctx.lineWidth = scaleRef.current > 1 ? 2.8 : 3;
        ctx.lineWidth = scaleRef.current > 1 ? 2.6 : 2.5;
        // : 3.2;
        // : 2.8 / scaleRef.current;

        // const maxBorderRadius = 20 + 12 / scaleRef.current;
        // const maxBorderRadius = 16 + 12 / scaleRef.current;
        // const maxBorderRadius = 10 + 12 / scaleRef.current;
        const maxBorderRadius = 10 / scaleRef.current;
        const borderRadius = Math.min(
            Math.min(Math.abs(width), Math.abs(height)) / 10,
            maxBorderRadius
        );

        // // orange
        // ctx.fillStyle = 'rgba(255, 129, 88, 0.11)'; // 0.07 // 0.12
        // ctx.strokeStyle = '#ff8158';

        // // grey
        // ctx.fillStyle = 'rgba(168, 170, 174, 0.1)';
        // ctx.strokeStyle = '#a8aaae';

        // // blue
        // ctx.fillStyle = 'rgba(28, 134, 255, 0.1)';
        // ctx.strokeStyle = '#1c86ff';

        // // green
        // ctx.fillStyle = 'rgba(59, 173, 132, 0.1)';
        // ctx.strokeStyle = '#3bad84';

        // // red
        // ctx.fillStyle = 'rgba(255, 0, 0, 0.09)'; // 0.06 // 0.08
        // ctx.strokeStyle = '#e80362';

        // // purple
        // ctx.fillStyle = 'rgba(139, 125, 235, 0.1)';
        // ctx.strokeStyle = '#8b7deb';

        // // violet
        // ctx.fillStyle = 'rgba(235, 3, 235, 0.075)';
        // ctx.strokeStyle = '#eb03eb';

        // COLOR

        // default (grey)
        ctx.fillStyle = 'rgba(168, 170, 174, 0.1)';
        ctx.fillStyle = 'rgba(175, 197, 240, 0.1)';
        ctx.fillStyle = 'rgba(175, 197, 240, 0.09)';
        ctx.strokeStyle = '#a8aaae';
        ctx.strokeStyle = 'rgba(176, 184, 200, 1)';
        ctx.strokeStyle = 'rgba(172, 182, 204, 1)';

        ctx.strokeStyle = 'rgba(165, 178, 207, 1)';
        ctx.strokeStyle = 'rgba(173, 187, 218, 1)';

        if (color) {
            switch (color) {
                case 'blue': {
                    ctx.fillStyle = 'rgba(28, 134, 255, 0.1)';
                    ctx.fillStyle = 'rgba(137, 172, 255, 0.125)';

                    ctx.fillStyle = 'rgba(99, 146, 255, 0.09)';
                    ctx.fillStyle = 'rgba(72, 127, 255, 0.07)';
                    ctx.fillStyle = 'rgba(56, 125, 254, 0.08)';

                    ctx.strokeStyle = '#1c86ff';
                    ctx.strokeStyle = 'rgba(137, 172, 255, 1)';

                    ctx.strokeStyle = 'rgba(128, 166, 255, 1)';
                    break;
                }
                case 'green': {
                    ctx.fillStyle = 'rgba(59, 173, 132, 0.1)';
                    ctx.fillStyle = 'rgba(59, 165, 120, 0.1)';
                    ctx.strokeStyle = '#3bad84';
                    ctx.strokeStyle = 'rgba(30, 172, 120, 1)';
                    ctx.strokeStyle = 'rgba(55, 167, 126, 1)';

                    ctx.fillStyle = 'rgba(6, 255, 151, 0.06)';
                    break;
                }
                case 'orange': {
                    ctx.fillStyle = 'rgba(255, 129, 88, 0.11)'; // 0.07 // 0.12
                    ctx.strokeStyle = '#ff8158';
                    break;
                }
                case 'yellow': {
                    ctx.fillStyle = 'rgba(225, 179, 93, 0.11)';
                    ctx.strokeStyle = '#e1b35d';
                    break;
                }
                case 'pink': {
                    ctx.fillStyle = 'rgba(225, 93, 137, 0.11)';
                    ctx.strokeStyle = '#e15d89';
                    break;
                }
                case 'purple': {
                    // ctx.fillStyle = 'rgba(139, 125, 235, 0.1)';
                    ctx.fillStyle = 'rgba(130, 125, 235, 0.1)';
                    ctx.strokeStyle = '#8b7deb';
                    ctx.strokeStyle = 'rgba(144, 138, 234, 1)';
                    ctx.strokeStyle = 'rgba(151, 145, 243, 1)';


                    ctx.fillStyle = 'rgba(116, 84, 232, 0.08)';

                    break;
                }
                case 'violet': {
                    ctx.fillStyle = 'rgba(235, 3, 235, 0.075)';
                    ctx.fillStyle = 'rgba(220, 97, 220, 0.07)';
                    ctx.strokeStyle = '#eb03eb';
                    ctx.strokeStyle = '#d273d2ff';
                    ctx.strokeStyle = '#dd78ddff';
                    break;
                }
                case 'red': {
                    ctx.fillStyle = 'rgba(255, 0, 0, 0.09)'; // 0.06 // 0.08
                    ctx.fillStyle = 'rgba(255, 0, 0, 0.08)'; // 0.06 // 0.08
                    ctx.strokeStyle = '#e80362';
                    break;
                }
                case 'default': {
                    break;
                }
                default:
                    break;
            }
        }

        // BORDER

        ctx.setLineDash([]);

        if (!border || border === 'solid') {
            ctx.lineCap = 'square';
        } else if (border === 'dashes') {
            const dashLineInterval =
                scaleRef.current > 1 ? 12 : 12 / scaleRef.current;
            const dashSpaceInterval =
                scaleRef.current > 1 ? 15 : 15 / scaleRef.current;
            ctx.lineCap = 'butt';
            ctx.setLineDash([dashLineInterval, dashSpaceInterval]);
        } else if (border === 'dots') {
            const dashLineInterval =
                scaleRef.current > 1 ? 4 : 4 / scaleRef.current;
            const dashSpaceInterval =
                scaleRef.current > 1 ? 6 : 6 / scaleRef.current;
            ctx.lineCap = 'butt';
            ctx.setLineDash([dashLineInterval, dashSpaceInterval]);
        }

        // ===================== FOCUS ON AREA | WHEN MOVED ELEMENT IS IN/OUT AREA =====================
        // if (
        //     isMovingElementId &&
        //     elementsRef.current[isMovingElementId]?.value?.areaId === id
        // ) {
        //     ctx.lineWidth = scaleRef.current > 1 ? 2.8 : 2.8 / scaleRef.current;

        //     ctx.lineCap = 'square';
        //     ctx.setLineDash([]);
        //     ctx.strokeStyle = '#5f88ff';
        //     ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
        //     // ctx.strokeStyle = '#e6e6e6';

        //     ctx.strokeStyle = '#e15d89';
        //     ctx.strokeStyle = '#e2f2ffff';
        //     ctx.strokeStyle = '#d2ebffff';

        // }

        ctx.beginPath();
        ctx.roundRect(x, y, width, height, borderRadius);
        ctx.stroke();
        ctx.fill();

        ctx.setLineDash([]);












        // ===================== 10. SELECTED TABLE | OUTLINE FRAME (IN AREA SELECTED FRAME) =====================
        // if (!isBtnExportImageEnabled &&
        //     // resizeAreaPropsRef.current?.selectedElements?.includes(id)
        //     isMovingElementId
        // ) {

        if (
            // isMovingElementId &&
            // elementsRef.current[isMovingElementId]?.value?.areaId === id &&
            // elementsRef.current[isMovingElementId]?.value?.isChangedAreaId
            resizeElementPropsRef.current?.areaId === id
            // resizeElementPropsRef.current = { elementId, areaId: newAreaIdForElement }
        ) {
            // ctx.strokeStyle = 'rgba(250, 250, 250, 0.2)';
            ctx.strokeStyle = 'rgba(246, 250, 253, 0.2)';

            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;

            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3;
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3 - 0.7;
            // ctx.strokeStyle = 'rgba(95, 136, 255, 0.8)';

            ctx.beginPath();
            ctx.roundRect(
                x - scaleNumbersRef.current.outlineOffset,
                y - scaleNumbersRef.current.outlineOffset - 0.5,
                width + 2 * scaleNumbersRef.current.outlineOffset,
                height + 2 * scaleNumbersRef.current.outlineOffset + 1,
                0.75
            );
            ctx.stroke();
            // ctx.shadowColor = 'transparent';

            // ctx.fillStyle = 'rgba(111, 190, 255, 0.15)';
            ctx.fillStyle = 'rgba(111, 190, 255, 0.06)';
            // rgb()
            // canvas background
            ctx.fillRect(
                x - scaleNumbersRef.current.outlineOffset,
                y - scaleNumbersRef.current.outlineOffset - 0.5,
                width + 2 * scaleNumbersRef.current.outlineOffset,
                height + 2 * scaleNumbersRef.current.outlineOffset + 1
            );
            // ctx.stroke();
        }





        // ===================== TEXT HEADER =====================

        // const radius = 18 / scaleRef.current;
        // // const borderRadius = Math.min((Math.min(Math.abs(width), Math.abs(height))) / 10, maxBorderRadius)

        // ctx.beginPath();
        // ctx.fillStyle = theme.table.body.background;
        // ctx.roundRect(x + 10, y + 10, 300, 50, radius);
        // ctx.fill();

        // ===================== AREA END =====================
    }

    // function getTextByWidthLimit(ctx, text, maxWidth) {
    //     let currentLine = "";

    //     const symbols = text.split('');
    //     for (let i = 0; i < symbols.length; i++) {
    //         const symbol = symbols[i];

    //         const tempLine = currentLine + symbol;
    //         const tempLineWidth = ctx.measureText(tempLine).width;

    //         if (tempLineWidth > maxWidth) {
    //             return currentLine + '...';
    //         } else {
    //             currentLine = tempLine;
    //         }
    //     }

    //     return currentLine;
    // }

    function getDescriptionMaxWidth(ctx, text) {
        const lines = [];
        let linesMaxWidth = 0;

        const paragraphs = text.split("\n");  // Розбиваємо текст на абзаци по \n (новий рядок)

        paragraphs.forEach((paragraph, index) => {
            const words = paragraph.split(" ");
            let currentLine = "";
            let currentLineWidth = 0;

            words.forEach(word => {
                // Якщо слово містить табуляцію, заміняємо її на пробіли
                word = word.replace(/\t/g, "    ");  // Заміна табуляції на 4 пробіли

                currentLine = currentLine + word + " ";
                currentLineWidth = ctx.measureText(currentLine).width;
            });

            // Додаємо останній рядок абзацу
            if (currentLine.length > 0) {
                lines.push(currentLine);
                linesMaxWidth = Math.max(linesMaxWidth, currentLineWidth);
            }
        });

        return linesMaxWidth;
    }

    function getTableNameByWidthLimit(ctx, text, firstLineMaxWidth, lineMaxWidth) {
        let lines = [];
        let currentLine = "";
        let textMaxWidth = firstLineMaxWidth;

        const symbols = text.split('');
        for (let i = 0; i < symbols.length; i++) {
            const symbol = symbols[i];

            const tempLine = currentLine + symbol;
            const tempLineWidth = ctx.measureText(tempLine).width;

            if (tempLineWidth > textMaxWidth) {
                lines.push(currentLine);
                currentLine = symbol;

                if (lines.length === 1 || textMaxWidth !== lineMaxWidth) {
                    textMaxWidth = lineMaxWidth;
                }
            } else {
                currentLine = tempLine;
            }
        }

        // Додаємо останній рядок абзацу
        if (currentLine.length > 0) {
            lines.push(currentLine);
        }

        if (lines.length === 0) {
            lines.push('');
        }

        return lines;
    }

    function getEnumValuesByWidthLimit(ctx, text, lineMaxWidth) {
        let lines = [];
        let currentLine = "";

        const symbols = text.split('');
        for (let i = 0; i < symbols.length; i++) {
            const symbol = symbols[i];

            const tempLine = currentLine + symbol;
            const tempLineWidth = ctx.measureText(tempLine).width;

            if (tempLineWidth > lineMaxWidth) {
                lines.push(currentLine);
                currentLine = symbol;
            } else {
                currentLine = tempLine;
            }
        }

        // Додаємо останній рядок абзацу
        if (currentLine.length > 0) {
            lines.push(currentLine);
        }

        if (lines.length === 0) {
            lines.push('');
        }

        return lines;
    }


    // // Функція для розбиття тексту на кілька рядків
    // function wrapTableName(ctx, text, maxWidth) {
    //     const lines = [];
    //     const paragraphs = text.split("\n");  // Розбиваємо текст на абзаци по \n (новий рядок)

    //     paragraphs.forEach(paragraph => {
    //         const words = paragraph.split(" ");
    //         let currentLine = "";

    //         words.forEach(word => {
    //             // Якщо слово містить табуляцію, заміняємо її на пробіли
    //             word = word.replace(/\t/g, "    ");  // Заміна табуляції на 4 пробіли

    //             // Якщо слово занадто довге, розбиваємо його на частини
    //             if (ctx.measureText(word).width > maxWidth) {
    //                 const splitWord = breakLongWord(ctx, word, maxWidth);
    //                 splitWord.forEach(part => {
    //                     const testLine = currentLine + part + " ";
    //                     const testWidth = ctx.measureText(testLine).width;

    //                     if (testWidth > maxWidth && currentLine !== "") {
    //                         lines.push(currentLine);
    //                         currentLine = part + " ";
    //                     } else {
    //                         currentLine = testLine;
    //                     }
    //                 });
    //             } else {
    //                 const testLine = currentLine + word + " ";
    //                 const testWidth = ctx.measureText(testLine).width;

    //                 if (testWidth > maxWidth && currentLine !== "") {
    //                     lines.push(currentLine);
    //                     currentLine = word + " ";
    //                 } else {
    //                     currentLine = testLine;
    //                 }
    //             }
    //         });

    //         // Додаємо останній рядок абзацу
    //         if (currentLine.length > 0) {
    //             lines.push(currentLine);
    //         }
    //     });

    //     return lines;
    // }

    // Функція для розбиття тексту на кілька рядків
    function wrapText(ctx, text, maxWidth) {
        const lines = [];
        const paragraphs = text.split("\n");  // Розбиваємо текст на абзаци по \n (новий рядок)

        paragraphs.forEach(paragraph => {
            const words = paragraph.split(" ");
            let currentLine = "";

            words.forEach(word => {
                // Якщо слово містить табуляцію, заміняємо її на пробіли
                word = word.replace(/\t/g, "    ");  // Заміна табуляції на 4 пробіли

                // Якщо слово занадто довге, розбиваємо його на частини
                if (ctx.measureText(word).width > maxWidth) {
                    const splitWord = breakLongWord(ctx, word, maxWidth);
                    splitWord.forEach(part => {
                        const testLine = currentLine + part + " ";
                        const testWidth = ctx.measureText(testLine).width;

                        if (testWidth > maxWidth && currentLine !== "") {
                            lines.push(currentLine);
                            currentLine = part + " ";
                        } else {
                            currentLine = testLine;
                        }
                    });
                } else {
                    const testLine = currentLine + word + " ";
                    const testWidth = ctx.measureText(testLine).width;

                    if (testWidth > maxWidth && currentLine !== "") {
                        lines.push(currentLine);
                        currentLine = word + " ";
                    } else {
                        currentLine = testLine;
                    }
                }
            });

            // Додаємо останній рядок абзацу
            if (currentLine.length > 0) {
                lines.push(currentLine);
            }
        });

        return lines;
    }

    // Функція для розбиття дуже довгого слова на частини
    function breakLongWord(ctx, word, maxWidth) {
        const parts = [];
        let part = "";

        // Розбиваємо слово на частини, поки кожна частина не буде вміщуватися в maxWidth
        for (let i = 0; i < word.length; i++) {
            part += word[i];
            if (ctx.measureText(part).width > maxWidth) {
                parts.push(part.slice(0, -1));  // Додаємо частину слова, що вміщується
                part = word[i];  // Починаємо нову частину
            }
        }

        // Додаємо останню частину слова
        if (part) {
            parts.push(part);
        }

        return parts;
    }

    function drawTableTooltip(table) {
        const isSelectionMoving = isSelectionClickedRef.current &&
            (selectionStartXYRef.current.x !== selectionCurrentXYRef.current.x ||
                selectionStartXYRef.current.y !== selectionCurrentXYRef.current.y);
        const isStartedNewLine = lineFromRef.current?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment = newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        if (isBoardMoving || isSelectionMoving || isStartedNewLine || isStartedNewPenSegment) {
            return;
        }

        const ctx = contextRef.current;
        const { x: tableX, y: tableY, width: tableWidth, value: { header: { name }, description: tableDescription, columns } } = table;

        const description = tableDescription || '';
        const indexes = [];

        const columnsKeys = Object.keys(columns);
        columnsKeys.forEach(columnKey => {
            const column = columns[columnKey];
            if (column.index) {
                indexes.push({ columns: [column.name] });
            }
        });

        // indexes = [
        //     { columns: ['id'] },
        //     { columns: ['id', 'created_at'] },
        //     { columns: ['status', 'registration_date'] },
        // ]
        const scale = 1 / scaleRef.current;

        // ===================================================================
        // ============ 0. TOOLTIP (BASIC) ===================================
        // ===================================================================

        // ============ TOOLTIP (BASIC) ====================
        // tooltip (x, y) with distance to table on 'x' and align by table focused frame on 'y'
        const tooltipX = tableX + tableWidth + 21 * scale;
        const tooltipY = tableY - 6 * scale;

        // TODO: tooltip left to table / tooltip right to table

        const minTooltipWidth = 260;
        const maxTooltipWidth = 420;

        let tooltipWidth = minTooltipWidth;
        // const tooltipHeight = 160 * scale;

        const fontSize = 20;

        // ===================================================================
        // ============ 1. TOOLTIP (MEASURE TEXT FIELDS) =====================
        // ===================================================================

        // ============ TOOLTIP (MEASURE NAME) ====================
        // name styles
        ctx.font = `500 ${20}px Inter, sans-serif`;
        ctx.textAlign = 'left';

        const nameWidth = ctx.measureText(name).width;
        const oneLineHeaderWidth = nameWidth + 21 + 74 + 21 + 2;

        // ============ TOOLTIP (MEASURE DESCRIPTION) ====================
        // description styles
        ctx.font = `${20}px Inter, sans-serif`;
        ctx.textBaseline = "top";
        ctx.textAlign = 'left';

        // description width without 'wrapping' text (without splitting by smaller parts and move to the next line)
        const oneLineDescriptionWidth = 18 + 18 + getDescriptionMaxWidth(ctx, description); // descriptionMaxWidth

        // ============ TOOLTIP (MEASURE INDEXES) ========================
        let oneLineIndexMaxWidth = 0;
        const spaceSymbolWidth = 2.25;

        indexes.forEach(idx => {
            const indexName = idx.columns.join(', ');

            // measure 'left bracket' / '('
            ctx.font = `500 ${(fontSize)}px Inter, sans-serif`;
            const leftBracketWidth = ctx.measureText('(').width;

            // measure 'index name'
            ctx.font = `400 ${(fontSize)}px Roboto, sans-serif`;
            const indexWidth = ctx.measureText(indexName).width;

            const oneLineIndexWidth = 18 + indexWidth + leftBracketWidth * 2 + spaceSymbolWidth * 2.15 + 21;
            oneLineIndexMaxWidth = Math.max(oneLineIndexMaxWidth, oneLineIndexWidth);
        })

        // ===================================================================
        // ============ 2. TOOLTIP (CALCULATE TOOLTIP WIDTH) =================
        // ===================================================================
        // let textMaxWidth = Math.max(oneLineHeaderWidth, oneLineDescriptionWidth, pkWidth, indexesWidth);
        let textMaxWidth = Math.max(oneLineHeaderWidth, oneLineDescriptionWidth, oneLineIndexMaxWidth);

        if (textMaxWidth > maxTooltipWidth) {
            tooltipWidth = maxTooltipWidth;
        } else if (textMaxWidth < minTooltipWidth) {
            tooltipWidth = minTooltipWidth;
        } else {
            tooltipWidth = textMaxWidth;
        }

        if (oneLineIndexMaxWidth > maxTooltipWidth) {
            tooltipWidth = oneLineIndexMaxWidth;
        }

        // ===================================================================
        // ============ 3. TOOLTIP (CALCULATE TEXT HEIGHT) ===================
        // ===================================================================

        // ============ TOOLTIP (CALCULATE NAME HEIGHT) ======================
        // name styles
        ctx.font = `500 ${20 * scale}px Inter, sans-serif`;
        ctx.textAlign = 'left';

        const nameFirstLineMaxWidth = (tooltipWidth - (21 + 70 - 6 + 21)) * scale;
        const nameLineMaxWidth = (tooltipWidth - (21 + 21)) * scale;

        const nameLines = getTableNameByWidthLimit(ctx, name, nameFirstLineMaxWidth, nameLineMaxWidth);
        const nameHeight = (27 + 6 - 18) * scale + nameLines.length * fontSize * 1.4 * scale + 21 * scale;

        // ============ TOOLTIP (CALCULATE DESCRIPTION HEIGHT) ===============
        // description styles
        ctx.font = `${fontSize}px Inter, sans-serif`;
        ctx.textBaseline = "top";

        const maxTextWidth = tooltipWidth - 38;

        const descriptionLines = wrapText(ctx, description, maxTextWidth);
        const descriptionHeight = description
            ? descriptionLines.length * fontSize * 1.4
            : 0;

        // FIX: if descriptionLines.length > 7 => then maxTooltipWidth from 420 to 460/480 or hide 'description next after 7 lines'

        // ============ TOOLTIP (CALCULATE INDEXES HEIGHT) ===============
        let indexesHeight = 22 * 1.4 + indexes.length * (20 * 1.6);

        if (description) {
            indexesHeight += 7;
        }

        if (!indexes.length) {
            indexesHeight = 0;
        }
        // ===================================================================
        // ============ 4. TOOLTIP BACKGROUND (CONNECTOR) ====================
        // ===================================================================

        // ============ TOOLTIP CONNECTOR (LINE) ===========
        const lineX = tableX + tableWidth - 15;
        const lineY = tableY + (TABLE_HEADER_HEIGHT / 2);

        ctx.lineWidth = 2.5 * scale;
        ctx.strokeStyle = '#e6e8e9';
        ctx.strokeStyle = '#f5f5f5';
        ctx.beginPath();
        ctx.moveTo(lineX, lineY);
        ctx.lineTo(lineX + 15 + 25 * scale, lineY);
        ctx.stroke();

        // ============ TOOLTIP CONNECTOR (CIRCLE) =========
        const pointRadius = 5.5 * scale;
        ctx.fillStyle = '#d0d7dc';
        ctx.fillStyle = '#eeeeee';
        ctx.beginPath();
        ctx.arc(lineX, lineY, pointRadius, 0, 2 * Math.PI);
        ctx.fill();

        // ===================================================================
        // ============ 5. TOOLTIP BACKGROUND (FRAME) ========================
        // ===================================================================

        // ============ TOOLTIP BACKGROUND (RECTANGLE + BORDER LINE)
        // ctx.fillStyle = '#0f1113';
        ctx.fillStyle = 'black';
        ctx.strokeStyle = '#5f88ff';
        ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
        ctx.lineWidth = 1.6 * scale;
        ctx.beginPath();
        // ctx.roundRect(tooltipX, tooltipY, tooltipWidth * scale, (descriptionHeight + 73) * scale, 10 * scale);
        ctx.roundRect(tooltipX, tooltipY, tooltipWidth * scale, nameHeight + (descriptionHeight + indexesHeight) * scale, 10 * scale);
        ctx.fill();
        ctx.stroke();

        // ============ TOOLTIP BACKGROUND (SHADOW) ========
        ctx.shadowColor = theme.table.shadow.color;
        ctx.shadowBlur = 8; // Розмиття тіні
        ctx.shadowOffsetX = 2; // Зміщення по горизонталі
        ctx.shadowOffsetY = 2; // Зміщення по вертикалі

        // ===================================================================
        // ============ 6. TOOLTIP HEADER (TABLE NAME) =======================
        // ===================================================================

        // ============ TOOLTIP HEADER (NAME TEXT) =========
        ctx.font = `500 ${20 * scale}px Inter, sans-serif`;
        ctx.fillStyle = '#e6e8e9';
        ctx.fillStyle = '#eeeeee';
        ctx.textAlign = 'left';

        let headerX = tooltipX + 21 * scale;
        let headerY = tooltipY + (27 + 6 - 18) * scale + fontSize * 1.4 * scale;

        for (let i = 0; i < nameLines.length; i++) {
            if (i === 0) {
                ctx.font = `500 ${19 * scale}px Inter, sans-serif`;
                ctx.fillStyle = '#dea02b';
                ctx.fillStyle = '#eaac37';
                ctx.fillStyle = '#e8aa36';
                ctx.fillStyle = '#ffc65e';

                ctx.fillText('Table', tooltipX + 21 * scale, tooltipY + (27 + 6 - 18) * scale);
                ctx.font = `500 ${19 * scale}px Inter, sans-serif`;
                ctx.fillStyle = '#e6e8e9';
                ctx.fillStyle = '#eeeeee';
                ctx.fillText(nameLines[i], tooltipX + (21 + 70 - 6) * scale, tooltipY + (27 + 6 - 18) * scale);
            } else {
                ctx.fillText(nameLines[i], headerX, headerY);
                headerX = tooltipX + 21 * scale;
                headerY += fontSize * 1.4 * scale;
            }
        }
        // ctx.fillText('Table:', tooltipX + 21 * scale, tooltipY + (27 + 6 - 18) * scale);
        // ctx.fillText(name, tooltipX + (21 + 74) * scale, tooltipY + (27 + 6 - 18) * scale);

        // ============ TOOLTIP HEADER (LINE AFTER) ========
        const headerLineY = headerY + 3 * scale;
        ctx.fillStyle = '#e6e8e9';
        ctx.fillStyle = '#eeeeee';
        ctx.fillStyle = '#fefefe';
        ctx.fillRect(tooltipX + 16 * scale, headerLineY, tooltipWidth * scale - 32 * scale, 1.7 * scale);

        let nextSectionY = headerLineY;
        // ===================================================================
        // ============ 7. TOOLTIP BODY (DESCRIPTION) ========================
        // ===================================================================

        // ============ TOOLTIP BODY (DESCRIPTION TEXT) ====
        if (description) {
            ctx.font = `${fontSize * scale}px Inter, sans-serif`;

            let descriptionX = tooltipX + 21 * scale;
            let descriptionY = nextSectionY + fontSize * 1.4 * 0.5 * scale;

            for (let i = 0; i < descriptionLines.length; i++) {
                ctx.fillText(descriptionLines[i], descriptionX, descriptionY);
                descriptionY += fontSize * 1.4 * scale;
            }

            nextSectionY = descriptionY;

            // ============ TOOLTIP BODY (LINE AFTER) ==========
            // const descriptionLineY = descriptionY + 4 * scale;
            // ctx.fillStyle = '#e6e8e9';
            // ctx.fillRect(tooltipX + 16 * scale, descriptionLineY, tooltipWidth * scale - 32 * scale, 1.7 * scale);
        }

        // ===================================================================
        // ============ 9. TOOLTIP BODY (INDEXES) ============================
        // ===================================================================

        // ============ TOOLTIP BODY (INDEXES) =========
        if (indexes.length) {
            const newLineDiff = (32) * scale;
            const spaceSymbolWidth = 2.25;

            const indexesX = tooltipX + 18 * scale;

            const indexesY = description
                ? nextSectionY + (27 - 18 - 2) * scale
                : nextSectionY + (27 + 6 - 18) * scale;

            // fill text 'Indexes'
            ctx.font = `500 ${19 * scale}px Inter, sans-serif`;
            // ctx.fillStyle = '#d6384d';
            // ctx.fillStyle = '#ff5c5c';
            // ctx.fillStyle = '#ff6969';

            // ctx.fillStyle = '#ff7070';
            ctx.fillStyle = '#dea02b';
            ctx.fillStyle = '#eaac37';
            ctx.fillStyle = '#e8aa36';
            ctx.fillStyle = '#ffc65e';

            ctx.textAlign = 'left';
            ctx.fillText('Indexes', indexesX, indexesY);

            const indexX = tooltipX + 23 * scale;
            let indexY = indexesY + (33) * scale;

            indexes.forEach(idx => {
                const indexName = idx.columns.join(', ');

                // measure 'index name'
                ctx.font = `400 ${(fontSize) * scale}px Roboto, sans-serif`;
                const indexWidth = ctx.measureText(indexName).width;

                // measure 'left bracket' / '('
                ctx.font = `500 ${(fontSize) * scale}px Inter, sans-serif`;
                const leftBracketWidth = ctx.measureText('(').width;

                // fill 'left bracket' / '('
                ctx.font = `500 ${(fontSize) * scale}px Inter, sans-serif`;
                // ctx.fillStyle = '#f0ff8c';
                ctx.fillStyle = '#e6e8e9';
                ctx.fillStyle = '#eeeeee';
                ctx.fillText('(', indexX, indexY);

                // fill 'index name'
                ctx.font = `400 ${(fontSize) * scale}px Roboto, sans-serif`;
                // ctx.fillStyle = '#f0ff8c';
                ctx.fillStyle = '#e6e8e9';
                ctx.fillStyle = '#eeeeee';
                ctx.fillText(indexName, indexX + leftBracketWidth + (spaceSymbolWidth + 0.5) * scale, indexY);

                // fill 'right bracket' / ')'
                // ctx.fillStyle = '#f0ff8c';
                ctx.fillStyle = '#e6e8e9';
                ctx.fillStyle = '#eeeeee';
                ctx.font = `500 ${(fontSize) * scale}px Inter, sans-serif`;
                ctx.fillText(')', indexX + indexWidth + leftBracketWidth + spaceSymbolWidth * 2.15 * scale, indexY);

                indexY = indexY + newLineDiff;
            })
        }
    }

    function drawColumnTooltip(table, column, columnIndex) {
        const isSelectionMoving = isSelectionClickedRef.current &&
            (selectionStartXYRef.current.x !== selectionCurrentXYRef.current.x ||
                selectionStartXYRef.current.y !== selectionCurrentXYRef.current.y);
        const isStartedNewLine = lineFromRef.current?.elementId || isBtnNewLineEnabled;
        const isStartedNewPenSegment = newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        if (isBoardMoving || isSelectionMoving || isStartedNewLine || isStartedNewPenSegment) {
            return;
        }

        const ctx = contextRef.current;
        const {
            x: tableX,
            y: tableY,
            width: tableWidth,
        } = table;
        const {
            name: columnName,
            type,
            description: columnDescription,
            enumValues: columnEnumValues,
            defaultValue: columnDefaultValue,
            generatedValue: columnGeneratedValue,
            checkValue: columnCheckValue,
        } = column;

        const description = columnDescription || '';
        const enumValues = columnEnumValues || [];
        const defaultValue = columnDefaultValue || '';
        const generatedValue = columnGeneratedValue || '';
        const checkValue = columnCheckValue || '';

        const scale = 1 / scaleRef.current;

        // ===================================================================
        // ============ 0. TOOLTIP (BASIC) ===================================
        // ===================================================================

        // ============ TOOLTIP (BASIC) ====================
        // tooltip (x, y) with distance to table on 'x' and align by table focused frame on 'y'
        const tooltipX = tableX + tableWidth + 21 * scale;

        // const tooltipY = tableY - 6 * scale;
        const columnY = tableY + TABLE_HEADER_HEIGHT + columnIndex * TABLE_ROW_HEIGHT + HEADER_BODY_MARGIN;

        const tooltipY = columnY - 6 * scale;
        // TODO: tooltip left to table / tooltip right to table

        const minTooltipWidth = 240;
        const maxTooltipWidth = 480;

        let tooltipWidth = minTooltipWidth;
        // const tooltipHeight = 160 * scale;

        const fontSize = 19;
        const spaceSymbolWidth = 2.25;

        // ===================================================================
        // ============ 1. TOOLTIP (MEASURE TEXT FIELDS) =====================
        // ===================================================================

        // ============ TOOLTIP (MEASURE NAME) ====================
        // name styles
        ctx.font = `500 ${20}px Inter, sans-serif`;
        ctx.textAlign = 'left';

        const nameText = `${columnName}  `;
        const nameTypeText = `${columnName}  ${type}`;

        const nameWidth = ctx.measureText(nameTypeText).width;
        const oneLineHeaderWidth = 21 + nameWidth + 21 + 2;

        // ============ TOOLTIP (MEASURE DESCRIPTION) ====================
        // description styles
        ctx.font = `${20}px Inter, sans-serif`;
        ctx.textBaseline = "top";
        ctx.textAlign = 'left';

        // description width without 'wrapping' text (without splitting by smaller parts and move to the next line)
        const oneLineDescriptionWidth = 18 + 18 + getDescriptionMaxWidth(ctx, description); // descriptionMaxWidth

        // ============ TOOLTIP (MEASURE 'ENUM') ========================
        const enumString = '( ' + enumValues.join(', ') + ' )';

        // measure 'enum'
        ctx.font = `400 ${(fontSize - 1)}px Inter, sans-serif`;
        const enumWidth = ctx.measureText(enumString).width;
        let oneLineEnumMaxWidth = 18 + enumWidth + spaceSymbolWidth * 2.15 + 21;

        // ============ TOOLTIP (MEASURE 'DEFAULT VALUE') ========================
        const defaultValueString = '( ' + defaultValue + ' )';

        // measure 'default value'
        ctx.font = `400 ${(fontSize - 1)}px Inter, sans-serif`;
        const defaultValueWidth = ctx.measureText(defaultValueString).width;
        let oneLineDefaultValueMaxWidth = 18 + defaultValueWidth + spaceSymbolWidth * 2.15 + 21;

        // ============ TOOLTIP (MEASURE 'GENERATED VALUE') ========================
        const generatedValueString = '( ' + generatedValue + ' )';

        // measure 'generated value'
        ctx.font = `400 ${(fontSize - 1)}px Inter, sans-serif`;
        const generatedValueWidth = ctx.measureText(generatedValueString).width;
        let oneLineGeneratedValueMaxWidth = 18 + generatedValueWidth + spaceSymbolWidth * 2.15 + 21;

        // ============ TOOLTIP (MEASURE 'CHECK VALUE') ========================
        const checkValueString = '( ' + checkValue + ' )';

        // measure 'check value'
        ctx.font = `400 ${(fontSize - 1)}px Inter, sans-serif`;
        const checkValueWidth = ctx.measureText(checkValueString).width;
        let oneLineCheckValueMaxWidth = 18 + checkValueWidth + spaceSymbolWidth * 2.15 + 21;

        // ===================================================================
        // ============ 2. TOOLTIP (CALCULATE TOOLTIP WIDTH) =================
        // ===================================================================
        let textMaxWidth = Math.max(
            oneLineHeaderWidth,
            oneLineDescriptionWidth,
            oneLineEnumMaxWidth,
            oneLineGeneratedValueMaxWidth,
            oneLineCheckValueMaxWidth,
            oneLineDefaultValueMaxWidth,
        );

        if (textMaxWidth > maxTooltipWidth) {
            tooltipWidth = maxTooltipWidth;
        } else if (textMaxWidth < minTooltipWidth) {
            tooltipWidth = minTooltipWidth;
        } else {
            tooltipWidth = textMaxWidth;
        }

        // if (oneLineEnumMaxWidth > maxTooltipWidth) {
        //     tooltipWidth = oneLineEnumMaxWidth;
        // }

        // ===================================================================
        // ============ 3. TOOLTIP (CALCULATE TEXT HEIGHT) ===================
        // ===================================================================

        // ============ TOOLTIP (CALCULATE NAME HEIGHT) ======================
        // name styles
        ctx.font = `500 ${20 * scale}px Inter, sans-serif`;
        ctx.textAlign = 'left';

        const nameLineMaxWidth = (tooltipWidth - (21 + 21)) * scale;

        const nameTypeLines = getTableNameByWidthLimit(ctx, nameTypeText, nameLineMaxWidth, nameLineMaxWidth);
        const nameHeight = (27 + 6 - 18) * scale + nameTypeLines.length * fontSize * 1.4 * scale + 21 * scale;
        const nameLines = getTableNameByWidthLimit(ctx, nameText, nameLineMaxWidth, nameLineMaxWidth);

        // ============ TOOLTIP (CALCULATE DESCRIPTION HEIGHT) ===============
        // description styles
        ctx.font = `${fontSize}px Inter, sans-serif`;
        ctx.textBaseline = "top";

        const maxTextWidth = tooltipWidth - 38;

        const descriptionLines = wrapText(ctx, description, maxTextWidth);
        const descriptionHeight = description
            ? descriptionLines.length * fontSize * 1.4
            : 0;

        // FIX: if descriptionLines.length > 7 => then maxTooltipWidth from 420 to 460/480 or hide 'description next after 7 lines'

        // ============ TOOLTIP (CALCULATE 'ENUM' HEIGHT) ===============
        // enum styles
        ctx.font = `${fontSize - 1}px Inter, sans-serif`;
        ctx.textBaseline = "top";

        const maxEnumTextWidth = tooltipWidth - 38;
        const enumLines = getEnumValuesByWidthLimit(ctx, enumString, maxEnumTextWidth);

        const enumTitleHeight = 21 * 1.4;
        let enumHeight = enumValues.length
            ? enumTitleHeight + enumLines.length * fontSize * 1.4
            : 0;

        if (description) {
            enumHeight += 4;
        }

        if (!enumValues.length) {
            enumHeight = 0;
        }

        // ============ TOOLTIP (CALCULATE 'DEFAULT VALUE' HEIGHT) ===============
        // default value styles
        ctx.font = `${fontSize - 1}px Inter, sans-serif`;
        ctx.textBaseline = "top";

        const maxDefaultValueTextWidth = tooltipWidth - 38;
        const defaultValueLines = getEnumValuesByWidthLimit(ctx, defaultValueString, maxDefaultValueTextWidth);

        const defaultValueTitleHeight = 21 * 1.4;
        let defaultValueHeight = defaultValue.length
            ? defaultValueTitleHeight + defaultValueLines.length * fontSize * 1.4
            : 0;

        if (description && !enumValues.length) {
            defaultValueHeight += 4;
        }

        if (enumValues.length) {
            defaultValueHeight += 5;
        }

        if (!defaultValue.length) {
            defaultValueHeight = 0;
        }

        // ============ TOOLTIP (CALCULATE 'GENERATED VALUE' HEIGHT) ===============
        // generated value styles
        ctx.font = `${fontSize - 1}px Inter, sans-serif`;
        ctx.textBaseline = "top";

        const maxGeneratedValueTextWidth = tooltipWidth - 38;
        const generatedValueLines = getEnumValuesByWidthLimit(ctx, generatedValueString, maxGeneratedValueTextWidth);

        const generatedValueTitleHeight = 21 * 1.4;
        let generatedValueHeight = generatedValue
            ? generatedValueTitleHeight + generatedValueLines.length * fontSize * 1.4
            : 0;

        if (description && !enumValues.length && !defaultValue) {
            generatedValueHeight += 4;
        }
        if (enumValues.length && !defaultValue) {
            generatedValueHeight += 5;
        }
        if (defaultValue) {
            generatedValueHeight += 5;
        }

        if (!generatedValue) {
            generatedValueHeight = 0;
        }

        // ============ TOOLTIP (CALCULATE 'CHECK VALUE' HEIGHT) ===============
        // check value styles
        ctx.font = `${fontSize - 1}px Inter, sans-serif`;
        ctx.textBaseline = "top";

        const maxCheckValueTextWidth = tooltipWidth - 38;
        const checkValueLines = getEnumValuesByWidthLimit(ctx, checkValueString, maxCheckValueTextWidth);

        const checkValueTitleHeight = 21 * 1.4;
        let checkValueHeight = checkValue
            ? checkValueTitleHeight + checkValueLines.length * fontSize * 1.4
            : 0;

        if (description && !enumValues.length && !defaultValue && !generatedValue) {
            checkValueHeight += 4;
        }
        if (enumValues.length && !defaultValue) {
            checkValueHeight += 5;
        }
        if (defaultValue && !generatedValue) {
            checkValueHeight += 5;
        }
        if (generatedValue) {
            checkValueHeight += 5;
        }

        if (!checkValue) {
            checkValueHeight = 0;
        }

        // ===================================================================
        // ============ 4. TOOLTIP BACKGROUND (CONNECTOR) ====================
        // ===================================================================

        // ============ TOOLTIP CONNECTOR (LINE) ===========
        const lineX = tableX + tableWidth - 15 + 0.75;
        const lineY = columnY + (TABLE_ROW_HEIGHT / 2) + 0.75;

        ctx.lineWidth = 2.5 * scale;
        ctx.strokeStyle = '#e6e8e9';
        ctx.strokeStyle = '#f5f5f5';
        ctx.beginPath();
        ctx.moveTo(lineX, lineY);
        ctx.lineTo(lineX + 15 + 25 * scale, lineY);
        ctx.stroke();

        // ============ TOOLTIP CONNECTOR (CIRCLE) =========
        const pointRadius = 5.5 * scale;
        ctx.fillStyle = '#d0d7dc';
        ctx.fillStyle = '#eeeeee';
        ctx.beginPath();
        ctx.arc(lineX, lineY, pointRadius, 0, 2 * Math.PI);
        ctx.fill();

        // ===================================================================
        // ============ 5. TOOLTIP BACKGROUND (FRAME) ========================
        // ===================================================================

        // ============ TOOLTIP BACKGROUND (RECTANGLE + BORDER LINE)
        // ctx.fillStyle = '#0f1113';
        ctx.fillStyle = 'black';
        ctx.strokeStyle = '#5f88ff';
        ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
        ctx.lineWidth = 1.6 * scale;
        ctx.beginPath();
        // ctx.roundRect(tooltipX, tooltipY, tooltipWidth * scale, (descriptionHeight + 73) * scale, 10 * scale);
        ctx.roundRect(tooltipX, tooltipY, tooltipWidth * scale, nameHeight + (descriptionHeight + enumHeight + defaultValueHeight + generatedValueHeight + checkValueHeight) * scale, 10 * scale);
        ctx.fill();
        ctx.stroke();

        // ============ TOOLTIP BACKGROUND (SHADOW) ========
        ctx.shadowColor = theme.table.shadow.color;
        ctx.shadowBlur = 8; // Розмиття тіні
        ctx.shadowOffsetX = 2; // Зміщення по горизонталі
        ctx.shadowOffsetY = 2; // Зміщення по вертикалі

        // ===================================================================
        // ============ 6. TOOLTIP HEADER (NAME + TYPE) ======================
        // ===================================================================

        // ============ TOOLTIP HEADER (NAME TEXT) =========
        ctx.font = `500 ${19 * scale}px Inter, sans-serif`;
        ctx.fillStyle = '#e6e8e9';
        ctx.fillStyle = '#eeeeee';
        ctx.textAlign = 'left';

        let headerX = tooltipX + 21 * scale;
        let headerY = tooltipY + (27 + 6 - 18) * scale + fontSize * 1.4 * scale;

        for (let i = 0; i < nameLines.length; i++) {
            if (i === 0) {
                ctx.fillText(nameLines[i], tooltipX + 21 * scale, tooltipY + (27 + 6 - 18) * scale);
            } else {
                ctx.fillText(nameLines[i], headerX, headerY);
                headerX = tooltipX + 21 * scale;
                headerY += fontSize * 1.4 * scale;
            }
        }

        ctx.fillStyle = '#dea02b';
        ctx.fillStyle = '#eaac37';
        ctx.fillStyle = '#e8aa36';
        ctx.fillStyle = '#ffc65e';

        const lastNameLine = nameLines.length - 1;
        const lastNameLineText = nameLines[lastNameLine];
        const lastNameLineTextLength = lastNameLineText.length;

        for (let i = lastNameLine; i < nameTypeLines.length; i++) {
            if (i === lastNameLine) {
                const nameWidth = ctx.measureText(lastNameLineText).width;
                const typeText = nameTypeLines[i].slice(lastNameLineTextLength);

                headerY -= fontSize * 1.4 * scale;
                if (typeText) {
                    headerX += nameWidth;
                }

                ctx.fillText(typeText, headerX, headerY);
                headerX = tooltipX + 21 * scale;
                headerY += fontSize * 1.4 * scale;
            } else {
                ctx.fillText(nameTypeLines[i], headerX, headerY);
                headerX = tooltipX + 21 * scale;
                headerY += fontSize * 1.4 * scale;
            }
        }

        // ============ TOOLTIP HEADER (LINE AFTER) ========
        const headerLineY = headerY + 3 * scale;
        ctx.fillStyle = '#e6e8e9';
        ctx.fillStyle = '#eeeeee';
        ctx.fillStyle = '#fefefe';
        ctx.fillRect(tooltipX + 16 * scale, headerLineY, tooltipWidth * scale - 32 * scale, 1.7 * scale);

        let nextSectionY = headerLineY;
        // ===================================================================
        // ============ 7. TOOLTIP BODY (DESCRIPTION) ========================
        // ===================================================================

        // ============ TOOLTIP BODY (DESCRIPTION TEXT) ====
        if (description) {
            ctx.font = `${fontSize * scale}px Inter, sans-serif`;

            let descriptionX = tooltipX + 21 * scale;
            let descriptionY = nextSectionY + fontSize * 1.4 * 0.5 * scale;

            for (let i = 0; i < descriptionLines.length; i++) {
                ctx.fillText(descriptionLines[i], descriptionX, descriptionY);
                descriptionY += fontSize * 1.4 * scale;
            }

            nextSectionY = descriptionY - 14 * scale;
        }

        // ===================================================================
        // ============ 9. TOOLTIP BODY (ENUM VALUES) ========================
        // ===================================================================
        if (enumValues.length && enumLines.length) {
            const enumTitleX = tooltipX + 18 * scale;
            const enumTitleY = nextSectionY + (27 + 6 - 18) * scale;

            // fill text 'Enum Title'
            ctx.font = `500 ${18 * scale}px Inter, sans-serif`;
            ctx.fillStyle = '#dea02b';
            ctx.fillStyle = '#eaac37';
            ctx.fillStyle = '#e8aa36';
            ctx.fillStyle = '#ffc65e';
            ctx.textAlign = 'left';
            ctx.fillText('Enum', enumTitleX, enumTitleY);

            const enumX = tooltipX + 23 * scale;
            let enumY = enumTitleY + (31) * scale;

            // fill text 'Enum Values'
            ctx.fillStyle = '#e6e8e9';
            ctx.fillStyle = '#eeeeee';
            ctx.font = `400 ${(fontSize - 1) * scale}px Inter, sans-serif`;

            for (let i = 0; i < enumLines.length; i++) {
                ctx.fillText(enumLines[i], enumX, enumY);
                enumY += fontSize * 1.4 * scale;
            }

            nextSectionY = enumY - 14 * scale;
        }

        // ===================================================================
        // ============ 9. TOOLTIP BODY (DEFAULT VALUE) ======================
        // ===================================================================
        if (defaultValue && defaultValueLines.length) {
            const defaultValueTitleX = tooltipX + 18 * scale;

            const defaultValueTitleY = nextSectionY + (27 + 6 - 18) * scale;

            // fill text 'Default Value Title'
            ctx.font = `500 ${18 * scale}px Inter, sans-serif`;
            ctx.fillStyle = '#dea02b';
            ctx.fillStyle = '#eaac37';
            ctx.fillStyle = '#e8aa36';
            ctx.fillStyle = '#ffc65e';
            ctx.textAlign = 'left';
            ctx.fillText('Default', defaultValueTitleX, defaultValueTitleY);

            const defaultValueX = tooltipX + 23 * scale;
            let defaultValueY = defaultValueTitleY + (31) * scale;

            // fill text 'Default Value'
            ctx.fillStyle = '#e6e8e9';
            ctx.fillStyle = '#eeeeee';
            ctx.font = `400 ${(fontSize - 1) * scale}px Inter, sans-serif`;

            for (let i = 0; i < defaultValueLines.length; i++) {
                ctx.fillText(defaultValueLines[i], defaultValueX, defaultValueY);
                defaultValueY += fontSize * 1.4 * scale;
            }

            nextSectionY = defaultValueY - 14 * scale;
        }

        // ===================================================================
        // ============ 9. TOOLTIP BODY (GENERATED VALUE) ====================
        // ===================================================================
        if (generatedValue && generatedValueLines.length) {
            const generatedValueTitleX = tooltipX + 18 * scale;
            const generatedValueTitleY = nextSectionY + (27 + 6 - 18) * scale;

            // fill text 'Generated Value Title'
            ctx.font = `500 ${18 * scale}px Inter, sans-serif`;
            ctx.fillStyle = '#dea02b';
            ctx.fillStyle = '#eaac37';
            ctx.fillStyle = '#e8aa36';
            ctx.fillStyle = '#ffc65e';
            ctx.textAlign = 'left';
            ctx.fillText('Generated', generatedValueTitleX, generatedValueTitleY);

            const generatedValueX = tooltipX + 23 * scale;
            let generatedValueY = generatedValueTitleY + (31) * scale;

            // fill text 'Generated Value'
            ctx.fillStyle = '#e6e8e9';
            ctx.fillStyle = '#eeeeee';
            ctx.font = `400 ${(fontSize - 1) * scale}px Inter, sans-serif`;

            for (let i = 0; i < generatedValueLines.length; i++) {
                ctx.fillText(generatedValueLines[i], generatedValueX, generatedValueY);
                generatedValueY += fontSize * 1.4 * scale;
            }

            nextSectionY = generatedValueY - 14 * scale;
        }

        // ===================================================================
        // ============ 9. TOOLTIP BODY (CHECK VALUE) ========================
        // ===================================================================
        if (checkValue && checkValueLines.length) {
            const checkValueTitleX = tooltipX + 18 * scale;
            const checkValueTitleY = nextSectionY + (27 + 6 - 18) * scale;

            // fill text 'Check Value Title'
            ctx.font = `500 ${18 * scale}px Inter, sans-serif`;
            ctx.fillStyle = '#dea02b';
            ctx.fillStyle = '#eaac37';
            ctx.fillStyle = '#e8aa36';
            ctx.fillStyle = '#ffc65e';
            ctx.textAlign = 'left';
            ctx.fillText('Check', checkValueTitleX, checkValueTitleY);

            const checkValueX = tooltipX + 23 * scale;
            let checkValueY = checkValueTitleY + (31) * scale;

            // fill text 'Check Value'
            ctx.fillStyle = '#e6e8e9';
            ctx.fillStyle = '#eeeeee';
            ctx.font = `400 ${(fontSize - 1) * scale}px Inter, sans-serif`;

            for (let i = 0; i < checkValueLines.length; i++) {
                ctx.fillText(checkValueLines[i], checkValueX, checkValueY);
                checkValueY += fontSize * 1.4 * scale;
            }

            nextSectionY = checkValueY - 14 * scale;
        }
    }

    function setInitialValuesForExportImageBox() {
        const initialClientX = 460;
        const initialClientY = 150;

        // const initialClientBoxWidth = 1000;
        // const initialClientBoxHeight = 600;
        const initialClientBoxWidth = 900;
        const initialClientBoxHeight = 500;

        exportImageBoxPropsRef.current = {
            clientX: initialClientX,
            clientY: initialClientY,
            clientBoxWidth: initialClientBoxWidth,
            clientBoxHeight: initialClientBoxHeight,
        };
    }

    // function changePropsForExportImageBox(scaleValue) {

    // 	const initialClientX = 460;
    // 	const initialClientY = 150;
    // 	const initialX = (initialClientX - originXYRef.current.x) / scaleRef.current;
    // 	const initialY = (initialClientY - originXYRef.current.x) / scaleRef.current;

    // 	// const width = ref.current.width / scaleRef.current;
    // 	// const height = ref.current.height / scaleRef.current;

    // 	// const zeroX = (0 - originXYRef.current.x) / scaleRef.current;
    // 	// const zeroY = (0 - originXYRef.current.y) / scaleRef.current;

    // 	exportImageBoxPropsRef.current.x = initialX  / scaleRef.current,
    // 		y: initialY,
    // 		boxWidth: 1000 / scaleRef.current,
    // 		boxHeight: 600 / scaleRef.current,
    // 	}

    // }

    function drawExportImageBox() {
        // const canvas = ref.current;
        // const context = contextRef.current;

        // const exportImgCanvas = exportImgCanvasRef.current;
        // const exportImgContext = exportImgContextRef.current;

        // if (!canvas || !context || !exportImgCanvas || !exportImgContext) {
        //     return;
        // }

        // exportImgCanvas.style.width = canvas.style.width;
        // exportImgCanvas.style.height = canvas.style.height;

        // exportImgCanvas.width = canvas.width;
        // exportImgCanvas.height = canvas.height;

        // exportImgContext.imageSmoothingEnabled = true;
        // exportImgContext.imageSmoothingQuality = 'high';

        // // =========================================

        // const dpr = window.devicePixelRatio || 1;

        // exportImgContext.clearRect(0, 0, canvas.width, canvas.height);
        // exportImgContext.save();
        // exportImgContext.setTransform(
        //     scaleRef.current * dpr,
        //     0,
        //     0,
        //     scaleRef.current * dpr,
        //     originXYRef.current.x * dpr,
        //     originXYRef.current.y * dpr
        // );




        // const ctx = exportImgContext;
        const ctx = contextRef.current;

        const width = ref.current.width / scaleRef.current;
        const height = ref.current.height / scaleRef.current;

        const zeroX = (0 - originXYRef.current.x) / scaleRef.current;
        const zeroY = (0 - originXYRef.current.y) / scaleRef.current;

        let { clientX, clientY, clientBoxWidth, clientBoxHeight } =
            exportImageBoxPropsRef.current;

        const x = (clientX - originXYRef.current.x) / scaleRef.current;
        const y = (clientY - originXYRef.current.y) / scaleRef.current;
        const boxWidth = clientBoxWidth / scaleRef.current;
        const boxHeight = clientBoxHeight / scaleRef.current;

        const distanceTop = y - zeroY;
        const distanceBottom = height - boxHeight - distanceTop;
        const distanceLeft = x - zeroX;

        const distanceRight = width - boxWidth - distanceLeft;

        // Затемнення зверху
        // ctx.fillStyle = 'rgba(0, 0, 0, 0.56)';
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        // ctx.fillRect(zeroX, zeroY, width, distanceY);
        ctx.fillRect(zeroX, zeroY, width, distanceTop);

        // Затемнення знизу
        // ctx.fillRect(zeroX, zeroY + height - distanceY, width, distanceY);
        ctx.fillRect(
            zeroX,
            zeroY + height - distanceBottom,
            width,
            distanceBottom
        );

        // Затемнення зліва
        // ctx.fillRect(zeroX, zeroY + distanceY, distanceX, height - 2 * distanceY);
        ctx.fillRect(
            zeroX,
            zeroY + distanceTop,
            distanceLeft,
            height - distanceTop - distanceBottom
        );

        // Затемнення справа
        // ctx.fillRect(zeroX + width - distanceX, zeroY + distanceY, distanceX, height - 2 * distanceY);
        ctx.fillRect(
            zeroX + width - distanceRight,
            zeroY + distanceTop,
            distanceRight,
            height - distanceTop - distanceBottom
        );

        const lineLength = 26 / scaleRef.current;
        const lineRadius = 3 / scaleRef.current;
        const distance = 9 / scaleRef.current;
        drawExportImageBoxCorners(
            ctx,
            x,
            y,
            boxWidth,
            boxHeight,
            lineLength,
            lineRadius,
            distance
        );
    }

    function drawExportImageBoxCorners(
        ctx,
        x,
        y,
        width,
        height,
        length,
        radius,
        distance
    ) {
        ctx.lineWidth = 6 / scaleRef.current;
        ctx.lineCap = 'round';

        // top left
        const pointNW = { x: x - distance, y: y - distance };
        drawExportImageBoxCornerNW(ctx, pointNW, false, radius, length);

        // top right
        const pointNE = { x: x + width + distance, y: y - distance };
        drawExportImageBoxCornerNE(ctx, pointNE, false, radius, length);

        // bottom left
        const pointSW = { x: x - distance, y: y + height + distance };
        drawExportImageBoxCornerSW(ctx, pointSW, false, radius, length);

        // bottom right
        const pointSE = { x: x + width + distance, y: y + height + distance };
        drawExportImageBoxCornerSE(ctx, pointSE, false, radius, length);

        // one more corner - selected corner
        switch (exportImageBoxPropsRef.current.clickedResizeKey) {
            case 'nw':
                drawExportImageBoxCornerNW(ctx, pointNW, true, radius, length);
                break;
            case 'ne':
                drawExportImageBoxCornerNE(ctx, pointNE, true, radius, length);
                break;
            case 'sw':
                drawExportImageBoxCornerSW(ctx, pointSW, true, radius, length);
                break;
            case 'se':
                drawExportImageBoxCornerSE(ctx, pointSE, true, radius, length);
                break;
            default:
                break;
        }
    }

    function drawExportImageBoxCornerNW(
        ctx,
        p,
        isSelected = false,
        radius,
        length
    ) {
        ctx.strokeStyle = isSelected ? '#f9f9f9' : '#a8adb7';

        ctx.beginPath();
        ctx.moveTo(p.x, p.y + radius + length);
        ctx.lineTo(p.x, p.y + radius);
        ctx.arcTo(p.x, p.y, p.x + radius, p.y, radius);
        ctx.lineTo(p.x + radius + length, p.y);
        ctx.stroke();
    }
    function drawExportImageBoxCornerNE(
        ctx,
        p,
        isSelected = false,
        radius,
        length
    ) {
        ctx.strokeStyle = isSelected ? '#f9f9f9' : '#a8adb7';

        ctx.beginPath();
        ctx.moveTo(p.x - radius - length, p.y);
        ctx.lineTo(p.x - radius, p.y);
        ctx.arcTo(p.x, p.y, p.x, p.y + radius, radius);
        ctx.lineTo(p.x, p.y + radius + length);
        ctx.stroke();
    }
    function drawExportImageBoxCornerSW(
        ctx,
        p,
        isSelected = false,
        radius,
        length
    ) {
        ctx.strokeStyle = isSelected ? '#f9f9f9' : '#a8adb7';

        ctx.beginPath();
        ctx.moveTo(p.x, p.y - radius - length);
        ctx.lineTo(p.x, p.y - radius);
        ctx.arcTo(p.x, p.y, p.x + radius, p.y, radius);
        ctx.lineTo(p.x + radius + length, p.y);
        ctx.stroke();
    }
    function drawExportImageBoxCornerSE(
        ctx,
        p,
        isSelected = false,
        radius,
        length
    ) {
        ctx.strokeStyle = isSelected ? '#f9f9f9' : '#a8adb7';

        ctx.beginPath();
        ctx.moveTo(p.x - radius - length, p.y);
        ctx.lineTo(p.x - radius, p.y);
        ctx.arcTo(p.x, p.y, p.x, p.y - radius, radius);
        ctx.lineTo(p.x, p.y - radius - length);
        ctx.stroke();
    }

    function drawPenSegment(segment, exportImgCanvas = null) {
        // pen-segment
        // const isSelectionMoving =
        //     isSelectionClickedRef.current &&
        //     (selectionStartXYRef.current.x !==
        //         selectionCurrentXYRef.current.x ||
        //         selectionStartXYRef.current.y !==
        //             selectionCurrentXYRef.current.y);
        // const isStartedNewLine =
        //     lineFromRef.current?.elementId || isBtnNewLineEnabled;
        // const isStartedNewPenSegment =
        //     newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        // const ctx = contextRef.current;
        let ctx = contextRef.current;

        if (isBtnExportImageEnabled && exportImgCanvas) {
            const exportImgContext = exportImgCanvas?.getContext('2d');
            ctx = exportImgContext;
        }

        // let {
        //     id,
        //     x,
        //     y,
        //     translateX,
        //     translateY,
        //     width,
        //     height,
        //     value: { path, scale, origin, color },
        // } = segment;
        let {
            id,
            translateX,
            translateY,
            value: { path, color },
        } = segment;

        const isPenSegmentFocused =
            !isBtnNewAreaEnabled &&
            !isBtnExportImageEnabled &&
            (selectedElementsIdsRef.current.includes(id) ||
                focusedGroupElementsIdsRef.current.includes(id) ||
                // table is focused
                focusedElementsIdsRef.current.includes(id)); // ||
        // table is hovered and its not new line drawing
        // (hoveredElementIdRef.current === id && !isStartedNewLine && !isStartedNewPenSegment)

        // const isPenSegmentHovered =
        //     !isBtnNewAreaEnabled &&
        //     !isBtnExportImageEnabled &&
        //     (selectedElementsIdsRef.current.includes(id) ||
        //         // table is focused
        //         focusedElementsIdsRef.current.includes(id) ||
        //         // table is hovered and its not new line drawing
        //         (hoveredElementIdRef.current === id &&
        //             !isStartedNewLine &&
        //             !isStartedNewPenSegment));

        // // ===================== 0. FOCUSED SEGMENT | OUTLINE FRAME =====================
        // if (
        // 	// segment is focused
        // 	focusedElementsIdsRef.current.includes(id)
        // 	// focusedElementsIdsRef.current.includes(id) ||
        // 	// segment is hovered and its not new line drawing
        // 	// (hoveredElementIdRef.current === id && !isStartedNewLine && !isStartedNewPenSegment)
        // ) {

        // 	ctx.strokeStyle = '#5f88ff';
        // 	// ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;
        // 	// ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.1;
        // 	ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3;

        // 	ctx.beginPath();
        // 	// ctx.roundRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1, 1);
        // 	ctx.roundRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset, width + 2 * scaleNumbersRef.current.outlineOffset, height + 2 * scaleNumbersRef.current.outlineOffset, 0.75);

        // 	ctx.stroke();

        // 	// ctx.shadowColor = 'transparent';
        // 	// ctx.fillStyle = '#39424c'; // canvas background
        // 	// ctx.fillRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1);
        // 	// ctx.stroke();
        // }

        // // ===================== 1. FOCUSED SEGMENT | RESIZE POINTS =====================
        // if (
        // 	!isBtnRedGreenViewEnabled &&
        // 	// is not export enabled
        // 	!isBtnExportImageEnabled &&
        // 	// is not mouse selection
        // 	!isSelectionMoving &&
        // 	// segment is focused
        // 	focusedElementsIdsRef.current.includes(id) &&
        // 	// focused only one segment
        // 	focusedElementsIdsRef.current.length === 1
        // ) {
        // 	let resizeHandleSize = scaleNumbersRef.current.resizeHandleSize - 4 > 0 ? scaleNumbersRef.current.resizeHandleSize - 3 : scaleNumbersRef.current.resizeHandleSize;
        // 	resizeHandleSize = Math.max(resizeHandleSize, 4)
        // 	let resizeHandleRadius = scaleNumbersRef.current.resizeHandleRadius - 1 > 0 ? scaleNumbersRef.current.resizeHandleRadius - 1 : scaleNumbersRef.current.resizeHandleRadius;
        // 	resizeHandleRadius = Math.max(resizeHandleRadius, 1)

        // 	const resizeOutlineWidth = width + scaleNumbersRef.current.outlineOffset * 2;
        // 	const resizeOutlineHeight = height + scaleNumbersRef.current.outlineOffset * 2;
        // 	const resizeX = x - scaleNumbersRef.current.outlineOffset;
        // 	const resizeY = y - scaleNumbersRef.current.outlineOffset;

        // 	ctx.beginPath();
        // 	ctx.strokeStyle = '#5f88ff';
        // 	ctx.fillStyle = '#e6e6e6';
        // 	// ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7 + 1.5;
        // 	ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 1;

        // 	ctx.roundRect(resizeX - resizeHandleSize / 2 - 1, resizeY - resizeHandleSize / 2 - 1, resizeHandleSize + 2, resizeHandleSize + 2, resizeHandleRadius);
        // 	ctx.roundRect(resizeX + resizeOutlineWidth - resizeHandleSize / 2 - 1, resizeY - resizeHandleSize / 2 - 1, resizeHandleSize + 2, resizeHandleSize + 2, resizeHandleRadius);
        // 	ctx.roundRect(resizeX - resizeHandleSize / 2 - 1, resizeY + resizeOutlineHeight - resizeHandleSize / 2 - 1, resizeHandleSize + 2, resizeHandleSize + 2, resizeHandleRadius);
        // 	ctx.roundRect(resizeX + resizeOutlineWidth - resizeHandleSize / 2 - 1, resizeY + resizeOutlineHeight - resizeHandleSize / 2 - 1, resizeHandleSize + 2, resizeHandleSize + 2, resizeHandleRadius);
        // 	ctx.stroke();
        // 	ctx.fill();
        // }

        // -----------------------------
        const handPathCanvas = new Path2D(path);
        handPathCanvas.closePath();

        scaleNumbersRef.current.test = handPathCanvas;
        // ctx.save();
        // ctx.setTransform(scaleRef.current, 0, 0, scaleRef.current, originXYRef.current.x, originXYRef.current.y);

        // ctx.save(); // Зберігаємо поточний стан канвасу
        // ctx.translate(translateX, translateY); // Переміщуємо початкову точку малювання
        // ctx.translate(0, 0); // Переміщуємо початкову точку малювання

        // if (scale) {
        // 	ctx.scale(scale, scale); // Масштабування шляху для більшого розміру
        // }

        // ctx.lineWidth = scaleNumbersRef.current.outlineWidth;
        // ctx.lineWidth = 1; // Лінії мають бути тонкими після масштабування

        // ctx.fillStyle = '#ffc65e'// : '#b955eb';
        ctx.fillStyle =
            color === 'yellow' ? '#ffc65e' : color ? color : '#ffc65e'; // : '#b955eb';

        // ==================================
        // // Перевіряємо, чи миша над лінією
        // const mouseX = (newPenCursorPointRef.current.x) * scaleRef.current + originXYRef.current.x;
        // const mouseY = (newPenCursorPointRef.current.y) * scaleRef.current + originXYRef.current.y;
        // if (ctx.isPointInPath(handPathCanvas, mouseX, mouseY)) {
        // 	ctx.fillStyle = 'red'// : '#b955eb';
        // } else {
        // 	ctx.fillStyle = '#ffc65e'// : '#b955eb';
        // }

        ctx.save(); // Зберігаємо поточний стан канвасу
        ctx.translate(translateX, translateY); // Переміщуємо початкову точку малювання

        // ctx.save(); // Зберігаємо поточний стан канвасу
        // ctx.translate(0, 0); // Переміщуємо початкову точку малювання
        // ctx.scale(0.9, 0.9); // Переміщуємо початкову точку малювання
        // if (scale && origin) {
        // 	ctx.setTransform(scale, 0, 0, scale, origin.x, origin.y);
        // }

        ctx.beginPath();
        ctx.fill(handPathCanvas);

        if (isPenSegmentFocused) {
            ctx.strokeStyle = '#5f88ff';
            ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
            // ctx.fillStyle = '#e6e6e6';
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7 + 1.5;
            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 1;

            // ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.7;// - 0.3 - 0.7 - 0.1;
            ctx.lineWidth = scaleNumbersRef.current.outlineWidth + 0.4; // - 0.3 - 0.7 - 0.1;

            if (
                hoveredElementIdRef.current === id &&
                !focusedElementsIdsRef.current.includes(id) &&
                !selectedElementsIdsRef.current.includes(id) &&
                !focusedGroupElementsIdsRef.current.includes(id)
            ) {
                // ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3 - 0.7;
                // ctx.lineWidth = scaleNumbersRef.current.outlineWidth - 0.3 - 0.7 - 0.1; // ASAP LAST
                ctx.lineWidth = scaleNumbersRef.current.outlineWidth; // - 0.3 - 0.7 - 0.1;
                // ctx.strokeStyle = 'rgba(95, 136, 255, 0.8)';
            }

            ctx.stroke(handPathCanvas);
        }

        ctx.restore(); // Відновлюємо попередній стан канвасу

        // // ctx.strokeStyle = 'red'; //theme.controls.background;
        // ctx.fillStyle = '#ffc65e'// : '#b955eb';

        // // ctx.stroke(handPathCanvas);
        // ctx.fill(handPathCanvas);

        // ctx.restore(); // Відновлюємо попередній стан канвасу
        // -----------------------------
    }
    function drawNewPenSegment() {
        const ctx = contextRef.current;

        const { points } = newPenSegmentRef.current;

        // const options = {
        // 	size: 15,
        // 	smoothing: 0.5,
        // 	thinning: 0.25,
        // 	streamline: 0.7,
        // };
        const options = {
            size: 20,
            smoothing: 0.5,
            thinning: 0.25,
            streamline: 0.7,
        };

        ctx.fillStyle =
            newPenProps.color === 'yellow'
                ? '#ffc65e'
                : newPenProps.color
                    ? newPenProps.color
                    : '#ffc65e'; // : '#b955eb';

        ctx.lineWidth = 1;

        // const stroke = getStroke([...points, points[points.length -2], points[points.length -1]], options);
        const stroke = getStroke(points, options);
        const path = getSvgPathFromStroke(stroke);
        const myPath = new Path2D(path);

        ctx.save(); // Зберігаємо поточний стан канвасу
        ctx.translate(0, 0); // Переміщуємо початкову точку малювання
        ctx.beginPath();
        ctx.fill(myPath);
        ctx.restore(); // Відновлюємо попередній стан канвасу
    }

    function drawNewPenCursor() {
        const ctx = contextRef.current;

        ctx.fillStyle = '#ffc65e'; // : '#b955eb';
        ctx.lineWidth = 1 / scaleRef.current;

        // DRAW CURSOR POINTER FOR PEN

        const cursorPoint = newPenCursorPointRef.current;

        ctx.beginPath();
        ctx.strokeStyle = 'grey';
        ctx.arc(cursorPoint.x, cursorPoint.y, 9, 0, 2 * Math.PI); // Малюємо коло
        ctx.stroke();
    }

    function getBoundsByPoints(points) {
        if (!Array.isArray(points) || !points.length) {
            return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
        }

        let bounds = {};
        points.forEach(([x, y], index) => {
            if (index === 0) {
                bounds = { minX: x, maxX: x, minY: y, maxY: y };
            } else {
                bounds = {
                    minX: Math.min(bounds.minX, x),
                    maxX: Math.max(bounds.maxX, x),
                    minY: Math.min(bounds.minY, y),
                    maxY: Math.max(bounds.maxY, y),
                };
            }
        });

        bounds.width = bounds.maxX - bounds.minX;
        bounds.height = bounds.maxY - bounds.minY;

        return bounds;
    }

    function buildNewPenSegment(points) {
        // const { points } = newPenSegmentRef.current;

        // const options = {
        // 	size: 15,
        // 	smoothing: 0.5,
        // 	thinning: 0.25,
        // 	streamline: 0.7,
        // };
        const options = {
            size: 20,
            smoothing: 0.5,
            thinning: 0.25,
            streamline: 0.7,
        };


        // const pointsObjects = points.map(([x, y]) => ({ x: x.toFixed(2), y: y.toFixed(2) }));
        const pointsObjects = points.map(([x, y]) => ({ x, y }));

        let simplifiedPoints = simplify(pointsObjects, 0.1, true); // tolerance=2, highQuality=true

        // const pointsFinal = simplifiedPoints.map(({x, y}) => ([x, y]));
        const pointsFinal = simplifiedPoints.map(({ x, y }) => ([parseFloat(x.toFixed(2)), parseFloat(y.toFixed(2))]));

        // console.log('POINTS\n\n', points, '\n\n', pointsObjects, '\n\n', pointsFinal)
        const stroke = getStroke(pointsFinal, options);
        const path = getSvgPathFromStroke(stroke);

        const bounds = getBoundsByPoints(stroke);

        const newPenSegmentId = nanoid();
        const newPenSegment = {
            id: newPenSegmentId,
            type: 'pen-segment',
            x: bounds.minX,
            y: bounds.minY,
            width: bounds.width,
            height: bounds.height,

            translateX: 0,
            translateY: 0,
            value: {
                path,
                // scale: 0,
                scale: scaleRef.current,
                origin: originXYRef.current,
                color: newPenProps.color || 'yellow',
            },
        };

        return newPenSegment;
    }

    function buildLineData(lineObject) {
        const {
            fromElementId,
            fromInsideElementId,
            fromRelationType,
            toElementId,
            toInsideElementId,
            toRelationType,
        } = lineObject;

        const fromElement = elementsRef.current[fromElementId];
        const toElement = elementsRef.current[toElementId];

        if (!fromElement || !toElement) {
            return;
        }
        const fromInsideElement =
            fromElement.value.columns[fromInsideElementId];
        const toInsideElement = toElement.value.columns[toInsideElementId];

        if (!fromInsideElement || !toInsideElement) {
            return;
        }

        const lineData = {
            mouseX: null,
            mouseY: null,

            fromElementType: fromElement.type,
            fromElementWidth: fromElement.width,
            fromElementX: fromElement.x,
            fromElementY: fromElement.y,
            fromInsideElementIndex: getIndexNumber(
                fromElement.value.columns,
                fromInsideElementId
            ),
            fromRelationType,

            toElementType: toElement.type,
            toElementWidth: toElement.width,
            toElementX: toElement.x,
            toElementY: toElement.y,
            toInsideElementIndex: getIndexNumber(
                toElement.value.columns,
                toInsideElementId
            ),
            toRelationType,
        };

        return lineData;
    }

    function drawCreatedLine(lineObject, exportImgCanvas = null) {
        let ctx = contextRef.current;

        if (isBtnExportImageEnabled && exportImgCanvas) {
            const exportImgContext = exportImgCanvas?.getContext('2d');
            ctx = exportImgContext;
        }

        if (viewMode === 'keys') {
            return drawLineForViewKeys(lineObject);
        }

        let {
            id,
            value: { path, solidType, color },
        } = lineObject;
        const isStartedNewLine = lineFromRef.current?.elementId;
        const isStartedNewPenSegment =
            newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        const isLineFocused =
            focusedLineIdRef.current === id && !isBtnExportImageEnabled;
        const isLineHighlighted =
            !isBtnExportImageEnabled &&
            (focusedElementsRelatedPropsRef.current?.linesIds.includes(id) ||
                (hoveredElementRelatedPropsRef.current?.linesIds.includes(id) &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment) ||
                (hoveredLineIdRef.current === id &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment));
        const isNewLine = false;

        if (path) {
            _drawLinePath(
                ctx,
                path,
                isLineHighlighted,
                isLineFocused,
                isNewLine,
                scaleNumbersRef.current.relationLineWidth,
                solidType,
                color
            );
        } else {
            const {
                value: {
                    fromElementId,
                    fromInsideElementId,
                    fromRelationType,
                    toElementId,
                    toInsideElementId,
                    toRelationType,
                    solidType,
                    color,
                },
            } = lineObject;

            const fromElement = elementsRef.current[fromElementId];
            const toElement = elementsRef.current[toElementId];

            if (!fromElement || !toElement) {
                return;
            }

            const fromInsideElement =
                fromElement.value.columns[fromInsideElementId];
            const toInsideElement = toElement.value.columns[toInsideElementId];

            if (!fromInsideElement || !toInsideElement) {
                return;
            }

            const fromElementColumnsKeys = Object.keys(
                fromElement.value.columns
            );
            let fromInsideElementIndex = -1;
            fromElementColumnsKeys.forEach((key, index) => {
                if (fromInsideElementId === key) {
                    fromInsideElementIndex = index;
                }
            });

            const toElementColumnsKeys = Object.keys(toElement.value.columns);
            let toInsideElementIndex = -1;
            toElementColumnsKeys.forEach((key, index) => {
                if (toInsideElementId === key) {
                    toInsideElementIndex = index;
                }
            });

            const lineData = {
                mouseX: null,
                mouseY: null,

                fromElementType: fromElement.type,
                fromElementWidth: fromElement.width,
                fromElementX: fromElement.x,
                fromElementY: fromElement.y,
                fromInsideElementIndex,
                fromRelationType,

                toElementType: toElement.type,
                toElementWidth: toElement.width,
                toElementX: toElement.x,
                toElementY: toElement.y,
                toInsideElementIndex,
                toRelationType,
            };

            path = _buildLinePath(lineData);
            _drawLinePath(
                ctx,
                path,
                isLineHighlighted,
                isLineFocused,
                isNewLine,
                scaleNumbersRef.current.outlineWidth,
                solidType,
                color
            );
        }
    }
    function drawLineForViewKeys(lineObject) {
        const {
            id,
            value: {
                fromElementId,
                fromInsideElementId,
                fromRelationType,
                toElementId,
                toInsideElementId,
                toRelationType,
                solidType,
                color,
            },
        } = lineObject;

        const isStartedNewLine = lineFromRef.current?.elementId;
        const isStartedNewPenSegment =
            newPenSegmentRef.current.isPressed || isBtnPenEnabled;

        const isLineFocused =
            focusedLineIdRef.current === id && !isBtnExportImageEnabled;
        const isLineHighlighted =
            !isBtnExportImageEnabled &&
            (focusedElementsRelatedPropsRef.current?.linesIds.includes(id) ||
                (hoveredElementRelatedPropsRef.current?.linesIds.includes(id) &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment) ||
                (hoveredLineIdRef.current === id &&
                    !isStartedNewLine &&
                    !isStartedNewPenSegment));
        const isNewLine = false;

        const fromElement = elementsRef.current[fromElementId];
        const toElement = elementsRef.current[toElementId];

        if (!fromElement || !toElement) {
            return;
        }
        const fromInsideElement =
            fromElement.value.columns[fromInsideElementId];
        const toInsideElement = toElement.value.columns[toInsideElementId];

        if (!fromInsideElement || !toInsideElement) {
            return;
        }

        const fromElementColumnsKeys = Object.keys(
            fromElement.value.columns
        ).filter(
            (columnKey) =>
                fromElement.value.columns[columnKey]?.isPK ||
                fromElement.value.columns[columnKey]?.isFK ||
                fromElement.value.columns[columnKey]?.isRelated
        );
        let fromInsideElementIndex = -1;
        fromElementColumnsKeys.forEach((key, index) => {
            if (fromInsideElementId === key) {
                fromInsideElementIndex = index;
            }
        });

        const toElementColumnsKeys = Object.keys(
            toElement.value.columns
        ).filter(
            (columnKey) =>
                toElement.value.columns[columnKey]?.isPK ||
                toElement.value.columns[columnKey]?.isFK ||
                toElement.value.columns[columnKey]?.isRelated
        );
        let toInsideElementIndex = -1;
        toElementColumnsKeys.forEach((key, index) => {
            if (toInsideElementId === key) {
                toInsideElementIndex = index;
            }
        });

        const lineData = {
            mouseX: null,
            mouseY: null,

            fromElementType: fromElement.type,
            fromElementWidth: fromElement.width,
            fromElementX: fromElement.x,
            fromElementY: fromElement.y,
            // fromInsideElementIndex: fromElement.value.columns[fromInsideElementId].indexNumber,
            fromInsideElementIndex,
            fromRelationType,

            toElementType: toElement.type,
            toElementWidth: toElement.width,
            toElementX: toElement.x,
            toElementY: toElement.y,
            // toInsideElementIndex: toElement.value.columns[toInsideElementId].indexNumber,
            toInsideElementIndex,
            toRelationType,
        };

        const path = _buildLinePath(lineData);
        _drawLinePath(
            contextRef.current,
            path,
            isLineHighlighted,
            isLineFocused,
            isNewLine,
            scaleNumbersRef.current.outlineWidth,
            solidType,
            color
        );
    }
    function drawNewLine() {
        const lineData = {
            mouseX: lineToRef.current.x,
            mouseY: lineToRef.current.y,

            fromElementType: lineFromRef.current.elementType,
            fromElementWidth: lineFromRef.current.elementWidth,
            fromElementX: lineFromRef.current.elementX,
            fromElementY: lineFromRef.current.elementY,
            fromInsideElementIndex: lineFromRef.current.insideElementIndex,
            fromRelationType: 'many',

            toElementType: lineToRef.current.elementType,
            toElementWidth: lineToRef.current.elementWidth,
            toElementX: lineToRef.current.elementX,
            toElementY: lineToRef.current.elementY,
            toInsideElementIndex: lineToRef.current.insideElementIndex,
            toRelationType: 'one',
        };

        const isLineHighlighted = false;
        const isLineFocused = false;
        const isNewLine = true;

        const path = _buildLinePath(lineData);
        _drawLinePath(
            contextRef.current,
            path,
            isLineHighlighted,
            isLineFocused,
            isNewLine,
            scaleNumbersRef.current.outlineWidth,
            'solid'
        );
    }

    function buildNewTable(mouseX, mouseY) {
        const newTableId = nanoid();
        const newColumnId = nanoid();

        const newTable = {
            id: newTableId,
            type: 'table',
            x: mouseX,
            y: mouseY,
            width: NEW_TABLE_WIDTH,
            height: NEW_TABLE_HEIGHT,
            value: {
                header: { name: 'table_new' },
                columns: {
                    [newColumnId]: {
                        id: newColumnId,
                        name: 'id',
                        type: 'int',
                        indexNumber: 0,
                        isPK: true,
                    },
                },
            },
        };

        return newTable;
    }
    function buildNewNote(mouseX, mouseY) {
        const newNoteId = nanoid();

        const newNote = {
            id: newNoteId,
            type: 'note',
            x: mouseX,
            y: mouseY,
            width: NEW_NOTE_WIDTH,
            height: NEW_NOTE_HEIGHT,
            value: {
                text: 'Tables represent core entities in our project: e.g., Customers, Orders, Products.',
                // text: 'new note_1'
            },
        };

        return newNote;
    }
    function buildNewLine() {
        const newLineId = nanoid();

        const lineData = buildLineData({
            fromElementId: lineFromRef.current.elementId,
            fromInsideElementId: lineFromRef.current.insideElementId,
            fromRelationType: 'many',
            toElementId: lineToRef.current.elementId,
            toInsideElementId: lineToRef.current.insideElementId,
            toRelationType: 'one',
        });

        const path = lineData ? _buildLinePath(lineData) : {};

        const newLine = {
            id: newLineId,
            type: 'line',
            value: {
                fromElementId: lineFromRef.current.elementId,
                fromInsideElementId: lineFromRef.current.insideElementId,
                fromRelationType: 'many',
                toElementId: lineToRef.current.elementId,
                toInsideElementId: lineToRef.current.insideElementId,
                toRelationType: 'one',

                path: path,
                fromElementX: lineData.fromElementX,
                fromElementY: lineData.fromElementY,
                toElementX: lineData.toElementX,
                toElementY: lineData.toElementY,
            },
            // x: 1100,
            // y: 448,
            // width: 1,
            // height: 1,
        };

        return newLine;
    }

    function buildNewArea() {
        const newAreaId = nanoid();

        const x1 = newAreaPropsRef.current.startX;
        const y1 = newAreaPropsRef.current.startY;
        const x2 = newAreaPropsRef.current.endX;
        const y2 = newAreaPropsRef.current.endY;

        const minX = Math.min(x1, x2);
        const maxX = Math.max(x1, x2);
        const minY = Math.min(y1, y2);
        const maxY = Math.max(y1, y2);

        const newArea = {
            id: newAreaId,
            type: 'area',
            value: {
                // border: 'dashes',
                border: 'solid',
                color: 'blue',
                name: 'area_new',
                nestedElementsIds:
                    newAreaPropsRef?.current?.selectedElements || [],
            },
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
        };

        return newArea;
    }

    function getNewClickedElements(
        elementsValue,
        mouseX,
        mouseY,
        eventX,
        eventY
    ) {
        const ctx = contextRef.current;

        if (viewMode === 'keys') {
            return getNewClickedElementsForViewKeys(
                elementsValue,
                mouseX,
                mouseY
            );
        }

        let clickedElementId = null;
        let clickedInsideElement = null;

        const radius = 18;
        const distance = 36;
        let clickedCircleElementId = null;

        // 1. check inside clicks
        for (const key in elementsValue) {
            const element = elementsValue[key];
            const {
                id,
                x,
                y,
                width,
                height,
                value: { columns, header },
            } = element;

            if (element.type === 'pen-segment') {
                ctx.save();
                ctx.setTransform(
                    scaleRef.current,
                    0,
                    0,
                    scaleRef.current,
                    originXYRef.current.x,
                    originXYRef.current.y
                );

                const penPath = new Path2D(element.value.path);

                if (ctx.isPointInPath(penPath, eventX, eventY)) {
                    clickedElementId = id;
                }

                ctx.restore(); // Переміщуємо початкову точку малювання
            }

            // if (element.type === 'pen-segment') {
            // 	ctx.save();
            // 	ctx.setTransform(scaleRef.current, 0, 0, scaleRef.current, originXYRef.current.x, originXYRef.current.y);
            // 	const penPath = new Path2D(element.value.path);
            // 	penPath.closePath();

            // 	// ctx.fill(penPath);

            // 	const mousePenX = (mouseX) * scaleRef.current + originXYRef.current.x;
            // 	const mousePenY = (mouseY) * scaleRef.current + originXYRef.current.y;

            // 	if (ctx.isPointInPath(scaleNumbersRef.current.test, eventX, eventY)) {
            // 		clickedElementId = id;

            // 	} else {
            // 		clickedElementId = null;
            // 	}
            // 	ctx.restore(); // Переміщуємо початкову точку малювання

            // }

            // 1.1. check clicks in element x, y area, so click inside one of the elements
            else if (
                mouseX >= x &&
                mouseX <= x + width &&
                mouseY >= y &&
                mouseY <= y + height
            ) {
                clickedElementId = id;

                if (element.type === 'table') {
                    // CHECK CLICK ON HEADER
                    const relativeHeaderY = mouseY - y;
                    if (
                        relativeHeaderY > 0 &&
                        relativeHeaderY < TABLE_HEADER_HEIGHT
                    ) {
                        clickedInsideElement = {
                            elementId: element.id,
                            insideElement: header,
                            insideElementType: 'header',
                        };
                    }

                    const columnsCount = Object.keys(columns).length;

                    if (focusedElementsIdsRef.current.includes(id)) {
                        // CHECK CLICK ON COLUMNS
                        const relativeY =
                            mouseY -
                            y -
                            TABLE_HEADER_HEIGHT -
                            HEADER_BODY_MARGIN;
                        if (
                            relativeY > 0 &&
                            relativeY < columnsCount * TABLE_ROW_HEIGHT
                        ) {
                            const columnIndex = Math.floor(
                                relativeY / TABLE_ROW_HEIGHT
                            );
                            const columnId = Object.keys(columns)[columnIndex];
                            const column = columns[columnId];

                            clickedInsideElement = {
                                insideElementType: 'column',
                                elementId: element.id,
                                insideElementId: column.id,
                                insideElement: column,
                                insideElementIndex: columnIndex,
                                index: columnIndex,
                            };
                        }
                    }
                }
            }

            // 1.2. check clicks on circle to add new row
            if (element.type === 'table') {
                if (
                    mouseX >= x + width / 2 - radius &&
                    mouseX <= x + width / 2 + radius &&
                    mouseY >= y + height + distance - radius &&
                    mouseY <= y + height + distance + radius
                ) {
                    clickedCircleElementId = id;
                }
            }
        }

        // const POINT_RADIUS = 6.5;
        const POINT_RADIUS = 10.5;
        const POINT_DISTANCE = 20;
        const POINT_AREA = 50;

        let clickedPointElementId = null;
        let clickedPointInsideElementId = null;
        let clickedPointInsideElementIndex = null;
        let clickedPointDirection = null; // 'left' / 'right'

        // 2. check outside clicks, so clicks on relation points
        for (const key in elementsValue) {
            const element = elementsValue[key];
            const {
                id,
                x,
                y,
                width,
                value: { columns },
            } = element;

            if (element.type === 'table') {
                if (
                    (mouseX >= x - POINT_AREA && mouseX <= x) ||
                    (mouseX >= x + width && mouseX <= x + width + POINT_AREA)
                ) {
                    // check relation points
                    Object.keys(columns).forEach((key, index) => {
                        const column = columns[key];
                        const columnY =
                            y +
                            TABLE_HEADER_HEIGHT +
                            index * TABLE_ROW_HEIGHT +
                            HEADER_BODY_MARGIN;

                        // 2.1.1. check left side's relation point
                        if (
                            mouseX >= x - POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <= x - POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            clickedPointElementId = id;
                            clickedPointInsideElementId = column.id;
                            clickedPointInsideElementIndex = index;
                            clickedPointDirection = 'left';

                            // 2.1.2. check right side's relation point
                        } else if (
                            mouseX >=
                            x + width + POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <=
                            x + width + POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            clickedPointElementId = id;
                            clickedPointInsideElementId = column.id;
                            clickedPointInsideElementIndex = index;
                            clickedPointDirection = 'right';
                        }
                    });
                }
            }
        }

        // 3. check lines
        let clickedLineId = null;

        for (const key in linesRef.current) {
            const lineObject = linesRef.current[key];
            let {
                id,
                value: { path },
            } = lineObject;

            if (!path) {
                const lineObject = linesRef.current[key];
                const {
                    value: {
                        fromElementId,
                        fromInsideElementId,
                        fromRelationType,
                        toElementId,
                        toInsideElementId,
                        toRelationType,
                    },
                } = lineObject;

                const fromElement = elementsRef.current[fromElementId];
                const toElement = elementsRef.current[toElementId];

                if (!fromElement || !toElement) {
                    // return;
                    continue;
                }

                const fromInsideElement =
                    fromElement.value.columns[fromInsideElementId];
                const toInsideElement =
                    toElement.value.columns[toInsideElementId];

                if (!fromInsideElement || !toInsideElement) {
                    // return;
                    continue;
                }

                const fromElementColumnsKeys = Object.keys(
                    fromElement.value.columns
                );
                let fromInsideElementIndex = -1;
                fromElementColumnsKeys.forEach((key, index) => {
                    if (fromInsideElementId === key) {
                        fromInsideElementIndex = index;
                    }
                });

                const toElementColumnsKeys = Object.keys(
                    toElement.value.columns
                );
                let toInsideElementIndex = -1;
                toElementColumnsKeys.forEach((key, index) => {
                    if (toInsideElementId === key) {
                        toInsideElementIndex = index;
                    }
                });

                const lineData = {
                    mouseX: null,
                    mouseY: null,

                    fromElementType: fromElement.type,
                    fromElementWidth: fromElement.width,
                    fromElementX: fromElement.x,
                    fromElementY: fromElement.y,
                    fromInsideElementIndex,
                    fromRelationType,

                    toElementType: toElement.type,
                    toElementWidth: toElement.width,
                    toElementX: toElement.x,
                    toElementY: toElement.y,
                    toInsideElementIndex,
                    toRelationType,
                };

                path = _buildLinePath(lineData);
            }

            const [lines, points, bounds] = path;
            Math.random() === 12 && console.log('lines', lines);

            if (
                // couse there are only left-right, right-left, right-right lines, not left-left
                mouseX >= bounds.minX - 40 &&
                mouseX <= bounds.maxX + 40 + 20 &&
                mouseY >= bounds.minY - 10 &&
                mouseY <= bounds.maxY + 10
            ) {
                for (let index = 0; index <= points.length - 2; index++) {
                    const p1 = points[index];
                    const p2 = points[index + 1];
                    const isNear = isPointNearLine(
                        mouseX,
                        mouseY,
                        p1.x,
                        p1.y,
                        p2.x,
                        p2.y,
                        20
                    );
                    if (isNear) {
                        clickedLineId = id;
                        break;
                    }
                }
            }
        }

        let isClickedInsideExportImageBox = null;

        // 4. check clicks in export image box
        if (isBtnExportImageEnabled) {
            const { clientX, clientY, clientBoxWidth, clientBoxHeight } =
                exportImageBoxPropsRef.current;

            const x = (clientX - originXYRef.current.x) / scaleRef.current;
            const y = (clientY - originXYRef.current.y) / scaleRef.current;
            const boxWidth = clientBoxWidth / scaleRef.current;
            const boxHeight = clientBoxHeight / scaleRef.current;

            if (
                mouseX >= x &&
                mouseX <= x + boxWidth &&
                mouseY >= y &&
                mouseY <= y + boxHeight
            ) {
                isClickedInsideExportImageBox = true;
            }
        }

        // 5. check areas
        let clickedAreaId = null;

        for (const key in areasRef.current) {
            const area = areasRef.current[key];
            const { id, x, y, width, height } = area;

            if (
                mouseX >= x &&
                mouseX <= x + width &&
                mouseY >= y &&
                mouseY <= y + height
            ) {
                clickedAreaId = id;
            }
        }

        return [
            clickedElementId,
            clickedInsideElement,
            clickedCircleElementId,
            clickedPointElementId,
            clickedPointInsideElementId,
            clickedPointInsideElementIndex,
            clickedPointDirection,
            clickedLineId,
            isClickedInsideExportImageBox,
            clickedAreaId,
        ];
    }

    function getNewClickedElementsForViewKeys(elementsValue, mouseX, mouseY) {
        let clickedElementId = null;
        let clickedInsideElement = null;

        // let clickedCircleElementId = null;

        // 1. check inside clicks
        for (const key in elementsValue) {
            const element = elementsValue[key];

            if (element.type === 'table') {
                const {
                    id,
                    x,
                    y,
                    width,
                    value: { columns },
                } = element;

                const columnsKeys = Object.keys(columns).filter(
                    (columnKey) =>
                        columns[columnKey]?.isPK ||
                        columns[columnKey]?.isFK ||
                        columns[columnKey]?.isRelated
                );
                const columnsCount = columnsKeys?.length || 0;
                const tableHeight =
                    TABLE_HEADER_HEIGHT +
                    HEADER_BODY_MARGIN +
                    (columnsCount + 1) * TABLE_ROW_HEIGHT +
                    TABLE_BORDER_RADIUS +
                    1;

                // 1.1. check clicks in element x, y area, so click inside one of the elements
                if (
                    mouseX >= x &&
                    mouseX <= x + width &&
                    mouseY >= y &&
                    mouseY <= y + tableHeight
                ) {
                    clickedElementId = id;

                    if (focusedElementsIdsRef.current.includes(id)) {
                        const relativeY =
                            mouseY -
                            y -
                            TABLE_HEADER_HEIGHT -
                            HEADER_BODY_MARGIN;
                        if (
                            relativeY > 0 &&
                            relativeY < columnsCount * TABLE_ROW_HEIGHT
                        ) {
                            const columnIndex = Math.floor(
                                relativeY / TABLE_ROW_HEIGHT
                            );
                            const columnId = columnsKeys[columnIndex];
                            const column = columns[columnId];

                            clickedInsideElement = {
                                elementId: element.id,
                                insideElementId: column.id,
                                index: columnIndex,
                            };
                        }
                    }
                }
            } else {
                const { id, x, y, width, height } = element;

                // 1.1. check clicks in element x, y area, so click inside one of the elements
                if (
                    mouseX >= x &&
                    mouseX <= x + width &&
                    mouseY >= y &&
                    mouseY <= y + height
                ) {
                    clickedElementId = id;
                }

                // if (element.type === 'pen-segment') {
                // }
            }
        }

        // const POINT_RADIUS = 6.5;
        const POINT_RADIUS = 10.5;
        const POINT_DISTANCE = 20;
        const POINT_AREA = 50;

        let clickedPointElementId = null;
        let clickedPointInsideElementId = null;
        let clickedPointInsideElementIndex = null;
        let clickedPointDirection = null; // 'left' / 'right'

        // 2. check outside clicks, so clicks on relation points
        for (const key in elementsValue) {
            const element = elementsValue[key];

            if (element.type === 'table') {
                const {
                    id,
                    x,
                    y,
                    width,
                    value: { columns },
                } = element;

                const columnsKeys = Object.keys(columns).filter(
                    (columnKey) =>
                        columns[columnKey]?.isPK ||
                        columns[columnKey]?.isFK ||
                        columns[columnKey]?.isRelated
                );

                if (
                    (mouseX >= x - POINT_AREA && mouseX <= x) ||
                    (mouseX >= x + width && mouseX <= x + width + POINT_AREA)
                ) {
                    // check relation points
                    columnsKeys.forEach((key, index) => {
                        const column = columns[key];
                        const columnY =
                            y +
                            TABLE_HEADER_HEIGHT +
                            index * TABLE_ROW_HEIGHT +
                            HEADER_BODY_MARGIN;

                        // 2.1.1. check left side's relation point
                        if (
                            mouseX >= x - POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <= x - POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            clickedPointElementId = id;
                            clickedPointInsideElementId = column.id;
                            clickedPointInsideElementIndex = index;
                            clickedPointDirection = 'left';

                            // 2.1.2. check right side's relation point
                        } else if (
                            mouseX >=
                            x + width + POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <=
                            x + width + POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            clickedPointElementId = id;
                            clickedPointInsideElementId = column.id;
                            clickedPointInsideElementIndex = index;
                            clickedPointDirection = 'right';
                        }
                    });
                }
            }
        }

        // 3. check lines
        let clickedLineId = null;

        for (const key in linesRef.current) {
            const lineObject = linesRef.current[key];
            const {
                id,
                value: {
                    fromElementId,
                    fromInsideElementId,
                    fromRelationType,
                    toElementId,
                    toInsideElementId,
                    toRelationType,
                },
            } = lineObject;

            const fromElement = elementsRef.current[fromElementId];
            const toElement = elementsRef.current[toElementId];

            if (!fromElement || !toElement) {
                continue;
            }
            const fromInsideElement =
                fromElement.value.columns[fromInsideElementId];
            const toInsideElement = toElement.value.columns[toInsideElementId];

            if (!fromInsideElement || !toInsideElement) {
                continue;
            }

            const fromElementColumnsKeys = Object.keys(
                fromElement.value.columns
            ).filter(
                (columnKey) =>
                    fromElement.value.columns[columnKey]?.isPK ||
                    fromElement.value.columns[columnKey]?.isFK ||
                    fromElement.value.columns[columnKey]?.isRelated
            );
            let fromInsideElementIndex = -1;
            fromElementColumnsKeys.forEach((key, index) => {
                if (fromInsideElementId === key) {
                    fromInsideElementIndex = index;
                }
            });

            const toElementColumnsKeys = Object.keys(
                toElement.value.columns
            ).filter(
                (columnKey) =>
                    toElement.value.columns[columnKey]?.isPK ||
                    toElement.value.columns[columnKey]?.isFK ||
                    toElement.value.columns[columnKey]?.isRelated
            );
            let toInsideElementIndex = -1;
            toElementColumnsKeys.forEach((key, index) => {
                if (toInsideElementId === key) {
                    toInsideElementIndex = index;
                }
            });

            const lineData = {
                mouseX: null,
                mouseY: null,

                fromElementType: fromElement.type,
                fromElementWidth: fromElement.width,
                fromElementX: fromElement.x,
                fromElementY: fromElement.y,
                fromInsideElementIndex,
                fromRelationType,

                toElementType: toElement.type,
                toElementWidth: toElement.width,
                toElementX: toElement.x,
                toElementY: toElement.y,
                toInsideElementIndex,
                toRelationType,
            };

            const path = _buildLinePath(lineData);

            const [lines, points, bounds] = path;
            Math.random() === 12 && console.log('lines', lines);

            if (
                // couse there are only left-right, right-left, right-right lines, not left-left
                mouseX >= bounds.minX - 40 &&
                mouseX <= bounds.maxX + 40 + 20 &&
                mouseY >= bounds.minY - 10 &&
                mouseY <= bounds.maxY + 10
            ) {
                for (let index = 0; index <= points.length - 2; index++) {
                    const p1 = points[index];
                    const p2 = points[index + 1];
                    const isNear = isPointNearLine(
                        mouseX,
                        mouseY,
                        p1.x,
                        p1.y,
                        p2.x,
                        p2.y,
                        20
                    );
                    if (isNear) {
                        clickedLineId = id;
                        break;
                    }
                }
            }
        }

        let isClickedInsideExportImageBox = null;

        // 4. check clicks in export image box
        if (isBtnExportImageEnabled) {
            const { clientX, clientY, clientBoxWidth, clientBoxHeight } =
                exportImageBoxPropsRef.current;

            const x = (clientX - originXYRef.current.x) / scaleRef.current;
            const y = (clientY - originXYRef.current.y) / scaleRef.current;
            const boxWidth = clientBoxWidth / scaleRef.current;
            const boxHeight = clientBoxHeight / scaleRef.current;

            if (
                mouseX >= x &&
                mouseX <= x + boxWidth &&
                mouseY >= y &&
                mouseY <= y + boxHeight
            ) {
                isClickedInsideExportImageBox = true;
            }
        }

        // 5. check areas
        let clickedAreaId = null;

        for (const key in areasRef.current) {
            const area = areasRef.current[key];
            const { id, x, y, width, height } = area;

            if (
                mouseX >= x &&
                mouseX <= x + width &&
                mouseY >= y &&
                mouseY <= y + height
            ) {
                clickedAreaId = id;
            }
        }

        return [
            clickedElementId,
            clickedInsideElement,
            null, // clickedCircleElementId,
            null, // clickedPointElementId,
            null, // clickedPointInsideElementId,
            null, // clickedPointInsideElementIndex,
            null, // clickedPointDirection,
            clickedLineId,
            isClickedInsideExportImageBox,
            clickedAreaId,
        ];
    }

    function isPointNearLine(x, y, x1, y1, x2, y2, distance) {
        const deltaX = x2 - x1;
        const deltaY = y2 - y1;
        const dx = x - x1;
        const dy = y - y1;

        // Параметр t для проекції точки на відрізок
        const t = Math.max(
            0,
            Math.min(
                1,
                (dx * deltaX + dy * deltaY) /
                (deltaX * deltaX + deltaY * deltaY)
            )
        );

        // Найближча точка на відрізку
        const nearestX = x1 + t * deltaX;
        const nearestY = y1 + t * deltaY;

        // Квадрат відстані від точки до найближчої точки на відрізку
        const distSquared = (x - nearestX) ** 2 + (y - nearestY) ** 2;
        const distanceSquared = distance ** 2;

        // Перевірка, чи відстань менше або дорівнює порогу
        return distSquared <= distanceSquared;
    }

    function getNewHoveredElements(
        elementsValue,
        mouseX,
        mouseY,
        eventX,
        eventY
    ) {
        const ctx = contextRef.current;

        if (viewMode === 'keys') {
            return getNewHoveredElementsForViewKeys(
                elementsValue,
                mouseX,
                mouseY
            );
        }

        let hoveredElementId = null;
        let hoveredInsideElementId = null;
        let hoveredInsideElementIndex = null;
        let hoveredElementIdEmptyColumn = null;
        let hoveredElementIdHeader = null;

        // 1. check elements and columns (hover on table body columns)
        for (const key in elementsValue) {
            const element = elementsValue[key];
            const {
                id,
                x,
                y,
                width,
                height,
                value: { columns },
            } = element;

            if (element.type === 'pen-segment') {
                ctx.save();
                ctx.setTransform(
                    scaleRef.current,
                    0,
                    0,
                    scaleRef.current,
                    originXYRef.current.x,
                    originXYRef.current.y
                );

                const penPath = new Path2D(element.value.path);

                if (ctx.isPointInPath(penPath, eventX, eventY)) {
                    hoveredElementId = id;
                    hoveredInsideElementId = null;
                    hoveredInsideElementIndex = null;
                }

                ctx.restore(); // Переміщуємо початкову точку малювання
            } else if (
                mouseX >= x &&
                mouseX <= x + width &&
                mouseY >= y &&
                mouseY <= y + height &&
                element.type !== 'pen-segment'
            ) {
                hoveredElementId = id;
                hoveredInsideElementId = null;
                hoveredInsideElementIndex = null;

                if (element.type === 'table') {
                    if (mouseY <= y + TABLE_HEADER_HEIGHT) {
                        // hover on the table's header
                        hoveredElementIdHeader = id;
                    }

                    Object.keys(columns).forEach((key, index) => {
                        const column = columns[key];
                        const columnY = y + TABLE_HEADER_HEIGHT + index * TABLE_ROW_HEIGHT + HEADER_BODY_MARGIN;

                        if (mouseY >= columnY && mouseY <= columnY + TABLE_ROW_HEIGHT) {
                            hoveredInsideElementId = column.id;
                            hoveredInsideElementIndex = index;
                        }
                    });
                }
            }
        }

        const radius = 18;
        const distance = 36;
        const SHOW_CIRCLE_DISTANCE = 40;
        let hoveredCircleAreaElementsIds = [];
        let hoveredCircleElementId = null;

        // const POINT_RADIUS = 6.5;
        const POINT_RADIUS = 10.5;
        const POINT_DISTANCE = 20;
        const POINT_AREA = 50;
        let hoveredPointAreaInsideElementsIds = [];
        let hoveredPointInsideElementId = null;
        let hoveredPointDirection = null; // 'left' / 'right'

        // 2. check relation points (hover on column's relation points)
        for (const key in elementsValue) {
            const element = elementsValue[key];
            const {
                id,
                x,
                y,
                width,
                height,
                value: { columns },
            } = element;

            if (element.type === 'table') {
                // 2.1. check hover on column's relation points
                // if ((mouseX >= x - POINT_DISTANCE - POINT_RADIUS && mouseX <= x - POINT_DISTANCE + POINT_RADIUS) ||
                // 	(mouseX >= x + width + POINT_DISTANCE - POINT_RADIUS && mouseX <= x + width + POINT_DISTANCE + POINT_RADIUS)

                if (
                    (mouseX >= x - POINT_AREA && mouseX <= x) ||
                    (mouseX >= x + width && mouseX <= x + width + POINT_AREA)
                ) {
                    // check relation points
                    Object.keys(columns).forEach((key, index) => {
                        const column = columns[key];
                        const columnY =
                            y +
                            TABLE_HEADER_HEIGHT +
                            index * TABLE_ROW_HEIGHT +
                            HEADER_BODY_MARGIN;

                        // 2.1.1. check left side's relation point
                        if (
                            mouseX >= x - POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <= x - POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            hoveredPointAreaInsideElementsIds.push(column.id);
                            hoveredPointInsideElementId = column.id;
                            hoveredPointDirection = 'left';

                            // ?? should be another variable, couse should be hovered in the case, but not hovered in other cases
                            // hoveredElementId = id;
                            // hoveredInsideElementId = column.id;
                            // hoveredInsideElementIndex = index;

                            // 2.1.2. check right side's relation point
                        } else if (
                            mouseX >=
                            x + width + POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <=
                            x + width + POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            hoveredPointAreaInsideElementsIds.push(column.id);
                            hoveredPointInsideElementId = column.id;
                            hoveredPointDirection = 'right';

                            // ?? should be another variable, couse should be hovered in the case, but not hovered in other cases
                            // hoveredElementId = id;
                            // hoveredInsideElementId = column.id;
                            // hoveredInsideElementIndex = index;
                        }

                        // 2.1.3. check relation points areas
                        else if (
                            mouseY >= columnY &&
                            mouseY <= columnY + TABLE_ROW_HEIGHT
                        ) {
                            hoveredPointAreaInsideElementsIds.push(column.id);
                        }
                    });
                }

                // 2.2. check hover on table's circle to add new row
                if (
                    mouseX >= x - SHOW_CIRCLE_DISTANCE &&
                    mouseX <= x + width + SHOW_CIRCLE_DISTANCE &&
                    mouseY >= y - SHOW_CIRCLE_DISTANCE &&
                    mouseY <= y + height + SHOW_CIRCLE_DISTANCE * 2
                ) {
                    hoveredCircleAreaElementsIds.push(element.id);

                    if (
                        mouseX >= x + width / 2 - radius &&
                        mouseX <= x + width / 2 + radius &&
                        mouseY >= y + height + distance - radius &&
                        mouseY <= y + height + distance + radius
                    ) {
                        hoveredCircleElementId = id;
                    }
                }
            }
        }

        let hoveredLineId = null;

        // 3. check lines
        for (const key in linesRef.current) {
            const lineObject = linesRef.current[key];
            let {
                id,
                value: { path },
            } = lineObject;

            if (!path) {
                const lineObject = linesRef.current[key];
                const {
                    value: {
                        fromElementId,
                        fromInsideElementId,
                        fromRelationType,
                        toElementId,
                        toInsideElementId,
                        toRelationType,
                    },
                } = lineObject;

                const fromElement = elementsRef.current[fromElementId];
                const toElement = elementsRef.current[toElementId];

                if (!fromElement || !toElement) {
                    // return;
                    continue;
                }

                const fromInsideElement =
                    fromElement.value.columns[fromInsideElementId];
                const toInsideElement =
                    toElement.value.columns[toInsideElementId];

                if (!fromInsideElement || !toInsideElement) {
                    // return;
                    continue;
                }

                const fromElementColumnsKeys = Object.keys(
                    fromElement.value.columns
                );
                let fromInsideElementIndex = -1;
                fromElementColumnsKeys.forEach((key, index) => {
                    if (fromInsideElementId === key) {
                        fromInsideElementIndex = index;
                    }
                });

                const toElementColumnsKeys = Object.keys(
                    toElement.value.columns
                );
                let toInsideElementIndex = -1;
                toElementColumnsKeys.forEach((key, index) => {
                    if (toInsideElementId === key) {
                        toInsideElementIndex = index;
                    }
                });

                const lineData = {
                    mouseX: null,
                    mouseY: null,

                    fromElementType: fromElement.type,
                    fromElementWidth: fromElement.width,
                    fromElementX: fromElement.x,
                    fromElementY: fromElement.y,
                    fromInsideElementIndex,
                    fromRelationType,

                    toElementType: toElement.type,
                    toElementWidth: toElement.width,
                    toElementX: toElement.x,
                    toElementY: toElement.y,
                    toInsideElementIndex,
                    toRelationType,
                };

                path = _buildLinePath(lineData);
            }
            const [lines, points, bounds] = path;
            Math.random() === 12 && console.log('lines', lines);

            if (
                // couse there are only left-right, right-left, right-right lines, not left-left
                mouseX >= bounds.minX - 40 &&
                mouseX <= bounds.maxX + 40 + 20 &&
                mouseY >= bounds.minY - 10 &&
                mouseY <= bounds.maxY + 10
            ) {
                for (let index = 0; index <= points.length - 2; index++) {
                    const p1 = points[index];
                    const p2 = points[index + 1];
                    const isNear = isPointNearLine(
                        mouseX,
                        mouseY,
                        p1.x,
                        p1.y,
                        p2.x,
                        p2.y,
                        10
                    );
                    if (isNear) {
                        hoveredLineId = id;
                        break;
                    }
                }
            }
        }

        let hoveredAreaId = null;

        // // 4. check areas
        // for (const key in areasRef.current) {
        // 	const area = areasRef.current[key];
        // 	const { id, x, y, width, height } = area;

        // 	if (mouseX >= x && mouseX <= x + width && mouseY >= y && mouseY <= y + height) {
        // 		hoveredAreaId = id;
        // 	}
        // }

        return [
            !isElementResizing && hoveredElementId || null,
            !isElementResizing && hoveredInsideElementId || null,
            hoveredInsideElementIndex,
            !isElementResizing && hoveredCircleAreaElementsIds || [],
            !isElementResizing && hoveredCircleElementId || null,
            !isElementResizing && hoveredPointInsideElementId || null,
            hoveredPointDirection,
            !isElementResizing && hoveredPointAreaInsideElementsIds || [],
            !isElementResizing && hoveredLineId || null,
            hoveredElementIdEmptyColumn,
            hoveredAreaId,
            !isElementResizing && hoveredElementIdHeader || null,
        ];
    }

    function getNewHoveredElementsForViewKeys(elementsValue, mouseX, mouseY) {
        let hoveredElementId = null;
        let hoveredInsideElementId = null;
        let hoveredInsideElementIndex = null;
        let hoveredElementIdEmptyColumn = null;
        let hoveredElementIdHeader = null;

        // 1. check hover on table body columns
        for (const key in elementsValue) {
            const element = elementsValue[key];

            if (element.type === 'table') {
                const {
                    id,
                    x,
                    y,
                    width,
                    value: { columns },
                } = element;

                const columnsKeys = Object.keys(columns).filter(
                    (columnKey) =>
                        columns[columnKey]?.isPK ||
                        columns[columnKey]?.isFK ||
                        columns[columnKey]?.isRelated
                );

                const columnsCount = columnsKeys?.length || 0;
                const tableHeight =
                    TABLE_HEADER_HEIGHT +
                    HEADER_BODY_MARGIN +
                    (columnsCount + 1) * TABLE_ROW_HEIGHT +
                    TABLE_BORDER_RADIUS +
                    1;

                if (
                    mouseX >= x &&
                    mouseX <= x + width &&
                    mouseY >= y &&
                    mouseY <= y + tableHeight
                ) {
                    hoveredElementId = id;
                    hoveredInsideElementId = null;
                    hoveredInsideElementIndex = null;
                    hoveredElementIdEmptyColumn = null;

                    if (mouseY <= y + TABLE_HEADER_HEIGHT) {
                        // hover on the table's header
                        hoveredElementIdHeader = id;
                    }

                    // columns
                    columnsKeys.forEach((key, index) => {
                        const column = columns[key];
                        const columnY =
                            y +
                            TABLE_HEADER_HEIGHT +
                            HEADER_BODY_MARGIN +
                            index * TABLE_ROW_HEIGHT;

                        if (
                            mouseY >= columnY &&
                            mouseY <= columnY + TABLE_ROW_HEIGHT
                        ) {
                            hoveredInsideElementId = column.id;
                            hoveredInsideElementIndex = index;
                        }
                    });

                    // empty column
                    const emptyColumnY =
                        y +
                        TABLE_HEADER_HEIGHT +
                        HEADER_BODY_MARGIN +
                        columnsCount * TABLE_ROW_HEIGHT;
                    if (
                        mouseY >= emptyColumnY &&
                        mouseY <= emptyColumnY + TABLE_ROW_HEIGHT
                    ) {
                        hoveredElementIdEmptyColumn = id;
                    }
                }
            } else {
                const { id, x, y, width, height } = element;
                if (
                    mouseX >= x &&
                    mouseX <= x + width &&
                    mouseY >= y &&
                    mouseY <= y + height
                ) {
                    hoveredElementId = id;
                    hoveredInsideElementId = null;
                    hoveredInsideElementIndex = null;
                }
            }
        }

        let hoveredCircleAreaElementsIds = [];
        let hoveredCircleElementId = null;

        // const POINT_RADIUS = 6.5;
        const POINT_RADIUS = 10.5;
        const POINT_DISTANCE = 20;
        const POINT_AREA = 50;
        let hoveredPointAreaInsideElementsIds = [];
        let hoveredPointInsideElementId = null;
        let hoveredPointDirection = null; // 'left' / 'right'

        // 2. check hover on column's relation points
        for (const key in elementsValue) {
            const element = elementsValue[key];

            if (element.type === 'table') {
                const {
                    x,
                    y,
                    width,
                    value: { columns },
                } = element;
                const columnsKeys = Object.keys(columns).filter(
                    (columnKey) =>
                        columns[columnKey]?.isPK ||
                        columns[columnKey]?.isFK ||
                        columns[columnKey]?.isRelated
                );

                // 2.1. check hover on column's relation points
                // if ((mouseX >= x - POINT_DISTANCE - POINT_RADIUS && mouseX <= x - POINT_DISTANCE + POINT_RADIUS) ||
                // 	(mouseX >= x + width + POINT_DISTANCE - POINT_RADIUS && mouseX <= x + width + POINT_DISTANCE + POINT_RADIUS)

                if (
                    (mouseX >= x - POINT_AREA && mouseX <= x) ||
                    (mouseX >= x + width && mouseX <= x + width + POINT_AREA)
                ) {
                    // check relation points
                    columnsKeys.forEach((key, index) => {
                        const column = columns[key];
                        const columnY =
                            y +
                            TABLE_HEADER_HEIGHT +
                            index * TABLE_ROW_HEIGHT +
                            HEADER_BODY_MARGIN;

                        // 2.1.1. check left side's relation point
                        if (
                            mouseX >= x - POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <= x - POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            hoveredPointAreaInsideElementsIds.push(column.id);
                            hoveredPointInsideElementId = column.id;
                            hoveredPointDirection = 'left';

                            // ?? should be another variable, couse should be hovered in the case, but not hovered in other cases
                            // hoveredElementId = id;
                            // hoveredInsideElementId = column.id;
                            // hoveredInsideElementIndex = index;

                            // 2.1.2. check right side's relation point
                        } else if (
                            mouseX >=
                            x + width + POINT_DISTANCE - POINT_RADIUS &&
                            mouseX <=
                            x + width + POINT_DISTANCE + POINT_RADIUS &&
                            mouseY >=
                            columnY + TABLE_ROW_HEIGHT / 2 - POINT_RADIUS &&
                            mouseY <=
                            columnY + TABLE_ROW_HEIGHT / 2 + POINT_RADIUS
                        ) {
                            hoveredPointAreaInsideElementsIds.push(column.id);
                            hoveredPointInsideElementId = column.id;
                            hoveredPointDirection = 'right';

                            // ?? should be another variable, couse should be hovered in the case, but not hovered in other cases
                            // hoveredElementId = id;
                            // hoveredInsideElementId = column.id;
                            // hoveredInsideElementIndex = index;
                        }

                        // 2.1.3. check relation points areas
                        else if (
                            mouseY >= columnY &&
                            mouseY <= columnY + TABLE_ROW_HEIGHT
                        ) {
                            hoveredPointAreaInsideElementsIds.push(column.id);
                        }
                    });
                }
            }
        }

        // 3. check lines
        let hoveredLineId = null;

        for (const key in linesRef.current) {
            const lineObject = linesRef.current[key];
            const {
                id,
                value: {
                    fromElementId,
                    fromInsideElementId,
                    fromRelationType,
                    toElementId,
                    toInsideElementId,
                    toRelationType,
                },
            } = lineObject;

            const fromElement = elementsRef.current[fromElementId];
            const toElement = elementsRef.current[toElementId];

            if (!fromElement || !toElement) {
                // return;
                continue;
            }

            const fromInsideElement =
                fromElement.value.columns[fromInsideElementId];
            const toInsideElement = toElement.value.columns[toInsideElementId];

            if (!fromInsideElement || !toInsideElement) {
                // return;
                continue;
            }

            const fromElementColumnsKeys = Object.keys(
                fromElement.value.columns
            ).filter(
                (columnKey) =>
                    fromElement.value.columns[columnKey]?.isPK ||
                    fromElement.value.columns[columnKey]?.isFK ||
                    fromElement.value.columns[columnKey]?.isRelated
            );
            let fromInsideElementIndex = -1;
            fromElementColumnsKeys.forEach((key, index) => {
                if (fromInsideElementId === key) {
                    fromInsideElementIndex = index;
                }
            });

            const toElementColumnsKeys = Object.keys(
                toElement.value.columns
            ).filter(
                (columnKey) =>
                    toElement.value.columns[columnKey]?.isPK ||
                    toElement.value.columns[columnKey]?.isFK ||
                    toElement.value.columns[columnKey]?.isRelated
            );
            let toInsideElementIndex = -1;
            toElementColumnsKeys.forEach((key, index) => {
                if (toInsideElementId === key) {
                    toInsideElementIndex = index;
                }
            });

            const lineData = {
                mouseX: null,
                mouseY: null,

                fromElementType: fromElement.type,
                fromElementWidth: fromElement.width,
                fromElementX: fromElement.x,
                fromElementY: fromElement.y,
                fromInsideElementIndex,
                fromRelationType,

                toElementType: toElement.type,
                toElementWidth: toElement.width,
                toElementX: toElement.x,
                toElementY: toElement.y,
                toInsideElementIndex,
                toRelationType,
            };

            const path = _buildLinePath(lineData);

            const [lines, points, bounds] = path;
            Math.random() === 12 && console.log('lines', lines);

            if (
                // couse there are only left-right, right-left, right-right lines, not left-left
                mouseX >= bounds.minX - 40 &&
                mouseX <= bounds.maxX + 40 + 20 &&
                mouseY >= bounds.minY - 10 &&
                mouseY <= bounds.maxY + 10
            ) {
                for (let index = 0; index <= points.length - 2; index++) {
                    const p1 = points[index];
                    const p2 = points[index + 1];
                    const isNear = isPointNearLine(
                        mouseX,
                        mouseY,
                        p1.x,
                        p1.y,
                        p2.x,
                        p2.y,
                        10
                    );
                    if (isNear) {
                        hoveredLineId = id;
                        break;
                    }
                }
            }
        }

        // 4. check areas
        let hoveredAreaId = null;

        // for (const key in areasRef.current) {
        // 	const area = areasRef.current[key];
        // 	const { id, x, y, width, height } = area;

        // 	if (mouseX >= x && mouseX <= x + width && mouseY >= y && mouseY <= y + height) {
        // 		hoveredAreaId = id;
        // 	}
        // }

        return [
            !isElementResizing && hoveredElementId || null,
            !isElementResizing && hoveredInsideElementId || null,
            hoveredInsideElementIndex,
            [], // hoveredCircleAreaElementsIds,
            null, // hoveredCircleElementId,
            null, // hoveredPointInsideElementId,
            null, // hoveredPointDirection,
            [], // hoveredPointAreaInsideElementsIds,
            !isElementResizing && hoveredLineId || null,
            hoveredElementIdEmptyColumn,
            hoveredAreaId,
            !isElementResizing && hoveredElementIdHeader || null,
        ];
    }

    function getNewAreaIdForElement(areasValue, elementId) {
        const element = elementsRef.current[elementId];
        const {
            x: elementX,
            y: elementY,
            width: elementWidth,
            height: elementHeight,
        } = element;

        let newAreaId = null;

        for (const key in areasValue) {
            const area = areasValue[key];
            const {
                id,
                x: areaX,
                y: areaY,
                width: areaWidth,
                height: areaHeight,
            } = area;

            const minX = areaX;
            const minY = areaY;
            const maxX = areaX + areaWidth;
            const maxY = areaY + areaHeight;

            if (
                elementX + elementWidth > minX &&
                elementX < maxX &&
                elementY + elementHeight > minY &&
                elementY < maxY
            ) {
                newAreaId = id;
            }
        }

        return newAreaId;
    }

    function isHoverOnResizeHandle(mouseX, mouseY) {
        if (
            focusedElementsIdsRef.current.length !== 1 &&
            !focusedAreaIdRef.current
        ) {
            return [false, null];
        }

        const focusedItem = focusedElementsIdsRef.current.length
            ? elementsRef.current[focusedElementsIdsRef.current[0]]
            : areasRef.current[focusedAreaIdRef.current];

        const outlineOffset = focusedElementsIdsRef.current.length
            ? scaleNumbersRef.current.outlineOffset
            : // : scaleNumbersRef.current.outlineOffset + 5;
            scaleNumbersRef.current.outlineOffset;

        const resizeHandleSize = scaleNumbersRef.current.resizeHandleSize;

        const width = resizeHandleSize;
        let resizeKey = null;
        let isOnResize = false;

        const focusedItemResizes = calculateItemResizeCoordinates(
            focusedItem,
            resizeHandleSize,
            outlineOffset
        );

        for (const key in focusedItemResizes.points) {
            const { x, y } = focusedItemResizes.points[key];
            if (
                mouseX >= x &&
                mouseX <= x + width &&
                mouseY >= y &&
                mouseY <= y + width
            ) {
                resizeKey = key;
                isOnResize = true;
            }
        }

        for (const key in focusedItemResizes.lines) {
            const { x, y } = focusedItemResizes.lines[key];
            const { value: xValue, min: xMin, max: xMax } = x;
            const { value: yValue, min: yMin, max: yMax } = y;

            let isHoverByX = false;
            let isHoverByY = false;

            // eslint-disable-next-line no-prototype-builtins
            if (
                (x.hasOwnProperty('value') && Math.abs(mouseX - xValue) < 10) ||
                (mouseX >= xMin && mouseX <= xMax)
            ) {
                isHoverByX = true;
            }
            // eslint-disable-next-line no-prototype-builtins
            if (
                (y.hasOwnProperty('value') && Math.abs(mouseY - yValue) < 10) ||
                (mouseY >= yMin && mouseY <= yMax)
            ) {
                isHoverByY = true;
            }

            if (isHoverByX && isHoverByY) {
                resizeKey = key;
                isOnResize = true;
            }
        }

        return [isOnResize, resizeKey];
    }

    function getHoveredResizeKeyOnExportImageBox(mouseX, mouseY) {
        const RESIZE_HANDLE_CLIENT_DISTANCE = 40;
        const RESIZE_HANDLE_DISTANCE =
            RESIZE_HANDLE_CLIENT_DISTANCE / scaleRef.current;

        let hoveredResizeKey = null;

        const { clientX, clientY, clientBoxWidth, clientBoxHeight } =
            exportImageBoxPropsRef.current;

        const x = (clientX - originXYRef.current.x) / scaleRef.current;
        const y = (clientY - originXYRef.current.y) / scaleRef.current;
        const boxWidth = clientBoxWidth / scaleRef.current;
        const boxHeight = clientBoxHeight / scaleRef.current;

        const resizeProps = {
            // 2 * distance, couse distance inside the box and distance outside the box
            distance: 2 * RESIZE_HANDLE_DISTANCE,
            points: {
                nw: {
                    x: x - RESIZE_HANDLE_DISTANCE,
                    y: y - RESIZE_HANDLE_DISTANCE,
                },
                ne: {
                    x: x + boxWidth - RESIZE_HANDLE_DISTANCE,
                    y: y - RESIZE_HANDLE_DISTANCE,
                },
                sw: {
                    x: x - RESIZE_HANDLE_DISTANCE,
                    y: y + boxHeight - RESIZE_HANDLE_DISTANCE,
                },
                se: {
                    x: x + boxWidth - RESIZE_HANDLE_DISTANCE,
                    y: y + boxHeight - RESIZE_HANDLE_DISTANCE,
                },
            },
        };

        for (const key in resizeProps.points) {
            const { x, y } = resizeProps.points[key];
            if (
                mouseX >= x &&
                mouseX <= x + resizeProps.distance &&
                mouseY >= y &&
                mouseY <= y + resizeProps.distance
            ) {
                hoveredResizeKey = key;
            }
        }

        return hoveredResizeKey;
    }

    function handleResize(resizeKey, deltaX, deltaY) {
        let x = 0;
        let y = 0;
        let width = 0;
        let height = 0;

        switch (resizeKey) {
            // points
            case 'nw':
                x = deltaX;
                y = deltaY;
                width = -deltaX;
                height = -deltaY;
                break;
            case 'ne':
                y = deltaY;
                width = deltaX;
                height = -deltaY;
                break;
            case 'sw':
                x = deltaX;
                width = -deltaX;
                height = deltaY;
                break;
            case 'se':
                width = deltaX;
                height = deltaY;
                break;
            // lines
            case 'n':
                y = deltaY;
                height = -deltaY;
                break;
            case 's':
                height = deltaY;
                break;
            case 'w':
                x = deltaX;
                width = -deltaX;
                break;
            case 'e':
                width = deltaX;
                break;
            default:
                break;
        }

        return { x, y, width, height };
    }


    function updateElementColumnsTextsOnResize(elementId) {
        if (!elementId || !elementsRef.current[elementId] || elementsRef.current[elementId].type !== 'table') {
            return;
        }

        const element = elementsRef.current[elementId];
        const { value: { columns } } = element;
        const keys = Object.keys(columns);

        keys.forEach((key, index) => {
            const column = columns[key];
            const oldName = column.name;

            if (oldName.length >= 4 && oldName)
                elementsRef.current[elementId].value.columns[key].name = oldName + '...';
        });

        // for (let i = 0; i < keys.length; i++) {
        //     if (keys[i] === columnId) {
        //         newInsideElementIndex = i;
        //         break;
        //     }
        // }

        // columns
        // foreach
        // get column measured width
        // compare measured width with table width
        // update column text (name ? type ?) (name => 'n..', then type => 't..', => 'n..' 't..')




        // const updatedLinesIds = [];

        // if (!Object.keys(linesRef.current).length) {
        //     return;
        // }

        // Object.keys(linesRef.current).forEach((key) => {
        //     const lineObject = linesRef.current[key];

        //     if (elementId === lineObject.value.fromElementId || elementId === lineObject.value.toElementId) {
        //         const lineData = buildLineData(lineObject.value);
        //         const path = lineData ? _buildLinePath(lineData) : {};
        //         linesRef.current[key].value.path = path;
        //         updatedLinesIds.push(lineObject.id);
        //     }
        // });

        // return updatedLinesIds;
    }


    function updateLinesByElementId(elementId) {
        const updatedLinesIds = [];

        if (!Object.keys(linesRef.current).length) {
            return;
        }

        Object.keys(linesRef.current).forEach((key) => {
            const lineObject = linesRef.current[key];

            if (elementId === lineObject.value.fromElementId || elementId === lineObject.value.toElementId) {
                const lineData = buildLineData(lineObject.value);
                const path = lineData ? _buildLinePath(lineData) : {};
                linesRef.current[key].value.path = path;
                updatedLinesIds.push(lineObject.id);
            }
        });

        return updatedLinesIds;
    }

    function updateLinesByElementsIds(updatedElementsIds) {
        if (!Object.keys(linesRef.current).length) {
            return;
        }

        Object.keys(linesRef.current).forEach((key) => {
            const lineObject = linesRef.current[key];

            if (updatedElementsIds.includes(lineObject.value.fromElementId) ||
                updatedElementsIds.includes(lineObject.value.toElementId)
            ) {
                const lineData = buildLineData(lineObject.value);
                const path = lineData ? _buildLinePath(lineData) : {};
                linesRef.current[key].value.path = path;
            }
        });
    }
    function getUpdatedLinesByElementsIds(updatedElementsIds) {
        const updatedLines = {};

        if (!Object.keys(linesRef.current).length) {
            return {};
        }

        Object.keys(linesRef.current).forEach((key) => {
            const lineObject = linesRef.current[key];

            if (
                updatedElementsIds.includes(lineObject.value.fromElementId) ||
                updatedElementsIds.includes(lineObject.value.toElementId)
            ) {
                updatedLines[key] = linesRef.current[key];
            }
        });

        return updatedLines;
    }

    function getElementsRelatedProps(elementsIds = []) {
        let props = {
            linesIds: new Set(),
            elementsIds: new Set(),
            insideElementsIds: new Set(),
        };

        for (const key in linesRef.current) {
            const lineObject = linesRef.current[key];
            const {
                id,
                value: {
                    fromElementId,
                    fromInsideElementId,
                    toElementId,
                    toInsideElementId,
                },
            } = lineObject;

            if (
                elementsIds.includes(fromElementId) ||
                elementsIds.includes(toElementId)
            ) {
                props.linesIds.add(id);

                props.elementsIds.add(fromElementId);
                props.elementsIds.add(toElementId);

                props.insideElementsIds.add(fromInsideElementId);
                props.insideElementsIds.add(toInsideElementId);
            }
        }

        const updatedProps = {
            linesIds: Array.from(props.linesIds),
            elementsIds: Array.from(props.elementsIds),
            insideElementsIds: Array.from(props.insideElementsIds),
        };

        return updatedProps;
    }
    function getLineRelatedProps(lineId) {
        let props = {
            elementsIds: [],
            insideElementsIds: [],
        };

        if (!lineId) {
            return null;
        }

        const lineObject = linesRef.current[lineId];

        const {
            value: {
                fromElementId,
                fromInsideElementId,
                toElementId,
                toInsideElementId,
            },
        } = lineObject;

        props.elementsIds.push(fromElementId);
        props.elementsIds.push(toElementId);

        props.insideElementsIds.push(fromInsideElementId);
        props.insideElementsIds.push(toInsideElementId);

        return props;
    }

    // function calculateContextMenuXY() {
    // 	let x = 0;
    // 	let y = 0;
    // 	let width = 0;
    // 	let height = 0;

    // 	switch (resizeKeyRef.current) {
    // 		// points
    // 		case 'nw':
    // 			x = deltaX;
    // 			y = deltaY;
    // 			width = -deltaX;
    // 			height = -deltaY;
    // 			break;
    // 		case 'ne':
    // 			y = deltaY;
    // 			width = deltaX;
    // 			height = -deltaY;
    // 			break;
    // 		case 'sw':
    // 			x = deltaX;
    // 			width = -deltaX;
    // 			height = deltaY;
    // 			break;
    // 		case 'se':
    // 			width = deltaX;
    // 			height = deltaY;
    // 			break;
    // 		// lines
    // 		case 'n':
    // 			y = deltaY;
    // 			height = -deltaY;
    // 			break;
    // 		case 's':
    // 			height = deltaY;
    // 			break;
    // 		case 'w':
    // 			x = deltaX;
    // 			width = -deltaX;
    // 			break;
    // 		case 'e':
    // 			width = deltaX;
    // 			break;
    // 		default:
    // 			break;
    // 	}

    // }

    function getAlignmentLines(movedElementId) {
        // TODO: build moving when you draw line
        // - line = x1, y1 => x2, y2
        // if movingElement is left => line = x2, y2 => x1, y1
        // if movingElement is right => line = x1, y1 => x2, y2
        const alignmentLines = [];
        const threshold = 2;

        let movedElementNewXYPoints = [];

        const movedElement = elementsRef.current[movedElementId];

        for (const key in elementsRef.current) {
            let movedElementNewXY = {};

            const element = elementsRef.current[key];

            if (element.id === movedElement.id) continue;
            if (!visibleViewportElementsIdsRef.current.includes(element.id))
                continue;
            // if (element?.value?.header?.name !== 'bo.phones') continue;
            // if (element.type !== 'table') return;

            let line;

            // OUTLINE FRAME TABLE
            // ctx.roundRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1, 0.75);

            // Вирівнювання по лівій стороні
            if (Math.abs(movedElement.x - element.x) < threshold) {
                movedElementNewXY.x = element.x;
                movedElementNewXY.distanceX = Math.abs(
                    movedElement.x - element.x
                );

                line = {
                    x1: element.x - scaleNumbersRef.current.outlineOffset - 2.5,
                    y1: Math.min(movedElement.y, element.y) - 27,
                    x2: element.x - scaleNumbersRef.current.outlineOffset - 2.5,
                    y2:
                        Math.max(
                            movedElement.y + movedElement.height,
                            element.y + element.height
                        ) + 30,
                };
                alignmentLines.push(line);
            }

            // Вирівнювання по правій стороні
            if (
                Math.abs(
                    movedElement.x +
                    movedElement.width -
                    (element.x + element.width)
                ) < threshold
            ) {
                // movedElementNewXY.x = element.x + element.width - movedElement.width;
                movedElementNewXY.x =
                    element.x + element.width - movedElement.width;
                movedElementNewXY.distanceX = Math.abs(
                    movedElement.x +
                    movedElement.width -
                    (element.x + element.width)
                );

                line = {
                    x1:
                        element.x +
                        element.width +
                        scaleNumbersRef.current.outlineOffset +
                        2.5,
                    y1: Math.min(movedElement.y, element.y) - 27,
                    x2:
                        element.x +
                        element.width +
                        scaleNumbersRef.current.outlineOffset +
                        2.5,
                    y2:
                        Math.max(
                            movedElement.y + movedElement.height,
                            element.y + element.height
                        ) + 30,
                };
                alignmentLines.push(line);
            }

            // Вирівнювання по верхній стороні
            if (Math.abs(movedElement.y - element.y) < threshold) {
                movedElementNewXY.y = element.y;
                movedElementNewXY.distanceY = Math.abs(
                    movedElement.y - element.y
                );

                line = {
                    x1: Math.min(movedElement.x, element.x) - 27,
                    y1:
                        element.y -
                        scaleNumbersRef.current.outlineOffset -
                        0.5 -
                        2.5,
                    x2:
                        Math.max(
                            movedElement.x + movedElement.width,
                            element.x + element.width
                        ) + 30,
                    y2:
                        element.y -
                        scaleNumbersRef.current.outlineOffset -
                        0.5 -
                        2.5,
                };
                alignmentLines.push(line);
            }

            // Вирівнювання по нижній стороні
            if (
                Math.abs(
                    movedElement.y +
                    movedElement.height -
                    (element.y + element.height)
                ) < threshold
            ) {
                // movedElementNewXY.y = element.y + element.height - movedElement.height;
                movedElementNewXY.y =
                    element.y + element.height - movedElement.height;
                movedElementNewXY.distanceY = Math.abs(
                    movedElement.y +
                    movedElement.height -
                    (element.y + element.height)
                );

                line = {
                    x1: Math.min(movedElement.x, element.x) - 27,
                    y1:
                        element.y +
                        element.height +
                        scaleNumbersRef.current.outlineOffset +
                        0.5 +
                        2.5 +
                        0.5,
                    x2:
                        Math.max(
                            movedElement.x + movedElement.width,
                            element.x + element.width
                        ) + 30,
                    y2:
                        element.y +
                        element.height +
                        scaleNumbersRef.current.outlineOffset +
                        0.5 +
                        2.5 +
                        0.5,
                };
                alignmentLines.push(line);
            }

            // Вирівнювання по центру по горизонталі
            const movedCenterX = movedElement.x + movedElement.width / 2;
            const elementCenterX = element.x + element.width / 2;
            if (Math.abs(movedCenterX - elementCenterX) < threshold) {
                movedElementNewXY.x = elementCenterX - movedElement.width / 2;
                movedElementNewXY.distanceX = Math.abs(
                    movedCenterX - elementCenterX
                );

                line = {
                    x1: elementCenterX,
                    y1: Math.min(movedElement.y, element.y) - 25,
                    x2: elementCenterX,
                    y2:
                        Math.max(
                            movedElement.y + movedElement.height,
                            element.y + element.height
                        ) + 28,
                };
                alignmentLines.push(line);
            }

            // Вирівнювання по центру по вертикалі
            const movedCenterY = movedElement.y + movedElement.height / 2;
            const elementCenterY = element.y + element.height / 2;
            if (Math.abs(movedCenterY - elementCenterY) < threshold) {
                movedElementNewXY.y = elementCenterY - movedElement.height / 2;
                movedElementNewXY.distanceY = Math.abs(
                    movedCenterY - elementCenterY
                );

                line = {
                    x1: Math.min(movedElement.x, element.x) - 25,
                    y1: elementCenterY,
                    x2:
                        Math.max(
                            movedElement.x + movedElement.width,
                            element.x + element.width
                        ) + 28,
                    y2: elementCenterY,
                };
                alignmentLines.push(line);
            }

            if (
                typeof movedElementNewXY.x === 'number' ||
                typeof movedElementNewXY.y === 'number'
            ) {
                movedElementNewXYPoints.push(movedElementNewXY);
            }
        }

        // get closer alignment line by 'x' and line by 'y' to the element
        if (movedElementNewXYPoints.length) {
            let firstPoint = movedElementNewXYPoints[0];

            // initial just bigger than threshold
            let closerNewXYPoint = {
                x: firstPoint.x,
                y: firstPoint.y,
                distanceX: firstPoint.distanceX,
                distanceY: firstPoint.distanceY,
            };
            movedElementNewXYPoints.forEach((point) => {
                if (typeof point.distanceX === 'number') {
                    if (
                        typeof closerNewXYPoint.distanceX !== 'number' ||
                        point.distanceX < closerNewXYPoint.distanceX
                    ) {
                        closerNewXYPoint.x = point.x;
                    }
                }
                if (typeof point.distanceY === 'number') {
                    if (
                        typeof closerNewXYPoint.distanceY !== 'number' ||
                        point.distanceY < closerNewXYPoint.distanceY
                    ) {
                        closerNewXYPoint.y = point.y;
                    }
                }
            });

            return [alignmentLines, closerNewXYPoint];
        }

        return [alignmentLines, null];
    }

    const syncScaleToReduxRef = useRef(
        debounce((scaleValue, originXValue, originYValue) => {
            dispatch(updateScale(scaleValue, originXValue, originYValue));
        }, 100)
    );
    const syncOriginXYToReduxRef = useRef(
        debounce((originXValue, originYValue) => {
            calculateVisibleElementsAndLines();
            dispatch(updateOriginXY(originXValue, originYValue));
        }, 100)
    );
    const syncFocusedGroupToReduxRef = useRef(
        debounce((focusedElementsIds, focusedAreasIds) => {
            dispatch(updateFocusedGroup(focusedElementsIds, focusedAreasIds));
        }, 100)
    );
    const syncFocusedElementsToReduxRef = useRef(
        debounce((focusedIds) => {
            dispatch(updateFocusedElements(focusedIds));
        }, 100)
    );
    const syncFocusedLineToReduxRef = useRef(
        debounce((focusedId) => {
            dispatch(updateFocusedLine(focusedId));
        }, 100)
    );
    const syncFocusedAreaToReduxRef = useRef(
        debounce((focusedId) => {
            dispatch(updateFocusedArea(focusedId));
        }, 100)
    );

    const syncFocusedInsideElementToReduxRef = useRef(
        debounce(
            (
                elementId,
                insideElementId,
                insideElementIndex,
                insideElementType
            ) => {
                // if (focusedInsideElementStateValue?.elementId !== focusedInsideElementRedux?.elementId ||
                // 	focusedInsideElementStateValue?.insideElementId !== focusedInsideElementRedux?.insideElementId ||
                // 	focusedInsideElementStateValue?.insideElement?.name !== focusedInsideElementRedux?.insideElement?.name ||
                // 	focusedInsideElementStateValue?.insideElement?.type !== focusedInsideElementRedux?.insideElement?.type
                // ) {

                // if (scaleRef.current > 0.15) {
                dispatch(
                    updateFocusedInsideElement(
                        elementId,
                        insideElementId,
                        insideElementIndex,
                        insideElementType
                    )
                );
                // } else {
                //     dispatch(
                //         updateFocusedInsideElement(
                //             null,
                //             null,
                //             null,
                //             null
                //         )
                //     );
                // }
                // }
            },
            1
        )
    );

    const syncEditableTableColumnNameToReduxRef = useRef(
        debounce((tableId, columnId, value, newWidth) => {
            dispatch(updateTableColumnName(tableId, columnId, value, newWidth));
        }, 100)
    );
    const syncEditableTableColumnTypeToReduxRef = useRef(
        debounce((tableId, columnId, value, newWidth) => {
            dispatch(updateTableColumnType(tableId, columnId, value, newWidth));
        }, 100)
    );
    const syncEditableTableHeaderNameToReduxRef = useRef(
        debounce((tableId, value) => {
            dispatch(updateTableHeaderName(tableId, value));
        }, 100)
    );

    function onlyUnique(value, index, array) {
        return array.indexOf(value) === index;
    }

    function handleCursorClick() {
        clickTimesRef.current = [
            ...(clickTimesRef.current || []),
            new Date().getTime(),
        ];

        let isDoubleClick = false;
        if (clickTimesRef.current.length >= 2) {
            const firstTime =
                clickTimesRef.current[clickTimesRef.current.length - 2];
            const lastTime =
                clickTimesRef.current[clickTimesRef.current.length - 1];
            const diffTime = lastTime - firstTime;

            clickTimesRef.current = clickTimesRef.current.slice(
                1,
                clickTimesRef.current.length
            );
            // isDoubleClick = diffTime <= 250;
            isDoubleClick = diffTime <= 300;
        }

        // double click
        if (isDoubleClick) {
            dispatch(changeCursor());
        }
    }


    const drawUpdateZoom = useCallback((newScale, newOriginX, newOriginY) => {
        console.log('drawUpdateZoom', newScale, newOriginX, newOriginY)

        const canvas = ref.current;
        const context = contextRef.current;

        if (!canvas || !context) {
            return;
        }

        // context.save();
        // context.clearRect(0, 0, canvas.width / 2, canvas.height / 2);

        context.setTransform(newScale, 0, 0, newScale, newOriginX, newOriginY);
        // context.restore();


    }, [
        elementsRedux,
        focusedElementsIds,
        isCursorHand,
        drawTable,
        drawSelectionBox,
        isBtnRedGreenViewEnabled,
        focusedInsideElementState,
        isBtnSearchEnabled,
    ])

    const draw = useCallback((isFromMouseMove, exportImgCanvas = null) => {
        // 6 19 25 28 24 5 96

        const isResizingArea = resizeAreaPropsRef.current?.areaId;

        if (isFromMouseMove) {
            // console.log('mouse-draw', new Date().getTime())
        } else {
            // console.log('draw', new Date().getTime())
        }
        if (
            isBtnMinimapEnabled &&
            minimapRef.current &&
            minimapRef.current.staticCanvas
        ) {
            const staticCanvas = minimapRef.current.staticCanvas;
            const staticContext = staticCanvas.getContext('2d');

            drawMinimapStatic(staticCanvas, staticContext);
        }

        const canvas = ref.current;
        const context = contextRef.current;

        if (!canvas || !context) {
            return;
        }

        const elementsValue = {};

        for (const key in elementsRef.current) {
            const element = elementsRef.current[key];
            if (visibleElementsIdsRef.current.includes(element.id)) {
                elementsValue[element.id] = element;
            }
        }

        // =========================================
        const dpr = window.devicePixelRatio || 1;

        canvas.style.width = window.innerWidth + 'px';
        canvas.style.height = window.innerHeight + 'px';

        canvas.width = window.innerWidth * dpr;
        canvas.height = window.innerHeight * dpr;

        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';

        // =========================================

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.save();
        context.setTransform(
            scaleRef.current * dpr,
            0,
            0,
            scaleRef.current * dpr,
            originXYRef.current.x * dpr,
            originXYRef.current.y * dpr
        );

        const movingElementsRelatedLines = isMovingElementId
            ? getElementsRelatedProps([isMovingElementId]).linesIds || []
            : [];
        const movingElementsRelatedElements = isMovingElementId
            ? getElementsRelatedProps([isMovingElementId]).elementsIds || []
            : [];

        const relatedLinesIds = [
            ...(focusedElementsRelatedPropsRef.current?.linesIds || []),
            ...movingElementsRelatedLines,
        ];

        const relatedElementsIds = [
            ...(focusedLineRelatedPropsRef.current?.elementsIds || []),
            ...(focusedElementsRelatedPropsRef.current?.elementsIds || []),
            ...movingElementsRelatedElements,
        ].filter(onlyUnique);

        const focusedElementsIds = focusedElementsIdsRef.current || [];
        const movingElementsIds = isMovingElementId ? [isMovingElementId] : [];
        const focusedAndMovingElementsIds = [
            ...focusedElementsIds,
            ...movingElementsIds,
        ];

        // 0. RED / GREEN VIEW
        if (isBtnRedGreenViewEnabled) {
            // 2. ELEMENTS CHANGES |==| ALL
            for (const key in elementsChangesRef.current) {
                const element = elementsChangesRef.current[key];

                if (!element) {
                    continue;
                }

                if (
                    !focusedElementsIds?.includes(element.id) &&
                    !relatedElementsIds?.includes(element.id) &&
                    !focusedAndMovingElementsIds?.includes(element.id)
                ) {
                    switch (element.type) {
                        case 'table': {
                            drawTable(element, exportImgCanvas);
                            break;
                        }
                        default:
                            break;
                    }
                }
            }

            // 5. ELEMENTS |==| FOCUSED ELEMENTS AND MOVING ELEMENT
            for (let i = 0; i < focusedAndMovingElementsIds.length; i++) {
                const id = focusedAndMovingElementsIds[i];
                const element =
                    elementsChangesRef.current &&
                    elementsChangesRef.current[id];

                if (!element) {
                    continue;
                }

                switch (element.type) {
                    case 'table': {
                        drawTable(element, exportImgCanvas);
                        break;
                    }
                    default:
                        break;
                }
            }

            context.restore();

            return;
        }

        // 1. AREAS |==| ALL
        for (const key in areasRef.current) {
            const area = areasRef.current[key];
            if (
                area.id !== hoveredAreaIdRef.current &&
                area.id !== focusedAreaIdRef.current
            ) {
                drawArea(area, exportImgCanvas);
            }
        }

        // 1.1. AREAS |==| HOVERED AREA
        if (
            hoveredAreaIdRef.current &&
            hoveredAreaIdRef.current !== focusedAreaIdRef.current
        ) {
            const area = areasRef.current[hoveredAreaIdRef.current];

            if (area) {
                drawArea(area, exportImgCanvas);
            }
        }

        // 1.2. AREAS |==| FOCUSED AREA
        if (focusedAreaIdRef.current) {
            const area = areasRef.current[focusedAreaIdRef.current];

            if (area) {
                drawArea(area, exportImgCanvas);
            }
        }

        // 1. LINES |==| ALL
        for (const key in linesRef.current) {
            const line = linesRef.current[key];

            if (!line || !visibleLinesIdsRef.current.includes(line.id)) {
                // if (!line) {
                continue;
            }

            if (
                focusedLineIdRef.current !== line.id &&
                !relatedLinesIds?.includes(line.id)
            ) {
                drawCreatedLine(line, exportImgCanvas);
            }
        }

        // 2. ELEMENTS |==| ALL
        for (const key in elementsValue) {
            const element = elementsValue[key];

            if (!element) {
                continue;
            }

            if (
                !focusedElementsIds?.includes(element.id) &&
                !relatedElementsIds?.includes(element.id) &&
                !focusedAndMovingElementsIds?.includes(element.id) &&
                (!isBtnNewAreaEnabled ||
                    !newAreaPropsRef?.current?.selectedElements?.includes(
                        element.id
                    ))
            ) {
                switch (element.type) {
                    case 'table': {
                        drawTable(element, exportImgCanvas);
                        break;
                    }
                    case 'note': {
                        drawNote(element, exportImgCanvas);
                        break;
                    }
                    // case 'pen-segment': {
                    // 	drawPenSegment(element);
                    // 	break;
                    // }
                    default:
                        break;
                }
            }
        }

        // 3. LINES |==| RELATED LINES AND MOVING ELEMENT'S LINES
        for (let i = 0; i < relatedLinesIds.length; i++) {
            const id = relatedLinesIds[i];
            const line = linesRef.current[id];

            if (!line || !visibleLinesIdsRef.current.includes(line.id)) {
                // if (!line) {
                continue;
            }

            drawCreatedLine(line, exportImgCanvas);
        }

        // 4. ELEMENTS |==| RELATED ELEMENTS
        for (let i = 0; i < relatedElementsIds.length; i++) {
            const id = relatedElementsIds[i];
            const element = elementsValue[id];

            if (!element) {
                continue;
            }

            switch (element.type) {
                case 'table': {
                    drawTable(element, exportImgCanvas);
                    break;
                }
                case 'note': {
                    drawNote(element, exportImgCanvas);
                    break;
                }
                // case 'pen-segment': {
                // 	drawPenSegment(element, exportImgCanvas);
                // 	break;
                // }
                default:
                    break;
            }
        }

        // 7. LINES |==| HOVERED LINE
        // if (hoveredLineIdRef.current) {
        // 	const line = linesRef.current[hoveredLineIdRef.current];

        // 	if (line) {
        // 		drawCreatedLine(line, exportImgCanvas);
        // 	}
        // }

        // 7. ELEMENTS |==| HOVERED ELEMENT
        if (hoveredElementIdRef.current &&
            !focusedAndMovingElementsIds.includes(hoveredElementIdRef.current) &&
            (!isBtnNewAreaEnabled ||
                !newAreaPropsRef?.current?.selectedElements?.includes(hoveredElementIdRef.current)
            ) &&
            !isResizingArea &&
            !isBtnExportImageEnabled &&
            !isBtnPenEnabled
        ) {
            const element = elementsValue[hoveredElementIdRef.current];

            switch (element?.type) {
                case 'table': {
                    drawTable(element, exportImgCanvas);
                    break;
                }
                case 'note': {
                    drawNote(element, exportImgCanvas);
                    break;
                }
                // case 'pen-segment': {
                // 	drawPenSegment(element, exportImgCanvas);
                // 	break;
                // }
                default:
                    break;
            }

            // 7. LINES |==| HOVERED LINE
        } else if (
            hoveredLineIdRef.current &&
            hoveredLineIdRef.current !== focusedLineIdRef.current &&
            !isBtnExportImageEnabled &&
            !isBtnPenEnabled &&
            !isResizingArea
        ) {
            const line = linesRef.current[hoveredLineIdRef.current];

            if (line) {
                drawCreatedLine(line, exportImgCanvas);
            }
        }

        // 5. ELEMENTS |==| FOCUSED ELEMENTS AND MOVING ELEMENT
        for (let i = 0; i < focusedAndMovingElementsIds.length; i++) {
            const id = focusedAndMovingElementsIds[i];
            const element = elementsValue[id];

            if (
                !element ||
                (isBtnNewAreaEnabled &&
                    newAreaPropsRef?.current?.selectedElements?.includes(
                        element.id
                    ))
            ) {
                continue;
            }

            switch (element.type) {
                case 'table': {
                    drawTable(element, exportImgCanvas);
                    break;
                }
                case 'note': {
                    drawNote(element, exportImgCanvas);
                    break;
                }
                // case 'pen-segment': {
                // 	drawPenSegment(element, exportImgCanvas);
                // 	break;
                // }
                default:
                    break;
            }
        }

        if (
            isBtnNewAreaEnabled &&
            newAreaPropsRef?.current?.selectedElements?.length
        ) {
            // 5. ELEMENTS |==| FOCUSED ELEMENTS AND MOVING ELEMENT
            for (
                let i = 0;
                i < newAreaPropsRef?.current?.selectedElements.length;
                i++
            ) {
                const id = newAreaPropsRef?.current?.selectedElements[i];
                const element = elementsValue[id];

                if (!element) {
                    continue;
                }

                switch (element.type) {
                    case 'table': {
                        drawTable(element, exportImgCanvas);
                        break;
                    }
                    case 'note': {
                        drawNote(element, exportImgCanvas);
                        break;
                    }
                    // case 'pen-segment': {
                    // 	drawPenSegment(element, exportImgCanvas);
                    // 	break;
                    // }
                    default:
                        break;
                }
            }
        }

        // 6. LINES |==| FOCUSED LINE
        if (focusedLineIdRef.current) {
            const line = linesRef.current[focusedLineIdRef.current];

            if (line) {
                drawCreatedLine(line, exportImgCanvas);
            }
        }

        // 7. PEN SEGMENTS
        for (const key in elementsValue) {
            const element = elementsValue[key];

            if (!element) {
                continue;
            }

            // if (
            // 	!focusedElementsIds?.includes(element.id) &&
            // 	!relatedElementsIds?.includes(element.id) &&
            // 	!focusedAndMovingElementsIds?.includes(element.id) &&
            // 	(!isBtnNewAreaEnabled || !newAreaPropsRef?.current?.selectedElements?.includes(element.id))
            // ) {
            switch (element.type) {
                // case 'table': {
                // 	drawTable(element, exportImgCanvas);
                // 	break;
                // }
                // case 'note': {
                // 	drawNote(element, exportImgCanvas);
                // 	break;
                // }
                case 'pen-segment': {
                    drawPenSegment(element, exportImgCanvas);
                    break;
                }
                default:
                    break;
            }
            // }
        }

        // 8. NEW TEMPORARY AREA
        if (isBtnNewAreaEnabled && newAreaPropsRef.current) {
            const area = {
                id: 'temporary_area_id',
                x: newAreaPropsRef.current.startX,
                y: newAreaPropsRef.current.startY,
                width:
                    newAreaPropsRef.current.endX -
                    newAreaPropsRef.current.startX,
                height:
                    newAreaPropsRef.current.endY -
                    newAreaPropsRef.current.startY,
                value: {
                    // border: 'dashes',
                    border: 'solid',
                    color: 'blue',
                },
            };

            drawArea(area, exportImgCanvas);
        }

        // 8. NEW TEMPORARY LINE
        if (lineFromRef.current?.elementId && lineToRef.current) {
            drawNewLine();
        }

        const isFocusedAreaMoving =
            clickedAreaIdRef.current &&
            focusedAreaIdRef.current === clickedAreaIdRef.current;
        // 9. MOUSE SELECTION BOX
        if (
            !isCursorHand &&
            !isBtnNewLineEnabled &&
            !isBtnNewAreaEnabled &&
            isSelectionClickedRef.current &&
            !isFocusedAreaMoving
        ) {
            drawSelectionBox();
        }

        // 10. EXPORT IMAGE BOX
        if (isBtnExportImageEnabled && !isExportImageClickedRef.current) {
            drawExportImageBox();
        }

        // 10. PEN
        if (
            newPenSegmentRef.current.isPressed &&
            newPenSegmentRef.current.points.length
        ) {
            drawNewPenSegment();
        }
        // 10. PEN CURSOR
        if (isBtnPenEnabled && newPenCursorPointRef.current) {
            drawNewPenCursor();
        }

        if (alignmentLinesRef.current && viewMode === 'full') {
            const ctx = contextRef.current;

            // Малюємо лінії вирівнювання
            ctx.strokeStyle = '#5f88ff';
            ctx.strokeStyle = 'rgba(119, 160, 255, 1)';
            ctx.lineWidth = 0.9 / scaleRef.current;

            const dashLine = 5.5 / scaleRef.current;
            const dashSpace = 5 / scaleRef.current;
            ctx.setLineDash([dashLine, dashSpace]); // Маленький штрихпунктир

            alignmentLinesRef.current.forEach((line) => {
                ctx.beginPath();
                ctx.moveTo(line.x1, line.y1);
                ctx.lineTo(line.x2, line.y2);
                ctx.stroke();
            });

            ctx.setLineDash([]); // Скидаємо налаштування лінії
        }

        if (isBtnSearchEnabled) {
            const ctx = contextRef.current;
            const startX = (0 - originXYRef.current.x) / scaleRef.current;
            const startY = (0 - originXYRef.current.y) / scaleRef.current;

            const width = canvas.width / scaleRef.current;
            const height = canvas.height / scaleRef.current;

            ctx.fillStyle = 'rgba(255, 255, 255, 0.075)'; // Біле затемнення з прозорістю
            ctx.fillRect(startX, startY, width, height);
        }




        if (hoveredElementIdHeaderRef.current && !isMovingElementId) {
            const elementId = hoveredElementIdHeaderRef.current;
            const element = elementsValue[elementId];

            if (element && (element.value.description || element.value.isTableWithIndexes)) {
                drawTableTooltip(element);
            }
        }

        if (hoveredElementIdRef.current && hoveredColumnIdRef.current && !isMovingElementId) {
            const element = elementsValue[hoveredElementIdRef.current];

            if (element && element.type === 'table') {
                const column = element.value.columns[hoveredColumnIdRef.current];

                if (column && (
                    column.description || column.defaultValue || column.enumValues || column.generatedValue || column.checkValue
                )) {
                    drawColumnTooltip(element, column, hoveredColumnIndexRef.current);
                }
            }

        }



        context.restore();
    }, [
        elementsRedux,
        focusedElementsIds,
        isCursorHand,
        drawTable,
        drawSelectionBox,
        isBtnRedGreenViewEnabled,
        focusedInsideElementState,
        isBtnSearchEnabled,
        focusedGroupElementsIds,
        focusedGroupAreasIds,
    ]);

    useEffect(() => {
        const handleMinimapWheel = (event) => {
            event.preventDefault();

            calculateMinimapZoomNumbers();

            const mouseX = event.clientX;
            const mouseY = event.clientY;

            // // previous zoom
            // const wheel = event.deltaY < 0 ? 1 : -1;
            // const ZOOM_FACTOR = 0.15;
            // const zoom = Math.exp(wheel * ZOOM_FACTOR);
            // const newScale = scale * zoom;

            let delta = Math.max(-600, Math.min(600, event.deltaY));

            // const zoomIntensity = 0.001;
            let zoomIntensity = 0.001;

            if (scaleRef.current <= 1.1 && scaleRef.current > 0.65) {
                zoomIntensity = 0.001;
                delta = Math.max(-500, Math.min(500, event.deltaY));
            } else if (scaleRef.current <= 0.65 && scaleRef.current > 0.12) {
                // zoom in
                if (event.deltaY < 0) {
                    delta = Math.max(-600, Math.min(600, event.deltaY));
                    zoomIntensity = 0.0012;

                    // zoom out
                } else {
                    delta = Math.max(-450, Math.min(450, event.deltaY));
                    zoomIntensity = 0.0012;
                }
            } else if (scaleRef.current <= 0.12) {
                // zoom in
                if (event.deltaY < 0) {
                    delta = Math.max(-350, Math.min(350, event.deltaY));
                    zoomIntensity = 0.0025;

                    // zoom out
                } else {
                    delta = Math.max(-350, Math.min(350, event.deltaY));
                    zoomIntensity = 0.0015;
                }
            }

            const newScale = scaleRef.current * (1 - delta * zoomIntensity);
            // const newScale = scaleRef.current + event.deltaY * -0.001; // COEF should be dinamic changed on edge values(on min and max values) 			// const COEF = -0.002 or -0.0025 or -0.001;

            const clampedScale = Math.min(Math.max(newScale, 0.045), 4.05);

            const zoomFactor = clampedScale / scaleRef.current;

            // const newOriginX = (originXYRef.current.x - mouseX) * zoomFactor + mouseX;
            // const newOriginY = (originXYRef.current.y - mouseY) * zoomFactor + mouseY;

            let newOriginX = 0;
            let newOriginY = 0;

            const zoom = minimapZoomRef.current?.zoom || 1;
            // minimapZoomRef.current = {
            // 	zoom: newZoom,
            // 	originX: newOriginX,
            // 	originY: newOriginY,
            // };

            const mainCanvas = ref.current;
            const minimap = minimapRef.current.dynamicCanvas;

            const minimapWidth = minimap.width;
            const minimapHeight = minimap.height;
            let viewportWidth = (mainCanvas.width * zoom) / scaleRef.current;
            let viewportHeight = (mainCanvas.height * zoom) / scaleRef.current;

            // Отримуємо розташування мінімапи на екрані
            const minimapRect = minimap.getBoundingClientRect();

            // Вираховуємо координати кліку відносно мінімапи
            let relativeX = mouseX - minimapRect.left;
            let relativeY = mouseY - minimapRect.top;

            relativeX = relativeX - viewportWidth / 2;
            relativeY = relativeY - viewportHeight / 2;

            relativeX = Math.min(
                Math.max(1, relativeX),
                minimapWidth - viewportWidth - 1
            );
            relativeY = Math.min(
                Math.max(2, relativeY),
                minimapHeight - viewportHeight - 1
            );

            if (viewportWidth >= minimapWidth) {
                const diff = (viewportWidth - minimapWidth) / 2;
                relativeX = 1 - diff;
            }

            if (viewportHeight >= minimapHeight) {
                const diff = (viewportHeight - minimapHeight) / 2;
                relativeY = 2 - diff;
            }

            function minimapToCanvasCoordinates(minimapX, minimapY) {
                const realOriginX =
                    minimapZoomRef.current.originX /
                    minimapZoomRef.current.zoom;
                const realX = minimapX / minimapZoomRef.current.zoom;

                const realOriginY =
                    minimapZoomRef.current.originY /
                    minimapZoomRef.current.zoom;
                const realY = minimapY / minimapZoomRef.current.zoom;

                return {
                    x: (realOriginX - realX) * scaleRef.current,
                    y: (realOriginY - realY) * scaleRef.current,
                };
            }

            const canvasPoint = minimapToCanvasCoordinates(
                relativeX,
                relativeY
            );

            newOriginX = canvasPoint.x;
            newOriginY = canvasPoint.y;

            // ---------------------------------------------------------------
            // ---------------------------------------------------------------
            let newRelativeX = mouseX - minimapRect.left;
            let newRelativeY = mouseY - minimapRect.top;

            const screenZoomFactorWidth = window.innerWidth / minimap.width;
            const screenZoomFactorHeight = window.innerHeight / minimap.height;

            const newMouseX = newRelativeX * screenZoomFactorWidth;
            const newMouseY = newRelativeY * screenZoomFactorHeight;

            newOriginX =
                (originXYRef.current.x - newMouseX) * zoomFactor + newMouseX;
            newOriginY =
                (originXYRef.current.y - newMouseY) * zoomFactor + newMouseY;

            // ---------------------------------------------------------------
            // ---------------------------------------------------------------

            scaleRef.current = clampedScale;

            originXYRef.current = { x: newOriginX, y: newOriginY };
            calculateAndSetScaleNumbers(clampedScale);

            syncScaleToReduxRef.current(clampedScale, newOriginX, newOriginY);

            if (!isScaling) {
                setIsScaling(true);
            }
            clearTimeout(scalingTimerRef.current);
            scalingTimerRef.current = setTimeout(() => {
                setIsScaling(false);
            }, 200);

            calculateVisibleElementsAndLines();

            draw();
        };

        const handleMinimapMouseDown = (event) => {
            if (!minimapRef.current || !minimapRef.current.dynamicCanvas) {
                return;
            }
            event.preventDefault();

            // const minimap = minimapRef.current.dynamicCanvas;

            // // Отримуємо координати кліку миші відносно документу
            // const mouseX = event.clientX;
            // const mouseY = event.clientY;

            // Отримуємо розташування мінімапи на екрані
            // const minimapRect = minimap.getBoundingClientRect();

            // // Вираховуємо координати кліку відносно мінімапи
            // const relativeX = mouseX - minimapRect.left;
            // const relativeY = mouseY - minimapRect.top;

            // minimapClickedXYRef.current = { x: relativeX, y: relativeY };

            // draw();

            const zoom = minimapZoomRef.current?.zoom || 1;
            // minimapZoomRef.current = {
            // 	zoom: newZoom,
            // 	originX: newOriginX,
            // 	originY: newOriginY,
            // };

            const mainCanvas = ref.current;
            const minimap = minimapRef.current.dynamicCanvas;

            const minimapWidth = minimap.width;
            const minimapHeight = minimap.height;
            let viewportWidth = (mainCanvas.width * zoom) / scaleRef.current;
            let viewportHeight = (mainCanvas.height * zoom) / scaleRef.current;

            event.preventDefault();

            // Отримуємо координати кліку миші відносно документу
            const mouseX = event.clientX;
            const mouseY = event.clientY;

            // Отримуємо розташування мінімапи на екрані
            const minimapRect = minimap.getBoundingClientRect();

            // Вираховуємо координати кліку відносно мінімапи
            let relativeX = mouseX - minimapRect.left;
            let relativeY = mouseY - minimapRect.top;

            relativeX = relativeX - viewportWidth / 2;
            relativeY = relativeY - viewportHeight / 2;

            relativeX = Math.min(
                Math.max(1, relativeX),
                minimapWidth - viewportWidth - 1
            );
            relativeY = Math.min(
                Math.max(2, relativeY),
                minimapHeight - viewportHeight - 1
            );

            if (viewportWidth >= minimapWidth) {
                const diff = (viewportWidth - minimapWidth) / 2;
                relativeX = 1 - diff;
            }

            if (viewportHeight >= minimapHeight) {
                const diff = (viewportHeight - minimapHeight) / 2;
                relativeY = 2 - diff;
            }

            minimapClickedXYRef.current = { x: relativeX, y: relativeY };

            function minimapToCanvasCoordinates(minimapX, minimapY) {
                const realOriginX =
                    minimapZoomRef.current.originX /
                    minimapZoomRef.current.zoom;
                const realX = minimapX / minimapZoomRef.current.zoom;

                const realOriginY =
                    minimapZoomRef.current.originY /
                    minimapZoomRef.current.zoom;
                const realY = minimapY / minimapZoomRef.current.zoom;

                return {
                    x: (realOriginX - realX) * scaleRef.current,
                    y: (realOriginY - realY) * scaleRef.current,
                };
            }

            const canvasPoint = minimapToCanvasCoordinates(
                relativeX,
                relativeY
            );

            const newOriginX = canvasPoint.x; //originXYRef.current.x + dx;
            const newOriginY = canvasPoint.y; //originXYRef.current.y + dy;

            // update board originXY positions
            originXYRef.current = { x: newOriginX, y: newOriginY };
            syncOriginXYToReduxRef.current(newOriginX, newOriginY);

            calculateVisibleElementsAndLines();

            // set is board moving
            if (!isBoardMoving) {
                setIsBoardMoving(true);
                setIsBoardMovingStarted(true);
            }

            // set timeout on board moving
            clearTimeout(boardMovingTimerRef.current);
            boardMovingTimerRef.current = setTimeout(() => {
                setIsBoardMoving(false);
            }, 200);

            draw();
        };

        const handleMinimapMouseMove = (event) => {
            if (
                !ref.current ||
                !minimapRef.current ||
                !minimapRef.current.dynamicCanvas ||
                !minimapClickedXYRef.current ||
                !minimapZoomRef.current
            ) {
                return;
            }

            const zoom = minimapZoomRef.current?.zoom || 1;
            // minimapZoomRef.current = {
            // 	zoom: newZoom,
            // 	originX: newOriginX,
            // 	originY: newOriginY,
            // };

            const mainCanvas = ref.current;
            const minimap = minimapRef.current.dynamicCanvas;

            const minimapWidth = minimap.width;
            const minimapHeight = minimap.height;
            let viewportWidth = (mainCanvas.width * zoom) / scaleRef.current;
            let viewportHeight = (mainCanvas.height * zoom) / scaleRef.current;

            event.preventDefault();

            // Отримуємо координати кліку миші відносно документу
            const mouseX = event.clientX;
            const mouseY = event.clientY;

            // Отримуємо розташування мінімапи на екрані
            const minimapRect = minimap.getBoundingClientRect();

            // Вираховуємо координати кліку відносно мінімапи
            let relativeX = mouseX - minimapRect.left;
            let relativeY = mouseY - minimapRect.top;

            relativeX = relativeX - viewportWidth / 2;
            relativeY = relativeY - viewportHeight / 2;

            relativeX = Math.min(
                Math.max(1, relativeX),
                minimapWidth - viewportWidth - 1
            );
            relativeY = Math.min(
                Math.max(2, relativeY),
                minimapHeight - viewportHeight - 1
            );

            if (viewportWidth >= minimapWidth) {
                const diff = (viewportWidth - minimapWidth) / 2;
                relativeX = 1 - diff;
            }

            if (viewportHeight >= minimapHeight) {
                const diff = (viewportHeight - minimapHeight) / 2;
                relativeY = 2 - diff;
            }

            function minimapToCanvasCoordinates(minimapX, minimapY) {
                const realOriginX =
                    minimapZoomRef.current.originX /
                    minimapZoomRef.current.zoom;
                const realX = minimapX / minimapZoomRef.current.zoom;

                const realOriginY =
                    minimapZoomRef.current.originY /
                    minimapZoomRef.current.zoom;
                const realY = minimapY / minimapZoomRef.current.zoom;

                return {
                    x: (realOriginX - realX) * scaleRef.current,
                    y: (realOriginY - realY) * scaleRef.current,
                };
            }

            const canvasPoint = minimapToCanvasCoordinates(
                relativeX,
                relativeY
            );

            const newOriginX = canvasPoint.x; //originXYRef.current.x + dx;
            const newOriginY = canvasPoint.y; //originXYRef.current.y + dy;

            // update board originXY positions
            originXYRef.current = { x: newOriginX, y: newOriginY };
            syncOriginXYToReduxRef.current(newOriginX, newOriginY);

            calculateVisibleElementsAndLines();

            // set is board moving
            if (!isBoardMoving) {
                setIsBoardMoving(true);
                setIsBoardMovingStarted(true);
            }

            // set timeout on board moving
            clearTimeout(boardMovingTimerRef.current);
            boardMovingTimerRef.current = setTimeout(() => {
                setIsBoardMoving(false);
            }, 200);

            draw();
        };

        const handleDocumnetMouseUp = () => {
            if (minimapClickedXYRef.current) {
                minimapClickedXYRef.current = null;
                draw();
            }
        };

        if (minimapRef.current && minimapRef.current.dynamicCanvas) {
            minimapRef.current.dynamicCanvas.addEventListener(
                'wheel',
                handleMinimapWheel
            );
            minimapRef.current.dynamicCanvas.addEventListener(
                'mousedown',
                handleMinimapMouseDown
            );
            document.addEventListener('mousemove', handleMinimapMouseMove);
            // minimapRef.current.dynamicCanvas.addEventListener('pointerdown', handleMinimapMouseDown);
            // document.addEventListener('pointermove', handleMinimapMouseMove);

            document.addEventListener('mouseup', handleDocumnetMouseUp);
        }

        return () => {
            if (minimapRef.current && minimapRef.current.dynamicCanvas) {
                minimapRef.current.dynamicCanvas.removeEventListener(
                    'wheel',
                    handleMinimapWheel
                );
                minimapRef.current.dynamicCanvas.removeEventListener(
                    'mousedown',
                    handleMinimapMouseDown
                );
                document.removeEventListener(
                    'mousemove',
                    handleMinimapMouseMove
                );
                document.removeEventListener('mouseup', handleDocumnetMouseUp);
            }
        };
    }, [
        minimapRef,
        dispatch,
        elementsRedux,
        draw,
        isScaling,
        isMovingElementId,
        isBoardMoving,
        isBoardMovingStarted,
        isCursorHand,
        isBtnNewTableClickEnabled,
        isBtnNewTableGrabEnabled,
        isBtnNewNoteClickEnabled,
        isBtnNewNoteGrabEnabled,
        isBtnNewLineEnabled,
        isBtnNewAreaEnabled,
        isBtnExportImageEnabled,
        isBtnRedGreenViewEnabled,
    ]);

    function drawMinimapViewPort(ctx, x, y, newZoom) {
        const mainCanvas = ref.current;

        // const scaleValue = canvasRef.current.width / scaleRef.current / staticCanvasRef.current.width;
        // const scaleValue = 20;
        // const scaleValue = 60;

        ctx.strokeStyle = 'white';

        const width = (mainCanvas.width * newZoom) / scaleRef.current;
        const height = (mainCanvas.height * newZoom) / scaleRef.current;

        ctx.beginPath();
        ctx.roundRect(x, y, width, height, 1);
        ctx.lineWidth = 1.5;
        ctx.stroke();
    }

    function drawMinimapTable(ctx, element) {
        const { x, y, width, height } = element;

        ctx.strokeStyle = '#a2adb6';
        ctx.fillStyle = '#a2adb6';

        ctx.beginPath();
        ctx.roundRect(x, y, width * 0.8, height, 1);
        ctx.stroke();
        ctx.fill();
    }

    function drawMinimapArea(ctx, area) {
        const {
            x,
            y,
            width,
            height,
            value: { color },
        } = area;

        // ctx.strokeStyle = area.value.color;
        ctx.fillStyle = area.value.color;






        // COLOR

        // default (grey)
        ctx.fillStyle = 'rgba(168, 170, 174, 0.1)';
        ctx.strokeStyle = '#a8aaae';

        ctx.fillStyle = 'rgba(175, 197, 240, 0.12)';

        if (color) {
            switch (color) {
                case 'blue': {
                    ctx.fillStyle = 'rgba(28, 134, 255, 0.15)';
                    ctx.strokeStyle = '#1c86ff';
                    break;
                }
                case 'green': {
                    // ctx.fillStyle = 'rgba(59, 173, 132, 0.1)';
                    ctx.strokeStyle = '#3bad84';

                    ctx.fillStyle = 'rgba(59, 173, 132, 0.75)';

                    ctx.fillStyle = 'rgba(6, 255, 151, 0.19)';
                    break;
                }
                case 'orange': {
                    ctx.fillStyle = 'rgba(255, 129, 88, 0.11)'; // 0.07 // 0.12
                    ctx.strokeStyle = '#ff8158';
                    break;
                }
                case 'yellow': {
                    ctx.fillStyle = 'rgba(225, 179, 93, 0.11)';
                    ctx.strokeStyle = '#e1b35d';
                    break;
                }
                case 'pink': {
                    ctx.fillStyle = 'rgba(225, 93, 137, 0.11)';
                    ctx.strokeStyle = '#e15d89';
                    break;
                }
                case 'purple': {
                    // ctx.fillStyle = 'rgba(139, 125, 235, 0.1)';
                    ctx.strokeStyle = '#8b7deb';

                    ctx.fillStyle = 'rgba(116, 84, 232, 0.19)';
                    break;
                }
                case 'violet': {
                    // ctx.fillStyle = 'rgba(235, 3, 235, 0.075)';
                    ctx.strokeStyle = '#eb03eb';

                    ctx.fillStyle = 'rgba(220, 97, 220, 0.19)';
                    break;
                }
                case 'red': {
                    ctx.fillStyle = 'rgba(255, 0, 0, 0.175)'; // 0.06 // 0.08
                    ctx.strokeStyle = '#e80362';

                    break;
                }
                case 'default': {
                    break;
                }
                default:
                    break;
            }
        }

        ctx.beginPath();
        ctx.roundRect(x, y, width, height, 1);
        // ctx.stroke();
        ctx.fill();
        // ctx.shadowColor = 'transparent';

        // ctx.fillStyle = '#39424c'; // canvas background
        // ctx.fillRect(x - scaleNumbersRef.current.outlineOffset, y - scaleNumbersRef.current.outlineOffset - 0.5, tableWidth + 2 * scaleNumbersRef.current.outlineOffset, tableHeight + 2 * scaleNumbersRef.current.outlineOffset + 1);
        // ctx.stroke();
    }

    function calculateMinimapZoomNumbers() {
        const staticCanvas = minimapRef.current.staticCanvas;

        const limits = canvasLimitsRef.current;
        const offset = 15;

        const elementsWidth = limits.maxX - limits.minX;
        const elementsHeight = limits.maxY - limits.minY;
        const canvasWidth = staticCanvas.width - 2 * offset;
        const canvasHeight = staticCanvas.height - 2 * offset;

        const zoomX = canvasWidth / elementsWidth;
        const zoomY = canvasHeight / elementsHeight;
        const newZoom = Math.min(Math.min(zoomX, zoomY)); //, INITIAL_SCALE_VALUE);

        const originX =
            (canvasWidth - elementsWidth * newZoom) / 2 -
            limits.minX * newZoom +
            offset;
        const originY =
            (canvasHeight - elementsHeight * newZoom) / 2 -
            limits.minY * newZoom +
            offset;

        minimapZoomRef.current = {
            zoom: newZoom,
            originX,
            originY,
        };
    }

    useEffect(() => {
        canvasLimitsRef.current = canvasLimits;
        calculateMinimapZoomNumbers();
    }, [canvasLimits]);

    const drawMinimapStatic = useCallback(
        (staticCanvas, staticContext) => {
            staticContext.clearRect(
                0,
                0,
                staticCanvas.width,
                staticCanvas.height
            );
            staticContext.save();
            staticContext.setTransform(
                minimapZoomRef.current.zoom,
                0,
                0,
                minimapZoomRef.current.zoom,
                minimapZoomRef.current.originX,
                minimapZoomRef.current.originY
            );

            // 1. AREAS |==| ALL
            for (const key in areasRef.current) {
                const area = areasRef.current[key];
                drawMinimapArea(staticContext, area);
            }

            // 2. ELEMENTS |==| ALL
            for (const key in elementsRef.current) {
                const element = elementsRef.current[key];

                switch (element.type) {
                    case 'table': {
                        drawMinimapTable(staticContext, element);
                        break;
                    }
                    case 'note': {
                        drawMinimapTable(staticContext, element);
                        break;
                    }
                    default:
                        break;
                }
            }

            staticContext.restore();

            // =================== START DYNAMIC DRAW ===================
            const viewportX =
                0 +
                minimapZoomRef.current.originX -
                (originXYRef.current.x / scaleRef.current) *
                minimapZoomRef.current.zoom;
            const viewportY =
                0 +
                minimapZoomRef.current.originY -
                (originXYRef.current.y / scaleRef.current) *
                minimapZoomRef.current.zoom;

            drawMinimapViewPort(
                staticContext,
                viewportX,
                viewportY,
                minimapZoomRef.current.zoom
            );
            // =================== END DYNAMIC DRAW ===================
        },
        [isBtnMinimapEnabled]
    );

    function unsetAllVariablesOnClick() {
        // unset all variables which was clicked on 'mousedown'
        if (alignmentLinesRef.current) {
            alignmentLinesRef.current = null;
        }

        if (clickedPointRef.current) {
            clickedPointRef.current = null;
        }

        if (isStartedLineState) {
            setIsStartedLineState(false);
        }

        // if (lineFromRef.current && (!isBtnNewLineEnabled || isLineCreated)) {
        // 	lineFromRef.current = null;
        // }

        // if (lineToRef.current && (!isBtnNewLineEnabled || isLineCreated)) {
        // 	lineToRef.current = null;
        // }

        if (clickedCircleElementIdRef.current) {
            clickedCircleElementIdRef.current = null;
        }

        if (clickedLineIdRef.current) {
            clickedLineIdRef.current = null;
        }

        if (clickedAreaIdRef.current) {
            clickedAreaIdRef.current = null;
        }

        if (clickedElementIdRef.current) {
            clickedElementIdRef.current = null;
        }
        if (clickedInsideElementRef.current) {
            clickedInsideElementRef.current = null;
        }

        if (isBoardClickedRef.current) {
            isBoardClickedRef.current = false;
        }
        if (isSelectionClickedRef.current) {
            isSelectionClickedRef.current = false;
        }
        if (isItemResizeClickedRef.current) {
            isItemResizeClickedRef.current = false;
        }

        if (isMovingElementId) {
            setIsMovingElementId(null);
        }

        if (isElementResizing) {
            setIsElementResizing(false);
        }

        if (isBoardMoving) {
            setIsBoardMoving(false);
        }
        if (isBoardMovingStarted) {
            setIsBoardMovingStarted(false);
        }

        if (exportImageBoxPropsRef.current.isPressed) {
            exportImageBoxPropsRef.current.isPressed = false;
        }

        if (exportImageBoxPropsRef.current.isOutsidePressed) {
            exportImageBoxPropsRef.current.isOutsidePressed = false;
        }

        if (exportImageBoxPropsRef.current.clickedResizeKey) {
            exportImageBoxPropsRef.current.clickedResizeKey = false;
        }

        if (selectedElementsIdsRef.current.length) {
            selectedElementsIdsRef.current = [];
        }
        if (selectedAreasIdsRef.current.length) {
            selectedAreasIdsRef.current = [];
        }


        if (isUnfocusElementsBeforeRef.current) {
            isUnfocusElementsBeforeRef.current = false;
        }
    }

    useEffect(() => {
        const canvas = ref.current;
        const context = canvas.getContext('2d');
        contextRef.current = context;
        let lastCallTime = 0;

        // const exportImgCanvas = exportImgCanvasRef.current;
        // const exportImgContext = exportImgCanvas.getContext('2d');
        // exportImgContextRef.current = exportImgContext;


        // if (isCursorHand) {
        // 	canvas.style.cursor = 'grab';
        // } else {
        // 	canvas.style.cursor = 'default';
        // }

        const handleWindowResize = () => {
            // const dpr = window.devicePixelRatio || 1;

            // // Отримуємо розміри контейнера канвасу
            // const rect = canvas.parentNode.getBoundingClientRect();

            // // Встановлюємо CSS-розміри
            // // canvas.style.width = rect.width + 'px';
            // // canvas.style.height = rect.height + 'px';
            // canvas.style.width = rect.width * dpr + 'px';
            // canvas.style.height = rect.height * dpr + 'px';

            // // Встановлюємо атрибути розмірів з урахуванням DPR
            // canvas.width = rect.width * dpr;
            // canvas.height = rect.height * dpr;

            // console.log('=================||||||||||||||||', window.innerWidth, window.innerHeight, dpr, rect.width, rect.height, rect.width * dpr, rect.height * dpr)
            // // Масштабуємо контекст
            // context.scale(dpr, dpr);
            // canvas.width = window.innerWidth;
            // canvas.height = window.innerHeight;

            calculateVisibleElementsAndLines();

            draw(); // Redraw all canvas after size change
        };
        const handleCanvasMouseDown = async (e) => {
            // e.preventDefault();
            const event = e.type === 'touchstart' ? e.changedTouches[0] : e;

            console.log('e.type', e.type)
            if (isBtnNewTableClickEnabled || isBtnNewNoteClickEnabled) {
                draw();
                return;
            }

            if (isBtnPenEnabled) {
                const mouseX =
                    (event.clientX - originXYRef.current.x) / scaleRef.current;
                const mouseY =
                    (event.clientY - originXYRef.current.y) / scaleRef.current;

                newPenSegmentRef.current.isPressed = true;
                newPenSegmentRef.current.points = [[mouseX, mouseY]];

                // newPenSegmentRef.current.points.push([mouseX, mouseY]);
                // newPenSegmentRef.current.points.push([event.clientX, event.clientY]);
                draw();

                return;
            }

            // get mouse coordinates
            const mouseX =
                (event.clientX - originXYRef.current.x) / scaleRef.current;
            const mouseY =
                (event.clientY - originXYRef.current.y) / scaleRef.current;

            // set initial coordinates for moving startXY & start(first) point of selection box
            startXYRef.current = { x: event.clientX, y: event.clientY };
            selectionStartXYRef.current = { x: mouseX, y: mouseY };
            selectionCurrentXYRef.current = { x: mouseX, y: mouseY };

            // check if press any button exept 'left mouse button' then return nothing
            if (event.button !== 0) {
                // right button click
                if (event.button === 2) {
                    isBoardClickedRef.current = true;
                    canvas.style.cursor = 'grabbing';
                }

                return;
            }

            if (isBtnNewAreaEnabled) {
                newAreaPropsRef.current = {
                    startX: mouseX,
                    startY: mouseY,
                    endX: mouseX,
                    endY: mouseY,
                };
                // draw();

                return;
            }

            if (isCursorHand) {
                isBoardClickedRef.current = true;
                canvas.style.cursor = 'grabbing';
                return;
            }

            // get values for business logic
            const [
                newClickedElementId,
                newClickedInsideElement,
                newClickedCircleElementId,
                newClickedPointElementId,
                newClickedPointInsideElementId,
                newClickedPointInsideElementIndex,
                newClickedPointDirection,
                newClickedLineId,
                isClickedInsideExportImageBox,
                newClickedAreaId,
            ] = getNewClickedElements(
                elementsRef.current,
                mouseX,
                mouseY,
                event.clientX,
                event.clientY
            );

            let [isItemResizeClicked, resizeKey] = isHoverOnResizeHandle(mouseX, mouseY);

            // BUSINESS LOGIC | CLICK MOUSE DOWN

            if (isBtnExportImageEnabled) {
                let clickedResizeKey = getHoveredResizeKeyOnExportImageBox(
                    mouseX,
                    mouseY
                );

                // is clicked on resize key
                if (clickedResizeKey) {
                    exportImageBoxPropsRef.current.clickedResizeKey =
                        clickedResizeKey;
                    // is clicked on 'inside box'
                } else if (isClickedInsideExportImageBox) {
                    exportImageBoxPropsRef.current.isPressed = true;
                    // is clicked on outside the box
                } else {
                    exportImageBoxPropsRef.current.isOutsidePressed = true;
                }

                // draw();
            } else if (newClickedPointElementId) {
                clickedPointRef.current = {
                    elementId: newClickedPointElementId,
                    insideElementId: newClickedPointInsideElementId,
                    insideElementIndex: newClickedPointInsideElementIndex,
                    direction: newClickedPointDirection,
                };

                // ------------
                setIsStartedLineState(true);

                // ------------
                const element = elementsRef.current[newClickedPointElementId];
                const elementType = element?.type;
                const elementWidth = element?.width;

                const insideElement = newClickedPointInsideElementId
                    ? elementsRef.current[newClickedPointInsideElementId]
                    : null;
                const insideElementIndex =
                    newClickedPointInsideElementIndex || null;
                const hasInsideElement = !!insideElement;

                lineFromRef.current = {
                    elementId: newClickedPointElementId,
                    insideElementId: newClickedPointInsideElementId,
                    // insideElementIndex: newClickedPointInsideElementIndex,
                    x: mouseX,
                    y: mouseY,

                    elementType,
                    elementWidth,
                    elementX: element.x,
                    elementY: element.y,
                    hasInsideElement,
                    insideElementIndex,
                };
                lineToRef.current = {
                    elementId: null,
                    insideElementId: null,
                    x: mouseX,
                    y: mouseY,
                };

                // click on circle to add new row
            } else if (newClickedCircleElementId) {
                clickedCircleElementIdRef.current = newClickedCircleElementId;

                // click on element
            } else if (newClickedElementId) {
                clickedElementIdRef.current = newClickedElementId;

                if (newClickedInsideElement) {
                    clickedInsideElementRef.current = {
                        ...newClickedInsideElement,
                    };
                }
                // click on element resize points
            } else if (isItemResizeClicked) {
                isItemResizeClickedRef.current = true;
                resizeKeyRef.current = resizeKey;

                // click on line
            } else if (newClickedLineId) {
                clickedLineIdRef.current = newClickedLineId;

                // } else if (newClickedAreaId) {
                // 	clickedAreaIdRef.current = newClickedAreaId;
            } else if (newClickedAreaId) {
                if (!isCursorHand) {
                    clickedAreaIdRef.current = newClickedAreaId;

                    if (newClickedAreaId !== focusedAreaIdRef.current) {
                        isSelectionClickedRef.current = true && !focusedGroupAreasIdsRef.current?.includes(newClickedAreaId);
                        selectedElementsIdsRef.current = [];
                        selectedAreasIdsRef.current = [];

                        isUnfocusElementsBeforeRef.current = focusedElementsIdsRef.current.length || focusedLineIdRef.current;

                        if (focusedInsideElementState) {
                            setFocusedInsideElementState(null);
                            syncFocusedInsideElementToReduxRef.current(null);
                        }
                        if (focusedElementsIdsRef.current.length) {
                            focusedElementsIdsRef.current = [];
                            focusedElementsRelatedPropsRef.current = getElementsRelatedProps([]);
                            syncFocusedElementsToReduxRef.current([]);
                        }
                        if (focusedLineIdRef.current) {
                            focusedLineIdRef.current = null;
                            focusedLineRelatedPropsRef.current = null;
                            syncFocusedLineToReduxRef.current(null);
                        }
                        if (focusedAreaIdRef.current) {
                            focusedAreaIdRef.current = null;
                            syncFocusedAreaToReduxRef.current(null);
                        }
                    }

                    if (!focusedGroupAreasIdsRef.current?.includes(newClickedAreaId)) {
                        if (focusedGroupAreasIdsRef.current.length) {
                            focusedGroupAreasIdsRef.current = [];
                            focusedGroupElementsIdsRef.current = [];
                            syncFocusedGroupToReduxRef.current([], []);
                        }
                    }
                }
                // click on board
            } else {
                if (!isCursorHand) {
                    isSelectionClickedRef.current = true;
                    selectedElementsIdsRef.current = [];
                    selectedAreasIdsRef.current = [];

                    // ----------------------------- (by selectedElementsIdsRef.current)
                    const hasFocusedInsideElementChanges = !!focusedInsideElementState;
                    if (hasFocusedInsideElementChanges) {
                        setFocusedInsideElementState(null);
                        syncFocusedInsideElementToReduxRef.current(null);
                    }
                    // set selected elements to focused elements
                    const hasFocusedElementsChanges = focusedElementsIdsRef.current.length;
                    if (hasFocusedElementsChanges) {
                        focusedElementsIdsRef.current = [];
                        focusedElementsRelatedPropsRef.current = getElementsRelatedProps([]);
                        syncFocusedElementsToReduxRef.current([]);
                    }
                    if (focusedLineIdRef.current) {
                        focusedLineIdRef.current = null;
                        focusedLineRelatedPropsRef.current = null;
                        syncFocusedLineToReduxRef.current(null);
                    }
                    if (focusedAreaIdRef.current) {
                        focusedAreaIdRef.current = null;
                        syncFocusedAreaToReduxRef.current(null);
                    }
                    if (focusedGroupAreasIdsRef.current.length) {
                        focusedGroupAreasIdsRef.current = [];
                        focusedGroupElementsIdsRef.current = [];
                        syncFocusedGroupToReduxRef.current([], []);

                        focusedElementsRelatedPropsRef.current = getElementsRelatedProps([]);

                        // draw();
                        console.log('ASASASAS 222')
                    }
                    // -----------------------------
                } else {
                    isBoardClickedRef.current = true;
                }
            }


            // BUSINESS LOGIC | CURSOR
            if (
                (isCursorHand && isBoardClickedRef.current) ||
                (isBtnExportImageEnabled &&
                    (exportImageBoxPropsRef.current.isPressed ||
                        exportImageBoxPropsRef.current.isOutsidePressed))
            ) {
                canvas.style.cursor = 'grabbing';
                // canvas.style.cursor = 'grab';
            } else if (clickedPointRef.current) {
                canvas.style.cursor = 'crosshair';
            }

            // update canvas

            console.log('ASASASAS 333')

            draw();
        };
        const handleDocumentMouseMove = (e) => {
            // e.preventDefault();
            const event = e.type === 'touchmove' ? e.changedTouches[0] : e;

            console.log('e.type', e.type)

            // inside mouse move in canvas root elements tree
            const isMouseInCanvas =
                ref.current && ref.current.contains(event.target);

            // TODO:
            // if (isBtnPenEnabled || Math.random() !== 12) {
            const mouseXForPen =
                (event.clientX - originXYRef.current.x) / scaleRef.current;
            const mouseYForPen =
                (event.clientY - originXYRef.current.y) / scaleRef.current;
            newPenCursorPointRef.current = { x: mouseXForPen, y: mouseYForPen };
            // }

            if (isBtnPenEnabled && newPenSegmentRef.current.isPressed) {
                const mouseX =
                    (event.clientX - originXYRef.current.x) / scaleRef.current;
                const mouseY =
                    (event.clientY - originXYRef.current.y) / scaleRef.current;

                newPenSegmentRef.current.points.push([mouseX, mouseY]);
                // newPenSegmentRef.current.points.push([event.clientX, event.clientY]);

                draw();
                return;
            }

            // is clicked on resize key
            if (exportImageBoxPropsRef.current.clickedResizeKey) {
                const clientDX = event.clientX - startXYRef.current.x;
                const clientDY = event.clientY - startXYRef.current.y;

                // get new sizes for resizing box
                let {
                    x: newClientDX,
                    y: newClientDY,
                    width: newClientWidth,
                    height: newClientHeight,
                } = handleResize(
                    exportImageBoxPropsRef.current.clickedResizeKey,
                    clientDX,
                    clientDY
                );

                // update new-clicked box position

                const { clientBoxWidth, clientBoxHeight } =
                    exportImageBoxPropsRef.current;

                const MIN_BOX_SIZE = 15;

                if (
                    clientBoxWidth + newClientWidth >= MIN_BOX_SIZE &&
                    clientBoxHeight + newClientHeight >= MIN_BOX_SIZE
                ) {
                    exportImageBoxPropsRef.current.clientX += newClientDX;
                    exportImageBoxPropsRef.current.clientY += newClientDY;
                    exportImageBoxPropsRef.current.clientBoxWidth +=
                        newClientWidth;
                    exportImageBoxPropsRef.current.clientBoxHeight +=
                        newClientHeight;

                    // draw();
                }

                // is clicked on 'inside box'
            } else if (exportImageBoxPropsRef.current.isPressed) {
                // move the export image box
                const dx = event.clientX - startXYRef.current.x;
                const dy = event.clientY - startXYRef.current.y;

                exportImageBoxPropsRef.current.clientX += dx;
                exportImageBoxPropsRef.current.clientY += dy;

                // draw();

                // is clicked on 'outside the box'
            } else if (exportImageBoxPropsRef.current.isOutsidePressed) {
                // just board moving
                const dx = event.clientX - startXYRef.current.x;
                const dy = event.clientY - startXYRef.current.y;

                const newOriginX = originXYRef.current.x + dx;
                const newOriginY = originXYRef.current.y + dy;

                // update board originXY positions
                originXYRef.current = { x: newOriginX, y: newOriginY };
                syncOriginXYToReduxRef.current(newOriginX, newOriginY);

                calculateVisibleElementsAndLines();

                // update canvas
                // draw();
            }

            if (isBtnNewTableClickEnabled) {
                // change cursor to 'new_table';
            }
            if (isBtnNewNoteClickEnabled) {
                // change cursor to 'new_note';
            }

            if (
                isBtnNewTableGrabEnabled &&
                isNewTableGrabStartedRef.current === false
            ) {
                isNewTableGrabStartedRef.current = true;
            }

            if (
                isBtnNewNoteGrabEnabled &&
                isNewNoteGrabStartedRef.current === false
            ) {
                isNewNoteGrabStartedRef.current = true;
            }

            // get mouse coordinates
            const mouseX =
                (event.clientX - originXYRef.current.x) / scaleRef.current;
            const mouseY =
                (event.clientY - originXYRef.current.y) / scaleRef.current;

            // set new value for current(second) point of selection box
            selectionCurrentXYRef.current = { x: mouseX, y: mouseY };

            // set new value for temporary new area
            if (isBtnNewAreaEnabled && newAreaPropsRef.current) {
                newAreaPropsRef.current.endX = mouseX;
                newAreaPropsRef.current.endY = mouseY;

                // draw();
            }

            // get values for business logic
            const [
                newHoveredElementId,
                newHoveredInsideElementId,
                newHoveredInsideElementIndex,
                newHoveredCircleAreaElementsIds,
                newHoveredCircleElementId,
                newHoveredPointInsideElementId,
                newHoveredPointDirection,
                newHoveredPointAreaInsideElementsIds,
                newHoveredLineId,
                newHoveredElementIdEmptyColumn,
                newHoveredAreaId,
                newHoveredElementIdHeader,
            ] = getNewHoveredElements(
                elementsRef.current,
                mouseX,
                mouseY,
                event.clientX,
                event.clientY
            );

            // BUSINESS LOGIC | CLICKING AND MOVING

            if (isBtnNewAreaEnabled) {
                if (newAreaPropsRef.current) {
                    const point1 = {
                        x: newAreaPropsRef.current.startX,
                        y: newAreaPropsRef.current.startY,
                    };
                    const point2 = {
                        x: newAreaPropsRef.current.endX,
                        y: newAreaPropsRef.current.endY,
                    };

                    // get selected elements by creating area
                    const selectedElements = getAreaSelectedElements(
                        point1,
                        point2,
                        elementsRef.current,
                        areasRef.current,
                        viewMode
                    );

                    newAreaPropsRef.current.selectedElements = selectedElements;

                    // check if mouse moving away on viewport / window sizes
                    // scrollIfNeeded(e);

                    const hasFocusedInsideElementChanges = !!focusedInsideElementState;
                    if (hasFocusedInsideElementChanges) {
                        setFocusedInsideElementState(null);
                        // setColumnNameState('');
                        // setColumnTypeState('');

                        syncFocusedInsideElementToReduxRef.current(null);
                        // draw();
                    }
                    // set selected elements to focused elements
                    const hasFocusedElementsChanges = !!focusedElementsIdsRef.current.length;

                    if (hasFocusedElementsChanges) {
                        focusedElementsIdsRef.current = [];
                        focusedElementsRelatedPropsRef.current = null;
                        syncFocusedElementsToReduxRef.current([]);
                    }

                    if (focusedGroupAreasIdsRef.current.length) {
                        focusedGroupAreasIdsRef.current = [];
                        focusedGroupElementsIdsRef.current = [];
                        syncFocusedGroupToReduxRef.current([], []);
                    }

                    // update canvas
                    // draw();
                }
            } else if (isBtnNewLineEnabled) {
                if (!isStartedLineState) {
                    setIsStartedLineState(true);
                }

                // 1. line has no 'from' point
                if (!lineFromRef.current || !lineFromRef.current.isClicked) {
                    if (newHoveredElementId && newHoveredInsideElementId) {
                        const element =
                            elementsRef.current[newHoveredElementId];
                        const elementType = element?.type;
                        const elementWidth = element?.width;

                        lineFromRef.current = {
                            elementId: newHoveredElementId,
                            insideElementId: newHoveredInsideElementId,

                            hasInsideElement: !!newHoveredInsideElementId,
                            insideElementIndex: newHoveredInsideElementIndex,

                            x: mouseX,
                            y: mouseY,

                            elementType,
                            elementWidth,
                            elementX: element.x,
                            elementY: element.y,
                        };
                    } else {
                        lineFromRef.current = null;
                    }

                    // 2. line has 'from' point
                } else {
                    if (newHoveredElementId) {
                        const element =
                            elementsRef.current[newHoveredElementId];
                        const elementType = element?.type;
                        const elementWidth = element?.width;

                        lineToRef.current = {
                            elementId: newHoveredElementId,
                            insideElementId: newHoveredInsideElementId,

                            hasInsideElement: !!newHoveredInsideElementId,
                            insideElementIndex: newHoveredInsideElementIndex,

                            x: mouseX,
                            y: mouseY,

                            elementType,
                            elementWidth,
                            elementX: element.x,
                            elementY: element.y,
                        };
                    } else {
                        lineToRef.current = {
                            elementId: null,
                            insideElementId: null,
                            x: mouseX,
                            y: mouseY,
                        };
                    }
                }

                // draw();

                // 0. click on column's relation point
            } else if (clickedPointRef.current?.elementId) {
                const element = elementsRef.current[newHoveredElementId];
                const elementType = element?.type;
                const elementWidth = element?.width;

                const insideElement = newHoveredInsideElementId
                    ? elementsRef.current[newHoveredInsideElementId]
                    : null;
                const insideElementIndex = newHoveredInsideElementIndex || null;
                const hasInsideElement = !!insideElement;

                lineToRef.current = {
                    elementId: newHoveredElementId,
                    insideElementId: newHoveredInsideElementId,
                    // insideElementIndex: newHoveredInsideElementIndex,
                    x: mouseX,
                    y: mouseY,

                    elementType,
                    elementWidth,
                    elementX: element?.x,
                    elementY: element?.y,
                    hasInsideElement,
                    insideElementIndex,
                };

                // draw();

                // move with element
            } else if (clickedElementIdRef.current) {
                const dx =
                    (event.clientX - startXYRef.current.x) / scaleRef.current;
                const dy =
                    (event.clientY - startXYRef.current.y) / scaleRef.current;

                // ALIGNMENT LINES SECTION

                const isMultiElementsMove =
                    focusedElementsIdsRef.current.includes(
                        clickedElementIdRef.current
                    ) && focusedElementsIdsRef.current.length > 1;
                if (!isMultiElementsMove && scaleRef.current >= 0.26) {
                    const [alignmentLines, movedElementNewXY] =
                        getAlignmentLines(clickedElementIdRef.current);

                    if (movedElementNewXY) {
                        // if closer point x is the same as selected before
                        if (
                            typeof movedElementNewXY.x === 'number' &&
                            typeof alignmentLinePropsRef.current.x ===
                            'number' &&
                            movedElementNewXY.x ===
                            alignmentLinePropsRef.current.x
                        ) {
                            const moveDX = Math.abs(
                                e.clientX - alignmentLinePropsRef.current.mouseX
                            );

                            if (moveDX < 4) {
                                elementsRef.current[
                                    clickedElementIdRef.current
                                ].x = movedElementNewXY.x;
                                alignmentLinePropsRef.current.x =
                                    movedElementNewXY.x;
                                alignmentLinePropsRef.current.elementIdByX =
                                    clickedElementIdRef.current;
                            } else {
                                alignmentLinePropsRef.current.elementIdByX =
                                    null;
                            }
                        } else if (typeof movedElementNewXY.x === 'number') {
                            elementsRef.current[clickedElementIdRef.current].x =
                                movedElementNewXY.x;
                            alignmentLinePropsRef.current.x =
                                movedElementNewXY.x;
                            alignmentLinePropsRef.current.mouseX =
                                event.clientX;
                            alignmentLinePropsRef.current.elementIdByX =
                                clickedElementIdRef.current;
                        }

                        // if closer point y is the same as selected before
                        if (
                            typeof movedElementNewXY.y === 'number' &&
                            typeof alignmentLinePropsRef.current.y ===
                            'number' &&
                            movedElementNewXY.y ===
                            alignmentLinePropsRef.current.y
                        ) {
                            const moveDY = Math.abs(
                                e.clientY - alignmentLinePropsRef.current.mouseY
                            );

                            if (moveDY < 4) {
                                elementsRef.current[
                                    clickedElementIdRef.current
                                ].y = movedElementNewXY.y;
                                alignmentLinePropsRef.current.y =
                                    movedElementNewXY.y;
                                alignmentLinePropsRef.current.elementIdByY =
                                    clickedElementIdRef.current;
                            } else {
                                alignmentLinePropsRef.current.elementIdByY =
                                    null;
                            }
                        } else if (typeof movedElementNewXY.y === 'number') {
                            elementsRef.current[clickedElementIdRef.current].y =
                                movedElementNewXY.y;
                            alignmentLinePropsRef.current.y =
                                movedElementNewXY.y;
                            alignmentLinePropsRef.current.mouseY =
                                event.clientY;
                            alignmentLinePropsRef.current.elementIdByY =
                                clickedElementIdRef.current;
                        }
                    }
                    alignmentLinesRef.current = alignmentLines;
                }

                // set is element moving
                setIsMovingElementId(clickedElementIdRef.current);

                // move with focused element
                if (
                    focusedElementsIdsRef.current.includes(
                        clickedElementIdRef.current
                    )
                ) {
                    // update focused elements positions
                    focusedElementsIdsRef.current.forEach((elementId) => {
                        if (
                            elementId !==
                            alignmentLinePropsRef.current.elementIdByX
                        ) {
                            elementsRef.current[elementId].x += dx;
                        }
                        if (
                            elementId !==
                            alignmentLinePropsRef.current.elementIdByY
                        ) {
                            elementsRef.current[elementId].y += dy;
                        }

                        if (
                            elementsRef.current[elementId].type === 'pen-segment'
                        ) {
                            elementsRef.current[elementId].translateX += dx;
                            elementsRef.current[elementId].translateY += dy;
                        }

                        elementsRef.current[elementId].isChangedXY = true;

                        // ------------------------- check element's areaId

                        const element = elementsRef.current[elementId];
                        const newAreaIdForElement = getNewAreaIdForElement(
                            areasRef.current,
                            elementId
                        );

                        if (newAreaIdForElement) {
                            if (element.value.areaId !== newAreaIdForElement) {
                                // 1. if element.value.areaId is not null, remove nestedElementId from area
                                // 2. set element's areaId to new areaId
                                // 3. set newArea's nestedElementsIds with new element.id

                                elementsRef.current[elementId].value.areaId =
                                    newAreaIdForElement;
                                elementsRef.current[
                                    elementId
                                ].isChangedAreaId = true;
                                // dispatch(setNewAreaIdForElement(element.id, newAreaIdForElement));

                                resizeElementPropsRef.current = { elementId, areaId: newAreaIdForElement };
                            }
                        } else {
                            if (
                                element.value.areaId &&
                                areasRef.current[element.value.areaId]
                            ) {
                                // 1. remove nestedElementId from area
                                // 2. remove areaId from element

                                const prevNestedElementsIds =
                                    areasRef.current[element.value.areaId].value
                                        .nestedElementsIds;
                                areasRef.current[
                                    element.value.areaId
                                ].value.nestedElementsIds =
                                    prevNestedElementsIds.filter(
                                        (id) => id !== elementId
                                    );

                                elementsRef.current[elementId].value.areaId =
                                    null;
                                elementsRef.current[
                                    elementId
                                ].isChangedAreaId = true;

                                // dispatch(setNewAreaIdForElement(element.id, null));
                                resizeElementPropsRef.current = null;
                            }
                        }

                        // -------------------------
                    });

                    updateLinesByElementsIds(focusedElementsIdsRef.current);

                    // draw();
                    setElementsHasChanges(true);

                    // move with new-clicked element
                } else {
                    // update new-clicked element position
                    // elementsRef.current[clickedElementIdRef.current].x += dx;
                    // elementsRef.current[clickedElementIdRef.current].y += dy;
                    if (
                        clickedElementIdRef.current !==
                        alignmentLinePropsRef.current.elementIdByX
                    ) {
                        elementsRef.current[clickedElementIdRef.current].x +=
                            dx;
                    }
                    if (
                        clickedElementIdRef.current !==
                        alignmentLinePropsRef.current.elementIdByY
                    ) {
                        elementsRef.current[clickedElementIdRef.current].y +=
                            dy;
                    }

                    if (
                        elementsRef.current[clickedElementIdRef.current].type ===
                        'pen-segment'
                    ) {
                        elementsRef.current[
                            clickedElementIdRef.current
                        ].translateX += dx;
                        elementsRef.current[
                            clickedElementIdRef.current
                        ].translateY += dy;
                    }

                    elementsRef.current[
                        clickedElementIdRef.current
                    ].isChangedXY = true;

                    updateLinesByElementsIds([clickedElementIdRef.current]);

                    // ------------------------- check element's areaId

                    const element =
                        elementsRef.current[clickedElementIdRef.current];
                    const newAreaIdForElement = getNewAreaIdForElement(
                        areasRef.current,
                        clickedElementIdRef.current
                    );

                    if (newAreaIdForElement) {
                        if (element.value.areaId !== newAreaIdForElement) {
                            // 1. if element.value.areaId is not null, remove nestedElementId from area
                            // 2. set element's areaId to new areaId
                            // 3. set newArea's nestedElementsIds with new element.id

                            elementsRef.current[
                                clickedElementIdRef.current
                            ].value.areaId = newAreaIdForElement;
                            elementsRef.current[
                                clickedElementIdRef.current
                            ].isChangedAreaId = true;
                            // dispatch(setNewAreaIdForElement(element.id, newAreaIdForElement));

                            resizeElementPropsRef.current = { elementId: clickedElementIdRef.current, areaId: newAreaIdForElement };
                        }
                    } else {
                        if (
                            element.value.areaId &&
                            areasRef.current[element.value.areaId]
                        ) {
                            // 1. remove nestedElementId from area
                            // 2. remove areaId from element

                            const prevNestedElementsIds =
                                areasRef.current[element.value.areaId].value
                                    .nestedElementsIds;
                            areasRef.current[
                                element.value.areaId
                            ].value.nestedElementsIds =
                                prevNestedElementsIds.filter(
                                    (id) => id !== clickedElementIdRef.current
                                );

                            elementsRef.current[
                                clickedElementIdRef.current
                            ].value.areaId = null;
                            elementsRef.current[
                                clickedElementIdRef.current
                            ].isChangedAreaId = true;
                            // dispatch(setNewAreaIdForElement(element.id, null));
                            resizeElementPropsRef.current = null;
                        }
                    }

                    // -------------------------

                    // draw();
                    setElementsHasChanges(true);

                    // set focused elements to []
                    const hasFocusedInsideElementChanges =
                        !!focusedInsideElementState;
                    if (hasFocusedInsideElementChanges) {

                        setFocusedInsideElementState(null);
                        // setColumnNameState('');
                        // setColumnTypeState('');

                        syncFocusedInsideElementToReduxRef.current(null);
                        // draw();
                    }
                    const hasFocusedElementsChanges =
                        focusedElementsIdsRef.current.length !== 0;
                    if (hasFocusedElementsChanges) {
                        focusedElementsIdsRef.current = [];
                        focusedElementsRelatedPropsRef.current = null;
                        syncFocusedElementsToReduxRef.current([]);
                    }
                    const hasFocusedLineChanges = !!focusedLineIdRef.current;
                    if (hasFocusedLineChanges) {
                        focusedLineIdRef.current = null;
                        focusedLineRelatedPropsRef.current = null;
                        syncFocusedLineToReduxRef.current(null);
                    }

                    if (focusedAreaIdRef.current) {
                        focusedAreaIdRef.current = null;
                        syncFocusedAreaToReduxRef.current(null);
                    }
                    if (!focusedGroupElementsIdsRef.current?.includes(clickedElementIdRef.current)) {
                        if (focusedGroupAreasIdsRef.current.length) {
                            focusedGroupAreasIdsRef.current = [];
                            focusedGroupElementsIdsRef.current = [];
                            syncFocusedGroupToReduxRef.current([], []);
                        }
                    }
                }

                // draw();

                // move with board
            } else if (clickedAreaIdRef.current) {
                if (clickedAreaIdRef.current === focusedAreaIdRef.current) {
                    const dx =
                        (event.clientX - startXYRef.current.x) /
                        scaleRef.current;
                    const dy =
                        (event.clientY - startXYRef.current.y) /
                        scaleRef.current;

                    // set is area moving
                    setIsMovingElementId(clickedAreaIdRef.current);
                    // set is moving

                    // move with focused area
                    if (focusedAreaIdRef.current && focusedAreaIdRef.current === clickedAreaIdRef.current) {
                        // update focused area position
                        areasRef.current[focusedAreaIdRef.current].x += dx;
                        areasRef.current[focusedAreaIdRef.current].y += dy;
                        areasRef.current[focusedAreaIdRef.current].isChangedXY = true;

                        const area = areasRef.current[focusedAreaIdRef.current];


                        /// resize area props
                        const point1 = { x: area.x, y: area.y };
                        const point2 = { x: area.x + area.width, y: area.y + area.height };

                        // get selected elements by creating area
                        const selectedElements = getAreaSelectedElementsForResize(
                            point1,
                            point2,
                            elementsRef.current,
                            areasRef.current,
                            viewMode,
                            area.id
                        );

                        resizeAreaPropsRef.current = { areaId: area.id, selectedElements };

                        if (!isElementResizing) {
                            setIsElementResizing(true);
                        }

                        if (area.value.nestedElementsIds) {
                            // update nested elements positions
                            area.value.nestedElementsIds.forEach(
                                (elementId) => {
                                    elementsRef.current[elementId].x += dx;
                                    elementsRef.current[elementId].y += dy;

                                    if (elementsRef.current[elementId].type === 'pen-segment') {
                                        elementsRef.current[elementId].translateX += dx;
                                        elementsRef.current[elementId].translateY += dy;
                                    }

                                    elementsRef.current[elementId].isChangedXY = true;
                                }
                            );

                            updateLinesByElementsIds(
                                area.value.nestedElementsIds
                            );
                        }

                        // draw();
                        // setElementsHasChanges(true);

                        // move with new-clicked area
                    } else {
                        // update new-clicked area position
                        areasRef.current[clickedAreaIdRef.current].x += dx;
                        areasRef.current[clickedAreaIdRef.current].y += dy;
                        areasRef.current[
                            clickedAreaIdRef.current
                        ].isChangedXY = true;

                        const area = areasRef.current[clickedAreaIdRef.current];

                        if (area.value.nestedElementsIds) {
                            // update nested elements positions
                            area.value.nestedElementsIds.forEach(
                                (elementId) => {
                                    elementsRef.current[elementId].x += dx;
                                    elementsRef.current[elementId].y += dy;

                                    if (
                                        elementsRef.current[elementId].type ===
                                        'pen-segment'
                                    ) {
                                        elementsRef.current[
                                            elementId
                                        ].translateX += dx;
                                        elementsRef.current[
                                            elementId
                                        ].translateY += dy;
                                    }

                                    elementsRef.current[
                                        elementId
                                    ].isChangedXY = true;
                                }
                            );

                            updateLinesByElementsIds(
                                area.value.nestedElementsIds
                            );
                        }

                        if (focusedAreaIdRef.current) {
                            focusedAreaIdRef.current = null;
                            syncFocusedAreaToReduxRef.current(null);
                        }

                        setFocusedInsideElementState(null);
                        // setColumnNameState('');
                        // setColumnTypeState('');

                        syncFocusedInsideElementToReduxRef.current(null);

                        focusedElementsIdsRef.current = [];
                        focusedElementsRelatedPropsRef.current = null;
                        syncFocusedElementsToReduxRef.current([]);

                        focusedLineIdRef.current = null;
                        focusedLineRelatedPropsRef.current = null;
                        syncFocusedLineToReduxRef.current(null);

                        if (!focusedGroupAreasIdsRef.current?.includes(clickedAreaIdRef.current)) {
                            if (focusedGroupAreasIdsRef.current.length) {
                                focusedGroupAreasIdsRef.current = [];
                                focusedGroupElementsIdsRef.current = [];
                                syncFocusedGroupToReduxRef.current([], []);
                            }
                        }
                        // draw();
                    }

                    // const isMultiElementsMove = focusedElementsIdsRef.current.includes(clickedElementIdRef.current) && focusedElementsIdsRef.current.length > 1;

                    // if (!isMultiElementsMove) {
                    // const [alignmentLines, movedElementNewXY] = getAlignmentLines(clickedElementIdRef.current);
                    // alignmentLinesRef.current = alignmentLines;
                    // }
                } else {

                    // move with selected focused group (areas and elements)
                    if (
                        focusedGroupAreasIdsRef.current?.includes(clickedAreaIdRef.current) ||
                        focusedGroupElementsIdsRef.current?.includes(clickedElementIdRef.current)
                    ) {
                        const dx = (event.clientX - startXYRef.current.x) / scaleRef.current;
                        const dy = (event.clientY - startXYRef.current.y) / scaleRef.current;

                        if (focusedGroupAreasIdsRef.current?.length) {
                            focusedGroupAreasIdsRef.current.forEach((areaId) => {
                                areasRef.current[areaId].x += dx;
                                areasRef.current[areaId].y += dy;
                                areasRef.current[areaId].isChangedXY = true;

                                // const area = areasRef.current[areaId];

                                // if (area.value.nestedElementsIds) {
                                //     // update nested elements positions
                                //     area.value.nestedElementsIds.forEach((elementId) => {
                                //         elementsRef.current[elementId].x += dx;
                                //         elementsRef.current[elementId].y += dy;

                                //         if (elementsRef.current[elementId].type === 'pen-segment') {
                                //             elementsRef.current[elementId].translateX += dx;
                                //             elementsRef.current[elementId].translateY += dy;
                                //         }

                                //         elementsRef.current[elementId].isChangedXY = true;
                                //     }
                                //     );

                                //     updateLinesByElementsIds(area.value.nestedElementsIds);
                                // }
                            })
                        }


                        if (focusedGroupElementsIdsRef.current?.length) {
                            focusedGroupElementsIdsRef.current.forEach((elementId) => {
                                elementsRef.current[elementId].x += dx;
                                elementsRef.current[elementId].y += dy;
                                elementsRef.current[elementId].isChangedXY = true;
                            })
                        }





                        // if (!focusedGroupAreasIdsRef.current?.includes(clickedAreaIdRef.current)) {
                        //     if (focusedGroupAreasIdsRef.current.length) {
                        //         focusedGroupAreasIdsRef.current = [];
                        //         focusedGroupElementsIdsRef.current = [];
                        //         syncFocusedGroupToReduxRef.current([], []);
                        //     }
                        // }

                        // if (!focusedGroupElementsIdsRef.current?.includes(clickedElementIdRef.current)) {
                        //     if (focusedGroupAreasIdsRef.current.length) {
                        //         focusedGroupAreasIdsRef.current = [];
                        //         focusedGroupElementsIdsRef.current = [];
                        //         syncFocusedGroupToReduxRef.current([], []);
                        //     }
                        // }



                    } else {
                        // get selected areas by mouse selectionBox
                        const selectedAreasIds = getSelectedAreas(selectionStartXYRef.current, selectionCurrentXYRef.current, areasRef.current);
                        selectedAreasIdsRef.current = selectedAreasIds;

                        // get selected elements by mouse selectionBox
                        const selectedElementsIds = getSelectedElements(selectionStartXYRef.current, selectionCurrentXYRef.current, elementsRef.current, viewMode);
                        selectedElementsIdsRef.current = selectedElementsIds;

                        if (!selectedAreasIds?.length) {
                            focusedElementsRelatedPropsRef.current = getElementsRelatedProps(selectedElementsIds);
                        }

                        // set is board moving | for context menu and etc.
                        if (!isBoardMoving) {
                            setIsBoardMoving(true);
                            setIsBoardMovingStarted(true);
                        }
                    }

                }
            } else if (isBoardClickedRef.current) {
                const dx = event.clientX - startXYRef.current.x;
                const dy = event.clientY - startXYRef.current.y;

                const newOriginX = originXYRef.current.x + dx;
                const newOriginY = originXYRef.current.y + dy;

                // update board originXY positions
                originXYRef.current = { x: newOriginX, y: newOriginY };
                syncOriginXYToReduxRef.current(newOriginX, newOriginY);

                sumOriginDxyRef.current = {
                    dx: sumOriginDxyRef.current.dx + dx,
                    dy: sumOriginDxyRef.current.dy + dy,
                };

                if (
                    Math.abs(sumOriginDxyRef.current.dx) > 250 ||
                    Math.abs(sumOriginDxyRef.current.dy) > 250
                ) {
                    sumOriginDxyRef.current = { dx: 0, dy: 0 };
                    calculateVisibleElementsAndLines();
                }
                // update canvas
                // draw();

                // set is board moving
                if (!isBoardMoving) {
                    setIsBoardMoving(true);
                    setIsBoardMovingStarted(true);
                }

                // set timeout on board moving
                clearTimeout(boardMovingTimerRef.current);
                boardMovingTimerRef.current = setTimeout(() => {
                    setIsBoardMoving(false);
                }, 200);

                // move with mouse selection
            } else if (isSelectionClickedRef.current) {
                // get selected areas by mouse selectionBox
                const selectedAreasIds = getSelectedAreas(
                    selectionStartXYRef.current,
                    selectionCurrentXYRef.current,
                    areasRef.current
                );
                selectedAreasIdsRef.current = selectedAreasIds;

                // get selected elements by mouse selectionBox
                const selectedElementsIds = getSelectedElements(
                    selectionStartXYRef.current,
                    selectionCurrentXYRef.current,
                    elementsRef.current,
                    viewMode
                );
                selectedElementsIdsRef.current = selectedElementsIds;
                // focusedElementsRelatedPropsRef.current = getElementsRelatedProps(selectedElementsIds);
                if (!selectedAreasIds?.length) {
                    focusedElementsRelatedPropsRef.current = getElementsRelatedProps(selectedElementsIds);
                }

                // check if mouse moving away on viewport / window sizes
                // scrollIfNeeded(e);

                // ----------------------------- (disabled by selectedElementsIdsRef.current)
                // const hasFocusedInsideElementChanges = !!focusedInsideElementState;
                // if (hasFocusedInsideElementChanges) {

                // 	setFocusedInsideElementState(null);
                // 	// setColumnNameState('');
                // 	// setColumnTypeState('');

                // 	syncFocusedInsideElementToReduxRef.current(null);
                // 	// draw();
                // }
                // // set selected elements to focused elements
                // const hasFocusedElementsChanges = !arraysAreEqual(focusedElementsIdsRef.current, selectedElementsIds);
                // if (hasFocusedElementsChanges) {
                // 	focusedElementsIdsRef.current = selectedElementsIds;
                // 	focusedElementsRelatedPropsRef.current = getElementsRelatedProps(selectedElementsIds);

                // 	syncFocusedElementsToReduxRef.current(selectedElementsIds);
                // }

                // if (focusedAreaIdRef.current) {
                // 	focusedAreaIdRef.current = null;
                // 	syncFocusedAreaToReduxRef.current(null);
                // }
                // -----------------------------

                // update canvas
                // draw();

                // set is board moving | for context menu and etc.
                if (!isBoardMoving) {
                    setIsBoardMoving(true);
                    setIsBoardMovingStarted(true);
                }

                // move with resizing item
            } else if (isItemResizeClickedRef.current) {
                const dx =
                    (event.clientX - startXYRef.current.x) / scaleRef.current;
                const dy =
                    (event.clientY - startXYRef.current.y) / scaleRef.current;

                // get new sizes for resizing item
                const {
                    x: newX,
                    y: newY,
                    width: newWidth,
                    height: newHeight,
                } = handleResize(resizeKeyRef.current, dx, dy);

                // 1. resizing focused area
                if (focusedAreaIdRef.current) {

                    // now here
                    const areaId = focusedAreaIdRef.current;

                    // update new-clicked area position
                    areasRef.current[areaId].x += newX;
                    areasRef.current[areaId].y += newY;

                    areasRef.current[areaId].width += newWidth;
                    areasRef.current[areaId].height += newHeight;
                    areasRef.current[areaId].isChangedXY = true;

                    /// resize area props

                    const area = areasRef.current[areaId];
                    const point1 = { x: area.x, y: area.y };
                    const point2 = { x: area.x + area.width, y: area.y + area.height };

                    // get selected elements by creating area
                    const selectedElements = getAreaSelectedElementsForResize(
                        point1,
                        point2,
                        elementsRef.current,
                        areasRef.current,
                        viewMode,
                        areaId
                    );

                    resizeAreaPropsRef.current = { areaId, selectedElements };

                    // ?
                    // updateLinesByElementsIds(areasRef.current[areaId]?.value?.nestedElementsIds || []);

                    if (!isElementResizing) {
                        setIsElementResizing(true);
                    }

                    // 2. resizing focused element
                } else {
                    const elementId = focusedElementsIdsRef.current[0];
                    const element = elementsRef.current[elementId];

                    if (elementsRef.current[elementId].type !== 'pen-segment') {

                        if (element.type === 'table' && (element.width + newWidth) < 320) {
                            if (newX > 0) {
                                // const newValueX = (element.width + newWidth) - 320;
                                // elementsRef.current[elementId].x += newValueX;
                            } else {
                                elementsRef.current[elementId].x += newX;
                            }

                            // update new-clicked element position
                            // elementsRef.current[elementId].x += newX;


                            // elementsRef.current[elementId].y += newY;

                            elementsRef.current[elementId].width = 320;
                            // elementsRef.current[elementId].height += newHeight;
                            elementsRef.current[elementId].isChangedXY = true;

                        } else {
                            // update new-clicked element position
                            elementsRef.current[elementId].x += newX;
                            // elementsRef.current[elementId].y += newY;

                            // if (elementsRef.current[elementId].type === 'pen-segment') {
                            //     elementsRef.current[elementId].translateX += dx;
                            //     elementsRef.current[elementId].translateY += dy;
                            // }

                            elementsRef.current[elementId].width += newWidth;
                            // elementsRef.current[elementId].height += newHeight;
                            elementsRef.current[elementId].isChangedXY = true;
                        }

                        if (elementsRef.current[elementId].type === 'table') {
                            // updateElementColumnsTextsOnResize(elementId);
                        }

                        updateLinesByElementsIds([elementId]);

                        if (!isElementResizing) {
                            setIsElementResizing(true);
                        }

                    }
                }

                // draw();
                setElementsHasChanges(true);
            }

            // BUSINESS LOGIC | HOVERING

            if (isMouseInCanvas) {
                if (hoveredElementIdHeaderRef.current !== newHoveredElementIdHeader) {
                    hoveredElementIdHeaderRef.current = newHoveredElementIdHeader;
                }
                else if (hoveredColumnIdRef.current !== newHoveredInsideElementId) {
                    hoveredColumnIdRef.current = newHoveredInsideElementId;
                    hoveredColumnIndexRef.current = newHoveredInsideElementIndex;
                }
                // hover on Table Circle Area OR direct on Table Circle
                else if (
                    !arraysAreEqual(
                        hoveredCircleAreaRef.current?.elementsIds,
                        newHoveredCircleAreaElementsIds
                    ) ||
                    hoveredCircleRef.current?.elementId !==
                    newHoveredCircleElementId
                ) {
                    hoveredCircleAreaRef.current = {
                        elementsIds: newHoveredCircleAreaElementsIds,
                    };
                    hoveredCircleRef.current = {
                        elementId: newHoveredCircleElementId,
                    };
                    // draw();
                }

                // hover on Table Point Area OR direct on Table Point
                else if (
                    !arraysAreEqual(
                        hoveredPointAreaRef.current?.insideElementsIds,
                        newHoveredPointAreaInsideElementsIds
                    ) ||
                    hoveredPointRef.current?.insideElementId !==
                    newHoveredPointInsideElementId ||
                    hoveredPointRef.current?.direction !==
                    newHoveredPointDirection
                ) {
                    hoveredPointAreaRef.current = {
                        insideElementsIds: newHoveredPointAreaInsideElementsIds,
                    };
                    hoveredPointRef.current = {
                        insideElementId: newHoveredPointInsideElementId,
                        direction: newHoveredPointDirection,
                    };
                    // draw();
                }

                // hover on Line
                else if (hoveredLineIdRef.current !== newHoveredLineId) {
                    hoveredLineIdRef.current = newHoveredLineId;
                    hoveredLineRelatedPropsRef.current =
                        getLineRelatedProps(newHoveredLineId);
                    // draw();
                }

                // hover on Element OR Element and Inside Element
                else if (
                    hoveredElementIdRef.current !== newHoveredElementId ||
                    hoveredInsideElementRef.current?.id !== newHoveredInsideElementId ||
                    hoveredInsideElementRef.current?.index !== newHoveredInsideElementIndex ||
                    hoveredElementIdEmptyColumnRef.current !== newHoveredElementIdEmptyColumn
                ) {
                    hoveredElementIdRef.current = newHoveredElementId;
                    hoveredInsideElementRef.current = {
                        id: newHoveredInsideElementId,
                        index: newHoveredInsideElementIndex,
                    };
                    hoveredElementRelatedPropsRef.current =
                        getElementsRelatedProps([newHoveredElementId]);

                    hoveredElementIdEmptyColumnRef.current =
                        newHoveredElementIdEmptyColumn;
                    // draw();
                } else if (hoveredAreaIdRef.current !== newHoveredAreaId) {
                    hoveredAreaIdRef.current = newHoveredAreaId;
                    // draw();
                }
            } else {
                // table circle
                hoveredCircleAreaRef.current = null;
                hoveredCircleRef.current = null;

                // table points
                hoveredPointAreaRef.current = null;
                hoveredPointRef.current = null;

                // line
                hoveredLineIdRef.current = null;
                hoveredLineRelatedPropsRef.current = null;

                // element
                hoveredElementIdRef.current = null;
                hoveredInsideElementRef.current = null;

                hoveredColumnIdRef.current = null;

                hoveredElementRelatedPropsRef.current = null;

                hoveredElementIdEmptyColumnRef.current = null;

                hoveredAreaIdRef.current = null;
                // draw();
            }

            // BUSINESS LOGIC | CURSOR

            if (isBtnExportImageEnabled) {
                let hoveredExportImageResizeKey =
                    getHoveredResizeKeyOnExportImageBox(mouseX, mouseY);

                // hover on resize points on export image box: style cursor to resize
                if (hoveredExportImageResizeKey) {
                    const cursorMap = {
                        ne: 'nesw',
                        sw: 'nesw',
                        nw: 'nwse',
                        se: 'nwse',
                    };
                    canvas.style.cursor = `${cursorMap[hoveredExportImageResizeKey]}-resize`;
                } else {
                    if (
                        isBoardClickedRef.current ||
                        (isBtnExportImageEnabled &&
                            (exportImageBoxPropsRef.current.isPressed ||
                                exportImageBoxPropsRef.current
                                    .isOutsidePressed))
                    ) {
                        canvas.style.cursor = 'grabbing';
                    } else {
                        canvas.style.cursor = 'grab';
                    }
                }
            } else if (isBtnPenEnabled) {
                // canvas.style.cursor = 'pointer';
                canvas.style.cursor = 'none';

                // hover on resize points if focused only one element: style cursor to resize
            } else if (focusedElementsIdsRef.current.length === 1 || focusedAreaIdRef.current) {
                let [isHoverOnResize, resizeKey] = isHoverOnResizeHandle(
                    mouseX,
                    mouseY
                );
                const cursorMap = {
                    e: 'ew',
                    w: 'ew',
                    n: 'ns',
                    s: 'ns',
                    ne: 'nesw',
                    sw: 'nesw',
                    nw: 'nwse',
                    se: 'nwse',
                };
                if (isItemResizeClickedRef.current && resizeKeyRef.current) {
                    const cursorMap = {
                        ne: 'nesw',
                        sw: 'nesw',
                        nw: 'nwse',
                        se: 'nwse',
                    };
                    canvas.style.cursor = `${cursorMap[resizeKeyRef.current]}-resize`;
                } else if (isHoverOnResize) {
                    canvas.style.cursor = `${cursorMap[resizeKey]}-resize`;
                } else {
                    canvas.style.cursor = 'default';
                }
            } else {
                // if (isCursorHand) {
                // 	if (isBoardClickedRef.current) {
                // 		canvas.style.cursor = 'grabbing';
                // 	} else {
                // 		canvas.style.cursor = 'grab';
                // 	}
                // } else {
                // 	canvas.style.cursor = 'default';
                // }
                if (isBoardClickedRef.current) {
                    canvas.style.cursor = 'grabbing';
                } else if (isCursorHand) {
                    canvas.style.cursor = 'grab';
                } else {
                    canvas.style.cursor = 'default';
                }
            }

            if (clickedPointRef.current || isBtnNewLineEnabled) {
                canvas.style.cursor = 'crosshair';
            }

            if (isBtnNewAreaEnabled) {
                canvas.style.cursor = 'crosshair';
            }

            if (isBtnNewTableClickEnabled) {
                // canvas.style.cursor = 'url("data:image/svg+xml,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'20\' height=\'20\'><rect width=\'20\' height=\'20\' fill=\'white\'/></svg>") 10 10, auto';
                // canvas.style.cursor = 'url("data:image/svg+xml,<svg fill=\'#ffffff\' width=\'31\' height=\'31\' viewBox=\'0 0 32 32\' xmlns=\'http://www.w3.org/2000/svg\'><path stroke-width=\'0px\' stroke=\'#ffffff\' d=\'M 27 3 H 5 A 2 2 0 0 0 3 5 V 27 a 2 2 0 0 0 2 2 H 27 a 2 2 0 0 0 2 -2 V 5 A 2 2 0 0 0 27 3 Z m 0 4 V 13 H 5 V 7 Z M 17 15 H 27 v 5 H 17 Z m -2 5 H 5 V 15 H 15 Z M 5 22 H 15 v 5 H 5 Z m 12 5 V 22 H 27 v 5 Z\'></path></svg>" 10 10, auto';
                // document.body.style.cursor = 'url("data:image/svg+xml,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'150\' height=\'100\' viewBox=\'0 0 150 100\'><rect x=\'5\' y=\'5\' width=\'140\' height=\'90\' fill=\'white\' stroke=\'black\' stroke-width=\'2\'/><text x=\'10\' y=\'20\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>ID</text><text x=\'50\' y=\'20\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>Name</text><text x=\'100\' y=\'20\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>Age</text><text x=\'10\' y=\'40\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>1</text><text x=\'50\' y=\'40\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>John</text><text x=\'100\' y=\'40\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>30</text><text x=\'10\' y=\'60\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>2</text><text x=\'50\' y=\'60\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>Jane</text><text x=\'100\' y=\'60\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>25</text><text x=\'10\' y=\'80\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>3</text><text x=\'50\' y=\'80\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>Alice</text><text x=\'100\' y=\'80\' font-family=\'Arial\' font-size=\'12\' fill=\'black\'>28</text></svg>") 10 10, auto';
                // canvas.style.cursor = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABoAAAAcCAYAAAB/E6/TAAAAAXNSR0IArs4c6QAABHZJREFUSA2VVl1Im1cYzo/5szXTON1We6FOL2REnBuudtVNVxV64ypDxYrMi2GQgiAMtwsZFLwq65X0SreqI4gbJYZljd28EOffcOq0TOYfrKJumRloXRrTxOx5Pvw+E2O+xBce3vP+nPN+73vOe86nUJyfPsGUQeAukBwyPQljdYgcNoxqCPM6EUpyc3M/Wlpaura3t7c/Pz+vh2ke+Fyn07UGg8GbwB+QXSdTYo8S4PIZ8B1wDzABt7u7ux9hseDs7OzCsf5KZmamNRAI+Pr7+x9D9wUQQaoIzYmiqrS01Hx4eHijpaUlA+oPgQsmk8lAl/T09FSwFKAA9osqlUqTl5f3KmSWUwmEkVygrLq6OpNWqzV0dnZexawyQOX3+4U5SUlJRsgXgZySkpLL4Irt7e3/wA4AMZCOelK0QHR87nK5juiUkZFxOTU1lftxyefzBagzGo0Mkghk5efn51C3urrKQG4gH3igVqut4LcA2UB/z8zMHNKJVFlZSZbl9XqF4CiVCgfgKC0tLYjseOIUy8vLXjA/TJ9OTk6+gQzfgXyTtmgZ0bY+NzcnncrGxkaWR31wcCBkRIfExER/YWGhEJjy2traC7Bii8WiLS4ufnt3d/cZZEIdLVAQxqconWofhLGioqLiTeyXH6KfMiklJeWovLxcyIbyxsaGIiEhwdjV1fU+5YGBgQ2w3wBVtED044LrExMT6xQ0Go2uurrah0BSRjU1NbqGhgYz7aSdnR19a2trIDk5+WXKPT09zHacYzliH1na2tqEvmHvuN3uf7AP6xyfJvTRi+zs7Ico7T5t09PTy5g/APB0SluAcQTR+F5BQYHt9KLxyE1NTQ7MtwA8mUp+NYlReQwvAX8CTmAbWFtZWdGAn5tsNhtbZALgFgTFPbqDEpWNjo6WdXR0VBkMhi9h/BjY93g8CvSOdMyhi0mbm5t/YS/ZU/xoaU8Z+asnILEkuDDd6JtvoO8GHLzHRFs8fHx8/Anm3QeEsoELfcSsvi4qKlqdmppapBJdbxoZGbnV29ubU19f7+U9Rn28tLi4+C98WXpmw1YRiPvEPbqOK8NmtVon4/lqOZ/m5mYehAYg7AOZEW/kdOBdpVI5PDQ09IvcQrFsuBvtWOsKEHGsqbgAvAKUIrPvnU7nQqwFz7LjYn2KNb4FXgLEw4bhCTEYN4+ZVeEqeTQ2Nvb7WYvJ6Wpra1m2tuO1eNDOJDEYM6vG3ea02+1xZzY8PMxn/SHwOqAFZCm0jDfgaW9vb/8Jz4NHLpPBwcFZPBts9OsADxfXiUmhwd6C9z29Xv8YF+aPDodjYWtry4Um9pH39fXNmM3mH+DDfakAWHo+kmFlCxNgDCUG41PMQ8LX9DXgAyAT4P8Cv5rdz83/FfgZ4JPC94cPoHQjYBwelYpTxFPDWvML2QLk7DuxLGxGLugDPMBzICIIdDEDiT5cnGADEgzEajAQL00G4utKSC8uxhLJlU5yOh7QlxmKEO1cWLxqpOtGNIr8PIHEOeSh86IuHjrhf75uE+FNCrMEAAAAAElFTkSuQmCC") 14 16, auto`;
                canvas.style.cursor = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB8AAAAfCAMAAAAocOYLAAAAVFBMVEUAAAD///////////////////////////////////////////////////////////////////////////////////////////////////////////8wXzyWAAAAG3RSTlMACAkRGFVmdXZ3eIiZpcDJzNfd5Ofs7e/3+vucN3j2AAAAAWJLR0QbAmDUpAAAAH5JREFUKM/NkzcOgEAMBM2Rcw53+/9/UoBdmSABElOtNIW9kk30Ap6v4bFOHTRcsmmja8AZIiIKcESw+6XRWMS36tbtb/wQaQzir/qPOVOhkjwq8zNkp/t94+eCqVFLnm/360OmRCm5/0k/42zHTJgkW7vdLyX6gdv45v88YQXHUR7h7PdO0gAAAABJRU5ErkJggg==") 14 16, auto`;
            }
            // set new value to startXY for next 'mousemove' event
            startXYRef.current = { x: event.clientX, y: event.clientY };

            if (isMouseInCanvas || scaleRef.current > 0.5 || isSelectionClickedRef.current ||
                exportImageBoxPropsRef.current.clickedResizeKey ||
                exportImageBoxPropsRef.current.isPressed ||
                exportImageBoxPropsRef.current.isOutsidePressed
            ) {
                // update canvas
                draw(true);
            }
        };

        const handleDocumentClick = (e) => {
            // e.preventDefault();
            const event = e.type === 'touchstart' ? e.changedTouches[0] : e;

            console.log('e.type', e.type, isBtnNewTableClickEnabled)



            // console.log('MOUSE', mouseX, event.changedTouches[0].clientX, originXYRef.current.x)


            if (isHistoryClickedState) {
                setIsHistoryClickedState(false);
                dispatch(setIsHistoryClicked(false));
            }

            if (isBtnPenEnabled && newPenSegmentRef.current.isPressed) {
                const newPenSegment = buildNewPenSegment(
                    newPenSegmentRef.current.points
                );
                // dispatch(addNewPenSegment(newPenSegment));
                if (checkPens(elementsRef.current)) {
                    dispatch(addNewPenSegment(newPenSegment));
                } else {
                    dispatch(enableCanvasPricingPlanLimit('pens'));
                }

                newPenSegmentRef.current.isPressed = false;
                unsetAllVariablesOnClick();

                return;
            }

            // inside click in canvas root elements tree
            if (ref.current && ref.current.contains(event.target)) {
                if (isBtnNewTableClickEnabled) {
                    // const mouseX = (event.clientX - originXYRef.current.x) / scaleRef.current;
                    // const mouseY = (event.clientY - originXYRef.current.y) / scaleRef.current;
                    const mouseX =
                        (event.clientX -
                            originXYRef.current.x -
                            160 * scaleRef.current) /
                        scaleRef.current;
                    const mouseY =
                        (event.clientY -
                            originXYRef.current.y -
                            40 * scaleRef.current) /
                        scaleRef.current;

                    const newElement = buildNewTable(mouseX, mouseY);

                    // dispatch(addNewElement(newElement));
                    if (checkTables(elementsRef.current)) {
                        dispatch(addNewElement(newElement));
                    } else {
                        dispatch(enableCanvasPricingPlanLimit('tables'));
                    }
                    dispatch(disableBtnNewTableClick());
                    // cursor = default;
                    // open Context Menu; (isEditElement = true)
                    // focusedElementsIds = [new_table_id];

                    isBoardClickedRef.current = false;
                    isSelectionClickedRef.current = false;

                    unsetAllVariablesOnClick();

                    return;
                } else if (isBtnNewNoteClickEnabled) {
                    // const mouseX = (event.clientX - originXYRef.current.x) / scaleRef.current;
                    // const mouseY = (event.clientY - originXYRef.current.y) / scaleRef.current;
                    const mouseX =
                        (event.clientX -
                            originXYRef.current.x -
                            160 * scaleRef.current) /
                        scaleRef.current;
                    const mouseY =
                        (event.clientY -
                            originXYRef.current.y -
                            40 * scaleRef.current) /
                        scaleRef.current;

                    const newElement = buildNewNote(mouseX, mouseY);

                    // dispatch(addNewElement(newElement));
                    if (checkNotes(elementsRef.current)) {
                        dispatch(addNewElement(newElement));
                    } else {
                        dispatch(enableCanvasPricingPlanLimit('notes'));
                    }

                    dispatch(disableBtnNewNoteClick());
                    // cursor = default;
                    // open Context Menu; (isEditElement = true)
                    // focusedElementsIds = [new_table_id];

                    isBoardClickedRef.current = false;
                    isSelectionClickedRef.current = false;

                    unsetAllVariablesOnClick();

                    return;
                }
            }

            // if (isNewTableGrabStartedRef.current) {
            // 	const mouseX = (event.clientX - originXYRef.current.x - 160 * scaleRef.current) / scaleRef.current;
            // 	const mouseY = (event.clientY - originXYRef.current.y - 40 * scaleRef.current) / scaleRef.current;

            // 	const newElement = buildNewTable(mouseX, mouseY);
            // 	dispatch(addNewElement(newElement));
            // 	// dispatch(disableBtnNewTableClick());
            // 	// cursor = default;
            // 	// open Context Menu; (isEditElement = true)
            // 	// focusedElementsIds = [new_table_id];

            // 	isNewTableGrabStartedRef.current = false;
            // 	return;

            // } else if (isNewNoteGrabStartedRef.current) {
            // 	const mouseX = (event.clientX - originXYRef.current.x - 160 * scaleRef.current) / scaleRef.current;
            // 	const mouseY = (event.clientY - originXYRef.current.y - 40 * scaleRef.current) / scaleRef.current;

            // 	const newElement = buildNewNote(mouseX, mouseY);
            // 	dispatch(addNewElement(newElement));

            // 	// dispatch(disableBtnNewNoteClick());
            // 	// cursor = default;
            // 	// open Context Menu; (isEditElement = true)
            // 	// focusedElementsIds = [new_table_id];

            // 	isNewNoteGrabStartedRef.current = false;
            // 	return;
            // }

            // business logic | CLICK MOUSE UP
            let isLineCreated = false;
            if (isBtnNewLineEnabled) {
                // 1. line has no points 'from'/'to'
                if (!lineFromRef.current) {
                    // nothing
                    // 2. line has 'from' point
                } else if (lineFromRef.current && !lineToRef.current) {
                    lineFromRef.current.isClicked = true;

                    // 3. line has 'from' and 'to' points
                } else if (
                    lineFromRef.current?.elementId &&
                    lineToRef.current?.elementId
                ) {
                    if (
                        lineToRef.current.insideElementId &&
                        lineFromRef.current.insideElementId !==
                        lineToRef.current.insideElementId
                    ) {
                        const newLine = buildNewLine();
                        dispatch(addNewLine(newLine));
                        isLineCreated = true;

                        const hasFocusedInsideElementChanges =
                            !!focusedInsideElementState;
                        if (hasFocusedInsideElementChanges) {

                            setFocusedInsideElementState(null);
                            // setColumnNameState('');
                            // setColumnTypeState('');

                            syncFocusedInsideElementToReduxRef.current(null);
                            // draw();
                        }
                    }
                }
                //
            } else if (isBtnNewAreaEnabled) {
                if (newAreaPropsRef.current) {
                    if (
                        Math.abs(
                            newAreaPropsRef.current.endX -
                            newAreaPropsRef.current.startX
                        ) < 10 ||
                        Math.abs(
                            newAreaPropsRef.current.endY -
                            newAreaPropsRef.current.startY
                        ) < 10
                    ) {
                        // nothing;
                    } else {
                        if (newAreaPropsRef.current) {
                            const newArea = buildNewArea();
                            // dispatch(addNewArea(newArea));
                            if (checkAreas(areasRef.current)) {
                                dispatch(addNewArea(newArea));
                            } else {
                                dispatch(enableCanvasPricingPlanLimit('areas'));
                            }

                            dispatch(disableBtnNewArea());
                        }
                    }
                }

                newAreaPropsRef.current = null;

                // draw();

                // 0. click on column's point
            } else if (clickedPointRef.current) {
                if (
                    // 'elementTo' is valid
                    lineToRef.current.elementId &&
                    // 'insideElementTo' is valid
                    lineToRef.current.insideElementId &&
                    // line is between two different columns, not one
                    lineFromRef.current.insideElementId !==
                    lineToRef.current.insideElementId
                ) {
                    const newLine = buildNewLine();
                    dispatch(addNewLine(newLine));

                    const hasFocusedInsideElementChanges =
                        !!focusedInsideElementState;
                    if (hasFocusedInsideElementChanges) {

                        setFocusedInsideElementState(null);
                        // setColumnNameState('');
                        // setColumnTypeState('');

                        syncFocusedInsideElementToReduxRef.current(null);
                        // draw();
                    }
                }

                // 0. click on circle to add new row
            } else if (clickedCircleElementIdRef.current) {
                // dispatch(addNewColumnInTheEnd(clickedCircleElementIdRef.current));
                if (checkColumns(elementsRef.current, clickedCircleElementIdRef.current)) {
                    dispatch(addNewColumnInTheEnd(clickedCircleElementIdRef.current));
                } else {
                    dispatch(enableCanvasPricingPlanLimit('columns'));
                }


                // 1. click on a element => static click on an element |OR| move and click on an element
            } else if (clickedElementIdRef.current) {
                // static click on an element
                if (!isMovingElementId) {
                    // static click on a new element => focus on new clicked element
                    if (
                        !focusedElementsIdsRef.current.includes(
                            clickedElementIdRef.current
                        )
                    ) {
                        const hasFocusedInsideElementChanges =
                            !!focusedInsideElementState;
                        if (hasFocusedInsideElementChanges) {

                            setFocusedInsideElementState(null);
                            // setColumnNameState('');
                            // setColumnTypeState('');

                            syncFocusedInsideElementToReduxRef.current(null);
                            // draw();
                        }

                        // const hasFocusedElementsChanges = true;
                        focusedElementsIdsRef.current = [
                            clickedElementIdRef.current,
                        ];
                        focusedElementsRelatedPropsRef.current =
                            getElementsRelatedProps([
                                clickedElementIdRef.current,
                            ]);

                        syncFocusedElementsToReduxRef.current([clickedElementIdRef.current]);

                        // static click on an one focused element => open column context menu
                    } else if (
                        focusedElementsIdsRef.current.length === 1 &&
                        focusedElementsIdsRef.current[0] ===
                        clickedElementIdRef.current
                    ) {
                        if (clickedInsideElementRef.current) {
                            const hasFocusedInsideElementChanges =
                                !focusedInsideElementState ||
                                focusedInsideElementState.elementId !==
                                clickedInsideElementRef.current.elementId ||
                                focusedInsideElementState.insideElementId !==
                                clickedInsideElementRef.current
                                    .insideElementId;
                            if (hasFocusedInsideElementChanges) {

                                if (columnState && columnNameState) {
                                    // if (columnState && columnNameState && columnTypeState) {

                                    if (columnState?.insideElementType === 'header') {
                                        elementsRef.current[columnState.elementId].value.header.name = columnNameState;
                                    } else {
                                        elementsRef.current[columnState.elementId].value.columns[columnState.insideElementId].name = columnNameState;
                                        elementsRef.current[columnState.elementId].value.columns[columnState.insideElementId].type = columnTypeState;
                                    }
                                }

                                setFocusedInsideElementState({
                                    ...clickedInsideElementRef.current,
                                });
                                syncFocusedInsideElementToReduxRef.current(
                                    clickedInsideElementRef.current.elementId,
                                    clickedInsideElementRef.current
                                        .insideElementId,
                                    clickedInsideElementRef.current.index,
                                    clickedInsideElementRef.current
                                        .insideElementType
                                );

                                setColumnNameState(
                                    clickedInsideElementRef.current
                                        ?.insideElement?.name || ''
                                );
                                setColumnTypeState(
                                    clickedInsideElementRef.current
                                        ?.insideElement?.type || ''
                                );
                                setColumnState({
                                    ...clickedInsideElementRef.current,
                                });

                                // draw();
                            }
                        } else {
                            const hasFocusedInsideElementChanges =
                                !!focusedInsideElementState;
                            if (hasFocusedInsideElementChanges) {

                                setFocusedInsideElementState(null);
                                // setColumnNameState('');
                                // setColumnTypeState('');

                                syncFocusedInsideElementToReduxRef.current(
                                    null
                                );
                                // draw();
                            }
                        }

                        // static click on an one of the many focused elements => focus on one clicked element
                    } else {
                        const hasFocusedInsideElementChanges =
                            !!focusedInsideElementState;
                        if (hasFocusedInsideElementChanges) {

                            setFocusedInsideElementState(null);
                            // setColumnNameState('');
                            // setColumnTypeState('');


                            syncFocusedInsideElementToReduxRef.current(null);
                            // draw();
                        }

                        focusedElementsIdsRef.current = [
                            clickedElementIdRef.current,
                        ];
                        focusedElementsRelatedPropsRef.current =
                            getElementsRelatedProps([
                                clickedElementIdRef.current,
                            ]);

                        syncFocusedElementsToReduxRef.current([
                            clickedElementIdRef.current,
                        ]);
                    }

                    // move and click on an element
                } else {
                    // nothing
                    // if move and click on new-clicked element => unfocus and move element on 'mousemove'
                    // if move and click on focused element => move focused elements on 'mousemove'
                }

                // 2. click on item resize points
            } else if (isItemResizeClickedRef.current) {
                // nothing
            } else if (clickedLineIdRef.current) {
                focusedLineIdRef.current = clickedLineIdRef.current;
                focusedLineRelatedPropsRef.current = getLineRelatedProps(
                    clickedLineIdRef.current
                );
                syncFocusedLineToReduxRef.current(clickedLineIdRef.current);
            } else if (clickedAreaIdRef.current) {
                const staticClickWithSelection =
                    // static click on the board with mouse/selectionBox
                    isSelectionClickedRef.current &&
                    selectionStartXYRef.current.x ===
                    selectionCurrentXYRef.current.x &&
                    selectionStartXYRef.current.y ===
                    selectionCurrentXYRef.current.y;

                // static click on an element
                if (
                    staticClickWithSelection ||
                    (!isSelectionClickedRef.current &&
                        isMovingElementId !== clickedAreaIdRef.current)
                ) {
                    if (clickedAreaIdRef.current === focusedAreaIdRef.current) {
                        focusedAreaIdRef.current = null;
                        syncFocusedAreaToReduxRef.current(null);
                    } else {
                        if (isUnfocusElementsBeforeRef.current) {
                            // nothing
                        } else if (!isUnfocusElementsBeforeRef.current) {
                            focusedAreaIdRef.current = clickedAreaIdRef.current;
                            syncFocusedAreaToReduxRef.current(
                                clickedAreaIdRef.current
                            );
                        }
                    }
                } else {
                    if (clickedAreaIdRef.current === focusedAreaIdRef.current) {
                        // nothing. stay with focus on focused elements which selected with selectionBox on 'mousemove'
                    } else {
                        if (focusedInsideElementState) {
                            setFocusedInsideElementState(null);
                            syncFocusedInsideElementToReduxRef.current(null);
                        }

                        // set selected areas to focused group
                        if (selectedAreasIdsRef.current?.length) {
                            if (focusedElementsIdsRef.current?.length) {
                                focusedElementsIdsRef.current = [];
                                focusedElementsRelatedPropsRef.current = null;
                                syncFocusedElementsToReduxRef.current([]);
                            }
                            // focusedGroupIdsRef.current = [...selectedElementsIdsRef.current, ...selectedAreasIdsRef.current];
                            focusedGroupElementsIdsRef.current = [...selectedElementsIdsRef.current];
                            focusedGroupAreasIdsRef.current = [...selectedAreasIdsRef.current];
                            syncFocusedGroupToReduxRef.current(focusedGroupElementsIdsRef.current, focusedGroupAreasIdsRef.current);
                        } else {
                            // set selected elements to focused elements
                            const hasFocusedElementsChanges = !arraysAreEqual(focusedElementsIdsRef.current, selectedElementsIdsRef.current);
                            if (hasFocusedElementsChanges) {
                                focusedElementsIdsRef.current = selectedElementsIdsRef.current;
                                focusedElementsRelatedPropsRef.current = getElementsRelatedProps(selectedElementsIdsRef.current);
                                syncFocusedElementsToReduxRef.current(selectedElementsIdsRef.current);
                            }
                        }
                        if (focusedLineIdRef.current) {
                            focusedLineIdRef.current = null;
                            focusedLineRelatedPropsRef.current = null;
                            syncFocusedLineToReduxRef.current(null);
                        }
                        if (focusedAreaIdRef.current) {
                            focusedAreaIdRef.current = null;
                            syncFocusedAreaToReduxRef.current(null);
                        }
                        // -----------------------------
                    }
                }

                // 3. click on the board => static click on the board |OR| move and click on the board (hand => drag) (mouse => selectionBox)
            } else if (isBoardClickedRef.current) {
                // 3.1 static click on the board
                if (!isBoardMovingStarted) {
                    // unfocus focused elements
                    const hasFocusedInsideElementChanges =
                        !!focusedInsideElementState;
                    if (hasFocusedInsideElementChanges) {

                        setFocusedInsideElementState(null);
                        // setColumnNameState('');
                        // setColumnTypeState('');

                        syncFocusedInsideElementToReduxRef.current(null);
                        // draw();
                    }
                    const hasFocusedElementsChanges =
                        !!focusedElementsIdsRef.current.length;
                    if (hasFocusedElementsChanges) {
                        focusedElementsIdsRef.current = [];
                        focusedElementsRelatedPropsRef.current = null;
                        syncFocusedElementsToReduxRef.current([]);
                    }

                    const hasFocusedLineChanges = !!focusedLineIdRef.current;
                    if (hasFocusedLineChanges) {
                        focusedLineIdRef.current = null;
                        focusedLineRelatedPropsRef.current = null;
                        syncFocusedLineToReduxRef.current(null);
                    }

                    if (focusedAreaIdRef.current) {
                        focusedAreaIdRef.current = null;
                        syncFocusedAreaToReduxRef.current(null);
                    }

                    if (focusedGroupAreasIdsRef.current.length) {
                        focusedGroupAreasIdsRef.current = [];
                        focusedGroupElementsIdsRef.current = [];
                        syncFocusedGroupToReduxRef.current([], []);
                    }

                    handleCursorClick();

                    // 3.2 move and click on the board (hand => drag)
                } else {
                    // nothing. stay with focus on focused elements before dragging board
                }

                // 3.3 move and click on the board (mouse => selectionBox)
            } else if (isSelectionClickedRef.current) {
                // 3.3.1 static click on the board with mouse/selectionBox
                if (
                    selectionStartXYRef.current.x ===
                    selectionCurrentXYRef.current.x &&
                    selectionStartXYRef.current.y ===
                    selectionCurrentXYRef.current.y
                ) {
                    // unfocus focused elements
                    const hasFocusedInsideElementChanges =
                        !!focusedInsideElementState;
                    if (hasFocusedInsideElementChanges) {

                        setFocusedInsideElementState(null);
                        // setColumnNameState('');
                        // setColumnTypeState('');

                        syncFocusedInsideElementToReduxRef.current(null);
                        // draw();
                    }
                    const hasFocusedElementsChanges =
                        !!focusedElementsIdsRef.current.length;
                    if (hasFocusedElementsChanges) {
                        focusedElementsIdsRef.current = [];
                        focusedElementsRelatedPropsRef.current = null;
                        syncFocusedElementsToReduxRef.current([]);
                    }

                    const hasFocusedLineChanges = !!focusedLineIdRef.current;
                    if (hasFocusedLineChanges) {
                        focusedLineIdRef.current = null;
                        focusedLineRelatedPropsRef.current = null;
                        syncFocusedLineToReduxRef.current(null);
                    }

                    if (focusedAreaIdRef.current) {
                        focusedAreaIdRef.current = null;
                        syncFocusedAreaToReduxRef.current(null);
                    }

                    if (focusedGroupAreasIdsRef.current.length) {
                        focusedGroupAreasIdsRef.current = [];
                        focusedGroupElementsIdsRef.current = [];
                        syncFocusedGroupToReduxRef.current([], []);
                    }

                    handleCursorClick();
                } else {
                    // nothing. stay with focus on focused elements which selected with selectionBox on 'mousemove'

                    // ----------------------------- (added by )
                    const hasFocusedInsideElementChanges =
                        !!focusedInsideElementState;
                    if (hasFocusedInsideElementChanges) {
                        setFocusedInsideElementState(null);
                        syncFocusedInsideElementToReduxRef.current(null);
                    }

                    // set selected areas to focused group
                    if (selectedAreasIdsRef.current?.length) {
                        if (focusedElementsIdsRef.current?.length) {
                            focusedElementsIdsRef.current = [];
                            focusedElementsRelatedPropsRef.current = null;
                            syncFocusedElementsToReduxRef.current([]);
                        }
                        // focusedGroupIdsRef.current = [...selectedElementsIdsRef.current, ...selectedAreasIdsRef.current];
                        focusedGroupElementsIdsRef.current = [...selectedElementsIdsRef.current];
                        focusedGroupAreasIdsRef.current = [...selectedAreasIdsRef.current];
                        syncFocusedGroupToReduxRef.current(focusedGroupElementsIdsRef.current, focusedGroupAreasIdsRef.current);
                    } else {
                        // set selected elements to focused elements
                        const hasFocusedElementsChanges = !arraysAreEqual(focusedElementsIdsRef.current, selectedElementsIdsRef.current);
                        if (hasFocusedElementsChanges) {
                            focusedElementsIdsRef.current = selectedElementsIdsRef.current;
                            focusedElementsRelatedPropsRef.current = getElementsRelatedProps(selectedElementsIdsRef.current);
                            syncFocusedElementsToReduxRef.current(selectedElementsIdsRef.current);
                        }
                    }
                    if (focusedLineIdRef.current) {
                        focusedLineIdRef.current = null;
                        focusedLineRelatedPropsRef.current = null;
                        syncFocusedLineToReduxRef.current(null);
                    }
                    if (focusedAreaIdRef.current) {
                        focusedAreaIdRef.current = null;
                        syncFocusedAreaToReduxRef.current(null);
                    }
                    // -----------------------------
                }
            }

            unsetAllVariablesOnClick();

            // // unset all variables which was clicked on 'mousedown'
            // if (alignmentLinesRef.current) {
            // 	alignmentLinesRef.current = null;
            // }

            // if (clickedPointRef.current) {
            // 	clickedPointRef.current = null;
            // }

            // if (isStartedLineState) {
            // 	setIsStartedLineState(false);
            // }

            if (
                lineFromRef.current &&
                (!isBtnNewLineEnabled || isLineCreated)
            ) {
                lineFromRef.current = null;
            }

            if (lineToRef.current && (!isBtnNewLineEnabled || isLineCreated)) {
                lineToRef.current = null;
            }

            // if (clickedCircleElementIdRef.current) {
            // 	clickedCircleElementIdRef.current = null;
            // }

            // if (clickedLineIdRef.current) {
            // 	clickedLineIdRef.current = null;
            // }

            // if (clickedAreaIdRef.current) {
            // 	clickedAreaIdRef.current = null;
            // }

            // if (clickedElementIdRef.current) {
            // 	clickedElementIdRef.current = null;
            // }
            // if (clickedInsideElementRef.current) {
            // 	clickedInsideElementRef.current = null;
            // }

            // if (isBoardClickedRef.current) {
            // 	isBoardClickedRef.current = false;
            // }
            // if (isSelectionClickedRef.current) {
            // 	isSelectionClickedRef.current = false;
            // }
            // if (isItemResizeClickedRef.current) {
            // 	isItemResizeClickedRef.current = false;
            // }

            // if (isMovingElementId) {
            // 	setIsMovingElementId(null);
            // }
            // if (isBoardMoving) {
            // 	setIsBoardMoving(false);
            // }
            // if (isBoardMovingStarted) {
            // 	setIsBoardMovingStarted(false);
            // }

            // if (exportImageBoxPropsRef.current.isPressed) {
            // 	exportImageBoxPropsRef.current.isPressed = false;
            // }

            // if (exportImageBoxPropsRef.current.isOutsidePressed) {
            // 	exportImageBoxPropsRef.current.isOutsidePressed = false;
            // }

            // if (exportImageBoxPropsRef.current.clickedResizeKey) {
            // 	exportImageBoxPropsRef.current.clickedResizeKey = false;
            // }

            ///////////
            // update stored elements positions
            const updatedElements = {};
            for (const key in elementsRef.current) {
                const element = elementsRef.current[key];
                if (element.isChangedXY) {
                    updatedElements[element.id] = {
                        x: element.x,
                        y: element.y,

                        translateX: element.translateX,
                        translateY: element.translateY,

                        width: element.width,
                        height: element.height,
                    };
                }

                if (element.isChangedAreaId) {
                    dispatch(
                        setNewAreaIdForElement(
                            element.id,
                            elementsRef.current[element.id].value.areaId
                        )
                    );
                }
            }

            const updatedLines = getUpdatedLinesByElementsIds(
                Object.keys(updatedElements)
            );

            ///////////
            // update stored areas positions
            const updatedAreas = {};
            for (const key in areasRef.current) {
                const area = areasRef.current[key];
                if (area.isChangedXY) {
                    let updatedX = area.x;
                    let updatedY = area.y;
                    let updatedWidth = area.width;
                    let updatedHeight = area.height;

                    if (area.width < 0) {
                        updatedX = area.x + area.width;
                        updatedWidth = Math.abs(area.width);
                    }
                    if (area.height < 0) {
                        updatedY = area.y + area.height;
                        updatedHeight = Math.abs(area.height);
                    }

                    updatedAreas[area.id] = {
                        x: updatedX,
                        y: updatedY,
                        width: updatedWidth,
                        height: updatedHeight,
                        nestedElementsIds: resizeAreaPropsRef.current?.selectedElements || [],
                        // now here 2
                    };
                }
            }

            if (Object.keys(updatedAreas).length) {
                dispatch(updateAreasPositions(updatedAreas));
            }

            if (Object.keys(updatedElements).length) {
                // TODO: add in the same reducer: for all payload elements find lines and update them
                dispatch(
                    updateElementsPositions(updatedElements, updatedLines)
                );
            }



            resizeAreaPropsRef.current = null;
            resizeElementPropsRef.current = null;

            // update canvas
            draw();
        };

        const handleCanvasWheel = (event) => {
            event.preventDefault();

            const mouseX = event.clientX;
            const mouseY = event.clientY;

            // // previous zoom
            // const wheel = event.deltaY < 0 ? 1 : -1;
            // const ZOOM_FACTOR = 0.15;
            // const zoom = Math.exp(wheel * ZOOM_FACTOR);
            // const newScale = scale * zoom;

            let delta = Math.max(-600, Math.min(600, event.deltaY));

            // const zoomIntensity = 0.001;
            let zoomIntensity = 0.001;

            if (scaleRef.current <= 1.1 && scaleRef.current > 0.65) {
                zoomIntensity = 0.001;
                delta = Math.max(-500, Math.min(500, event.deltaY));
            } else if (scaleRef.current <= 0.65 && scaleRef.current > 0.12) {
                // zoom in
                if (event.deltaY < 0) {
                    delta = Math.max(-600, Math.min(600, event.deltaY));
                    zoomIntensity = 0.0012;

                    // zoom out
                } else {
                    delta = Math.max(-450, Math.min(450, event.deltaY));
                    zoomIntensity = 0.0012;
                }
            } else if (scaleRef.current <= 0.12) {
                // zoom in
                if (event.deltaY < 0) {
                    delta = Math.max(-350, Math.min(350, event.deltaY));
                    zoomIntensity = 0.0025;

                    // zoom out
                } else {
                    delta = Math.max(-350, Math.min(350, event.deltaY));
                    zoomIntensity = 0.0015;
                }
            }

            const newScale = scaleRef.current * (1 - delta * zoomIntensity);
            // const newScale = scaleRef.current + event.deltaY * -0.001; // COEF should be dinamic changed on edge values(on min and max values) 			// const COEF = -0.002 or -0.0025 or -0.001;

            const clampedScale = Math.min(Math.max(newScale, 0.045), 4.05);

            const zoomFactor = clampedScale / scaleRef.current;

            const newOriginX =
                (originXYRef.current.x - mouseX) * zoomFactor + mouseX;
            const newOriginY =
                (originXYRef.current.y - mouseY) * zoomFactor + mouseY;


            scaleRef.current = clampedScale;
            originXYRef.current = { x: newOriginX, y: newOriginY };
            calculateAndSetScaleNumbers(clampedScale);

            syncScaleToReduxRef.current(clampedScale, newOriginX, newOriginY);

            if (!isScaling) {
                setIsScaling(true);
            }
            clearTimeout(scalingTimerRef.current);
            scalingTimerRef.current = setTimeout(() => {
                setIsScaling(false);
            }, 200);

            calculateVisibleElementsAndLines();



            // const currentTime = Date.now();
            // const diffTime = currentTime - (scalingRedrawCounterRef.current || 0);

            // console.log('diffTime', diffTime, !!scalingRedrawTimerRef.current)
            // if (diffTime > 20) {
            //     draw();
            // } else if (!scalingRedrawTimerRef.current) {
            //     scalingRedrawTimerRef.current = setTimeout(() => {
            //         draw();
            //     }, diffTime);
            // }
            // scalingRedrawCounterRef.current = currentTime;  // Оновлюємо час останнього виклику

            draw();

            // drawUpdateZoom(clampedScale, newOriginX, newOriginY);
        };

        const handleDocumentContextMenuClick = (event) => {
            console.log('adadfa')
            event.preventDefault();

            // inside click in canvas root elements tree
            // if (ref.current && ref.current.contains(event.target)) {

            // }

            // // business logic | CLICK MOUSE UP
            // if (isBoardClickedRef.current) {

            // 	// 3.1 static click on the board
            // 	if (!isBoardMovingStarted) {
            // 		// unfocus focused elements
            // 		const hasFocusedInsideElementChanges = !!focusedInsideElementState;
            // 		if (hasFocusedInsideElementChanges) {
            // setFocusedInsideElementState(null);

            // 			syncFocusedInsideElementToReduxRef.current(null);
            // 		}
            // 		const hasFocusedElementsChanges = !!(focusedElementsIdsRef.current.length);
            // 		if (hasFocusedElementsChanges) {
            // 			focusedElementsIdsRef.current = [];
            // 			focusedElementsRelatedPropsRef.current = null;
            // 			syncFocusedElementsToReduxRef.current([]);
            // 		}

            // 		const hasFocusedLineChanges = !!focusedLineIdRef.current;
            // 		if (hasFocusedLineChanges) {
            // 			focusedLineIdRef.current = null;
            // 			focusedLineRelatedPropsRef.current = null;
            // 			syncFocusedLineToReduxRef.current(null);
            // 		}

            // 		handleCursorClick();

            // 		// 3.2 move and click on the board (hand => drag)
            // 	} else {
            // 		// nothing. stay with focus on focused elements before dragging board
            // 	}

            // 	// 3.3 move and click on the board (mouse => selectionBox)
            // } else if (isSelectionClickedRef.current) {

            // 	// 3.3.1 static click on the board with mouse/selectionBox
            // 	if (selectionStartXYRef.current.x === selectionCurrentXYRef.current.x && selectionStartXYRef.current.y === selectionCurrentXYRef.current.y) {
            // 		// unfocus focused elements
            // 		const hasFocusedInsideElementChanges = !!focusedInsideElementState;
            // 		if (hasFocusedInsideElementChanges) {

            // 			syncFocusedInsideElementToReduxRef.current(null);
            // 		}
            // 		const hasFocusedElementsChanges = !!(focusedElementsIdsRef.current.length);
            // 		if (hasFocusedElementsChanges) {
            // 			focusedElementsIdsRef.current = [];
            // 			focusedElementsRelatedPropsRef.current = null;
            // 			syncFocusedElementsToReduxRef.current([]);
            // 		}

            // 		const hasFocusedLineChanges = !!focusedLineIdRef.current;
            // 		if (hasFocusedLineChanges) {
            // 			focusedLineIdRef.current = null;
            // 			focusedLineRelatedPropsRef.current = null;
            // 			syncFocusedLineToReduxRef.current(null);
            // 		}

            // 		handleCursorClick();

            // 	} else {
            // 		// nothing. stay with focus on focused elements which selected with selectionBox on 'mousemove'
            // 		// dispatch(changeCursor());
            // 	}

            // }

            // if (isBoardClickedRef.current) {
            // 	isBoardClickedRef.current = false;
            // }
            // if (isSelectionClickedRef.current) {
            // 	isSelectionClickedRef.current = false;
            // }

            // if (isMovingElementId) {
            // 	setIsMovingElementId(null);
            // }
            // if (isBoardMoving) {
            // 	setIsBoardMoving(false);
            // }
            // if (isBoardMovingStarted) {
            // 	setIsBoardMovingStarted(false);
            // }

            // ///////////
            // // update stored elements positions
            // const updatedElements = {};
            // for (const key in elementsRef.current) {
            // 	const element = elementsRef.current[key];
            // 	if (element.isChangedXY) {
            // 		updatedElements[element.id] = {
            // 			x: element.x,
            // 			y: element.y,

            // 			translateX: element.translateX,
            // 			translateY: element.translateY,

            // 			width: element.width,
            // 			height: element.height,
            // 		}
            // 	}
            // }

            // draw();
        };

        const handleDocumentMouseUp = async (e) => {
            console.log('mouse-up 1')


            // e.preventDefault();
            const event = e.type === 'touchstart' ? e.changedTouches[0] : e;

            sumOriginDxyRef.current = { dx: 0, dy: 0 };

            alignmentLinePropsRef.current = {};

            const mouseX =
                (event.clientX - originXYRef.current.x) / scaleRef.current;
            const mouseY =
                (event.clientY - originXYRef.current.y) / scaleRef.current;

            if (isNewTableGrabStartedRef.current) {
                const mouseX =
                    (event.clientX -
                        originXYRef.current.x -
                        160 * scaleRef.current) /
                    scaleRef.current;
                const mouseY =
                    (event.clientY -
                        originXYRef.current.y -
                        40 * scaleRef.current) /
                    scaleRef.current;

                const newElement = buildNewTable(mouseX, mouseY);
                // dispatch(addNewElement(newElement));
                if (checkTables(elementsRef.current)) {
                    dispatch(addNewElement(newElement));
                } else {
                    dispatch(enableCanvasPricingPlanLimit('tables'));
                }


                // dispatch(disableBtnNewTableClick());
                // cursor = default;
                // open Context Menu; (isEditElement = true)
                // focusedElementsIds = [new_table_id];

                isNewTableGrabStartedRef.current = false;
                return;
            } else if (isNewNoteGrabStartedRef.current) {
                const mouseX =
                    (event.clientX -
                        originXYRef.current.x -
                        160 * scaleRef.current) /
                    scaleRef.current;
                const mouseY =
                    (event.clientY -
                        originXYRef.current.y -
                        40 * scaleRef.current) /
                    scaleRef.current;

                const newElement = buildNewNote(mouseX, mouseY);
                // dispatch(addNewElement(newElement));
                if (checkNotes(elementsRef.current)) {
                    dispatch(addNewElement(newElement));
                } else {
                    dispatch(enableCanvasPricingPlanLimit('notes'));
                }

                // dispatch(disableBtnNewNoteClick());
                // cursor = default;
                // open Context Menu; (isEditElement = true)
                // focusedElementsIds = [new_table_id];

                isNewNoteGrabStartedRef.current = false;
                return;
            }

            // BUSINESS LOGIC | CURSOR

            if (isBtnExportImageEnabled) {
                let hoveredExportImageResizeKey =
                    getHoveredResizeKeyOnExportImageBox(mouseX, mouseY);

                // hover on resize points on export image box: style cursor to resize
                if (hoveredExportImageResizeKey) {
                    const cursorMap = {
                        ne: 'nesw',
                        sw: 'nesw',
                        nw: 'nwse',
                        se: 'nwse',
                    };
                    canvas.style.cursor = `${cursorMap[hoveredExportImageResizeKey]}-resize`;
                } else {
                    canvas.style.cursor = 'grab';
                }
            } else if (isBtnPenEnabled) {
                // canvas.style.cursor = 'pointer';
                canvas.style.cursor = 'none';

                // hover on resize points if focused only one element: style cursor to resize
            } else if (
                focusedElementsIdsRef.current.length === 1 ||
                focusedAreaIdRef.current
            ) {
                let [isHoverOnResize, resizeKey] = isHoverOnResizeHandle(
                    mouseX,
                    mouseY
                );
                const cursorMap = {
                    e: 'ew',
                    w: 'ew',
                    n: 'ns',
                    s: 'ns',
                    ne: 'nesw',
                    sw: 'nesw',
                    nw: 'nwse',
                    se: 'nwse',
                };
                if (isHoverOnResize) {
                    canvas.style.cursor = `${cursorMap[resizeKey]}-resize`;
                } else {
                    canvas.style.cursor = 'default';
                }
            } else {
                if (isBtnNewLineEnabled) {
                    canvas.style.cursor = 'crosshair';
                } else if (isBtnNewAreaEnabled) {
                    canvas.style.cursor = 'crosshair';
                } else if (isCursorHand) {
                    canvas.style.cursor = 'grab';
                } else {
                    canvas.style.cursor = 'default';
                }
            }


            console.log('mouse-up up')
            // check if press any button exept 'left mouse button' then return nothing
            if (event.button !== 0) {
                // right button click
                if (event.button === 2) {
                    if (isBoardClickedRef.current) {
                        isBoardClickedRef.current = false;
                    }
                    if (isSelectionClickedRef.current) {
                        isSelectionClickedRef.current = false;
                    }

                    draw();
                }

                return;
            }
        };

        // Set up initial window size
        // handleWindowResize();

        window.addEventListener('resize', handleWindowResize);
        canvasWrapRef.current.addEventListener('wheel', handleCanvasWheel);
        canvas.addEventListener('mousedown', handleCanvasMouseDown);
        document.addEventListener('mousemove', handleDocumentMouseMove);
        document.addEventListener('click', handleDocumentClick);
        document.addEventListener('contextmenu', handleDocumentContextMenuClick);
        document.addEventListener('mouseup', handleDocumentMouseUp);

        // // FOR MOUSE DEVICES:
        // circle.addEventListener('mousedown', onDown);
        // container.addEventListener('mousemove', onMove);
        // container.addEventListener('mouseup', onUp);

        // FOR TOUCH DEVICES:
        // canvas.addEventListener('touchstart', handleCanvasMouseDown);
        // document.addEventListener('touchmove', handleDocumentMouseMove);
        // document.addEventListener('touchend', handleDocumentClick);
        // // document.addEventListener('contextmenu', handleDocumentContextMenuClick);
        // document.addEventListener('touchend', handleDocumentMouseUp);

        // canvas.addEventListener('pointerdown', handleCanvasMouseDown);
        // document.addEventListener('pointermove', handleDocumentMouseMove);
        // document.addEventListener('pointerup', handleDocumentClick);
        // document.addEventListener('contextmenu', handleDocumentContextMenuClick);
        // document.addEventListener('pointerup', handleDocumentMouseUp);


        // circle.addEventListener('touchstart', onDown);
        // container.addEventListener('touchmove', onMove);
        // container.addEventListener('touchend', onUp);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
            if (canvasWrapRef.current) {
                canvasWrapRef.current.removeEventListener(
                    'wheel',
                    handleCanvasWheel
                );
            }
            if (canvas) {
                canvas.removeEventListener('mousedown', handleCanvasMouseDown);
            }
            document.removeEventListener('mousemove', handleDocumentMouseMove);
            document.removeEventListener('click', handleDocumentClick);
            document.removeEventListener(
                'contextmenu',
                handleDocumentContextMenuClick
            );
            document.removeEventListener('mouseup', handleDocumentMouseUp);
        };
    }, [
        dispatch,
        elementsRedux,
        draw,
        isScaling,
        isMovingElementId,
        isBoardMoving,
        isBoardMovingStarted,
        isCursorHand,
        isBtnNewTableClickEnabled,
        isBtnNewTableGrabEnabled,
        isBtnNewNoteClickEnabled,
        isBtnNewNoteGrabEnabled,
        isBtnNewLineEnabled,
        isBtnNewAreaEnabled,
        isBtnExportImageEnabled,
        isBtnRedGreenViewEnabled,
    ]);

    // useEffect without any dependencies, so just exucute only once at the start of the component
    useEffect(() => {
        calculateAndSetScaleNumbers(scaleRef.current);
    }, []);

    useEffect(() => {
        draw();
    }, [draw]);

    // TODO: fix theme switch: on switch theme tables are not updated. couse used variable 'const theme', instead should be used ref/state/etc
    useEffect(() => {
        draw();
    }, [themeMode]);

    useEffect(() => {
        elementsRef.current = structuredClone(elementsRedux);

        calculateVisibleElementsAndLines();

        if (
            focusedInsideElementState?.elementId &&
            focusedInsideElementState?.insideElementId
        ) {
            if (
                !elementsRedux[focusedInsideElementState?.elementId] ||
                !elementsRedux[focusedInsideElementState?.elementId].value
                    .columns[focusedInsideElementState?.insideElementId]
            ) {

                setFocusedInsideElementState(null);
                // setColumnNameState('');
                // setColumnTypeState('');

                // syncFocusedInsideElementToReduxRef.current(null);
                // draw();
            }
        }

        draw();
    }, [elementsRedux]);

    useEffect(() => {
        elementsChangesRef.current = structuredClone(elementsChangesRedux);

        if (
            elementsChangesRedux &&
            focusedInsideElementState &&
            focusedInsideElementState?.elementId &&
            focusedInsideElementState?.insideElementId
        ) {
            if (
                !elementsChangesRedux[focusedInsideElementState?.elementId] ||
                !elementsChangesRedux[focusedInsideElementState?.elementId]
                    .value.columns[focusedInsideElementState?.insideElementId]
            ) {
                setFocusedInsideElementState(null);
                // setColumnNameState('');
                // setColumnTypeState('');

                syncFocusedInsideElementToReduxRef.current(null);
                // draw();
            }
        }

        draw();
    }, [elementsChangesRedux]);

    useEffect(() => {
        linesRef.current = structuredClone(linesRedux);
        calculateVisibleElementsAndLines();
        draw();
    }, [linesRedux]);

    useEffect(() => {
        areasRef.current = structuredClone(areasRedux);
        draw();
    }, [areasRedux]);

    useEffect(() => {
        if (
            !arraysAreEqual(focusedElementsIdsRef.current, focusedElementsIds)
        ) {
            focusedElementsIdsRef.current = focusedElementsIds;
            focusedElementsRelatedPropsRef.current =
                getElementsRelatedProps(focusedElementsIds);

            draw();
        }

        setIsStateUpdated(!isStateUpdated);
    }, [focusedElementsIds]);


    useEffect(() => {
        if (!arraysAreEqual(focusedGroupElementsIdsRef.current, focusedGroupElementsIds)) {
            focusedGroupElementsIdsRef.current = focusedGroupElementsIds;
            focusedElementsRelatedPropsRef.current = getElementsRelatedProps([]);

            draw();
        }

        setIsStateUpdated(!isStateUpdated);
    }, [focusedGroupElementsIds]);
    useEffect(() => {
        if (!arraysAreEqual(focusedGroupAreasIdsRef.current, focusedGroupAreasIds)) {
            focusedGroupAreasIdsRef.current = focusedGroupAreasIds;
            draw();
        }

        setIsStateUpdated(!isStateUpdated);
    }, [focusedGroupAreasIds]);


    useEffect(() => {
        if (focusedLineIdRef.current !== focusedLineId) {
            focusedLineIdRef.current = focusedLineId;
            focusedLineRelatedPropsRef.current =
                getLineRelatedProps(focusedLineId);

            draw();
            // calculateContextMenuXY();

            // if (focusedLineId) {
            // 	setIsStateUpdated(isStateUpdated);
            // }
            setIsStateUpdated(!isStateUpdated);
        }
    }, [focusedLineId]);

    useEffect(() => {
        if (focusedAreaIdRef.current !== focusedAreaId) {
            focusedAreaIdRef.current = focusedAreaId;

            draw();
            setIsStateUpdated(!isStateUpdated);
        }
    }, [focusedAreaId]);

    // TODO: get selector for focusedLine
    // + update state on focusedLine changes
    // + use focusedLine instead focusedLineId

    // useEffect(() => {
    // 	if (focusedLineIdRef.current !== focusedLineId) {
    // 		focusedLineIdRef.current = focusedLineId;
    // 		focusedLineRelatedPropsRef.current = getLineRelatedProps(focusedLineId);

    // 		draw();
    // 		// calculateContextMenuXY();

    // 		// if (focusedLineId) {
    // 		// 	setIsStateUpdated(isStateUpdated);
    // 		// }
    // 		setIsStateUpdated(isStateUpdated);

    // 	}
    // }, [focusedLine]);

    useEffect(() => {
        // if (focusedInsideElementState?.elementId !== focusedInsideElementRedux?.elementId ||
        // 	focusedInsideElementState?.insideElementId !== focusedInsideElementRedux?.insideElementId
        // ) {
        // TODO: fix it
        setIsFocusedInsideElementChanged(!isFocusedInsideElementChanged);

        // TODO:: NEED TO CHECK INSIDE ELEMENT TYPE BEFORE SET STATE, REDUX HAS EMPTY VALUE

        setFocusedInsideElementState(focusedInsideElementRedux);
        setColumnNameState(
            focusedInsideElementRedux?.insideElement?.name || ''
        );
        setColumnTypeState(
            focusedInsideElementRedux?.insideElement?.type || ''
        );
        setColumnState(focusedInsideElementRedux);

        // }
        draw();
    }, [focusedInsideElementRedux]);

    const updateLiveElementsWithRedux = () => {
        const updatedElements = {};
        for (const key in elementsRef.current) {
            const element = elementsRef.current[key];

            if (element.isChangedXY) {
                updatedElements[element.id] = {
                    x: element.x,
                    y: element.y,

                    translateX: element.translateX,
                    translateY: element.translateY,
                };
            }
        }
        dispatch(updateLiveElementsPositions(updatedElements));
    };

    useEffect(() => {
        const interval = setInterval(() => {
            if (elementsHasChanges) {
                Math.random() === 12 && updateLiveElementsWithRedux();
                setElementsHasChanges(false);
            }
        }, 500);

        return () => clearInterval(interval);
    }, [elementsHasChanges]);

    // const handleOriginXYorScaleChange = (scaleValue, originXYValue) => {

    // 	scaleValue,
    // 	if (scaleValue !== scaleRef.current) {
    // 		scaleRef.current = scaleValue;
    // 		calculateAndSetScaleNumbers(scaleValue);

    // 		setIsScaling(true);
    // 		clearTimeout(scalingTimerRef.current);
    // 		scalingTimerRef.current = setTimeout(() => { setIsScaling(false); }, 200);

    // 		draw();
    // 	}
    // };

    const handleScaleChange = (scaleValue) => {
        if (scaleValue !== scaleRef.current) {
            scaleRef.current = scaleValue;
            calculateAndSetScaleNumbers(scaleValue);

            setIsScaling(true);
            clearTimeout(scalingTimerRef.current);
            scalingTimerRef.current = setTimeout(() => {
                setIsScaling(false);
            }, 200);

            draw();
        }
    };
    useEffect(() => {
        handleScaleChange(scale);
    }, [scale]);

    const handleOriginXYChange = (originXYValue) => {
        if (
            originXYValue.x !== originXYRef.current.x ||
            originXYValue.y !== originXYRef.current.y
        ) {
            originXYRef.current = { x: originXYValue.x, y: originXYValue.y };

            calculateVisibleElementsAndLines();
            draw();
        }
    };
    useEffect(() => {
        handleOriginXYChange(originXY);
    }, [originXY]);

    useEffect(() => {
        if (isBtnExportImageEnabled) {
            // calculate initial props for export image box
            // exportImageBoxPropsRef.current = getInitialValuesForExportImageBox();
            setInitialValuesForExportImageBox();
        } else {
            // clear props for export image box
        }

        draw();
    }, [isBtnExportImageEnabled]);

    useEffect(() => {
        if (isHistoryClicked && !isHistoryClickedState) {
            setIsHistoryClickedState(true);
        }

        if (!isHistoryClicked && isHistoryClickedState) {
            setIsHistoryClickedState(false);
        }
    }, [isHistoryClicked]);

    // const handleExportByCoordinates = (exportCoordinates) => {
    // 	if (exportCoordinates) {
    // 		//
    // 	}
    // };

    // useEffect(() => {
    // 	handleExportByCoordinates(exportCoordinates);
    // }, [exportCoordinates]);

    useEffect(() => {
        // Перевіряємо, чи шрифти вже завантажені
        if (document.fonts.status === 'loaded') {
            //   setFontsLoaded(true);
            draw();
        } else {
            // Додаємо подію на завантаження шрифтів
            document.fonts.ready.then(() => {
                draw();
                // setFontsLoaded(true); // Оновлюємо стан після завантаження шрифтів
            });
        }
    }, []);
    useEffect(() => {
        if (!isBtnNewLineEnabled) {
            lineFromRef.current = null;
            lineToRef.current = null;
        }
        draw();
    }, [isBtnNewLineEnabled]);

    useEffect(() => {
        const canvas = ref.current;

        // BUSINESS LOGIC | CURSOR
        if (clickedPointRef.current || isBtnNewLineEnabled) {
            canvas.style.cursor = 'crosshair';
        } else if (isBtnNewAreaEnabled) {
            canvas.style.cursor = 'crosshair';
        } else if (isBtnExportImageEnabled) {
            canvas.style.cursor = 'grab';
        } else if (isBtnPenEnabled) {
            // canvas.style.cursor = 'pointer';
            canvas.style.cursor = 'none';
        } else if (isBtnNewTableClickEnabled) {
            canvas.style.cursor = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB8AAAAfCAMAAAAocOYLAAAAVFBMVEUAAAD///////////////////////////////////////////////////////////////////////////////////////////////////////////8wXzyWAAAAG3RSTlMACAkRGFVmdXZ3eIiZpcDJzNfd5Ofs7e/3+vucN3j2AAAAAWJLR0QbAmDUpAAAAH5JREFUKM/NkzcOgEAMBM2Rcw53+/9/UoBdmSABElOtNIW9kk30Ap6v4bFOHTRcsmmja8AZIiIKcESw+6XRWMS36tbtb/wQaQzir/qPOVOhkjwq8zNkp/t94+eCqVFLnm/360OmRCm5/0k/42zHTJgkW7vdLyX6gdv45v88YQXHUR7h7PdO0gAAAABJRU5ErkJggg==") 14 16, auto`;
        } else if (isCursorHand) {
            canvas.style.cursor = 'grab';
        } else {
            canvas.style.cursor = 'default';
        }

        draw();
    }, [
        isCursorHand,
        isBtnNewLineEnabled,
        isBtnPenEnabled,
        isBtnNewAreaEnabled,
        isBtnExportImageEnabled,
        isBtnNewTableClickEnabled,
    ]);

    useEffect(() => {
        if (isBtnExportImageClickEnabled && isBtnExportImageEnabled) {
            setTimeout(() => {
                // downloadCanvasImage();
                downloadCanvasImageNew();
                dispatch(disableExportImageClick());

                exportImageBoxPropsRef.current = {
                    isPressed: false,
                    x: 0,
                    y: 0,
                    boxWidth: 0,
                    boxHeight: 0,
                };
                isExportImageClickedRef.current = false;

            }, 150);
        }

    }, [isBtnExportImageClickEnabled, isBtnExportImageEnabled]);

    async function downloadCanvasImage() {
        const canvas = ref.current;

        const prevScale = scaleRef.current;
        const prevOriginXY = { x: originXYRef.current.x, y: originXYRef.current.y };
        const prevCanvasWH = { w: canvas.width, h: canvas.height };

        const imageX = exportImageBoxPropsRef.current.clientX;
        const imageY = exportImageBoxPropsRef.current.clientY;
        const imageWidth = exportImageBoxPropsRef.current.clientBoxWidth / scaleRef.current;
        const imageHeight = exportImageBoxPropsRef.current.clientBoxHeight / scaleRef.current;

        const newScale = 1;
        const zoomFactor = newScale / scaleRef.current;
        let newOriginX = (originXYRef.current.x - imageX) * zoomFactor;
        let newOriginY = (originXYRef.current.y - imageY) * zoomFactor;

        scaleRef.current = newScale;
        originXYRef.current = { x: newOriginX, y: newOriginY };
        calculateAndSetScaleNumbers(newScale);

        syncScaleToReduxRef.current(newScale, newOriginX, newOriginY);

        canvas.width = imageWidth * newScale;
        canvas.height = imageHeight * newScale;

        isExportImageClickedRef.current = true;
        draw();

        const targetCanvas = document.createElement('canvas');
        const targetContext = targetCanvas.getContext('2d');

        targetCanvas.width = imageWidth * newScale;
        targetCanvas.height = imageHeight * newScale;
        targetContext.fillStyle = '#3a495a'; // '#39424c'
        targetContext.fillRect(0, 0, targetCanvas.width, targetCanvas.height);

        targetContext.imageSmoothingEnabled = true;
        targetContext.imageSmoothingQuality = 'high';
        // targetContext.drawImage(ref.current, 0, 0, imageWidth, imageHeight, 0, 0, imageWidth * scaleFactor, imageHeight * scaleFactor);
        targetContext.setTransform(
            scaleRef.current,
            0,
            0,
            scaleRef.current,
            originXYRef.current.x,
            originXYRef.current.y
        );
        // targetContext.drawImage(ref.current, imageX, imageY, imageWidth, imageHeight, -350, -300, imageWidth, imageHeight);

        // targetContext.drawImage(canvas, imageX, imageY, imageWidth, imageHeight, newOriginX, newOriginY, imageWidth, imageHeight);
        targetContext.drawImage(
            canvas,
            -newOriginX / scaleRef.current,
            -newOriginY / scaleRef.current
        );
        // targetContext.drawImage(canvas, -newOriginX, -newOriginY);

        // targetContext.drawImage(canvas, imageX, imageY, imageWidth, imageHeight, newOriginX, newOriginY, imageWidth, imageHeight);

        // targetContext.setTransform(scale, 0, 0, scale, -originX * scale, -originY * scale);
        // targetContext.drawImage(ref.current, offsetX, offsetY, imageWidth / scale, imageHeight / scale, 0, 0, imageWidth, imageHeight);

        const blob = await new Promise((resolve) =>
            targetCanvas.toBlob((blob) => resolve(blob), 'image/png', 1)
        );

        const fileName = 'dbdesign_screen.png';
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);



        // go to back
        scaleRef.current = prevScale;
        originXYRef.current = { x: prevOriginXY.x, y: prevOriginXY.y };
        calculateAndSetScaleNumbers(newScale);

        syncScaleToReduxRef.current(prevScale, prevOriginXY.x, prevOriginXY.y);

        canvas.width = prevCanvasWH.w;
        canvas.height = prevCanvasWH.h;

        exportImageBoxPropsRef.current = {
            isPressed: false,
            x: 0,
            y: 0,
            boxWidth: 0,
            boxHeight: 0,
        };
        isExportImageClickedRef.current = false;
        draw();
    }

    async function downloadCanvasImageNew() {
        const canvas = ref.current;

        const prevScale = scaleRef.current;
        const prevOriginXY = { x: originXYRef.current.x, y: originXYRef.current.y };
        const prevCanvasWH = { w: canvas.width, h: canvas.height };

        const imageX = exportImageBoxPropsRef.current.clientX;
        const imageY = exportImageBoxPropsRef.current.clientY;
        let imageWidth = exportImageBoxPropsRef.current.clientBoxWidth / scaleRef.current;
        let imageHeight = exportImageBoxPropsRef.current.clientBoxHeight / scaleRef.current;

        let newScale = 1;
        const maxImageWidth = 4800;
        const maxImageHeight = 2500;
        if (imageWidth * newScale > maxImageWidth) {
            newScale = maxImageWidth / (imageWidth);
        }
        if (imageHeight * newScale > maxImageHeight) {
            newScale = maxImageHeight / (imageHeight);
        }

        const zoomFactor = newScale / scaleRef.current;
        let newOriginX = (originXYRef.current.x - imageX) * zoomFactor;
        let newOriginY = (originXYRef.current.y - imageY) * zoomFactor;

        scaleRef.current = newScale;
        originXYRef.current = { x: newOriginX, y: newOriginY };
        calculateAndSetScaleNumbers(newScale);

        syncScaleToReduxRef.current(newScale, newOriginX, newOriginY);

        canvas.width = imageWidth * newScale;
        canvas.height = imageHeight * newScale;


        let targetCanvas = document.createElement('canvas');
        let targetContext = targetCanvas.getContext('2d');

        targetCanvas.width = imageWidth * newScale;
        targetCanvas.height = imageHeight * newScale;
        targetContext.fillStyle = '#3a495a'; // '#39424c'
        targetContext.fillRect(0, 0, targetCanvas.width, targetCanvas.height);

        targetContext.imageSmoothingEnabled = true;
        targetContext.imageSmoothingQuality = 'high';
        targetContext.setTransform(
            scaleRef.current,
            0,
            0,
            scaleRef.current,
            originXYRef.current.x,
            originXYRef.current.y
        );

        isExportImageClickedRef.current = true;
        draw(false, targetCanvas);

        const blob = await new Promise((resolve) =>
            targetCanvas.toBlob((blob) => resolve(blob), 'image/png', 1)
        );

        const fileName = 'dbdesign_screen.png';
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);


        // go to back
        scaleRef.current = prevScale;
        originXYRef.current = { x: prevOriginXY.x, y: prevOriginXY.y };
        calculateAndSetScaleNumbers(newScale);

        syncScaleToReduxRef.current(prevScale, prevOriginXY.x, prevOriginXY.y);

        canvas.width = prevCanvasWH.w;
        canvas.height = prevCanvasWH.h;

        // delete target canvas + context
        targetContext.clearRect(0, 0, targetCanvas.width, targetCanvas.height);
        targetCanvas.width = 0;
        targetCanvas.height = 0;
        targetCanvas = null;

        exportImageBoxPropsRef.current = {
            isPressed: false,
            x: 0,
            y: 0,
            boxWidth: 0,
            boxHeight: 0,
        };
        isExportImageClickedRef.current = false;
        draw();
    }

    // useEffect(() => {
    //     const canvas = ref.current;

    //     if (isBtnNewTableClickEnabled) {
    //         canvas.style.cursor = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB8AAAAfCAMAAAAocOYLAAAAVFBMVEUAAAD///////////////////////////////////////////////////////////////////////////////////////////////////////////8wXzyWAAAAG3RSTlMACAkRGFVmdXZ3eIiZpcDJzNfd5Ofs7e/3+vucN3j2AAAAAWJLR0QbAmDUpAAAAH5JREFUKM/NkzcOgEAMBM2Rcw53+/9/UoBdmSABElOtNIW9kk30Ap6v4bFOHTRcsmmja8AZIiIKcESw+6XRWMS36tbtb/wQaQzir/qPOVOhkjwq8zNkp/t94+eCqVFLnm/360OmRCm5/0k/42zHTJgkW7vdLyX6gdv45v88YQXHUR7h7PdO0gAAAABJRU5ErkJggg==") 14 16, auto`;
    //     }
    // }, [isBtnNewTableClickEnabled]);


    function getContextMenuXY() {
        // 1. no focused elements
        if (!focusedElementsIdsRef.current.length) {
            // 1.1. focused line
            if (focusedLineIdRef.current && linesRef.current && linesRef.current[focusedLineIdRef.current]) {
                // const lineObject = linesRef.current[focusedLineIdRef.current];
                // let { value: { path } } = lineObject;

                // if (!path) {
                //     const { value: { fromElementId, fromInsideElementId, fromRelationType, toElementId, toInsideElementId, toRelationType } } = lineObject;

                //     const fromElement = elementsRef.current[fromElementId];
                //     const toElement = elementsRef.current[toElementId];

                //     if (!fromElement || !toElement) {
                //         return { elementCenterX: 0, elementY: 0 };
                //     }

                //     const fromInsideElement = fromElement.value.columns[fromInsideElementId];
                //     const toInsideElement = toElement.value.columns[toInsideElementId];

                //     if (!fromInsideElement || !toInsideElement) {
                //         return { elementCenterX: 0, elementY: 0 };
                //     }

                //     const fromElementColumnsKeys = Object.keys(fromElement.value.columns);
                //     let fromInsideElementIndex = -1;
                //     fromElementColumnsKeys.forEach((key, index) => {
                //         if (fromInsideElementId === key) {
                //             fromInsideElementIndex = index;
                //         }
                //     });

                //     const toElementColumnsKeys = Object.keys(toElement.value.columns);
                //     let toInsideElementIndex = -1;
                //     toElementColumnsKeys.forEach((key, index) => {
                //         if (toInsideElementId === key) {
                //             toInsideElementIndex = index;
                //         }
                //     });

                //     const lineData = {
                //         mouseX: null,
                //         mouseY: null,

                //         fromElementType: fromElement.type,
                //         fromElementWidth: fromElement.width,
                //         fromElementX: fromElement.x,
                //         fromElementY: fromElement.y,
                //         fromInsideElementIndex,
                //         fromRelationType,

                //         toElementType: toElement.type,
                //         toElementWidth: toElement.width,
                //         toElementX: toElement.x,
                //         toElementY: toElement.y,
                //         toInsideElementIndex,
                //         toRelationType,
                //     };

                //     path = _buildLinePath(lineData);
                // }

                // const [lines, points, bounds] = path;

                // const elementX = bounds.minX * scaleRef.current + originXYRef.current.x;
                // const elementWidth = (bounds.maxX - bounds.minX) * scaleRef.current;
                // const elementHeight = (bounds.maxY - bounds.minY) * scaleRef.current;

                // const elementCenterX = ((bounds.minX + bounds.maxX) / 2) * scaleRef.current + originXYRef.current.x;
                // const elementY = (bounds.minY - scaleNumbersRef.current.outlineOffset) * scaleRef.current + originXYRef.current.y;

                // return {
                //     elementCenterX,
                //     elementX,
                //     elementY,
                //     elementWidth,
                //     elementHeight,
                // };

                const lineObject = linesRef.current[focusedLineIdRef.current];
                let { value: { fromElementId, toElementId } } = lineObject;

                const fromElement = elementsRef.current[fromElementId];
                const toElement = elementsRef.current[toElementId];

                if (!fromElement || !toElement) {
                    return { elementCenterX: 0, elementY: 0 };
                }

                let minElementsX = Math.min(fromElement.x, toElement.x);
                let maxElementsX = Math.max(fromElement.x + fromElement.width, toElement.x + toElement.width);
                let minElementsY = Math.min(fromElement.y, toElement.y);
                let maxElementsY = Math.max(fromElement.y + fromElement.height, toElement.y + toElement.height);

                const elementX = minElementsX * scaleRef.current + originXYRef.current.x;
                const elementWidth = (maxElementsX - minElementsX) * scaleRef.current;
                const elementHeight = (maxElementsY - minElementsY) * scaleRef.current;

                const elementCenterX = ((minElementsX + maxElementsX) / 2) * scaleRef.current + originXYRef.current.x;
                const elementY = (minElementsY - scaleNumbersRef.current.outlineOffset) * scaleRef.current + originXYRef.current.y;

                return {
                    elementCenterX,
                    elementX,
                    elementY,
                    elementWidth,
                    elementHeight,
                };

                // 1.2. focused area
            } else if (focusedAreaIdRef.current && areasRef.current && areasRef.current[focusedAreaIdRef.current]) {
                const area = areasRef.current[focusedAreaIdRef.current];

                const elementX = area.x * scaleRef.current + originXYRef.current.x;
                const elementWidth = area.width * scaleRef.current;
                const elementHeight = area.height * scaleRef.current;

                const elementCenterX = (area.x + area.width / 2) * scaleRef.current + originXYRef.current.x;
                const elementY = (area.y - scaleNumbersRef.current.outlineOffset) * scaleRef.current + originXYRef.current.y;

                return {
                    elementCenterX,
                    elementX,
                    elementY,
                    elementWidth,
                    elementHeight,
                };

                // 1.3 focused group
            } else if (focusedGroupAreasIdsRef.current?.length) {
                let minElementsX = areasRef.current[focusedGroupAreasIdsRef.current[0]]?.x;
                let maxElementsX = areasRef.current[focusedGroupAreasIdsRef.current[0]].x + areasRef.current[focusedGroupAreasIdsRef.current[0]].width;
                let minElementsY = areasRef.current[focusedGroupAreasIdsRef.current[0]].y;
                let maxElementsY = areasRef.current[focusedGroupAreasIdsRef.current[0]].y + areasRef.current[focusedGroupAreasIdsRef.current[0]].height;

                focusedGroupAreasIdsRef.current.forEach((areaId) => {
                    const minX = areasRef.current[areaId].x;
                    const maxX = areasRef.current[areaId].x + areasRef.current[areaId].width;
                    const minY = areasRef.current[areaId].y;
                    const maxY = areasRef.current[areaId].y + areasRef.current[areaId].height;

                    if (minX < minElementsX) {
                        minElementsX = minX;
                    }
                    if (maxX > maxElementsX) {
                        maxElementsX = maxX;
                    }
                    if (minY < minElementsY) {
                        minElementsY = minY;
                    }
                    if (maxY > maxElementsY) {
                        maxElementsY = maxY;
                    }
                });

                if (focusedGroupElementsIdsRef.current?.length) {
                    focusedGroupElementsIdsRef.current.forEach((elementId) => {
                        const minX = elementsRef.current[elementId].x;
                        const maxX = elementsRef.current[elementId].x + elementsRef.current[elementId].width;
                        const minY = elementsRef.current[elementId].y;
                        const maxY = elementsRef.current[elementId].y + elementsRef.current[elementId].height;

                        if (minX < minElementsX) {
                            minElementsX = minX;
                        }
                        if (maxX > maxElementsX) {
                            maxElementsX = maxX;
                        }
                        if (minY < minElementsY) {
                            minElementsY = minY;
                        }
                        if (maxY > maxElementsY) {
                            maxElementsY = maxY;
                        }
                    });
                }




                const elementX = minElementsX * scaleRef.current + originXYRef.current.x;
                const elementWidth = (maxElementsX - minElementsX) * scaleRef.current;
                const elementHeight = (maxElementsY - minElementsY) * scaleRef.current;

                const elementCenterX = ((minElementsX + maxElementsX) / 2) * scaleRef.current + originXYRef.current.x;
                const elementY = (minElementsY - scaleNumbersRef.current.outlineOffset) * scaleRef.current + originXYRef.current.y;

                return {
                    elementCenterX,
                    elementX,
                    elementY,
                    elementWidth,
                    elementHeight,
                };

                // 1.4. no focused line and no focused area and focused elements
            } else {
                return { elementCenterX: 0, elementY: 0 };
            }

            // 2. one focused elements
        } else if (focusedElementsIdsRef.current.length === 1) {
            const element = elementsRef.current[focusedElementsIdsRef.current[0]];

            const elementX = element.x * scaleRef.current + originXYRef.current.x;
            const elementWidth = element.width * scaleRef.current;
            const elementHeight = element.height * scaleRef.current;

            const elementCenterX = (element.x + element.width / 2) * scaleRef.current + originXYRef.current.x;
            const elementY = (element.y - scaleNumbersRef.current.outlineOffset) * scaleRef.current + originXYRef.current.y;

            return {
                elementCenterX,
                elementX,
                elementY,
                elementWidth,
                elementHeight,
            };

            // 3. many focused elements
        } else {
            let minElementsX = elementsRef.current[focusedElementsIdsRef.current[0]].x;
            let maxElementsX = elementsRef.current[focusedElementsIdsRef.current[0]].x + elementsRef.current[focusedElementsIdsRef.current[0]].width;
            let minElementsY = elementsRef.current[focusedElementsIdsRef.current[0]].y;
            let maxElementsY = elementsRef.current[focusedElementsIdsRef.current[0]].y + elementsRef.current[focusedElementsIdsRef.current[0]].height;

            focusedElementsIdsRef.current.forEach((elementId) => {
                const minX = elementsRef.current[elementId].x;
                const maxX = elementsRef.current[elementId].x + elementsRef.current[elementId].width;
                const minY = elementsRef.current[elementId].y;
                const maxY = elementsRef.current[elementId].y + elementsRef.current[elementId].height;

                if (minX < minElementsX) {
                    minElementsX = minX;
                }
                if (maxX > maxElementsX) {
                    maxElementsX = maxX;
                }
                if (minY < minElementsY) {
                    minElementsY = minY;
                }
                if (maxY > maxElementsY) {
                    maxElementsY = maxY;
                }
            });

            const elementX = minElementsX * scaleRef.current + originXYRef.current.x;
            const elementWidth = (maxElementsX - minElementsX) * scaleRef.current;
            const elementHeight = (maxElementsY - minElementsY) * scaleRef.current;

            const elementCenterX = ((minElementsX + maxElementsX) / 2) * scaleRef.current + originXYRef.current.x;
            const elementY = (minElementsY - scaleNumbersRef.current.outlineOffset) * scaleRef.current + originXYRef.current.y;

            return {
                elementCenterX,
                elementX,
                elementY,
                elementWidth,
                elementHeight,
            };
        }
    }
    const { elementCenterX, elementY, elementX, elementWidth, elementHeight } = getContextMenuXY();


    const [isLoader, setIsLoader] = useState(false);

    useEffect(() => {
        setIsLoader(true);
        setTimeout(() => { setIsLoader(false) }, 800);
    }, [])

    return (
        <div className="CanvasWrapper">
            <div ref={canvasWrapRef}>
                {isLoader ? <LoaderBar /> : ''}
                <canvas
                    className={`${isLoader ? 'hide-canvas' : ''}`}
                    id="canvas"
                    ref={ref}
                    width={window.innerWidth}
                    height={window.innerHeight}
                />
                {/* <canvas
                    className={`${isLoader ? 'my-class' : ''}`}
                    id="canvas-i" // export-image-box-canvas
                    ref={exportImgCanvasRef}
                    width={window.innerWidth}
                    height={window.innerHeight}
                />
 */}
                {
                    // isDesktop &&
                    scaleRef.current > 0.15 &&
                    !isStartedLineState &&
                    !isBoardMovingStarted &&
                    !isBoardMoving &&
                    !isScaling &&
                    !isMovingElementId &&
                    !isElementResizing &&
                    !isBtnNewAreaEnabled &&

                    // !hoveredElementIdHeaderRef.current &&
                    // focusedElementsIdsRef.current.length &&
                    focusedInsideElementState &&
                    focusedInsideElementState.insideElementType === 'column' &&
                    viewMode === 'full' && (
                        // isOpenEditableInsideElement &&
                        <EditableTableColumn
                            tableX={elementsRef.current[focusedElementsIdsRef.current[0]]?.x}
                            tableY={elementsRef.current[focusedElementsIdsRef.current[0]]?.y}
                            tableWidth={elementsRef.current[focusedElementsIdsRef.current[0]].width}
                            scale={scaleRef.current}
                            originXY={originXYRef.current}
                            elementId={focusedInsideElementState?.elementId}
                            insideElementId={focusedInsideElementState?.insideElementId}
                            insideElementIndex={focusedInsideElementState?.insideElementIndex || 0}
                            name={columnNameState}
                            type={columnTypeState}

                            isAutoIncrement={focusedInsideElementState?.insideElement?.isAutoIncrement}
                            isEnum={focusedInsideElementState?.insideElement?.isEnum}
                            isNN={focusedInsideElementState?.insideElement?.isNN}
                            isDefaultValue={!!focusedInsideElementState?.insideElement?.defaultValue}
                            isGeneratedValue={!!focusedInsideElementState?.insideElement?.generatedValue}

                            isPK={focusedInsideElementState?.insideElement?.isPK}
                            isFK={focusedInsideElementState?.insideElement?.isFK}
                            handleNameChange={(value, newWidth) => {
                                setColumnNameState(value);
                                // syncEditableTableColumnNameToReduxRef.current(columnState.elementId, columnState.insideElementId, value, elementsRef.current[columnState.elementId].width);
                                const width = newWidth || elementsRef.current[columnState.elementId].width;

                                if (newWidth && columnState.elementId && elementsRef.current[columnState.elementId]) {
                                    elementsRef.current[columnState.elementId].width = newWidth;
                                }

                                // TODO: lines should be updated in redux too
                                const updatedLinesIds = updateLinesByElementId(columnState.elementId);
                                syncEditableTableColumnNameToReduxRef.current(columnState.elementId, columnState.insideElementId, value, width);
                            }}
                            handleTypeChange={(value, newWidth) => {
                                setColumnTypeState(value);

                                const width = newWidth || elementsRef.current[columnState.elementId].width;

                                if (newWidth && columnState.elementId && elementsRef.current[columnState.elementId]) {
                                    elementsRef.current[columnState.elementId].width = newWidth;
                                }

                                // TODO: lines should be updated in redux too
                                const updatedLinesIds = updateLinesByElementId(columnState.elementId);
                                syncEditableTableColumnTypeToReduxRef.current(columnState.elementId, columnState.insideElementId, value, width);
                            }}
                            handleFocusOnColumnBelow={(elementId, insideElementId) => {
                                dispatch(focusOnColumnBelow(elementId, insideElementId));
                            }}
                            handleFocusOnColumnAbove={(elementId, insideElementId) => {
                                dispatch(focusOnColumnAbove(elementId, insideElementId));
                            }}
                            handleAddNewColumnBelow={(elementId, insideElementId, isMoveToNextInsideElement) => {
                                // dispatch(addNewColumnBelow(elementId, insideElementId, isMoveToNextInsideElement));
                                if (checkColumns(elementsRef.current, elementId)) {
                                    dispatch(addNewColumnBelow(elementId, insideElementId, isMoveToNextInsideElement));
                                } else {
                                    dispatch(enableCanvasPricingPlanLimit('columns'));
                                }
                            }}
                            handleMoveColumnBelow={(elementId, insideElementId) => {
                                dispatch(moveColumnBelow(elementId, insideElementId));
                            }}
                            handleMoveColumnAbove={(elementId, insideElementId) => {
                                dispatch(moveColumnAbove(elementId, insideElementId));
                            }}
                            handleDeleteColumn={(elementId, insideElementId) => {
                                dispatch(deleteColumn(elementId, insideElementId));
                            }}
                            onNeedResize={(newWidth) => {

                                // if (focusedInsideElementState?.elementId && elementsRef.current[focusedInsideElementState?.elementId]?.width) {
                                //     elementsRef.current[focusedInsideElementState.elementId].width = newWidth;
                                // }


                                // dispatch(updateElementsPositions({
                                //     [focusedInsideElementState?.elementId]: {
                                //         width: newWidth,
                                //     }
                                // }));
                            }}

                        />
                    )}

                {
                    // isDesktop &&
                    scaleRef.current > 0.15 &&
                    !isStartedLineState &&
                    !isBoardMovingStarted &&
                    !isBoardMoving &&
                    !isScaling &&
                    !isMovingElementId &&
                    !isElementResizing &&
                    !isBtnNewAreaEnabled &&

                    // focusedElementsIdsRef.current.length &&
                    focusedInsideElementState &&
                    focusedInsideElementState.insideElementType === 'header' &&
                    viewMode === 'full' && (
                        // Math.random() === 12 &&
                        // isOpenEditableInsideElement &&
                        <EditableTableHeader
                            tableX={elementsRef.current[focusedElementsIdsRef.current[0]]?.x}
                            tableY={elementsRef.current[focusedElementsIdsRef.current[0]]?.y}
                            tableWidth={elementsRef.current[focusedElementsIdsRef.current[0]]?.width}
                            scale={scaleRef.current}
                            originXY={originXYRef.current}
                            elementId={focusedInsideElementState?.elementId}
                            insideElementId={focusedInsideElementState?.insideElementId}
                            name={columnNameState}
                            handleHeaderNameChange={(value) => {
                                setColumnNameState(value);
                                syncEditableTableHeaderNameToReduxRef.current(columnState.elementId, value);
                            }}
                            handleAddNewFirstColumn={(elementId, isMoveToNextInsideElement) => {
                                // this handle is not emits
                                // dispatch(addNewFirstColumn(elementId, isMoveToNextInsideElement));
                                if (checkColumns(elementsRef.current, elementId)) {
                                    dispatch(addNewFirstColumn(elementId, isMoveToNextInsideElement));
                                } else {
                                    dispatch(enableCanvasPricingPlanLimit('columns'));
                                }
                            }}
                            handleFocusOnFirstColumn={(elementId) => {
                                dispatch(focusOnFirstColumn(elementId));
                            }}
                            handleDeleteElements={() => {
                                dispatch(deleteElements());
                            }}
                        />
                    )}
            </div>
            {
                isDesktop &&
                // scaleRef.current > 0.15 &&
                // is not export enabled
                !isBtnRedGreenViewEnabled &&
                !isBtnExportImageEnabled &&
                !isBoardMoving &&
                !isScaling &&
                !isMovingElementId &&
                (focusedElementsIdsRef.current.length ||
                    focusedLineIdRef.current ||
                    focusedAreaIdRef.current ||
                    focusedGroupElementsIdsRef.current.length ||
                    focusedGroupAreasIdsRef.current.length
                ) &&
                viewMode === 'full' &&
                !isHistoryClickedState && (
                    !isElementResizing &&
                    <ContextMenu
                        // contextMenu width 472, so divide on 2 = 236
                        // contextMenu width 659, so divide on 2 = 327
                        // left={elementCenterX - 410}
                        // left={elementCenterX - 327}
                        left={elementCenterX - 195}
                        // top={elementY - 120}
                        top={elementY - 115}

                        elementCenterX={elementCenterX}
                        elementX={elementX}
                        elementY={elementY}
                        elementWidth={elementWidth}
                        elementHeight={elementHeight}
                        isFocusedInsideElementEmpty={!focusedInsideElementState}
                    // top={elementY - 95}
                    />
                )
            }
            {/* <div
			style={{

				position: 'absolute',
				left: '10px',
				top: '10px',
				zIndex: '100',
				fontSize: '60px'
			}}
			>{scaleRef.current}</div> */}
        </div>
    );
});

export default Canvas;
